import { LabourPanel } from '@/components/LabourPanel';
import { OrdersPanel } from '@/components/OrdersPanel';
import { WorkTruckMark, WORK_MARK_SPIN_SECONDS } from '@/components/WorkTruckMark';
/* ⭐ The static half of the work-state glyph family — see `PauseMark.tsx`'s header.
   ⛔ NOT a local copy of two bars: one shape, every surface, or the floor learns two. */
import { PauseMark } from '@/components/PauseMark';
import { DirectionTag } from '@/components/DirectionTag';
import { CommunicationsPanel } from '@/components/CommunicationsPanel';
import { HistoryPanel } from '@/components/HistoryPanel';
/* ⚠️ TYPE-ONLY, so it is erased at compile time and cannot create a runtime
   cycle. `Calendar` imports both of these files; neither imports the other. */
import type { DropCheckInOnAppointmentEvent } from '@/components/GanttCalendar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    getCheckIns, attachCheckIn, voidCheckIn, editCheckIn,
    getRailPanels, sendCheckInText, type RailPanelDto, type OrdersPanelRowDto,
} from '@/api/client';
import type { CheckInDto, VoidedCheckInDto, AppointmentDto, SuggestLoadDto } from '@/api/types';
import {
    CHECKIN_DND_MIME, CHECKIN_FROM_APPT_MIME,
    isPaperworkDrag, readPaperworkDrag, type PaperworkDragPayload, paperworkDragInProgress,
    type WmsOrderDragKey,
} from '@/lib/dnd';
import { ConfirmDialog } from '@/components/ConfirmDialog';
/* ⭐ The shared shell — it already caps height, scrolls its body and pins the
   close button outside that scroller (see ui/dialog.tsx, 2026-09-09). */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ContextMenu, type MenuItem } from '@/components/ContextMenu';
import { LicenceScanDialog } from '@/components/LicenceScanDialog';
/* ⭐ #73 — the number a driver owed at the gate, typed at the desk. It opens
   from THREE places (the alert sentence, the plate icon, the (…) menu) and all
   three call `setVehicleFor` — one destination, no fourth definition. */
import { VehicleNumberDialog } from '@/components/VehicleNumberDialog';
/* ⭐ ITS TWIN, 2026-09-24. Warning 3 opened three fields and warning 4 opened a
   nine-field arrival editor; two debts on one card behaved differently. Pete:
   *"Either we do a separate entry for the PO, BOL and Reference number just like the
   first one (which I think is good)."* */
import { PaperworkNumberDialog } from '@/components/PaperworkNumberDialog';
import { MatchArrivalDialog } from '@/components/MatchArrivalDialog';
import { SuggestedLoadDialog } from '@/components/SuggestedLoadDialog';
import { SubjectChips } from '@/components/SubjectChips';
import { SUBJECT_WORDS, panelTitle, panelHeadline, panelHeadlineParts, type PanelSubject } from '@/lib/panelSubject';
import {
    AlertTriangle,
    Ban,
    /* ⭐ The party card's "nothing filed" glyph (decision 188 increment 3). */
    FileText, ChevronDown, ChevronRight, Columns2, DoorOpen, Eye, MessageSquare,
    Paperclip, MoreHorizontal, PenLine, Phone, PhoneCall, Printer, ScanLine,
    Mail, Search, Square,
    /* ⭐ The party menu's "scope the board" mark (2026-09-20). ⚠️ `Filter`, not
       `Search`: the search box opens a different thing, and two menu rows wearing
       one glyph is how a scheduler clicks the wrong one. */
    Filter,
    Trash2,
    /* ⭐ F103 — the detention clock's mark on the driver card. */
    Timer,
} from 'lucide-react';
import { detentionState, detentionLabel, formatMinutes, isDetentionRed, settingsFor, type DetentionLevel, type DetentionSettings } from '@/lib/detention';
/* ⚠️ ~~FileDropZone~~ and ~~ui/dialog~~ are no longer imported here. The paperclip
   goes straight to an OS file picker (Pete, 2026-09-08: *"go directly to file
   explorer… then back to the driver panel"*), so this panel renders neither a drop
   box nor a dialog for it. ⭐ It still shares the LOOP — `useFileUpload` — which
   was always the part worth sharing. `FileDropZone` is the box around that loop,
   and this screen no longer wants a box. */
import { useFileUpload } from '@/lib/useFileUpload';
import { RailSlotContext, nextRailSlots } from '@/lib/railSlot';
import { createPortal } from 'react-dom';
/* ⭐ `focusFromAppointment` and `focusFromBookedOrder` joined 2026-09-27: the three
   rails send ids and order rows, and this panel turns them into focuses — see
   `railHoverAppt`. ⛔ Built by the module, never assembled inline here. */
import {
    focusFromCheckIn, focusFromParty, focusFromAppointment, focusFromBookedOrder,
    type RailFocus,
} from '@/lib/railFocus';
/* ⭐ `PartyRow` ITSELF IS IMPORTED NOW (2026-09-20). The `…` menu holds the row it
   was opened from, and narrowing it to a Pick<> is the shape that was deleted six
   lines down for drifting from its source. One type, one place. */
import { partyRows, partyKindFor, apptMatchesParty, partyNameKey, type PartyKind, type PartyRow } from '@/lib/partyRows';
/* ⛔⚤ ~~type PartyRowLike = Pick<PartyRow, 'kind'|'code'|'name'|'appointments'>~~ —
   DELETED 2026-09-20 with the dwell it existed for. ⭐ The click path takes the four
   fields directly off the row it already has, so nothing needs the narrowed shape.
   ⚠️ Found by `noUnusedLocals`, three files after the handlers went — which is the
   third thing that flag caught in this one change. */
/* ⛔⤴ ~~`focusFromParty`~~ no longer imported here: the click calls
   `onChooseParty`, and the PAGE builds the focus — one constructor, one writer. */
/* ⛔⤴ ~~`focusScopeLabel`~~ — ORPHANED 2026-09-27 when Ⓡ moved to the board header;
   its only use here was that button's tooltip, and `Calendar.tsx` imports it now.
   ⚠️ `tsc` found this, not a reader — because the claim that it was still used came
   from a regex comment-stripper that the malformed comment below had already fooled.
     ** A CHECK THAT PARSES THE FILE LOOSELY AGREES WITH WHATEVER THE FILE IS
        BROKEN INTO. ** */
import { sameFocus } from '@/lib/railFocus';
import { SECTION_HEADER, SUBSECTION_HEADER, FRAME_BORDER } from '@/lib/utils';
import { useClickIntent } from '@/lib/clickIntent';

/**
 * DRIVER ARRIVALS — the panel down the right of the dock schedule.
 *
 * ⭐ WHY THIS EXISTS: without it, a driver who checks in and does not match an
 * appointment is a row in a table nobody ever looks at. The kiosk can record an
 * arrival it cannot explain; this is where a human explains it.
 *
 * THE ORDERING IS THE POINT. Two numbers, both DERIVED (migration 139), never
 * stored:
 *   ARRIVAL #  — the order every truck rolled through the gate today, across all
 *                clients. Known the instant a driver checks in.
 *   TRUCK #    — this client's Nth truck today. NULL until the client is known,
 *                which for a walk-up is only once a scheduler attaches it. That
 *                is not a gap: the row then slots into the client's sequence at
 *                its TRUE arrival time, because the numbering is derived.
 */

interface Props {
    facilityCode: string;
    /** The facility's local date, YYYY-MM-DD — the same day the grid is showing. */
    date: string;
    /**
     * ---- ⛔⛔ THE SUBJECT IS THE **PAGE'S**, NOT THIS PANEL'S (2026-09-19) --------
     *
     * ⚠️ It lived here for one increment and that was wrong. Pete: *"Client and
     * Carrier search in the search box above should do the same"* — which gives the
     * party scope THREE entry points: these chips, this panel's own dropdowns, and
     * the page's search box. ⛔ And this panel renders only on the DAY view, while
     * the scope now has to survive Week and Month.
     *
     *   ** A SCOPE WITH MORE ENTRY POINTS THAN CONSUMERS IS PAGE STATE WEARING A
     *      PANEL'S CLOTHES. **
     *   ** A CONTROL SCOPED TO ONE VIEW CANNOT OWN STATE THAT OUTLIVES THAT VIEW. **
     *
     * ✅ So `Calendar` owns it, beside `railFocus` — page state since 2026-09-10 and
     * the model this copies, including the re-read on facility change that a lazy
     * initialiser alone would miss.
     */
    subject: PanelSubject;
    onSubject: (s: PanelSubject) => void;
    /**
     * ---- ⭐⭐ THE CHOSEN PARTY, AND IT IS THE PAGE'S TOO (increment 3/5) --------
     *
     * ⛔ OPTIONAL, AND THE PANEL STILL WORKS WITHOUT IT — the driver subject never
     * touches these. ⚠️ But `subject` being a party while `onChooseParty` is absent
     * would render cards that cannot be clicked, so the render guards on the handler
     * rather than assuming the page wired it.
     */
    partyScope?: RailFocus | null;
    onChooseParty?: (kind: PartyKind, code: string, name: string | null, appts: AppointmentDto[]) => void;
    onClearParty?: () => void;
    /**
     * ⭐ F130 (Decision 308): a DOUBLE-click on a client / carrier / consignee card opens
     * that party's record in Settings. Absent → the double-click does nothing.
     */
    onOpenPartyRecord?: (kind: PartyKind, code: string) => void;
    /**
     * ⭐ Master party names, keyed by `partyNameKey`. The booking's own
     * `carrierName`/`clientName` is a per-transaction COPY and is often blank —
     * GEODIS read as `GEOT` until this existed. ⚠️ Consignees are absent from it;
     * `dock.Consignee` is not in `getLookups`. See lib/partyRows.ts.
     */
    partyNames?: ReadonlyMap<string, string>;
    /** Today's appointments, for the attach picker. */
    appointments: AppointmentDto[];
    /**
     * ---- ⭐⭐⭐ WHAT THE BOARD IS SHOWING (Pete, 2026-09-25) -------------------
     *
     * > *"when you click loading, it sets the filter correctly on the calendar but it
     * > doesn't filter the drivers/client/carrier/consignee and the right side
     * > panels"* -> *"I guess that is the same with all the status' too"*.
     *
     * ⛔⛔ A SECOND AXIS, NOT A NARROWER `appointments`. The prop above is the ATTACH
     * PICKER's source and `apptById`'s; narrowing it would stop a truck being
     * attached to an appointment the board is filtering out, and strip the status
     * word from its card.
     *   ** A FILTER THAT NARROWS WHAT YOU CAN SEE MUST NOT NARROW WHAT YOU CAN DO. **
     *
     * ⚠️ OPTIONAL, AND ABSENT MEANS "NO BOUND" rather than "nothing is on the board".
     * An empty Set is a real answer (the filter excluded everything); `undefined` is
     * the absence of a question. Defaulting one to the other would blank the rail for
     * any caller that has not been updated.
     */
    boardApptIds?: ReadonlySet<string>;
    /**
     * ⚠️ NOT DERIVABLE FROM THE SET. A filter that happens to match every appointment
     * is still ON, and this panel already carries the rule that says so:
     *   ** A FILTER YOU CANNOT SEE IS A FILTER YOU DO NOT KNOW IS ON. **
     * `boardApptIds.size === appointments.length` would call that case unfiltered and
     * make the board's controls the one exception to the panel's own rule.
     */
    boardFiltered?: boolean;
    /**
     * ⭐⭐ BUMPED BY Ⓡ, WHICH NOW LIVES IN THE BOARD HEADER (Pete, 2026-09-27).
     * Each increment collapses the arrival cards — decision 194's *"it should collapse
     * the driver, client, carrier, consignee cards like it does now"*.
     * ⛔ A COUNTER, NOT A BOOLEAN: a flag can only be raised once without somebody
     * lowering it, and "collapse again" is a real thing to ask twice.
     * ⚠️ Starts at 0 and the watcher ignores 0 — see the effect for why that matters.
     */
    collapseSignal?: number;
    getToken: () => Promise<string>;
    /**
     * Bumped by the page's Refresh button. The panel polls on its own timer, so
     * without this a scheduler pressing Refresh reloaded the grid and left the
     * arrivals list stale — which looked like a check-in had not registered.
     */
    refreshKey?: number;
    /** The appointment the user is hovering on the grid, to raise its card here. */
    highlightAppointmentId?: string | null;
    /** Hovering a card here tells the grid which block to outline. */
    /**
     * ⛔ THE SECOND ARGUMENT IS THE POINT. A rail row and a grid chip both report an
     * appointment hover, and the page must treat them oppositely: a grid chip
     * populates the panels, a RAIL row only marks the board. Without an origin the
     * panels re-scope, which replaces the very row the cursor is on.
     *   ** A HOVER THAT RE-SCOPES THE LIST CONTAINING THE HOVERED ROW IS A LOOP BY
     *      CONSTRUCTION. ** (Pete, 2026-09-21)
     */
    onHoverAppointment?: (appointmentId: string | null, origin?: 'grid' | 'rail') => void;
    /**
     * ---- ⭐⭐ DWELLING ON A CARD SWITCHES THE PAPERWORK TRAY (2026-09-10) ----
     *
     * > Pete: *"Also not working when I hover the driver card"* — and it never
     * > was. `onHoverCardShown` was added to `GanttCalendar` only, so hovering a
     * > load switched the tray and hovering the driver ON that load did nothing.
     *
     * ⛔ THIS IS THE THIRD TIME A FIX LANDED ON ONE MEMBER OF A POPULATION
     * (§4.14.2, §4.14.3). Both places a scheduler can point at a load have to
     * carry it, or the feature is a coin toss depending on which they aim for.
     *
     * ⚠️ SEPARATE FROM `onHoverAppointment`, which fires on plain mouseenter to
     * outline a block. This one waits out a DWELL — Pete: *"if the user is going
     * really fast over appointments, it doesnt fire?"* — because it costs a
     * fetch, and the same 350ms the grid's hover card uses.
     *
     * ⛔ NULL ON AN UNMATCHED ARRIVAL. Its paperwork is reached through the
     * load, and there is no load yet; an empty tray would read as a fault.
     */
    /**
     * ⭐⭐ A WHOLE RAIL FOCUS NOW, NOT A PAPERWORK TARGET (2026-09-10).
     *
     * ⛔ IT USED TO BE `AttachedTarget`, WHICH REQUIRES AN APPOINTMENT — so an
     * arrival with no load reported `null` and the rail saw nothing. Correct for
     * paperwork, wrong for the four panels that joined it: Pete, *"Comms can be
     * sent to a driver after he checks in, no matter if he is assoicated to an
     * appointment yet."* ⭐ `Calendar` still derives the paperwork target from
     * this, so that behaviour is unchanged.
     */
    /**
     * ⭐⭐ RENAMED FROM `onHoverCardShown` 2026-09-20 (decision 190). It is no longer
     * fired by a hover — a CLICK calls it. ⛔ The old name would have been the only
     * thing on this seam still claiming the gesture that was removed, and a name that
     * lies is worse than no name.
     */
    onChooseRecord?: (focus: RailFocus | null) => void;
    /** ⭐ The loads in the current scope — drawn at full strength, while
     *  `highlightAppointmentId` (the cursor) is drawn lighter. See GanttCalendar. */
    selectedAppointmentIds?: ReadonlySet<string>;
    /**
     * ⭐ THE LATCHED SCOPE, COMING BACK DOWN. This component RAISES a focus from
     * the driver cards and RECEIVES the current one for the panels it renders —
     * ⚠️ they are not the same value and must not be shortcut into local state:
     * the grid can set the focus too, and a local copy would ignore it.
     */
    railFocus?: RailFocus | null;
    /**
     * ---- ⭐⭐⭐ THE HOVER PREVIEW — A THIRD SCOPE, WITH THE SHORTEST LIFETIME -------
     *
     * ⭐ Pete, 2026-09-20: *"when I hover over an appointment and/or a client, all the
     * panels should populate (client name) as well as the title Client - XXXX should be
     * filled. When I hover off, goes away."*
     *
     * ⛔⚤ **THIS REVERSES §1AV.179, WHICH SAYS IN CAPITALS: *"HEADER LINE 1 NEVER SHOWS
     * THE PREVIEW … it must not flicker under a moving cursor."*** Pete asked for that
     * flicker deliberately. ⚠️ The other two bounds of that section SURVIVE UNTOUCHED and
     * are the reason this is safe:
     *   ⛔ **the calendar filter never moves on hover** — the board is the expensive
     *     thing to redraw and the thing a scheduler is navigating BY;
     *   ⛔ **nothing writes the committed state from a hover handler** — which is what
     *     decision 190 actually fixed, and it is untouched.
     *
     * ⭐⭐ IT IS A WHOLE `RailFocus`, NOT AN ID, so hovering a CARD previews a party and
     * hovering a CHIP previews one load, through one prop and one code path.
     *   ** A PREVIEW THAT CANNOT OUTLIVE THE CURSOR CANNOT BE THE THING §1AR BANNED. **
     */
    previewFocus?: RailFocus | null;
    /** ⭐ What the cursor is over IN THIS PANEL, reported up so the page can collapse it
        with the grid's hover into one preview. ⛔ It reports a FOCUS and never calls
        `onChooseParty` — that separation is what keeps decision 190 intact: hover shows,
        click commits, and only one of them writes anything that survives the cursor. */
    onHoverParty?: (f: RailFocus | null) => void;
    onClearRailFocus?: () => void;
    /**
     * ---- ⭐⭐ THE WARNING FILTER — CALENDAR ONLY, BY PETE'S CHOICE -------------
     *
     * > *"when you click on the warning, it only shows the appointments with the
     * > warnings in the calendar."*
     *
     * ⛔⛔ IT IS NOT `partyScope` AND IT COULD NOT BE. The rails read
     * `railFocus ?? partyScope`, so routing this through the party scope would narrow
     * Communications, Labour, Orders and History to the overdue loads as well — the
     * opposite of *"in the calendar"*, and unaskable-for once set, because the panel
     * that explains WHY a load is overdue would have been filtered out of the way.
     *   ** "SHOW ME ONLY THESE" ON A BOARD IS NOT "TELL ME ONLY ABOUT THESE"
     *      EVERYWHERE ELSE. **
     *
     * ⚠️ A KEY, NOT A BOOLEAN. Two warnings on one card and the same two on the card
     * below it are four distinct filters, and the pressed box has to be the one that
     * was pressed. `kind:code|warning-code` is unique by construction.
     *
     * ⛔ `null` FOR THE KEY CLEARS IT. Clicking the pressed warning a second time is
     * the way out, exactly as clicking a chosen party card again resets it — *"the way
     * out should be where the way in was"*.
     */
    warningScopeKey?: string | null;
    onChooseWarningScope?: (key: string | null, appointmentIds: string[], label: string) => void;
    /**
     * The current arrivals, whenever they change. The page needs them to prefill
     * a new appointment from a DRAGGED card — the grid only hands over an id.
     */
    onListChanged?: (rows: CheckInDto[]) => void;
    /** Something changed that the schedule needs to reflect (a truck was arrived). */
    onChanged: () => void;
    /**
     * A scan or an unfiled document was dropped onto a driver's card (#150).
     *
     * ⭐ THIS IS THE WALK-IN PATH. A driver with no booking has no chip on the grid, so
     * his card is the only place his paperwork can land — and it stays on the ARRIVAL
     * until somebody gives him a load, at which point `backfillDocumentsForCheckIn`
     * carries it across and names it.
     */
    onDropPaperworkOnCheckIn?: (ev: {
        payload: PaperworkDragPayload;
        checkIn: CheckInDto;
    }) => void;
    /** A drop that was refused, with the reason to show the user. */
    onDropRefused?: (reason: string) => void;
    /**
     * ⛔ A VOIDED ARRIVAL'S PAPERWORK. Separate from `onPaperwork` because a
     * voided row is NOT a CheckInDto — it has no arrival number and never had one
     * (the sequence view excludes voided rows so that voiding a mistake does not
     * renumber the live trucks). Reusing the other callback would have meant
     * inventing the missing fields.
     */
    onVoidedPaperwork?: (v: VoidedCheckInDto) => void;
    /**
     * ⭐ ACT WHEN THERE IS NO CHOICE TO MAKE (#245). The panel does NOT decide
     * which document these hit — it does not hold the ids, only a count. The page
     * fetches, asks `choosePaperworkAction`, and either acts or opens the list.
     *
     * ⛔ THE DECISION LIVES IN ONE PLACE ON PURPOSE. The right-click menu and the
     * edit screen ask the same question, and three implementations of "which
     * document did they mean" would drift without anything failing (§1O).
     */
    /**
     * ⭐ WHAT OCR MADE OF A PAGE DROPPED ON AN UNMATCHED CARD (#191/#221).
     *
     * ⛔ IT IS RENDERED HERE RATHER THAN ON THE PAGE because accepting it calls
     * `doAttach` — the same path a manual match uses, which carries the
     * substitute-driver confirmation and the audit line. Attaching a truck to a
     * load must have exactly one set of rules.
     */
    suggestion?: {
        result: SuggestLoadDto;
        documentId: string;
        documentName: string;
        checkInId: string;
    } | null;
    onSuggestionClose?: () => void;
    onSuggestionView?: () => void;
    onSign?: (c: CheckInDto) => void;
    onPrint?: (c: CheckInDto) => void;
    /** Scheduler wants to create an appointment for this walk-up. */
    onCreateFromCheckIn: (checkIn: CheckInDto) => void;
    /**
     * ---- ⭐⭐ THE APPOINTMENT'S OWN RIGHT-CLICK MENU, FOR THIS ARRIVAL (#341)
     *
     * > Pete, 2026-09-09: *"Why can't you give me a dropdown too that has the
     * > same things as when you right click an appointment??????? So i have all
     * > options????"*
     *
     * ⛔⛔ IT IS A FUNCTION FROM `Calendar`, NOT A SECOND LIST HERE. Every entry
     * on that menu — View details, Text directions, Text delivery pin, Change
     * status (admin) — is built by `contextMenuItemsFor` from handlers that live
     * in `Calendar` and nowhere else. ⚠️ Re-listing them on this card would drift
     * the first time an entry was added to either, and offering LESS than the
     * right-click does is the exact complaint this answers.
     *
     * ⚠️ `undefined` FOR AN ARRIVAL WITH NO APPOINTMENT — there is nothing to
     * build a menu from. The card's own items still show; see `cardMenuItems`.
     */
    appointmentMenuItems?: (appointmentId: string) => MenuItem[];
    /**
     * ---- ⭐ THE WMS ORDERS PANEL'S SCOPE (#333) --------------------------
     *
     * §1Z.1 — *"a search's default scope should be what is on screen."* The
     * CALENDAR knows its day/week/month bounds, including the six-week grid the
     * month view actually draws. ⛔ Neither this file nor the panel re-derives
     * them: that arithmetic is duplicated once already and pinned by
     * `tools/search-scope-guard.cjs`.
     *
     * ⚠️ OPTIONAL. Absent means the panel does not mount, which is the honest
     * shape for a screen that cannot say what range it is showing.
     */
    ordersScope?: { fromDate: string; toDate: string; scopeLabel: string };
    onCreateFromOrder?: (order: { orderNumber: string; wmsSource: string | null; orderType: string | null }) => void;
    /** ⭐ D3 / Decision 238 — the Orders panel's Match button, passed straight through. */
    onMatchOrder?: (order: WmsOrderDragKey, row: OrdersPanelRowDto) => void;
    /**
     * Text this arrival's driver his dock door.
     *
     * ⭐ THE SAME ACTION AS THE GRID'S RIGHT-CLICK, raised to a button here
     * because this is the column a scheduler is looking at when the driver is
     * actually waiting. One implementation, two doors into it.
     */
    onTextDoor?: (checkIn: CheckInDto) => void;
    /**
     * "Your truck is finished, come and collect your signed paperwork."
     *
     * ⭐ THE CHEAPEST GOOD IDEA ON THE LIST (Pete, 2026-08-19) — it saves a walk
     * across the yard on every single load.
     */
    onTextPaperwork?: (checkIn: CheckInDto) => void;
    /**
     * ⭐ #302 — "unloading/loading has started". Pete: *"we need to have the
     * manual option to in right-click on the appointment and in the Comm icon on
     * the driver card."* This is the driver-card half.
     */
    onTextWork?: (checkIn: CheckInDto) => void;
    /**
     * Open the paperwork for this arrival's load (#144) — view what has been filed,
     * or scan the first document.
     *
     * ⚠️ Only ever called for a MATCHED arrival: documents hang off the appointment
     * (migration 145), so an unmatched walk-up has nothing to show.
     */
    onPaperwork?: (checkIn: CheckInDto) => void;
    /**
     * ⭐⭐ ATTACH A **NEW** FILE STRAIGHT ONTO THIS ARRIVAL (#XXX, 2026-09-08).
     *
     * > Pete: *"lets add a quick button to the driver card on the driver checkins
     * > that does the same thing as 'Drop files Here, or Browse'."*
     *
     * ⛔ THE PAGE SUPPLIES IT, NOT THIS PANEL, and that is the same rule
     * `onDropPaperworkOnCheckIn` already follows: this panel renders arrivals and
     * raises events; `Calendar` owns where a document lands. ⚠️ Uploading a new
     * file is TWO acts — `uploadDocument` then `fileDocument({ checkInId })` — and
     * the second is filing, which has never been this component's business.
     *
     * ⚠️ DIFFERENT FROM `onDropPaperworkOnCheckIn`, which takes an ALREADY-FILED
     * document dragged out of the tray. This takes a `File` off the disk.
     *
     * ⭐ The control it feeds is the shared `FileDropZone`, so this card gets
     * drag-and-drop, browse AND the remembered scan folder without knowing about
     * any of them.
     */
    uploadToCheckIn?: (checkIn: CheckInDto, token: string, file: File) => Promise<unknown>;
    /**
     * Mark this arrival's appointment departed (#141), to match what the appointment
     * itself can do.
     *
     * ⛔ THE HANDLER MUST GO THROUGH THE SAME DEPARTURE PATH THE APPOINTMENT USES.
     * That path holds the outbound release gate (#97); a driver card that departed a
     * truck any other way would be a documented way around a check Symbia relies on.
     */
    onMarkDeparted?: (checkIn: CheckInDto) => void;
    /**
     * An arrival was just matched to an appointment.
     *
     * ⭐ THE MOMENT THE DOOR TEXT IS FOR (Pete, 2026-08-19). A truck that has
     * just been matched is a truck whose driver is sitting in the lot not
     * knowing where to go — so the page offers to tell him rather than relying
     * on somebody remembering. Offered, never sent automatically.
     */
    onAttached?: (appointmentId: string) => void;
    /**
     * ---- ⭐⭐⭐ THE ATTACH, DELEGATED — ONE FLOW, TWO ENTRY POINTS (2026-09-23) --
     *
     * ⛔⛔ THE MENU USED TO BE A SECOND, WEAKER ATTACH. It called `attachCheckIn`
     * with no answers and handled neither 409, so a vehicle-number or paperwork
     * disagreement arrived here as `setError(err.message)` — a raw server string in
     * the panel's error slot, with no prompt, no overwrite choice, and no attach.
     * The identical drag onto the same chip offered two dialogs and worked.
     *   ** TWO CALL SITES FOR ONE DECISION IS ONE FEATURE AND ONE TRAP, AND THE
     *      TRAP IS ALWAYS THE ONE NOBODY DEMOED. **
     *
     * ⭐ AND THE FILE ALREADY SETTLED THIS ARGUMENT. `appointmentMenuItems` is
     * passed down from `Calendar` precisely so *"the card can never offer less than
     * the chip does"* — literally the grid's own closures, not a copy. This is that
     * rule applied to the one action it had not reached.
     *
     * ⛔ NOT A REIMPLEMENTATION OF THE TWO DIALOGS HERE. They own `Calendar` state,
     * they re-enter their own handler with the answer, and a copy would be ~200
     * lines that drift the first time either prompt changes.
     *
     * ⚠️ OPTIONAL, AND THE DIRECT CALL SURVIVES BELOW as the fallback — the panel
     * is mounted in one place today, but a required prop would make this component
     * unmountable anywhere that cannot supply a calendar's attach handler.
     */
    onAttachRequest?: (ev: DropCheckInOnAppointmentEvent) => void | Promise<void>;
    /**
     * Rendered at the very bottom of the rail, below everything.
     *
     * ⚠️ A SLOT RATHER THAN A SIBLING, because this <aside> is a fixed-width
     * column: a second component beside it in the page would become a second
     * 340px column and halve the schedule. The paperwork panel belongs INSIDE
     * this one, under the arrivals it relates to.
     */
    footer?: React.ReactNode;
    /** True while the user is mid-action; the poll holds off. */
    interactingRef: React.MutableRefObject<boolean>;
}

const REFRESH_MS = 30_000;

/**
 * ---- ⭐⭐ THE LAST FOUR, FOR THE ROLLED-UP CARD (Pete, 2026-09-09) -----------
 *
 * > *"to save space, the phone on the rollup panel can have the phone icon but
 * > instead of the full phone number next to the icon on have the last four
 * > digits like x9980. The full phone number can be shown in the breakout below."*
 *
 * ⛔⛔ THIS IS NOT THE THING THE FILE ALREADY FORBIDS, AND THE DIFFERENCE IS THE
 * WHOLE REASON IT IS ALLOWED. The rule below the facts row says a phone number
 * must never TRUNCATE, because *"(616) 555-12… still LOOKS like a phone number,
 * and somebody will dial it."* ⭐ `x9980` cannot be dialled and cannot be
 * mistaken for something that can — the `x` is what makes it a REFERENCE rather
 * than a damaged number. A truncation pretends to be whole; this announces that
 * it is not.
 *
 * ⚠️ SO THE FULL NUMBER MUST STILL EXIST SOMEWHERE ON THE CARD, and it does —
 * one line into the detail, where there is room for it to wrap. ⛔ If that line
 * is ever removed, this one has to go back to the full number, because then the
 * card would hold no dialable number at all and migration 144 calls the phone
 * the real-world lookup key.
 *
 * ⚠️ Digits only, so formatting never changes the answer: `(303) 505-8549`,
 * `303-505-8549` and `3035058549` all yield `x8549`.
 *
 * ⭐ A SHORT NUMBER IS SHOWN WHOLE rather than padded or refused. Four digits or
 * fewer is already its own last-four, and inventing an ellipsis for it would be
 * a second way to write "this is partial" for a value that is not.
 */
/**
 * ---- ⭐⭐ WHAT COUNTS AS A WARNING ON AN ARRIVAL — DEFINED ONCE -------------
 *
 * ⛔⛔ THIS EXISTS BECAUSE IT WAS ABOUT TO BE WRITTEN TWICE. The card badge had
 * this list inline in its JSX; the header summary needed the same list to count
 * across every arrival. Two copies of "what is a warning" disagree the day
 * somebody adds a third one — and they disagree QUIETLY, as a header saying 3
 * over a rail showing 4 triangles.
 *
 * ⭐ §1R.16, caught before the second copy existed rather than after.
 *
 * ⚠️ THE `code` IS FOR COUNTING, THE `text` IS FOR READING. The summary groups by
 * code because "2 licence warnings" is useful and two different licence
 * sentences stacked is not; the card shows the text because there it is about
 * ONE truck and the sentence is the whole point.
 */
/**
 * ⭐ #73 ADDED `vehicle-number` HERE RATHER THAN AS A FOURTH JSX BLOCK ON THE
 * CARD — which is what the comment above was written to prevent. The header
 * summary counts through this list; a warning rendered only in the card's JSX
 * would show a triangle the count does not know about.
 *   ** A SECOND LIST OF WHAT IS WRONG DISAGREES QUIETLY, AS A HEADER SAYING 3
 *      OVER A RAIL SHOWING 4. **
 */
export type ArrivalWarningCode =
    'no-appointment' | 'licence' | 'no-licence-outbound' | 'vehicle-number'
    | 'vehicle-mismatch' | 'paperwork-pending';

export interface ArrivalWarning {
    code: ArrivalWarningCode;
    text: string;
}

/** ⚠️ ORDER IS THE ORDER THEY ARE SHOWN IN. Most actionable first. */
function arrivalWarnings(c: CheckInDto): ArrivalWarning[] {
    const out: ArrivalWarning[] = [];
    /* ⭐ "NEEDS AN APPOINTMENT" IS A WARNING, NOT JUST A COLOUR (Pete,
       2026-09-09). The card had TWO warning languages — an amber BACKGROUND
       meaning "no appointment" and an amber ICON meaning "licence problem" — so a
       card wearing both looked exactly like a card wearing one. One language now;
       the amber fill stays as the scan-a-stack signal. */
    if (c.appointmentId === null) {
        out.push({ code: 'no-appointment', text: 'No appointment — this arrival has not been matched yet.' });
    }
    if (c.licenceWarning) out.push({ code: 'licence', text: c.licenceWarning.text });
    /**
     * ⛔⛔ `=== true`, NOT TRUTHINESS. The flag is a TRI-STATE: null means this
     * site never asked, false means he answered, true means he typed the bypass
     * code and owes the number. `if (c.vehicleNumberPending)` is right today and
     * becomes wrong the moment anything reads the other two states.
     *   ** A TRI-STATE READ AS A BOOLEAN IS A WARNING THAT GOES QUIET ON EXACTLY
     *      THE ROWS THAT NEEDED IT. **
     *
     * ⚠️ THE SENTENCE SAYS WHAT IS OWED AND WHAT IT COSTS, because a scheduler
     * reading a rail needs to know this one is holding up a door assignment
     * rather than merely being untidy.
     */
    if (c.vehicleNumberPending === true) {
        out.push({
            code: 'vehicle-number',
            text: 'Owes a trailer, container or plate number — no dock door until it is in.',
        });
    }
    /**
     * ⭐⭐ #73 — THE LOAD SAYS ONE BOX AND THIS TRUCK IS ANOTHER.
     *
     * ⛔ THE COMPARISON IS THE SERVER'S. `vehicleNumberMismatch` arrives already
     * judged by `sameValue` — the same function the attach prompt uses — and
     * comparing the two strings here would be a second opinion about whether there
     * is anything wrong at all.
     *
     * ⚠️ IT CANNOT CO-OCCUR WITH `vehicle-number` ABOVE: a truck that gave no
     * number has nothing to disagree with. So the card never wears both, and this
     * does not add a row to the arrivals that already carry one.
     *
     * ⚠️ THE SENTENCE NAMES BOTH VALUES, because the decision is WHICH ONE IS
     * RIGHT. "The numbers disagree" is a notification; "the load says ABC123, this
     * truck has XYZ789" is something a scheduler can act on from the rail.
     */
    const mismatch = c.vehicleNumberMismatch;
    if (mismatch && mismatch.length > 0) {
        out.push({
            code: 'vehicle-mismatch',
            text: mismatch
                .map(m => `The appointment's ${m.field} number is ${m.booked} — this truck has ${m.arriving}.`)
                .join(' '),
        });
    }
    /**
     * ---- ⭐⭐ THE PAPERWORK DEBT IS A WARNING (Pete, 2026-09-23) --------------
     *
     * > *"Same with Load Details - not sure what this is - another warning???"*
     *
     * ⛔⛤ IT READ AS ONE BECAUSE I PUT A WARNING INSIDE IT. "Owes a PO, BOL or
     * reference number" was rendered as a line in the Load details block, which is
     * otherwise pure data - so the block grew a triangle and became unclassifiable.
     *   ** A DATA BLOCK WITH A WARNING IN IT IS NEITHER. THE READER HAS TO DECIDE
     *      WHICH IT IS, EVERY TIME, AND THEY WILL NOT. **
     *
     * ⭐ IT BELONGS HERE, WITH THE OTHER DEBTS, WHICH ALSO MAKES IT COUNT toward
     * the header badge - it never did before, so a card could owe paperwork and
     * show a lower warning count than it had problems.
     *
     * ⛔⛔ `=== true`, the tri-state rule. NULL means this site does not collect
     * paperwork numbers and there is nothing to chase.
     */
    if (c.paperworkPending === true) {
        out.push({
            code: 'paperwork-pending',
            text: 'Owes a PO, BOL or reference number.',
        });
    }
    /* ⚠️ An OUTBOUND truck with no licence on file is the other condition the tool
       strip already paints amber. Naming it here keeps the two marks agreeing. */
    if (c.direction === 'OUTBOUND' && !c.hasDriverIdentity) {
        out.push({ code: 'no-licence-outbound', text: 'Outbound truck with no driver license on file.' });
    }
    return out;
}

/** ⚠️ Singular and plural are BOTH written out. Appending "s" is how "1 licence
 *  warnings" reaches a screen somebody is judging the product by. */
const WARNING_LABEL: Record<ArrivalWarningCode, (n: number) => string> = {
    'no-appointment':      (n) => `${n} need${n === 1 ? 's' : ''} an appointment`,
    'licence':             (n) => `${n} license warning${n === 1 ? '' : 's'}`,
    'no-licence-outbound': (n) => `${n} outbound truck${n === 1 ? '' : 's'} with no license on file`,
    'vehicle-number':      (n) => `${n} owe${n === 1 ? 's' : ''} a trailer or container number`,
    'vehicle-mismatch':    (n) => `${n} disagree${n === 1 ? 's' : ''} with its appointment about the box`,
    'paperwork-pending':   (n) => `${n} owe${n === 1 ? 's' : ''} a PO, BOL or reference number`,
};

/**
 * ============================================================================
 * ⭐⭐⭐ ONE WARNING, ONE SHAPE — FOR ALL FOUR CARD TYPES (Pete, 2026-09-23)
 * ============================================================================
 *
 * > *"these warnings seem all over the place format wise, all look different.
 * > Lets make them all look like 'Not a commercial drivers licens' meaning a box
 * > around each one, same spacing, same text and font size and color. Maybe a
 * > little circle 1, 2, 3 etc on the far right of the box, in upper right corner,
 * > counting the warning to match with the total on the box in the header. ...
 * > This layout needs to be consistent across driver, client carrier, consignee
 * > which it is not."*
 *
 * ---- ⛔⛤ WHY THEY DIVERGED, WHICH IS THE WHOLE LESSON --------------------
 *
 * `arrivalWarnings()` HAS ALWAYS RETURNED ONE LIST. The data was never the
 * problem. But nothing rendered the list - each warning was hand-placed in its
 * own block of JSX and pulled its text back out with
 * `arrivalWarnings(c).find(w => w.code === '...')`. Four call sites, four sets of
 * classes, and a ternary on `blocking` that boxed one of them and not the others.
 *   ** A SHARED MODEL DOES NOT PRODUCE A SHARED APPEARANCE. ONLY A SHARED
 *      RENDERER DOES. **
 * ⚠️ And a `.find()` by code is the tell: it is a LOOP written out longhand, with
 * the divergence hiding in the spaces between the copies.
 *
 * ---- ⛔⛔ IT IS A TOP-LEVEL COMPONENT AND IT HAS NO HOOKS --------------------
 *
 * Deliberately, and on the same day §1AV.254 took the kiosk down with a `useState`
 * placed below an early return. A renderer with no state cannot repeat that.
 *
 * ⚠️ THE NUMBER IS THE INDEX IN THIS LIST, so it agrees with the header badge by
 * construction - the badge is `arrivalWarnings(c).length` and these are the same
 * entries in the same order. ⛔ Not a code-to-number map: a card showing 1 and 3
 * would be telling the reader two are hidden.
 */
/**
 * ---- ⭐⭐ THE PARTY CARDS' WARNINGS, AS SENTENCES (Pete, 2026-09-23) --------
 *
 * > *"Cards should look very similiar regarding formats and placement of
 * > information, buttons, etc across driver, client, carrier and consignee."*
 *
 * ⛔ THE PARTY CARDS HAD NO WARNING SENTENCES AT ALL - only two glyphs with a
 * count, in the top-right corner, explained by a `title` nobody hovers on a touch
 * screen. The driver card has been saying what is wrong in words since #73.
 *   ** A COUNT IS NOT A WARNING. IT IS A PROMPT TO GO AND FIND OUT ELSEWHERE. **
 *
 * ⭐ THE SENTENCES ALREADY EXISTED, in those `title` attributes. This does not
 * invent wording; it moves it somewhere it can be read.
 *
 * ⚠️ THE GLYPHS STAY. They are the party card's equivalent of the driver
 * card's header badge - a count at the top, the detail in the body - which is
 * exactly the symmetry Pete asked for.
 */
/* ⚠️ THE ID LISTS ARE OPTIONAL IN THE SIGNATURE and `PartyRow` always supplies them.
   Optional here so the summing callers can keep passing a bare `{ atRisk, noPaperwork }`
   shape — and so a caller that forgets them produces a warning with nothing to point
   at, which renders inert, rather than a compile error that invites a `[]` cast.
     ** AN ABSENT SET AND AN EMPTY SET MEAN THE SAME THING HERE: NOTHING TO HIGHLIGHT. ** */
export function partyWarnings(pr: {
    atRisk: number; noPaperwork: number;
    atRiskIds?: string[]; noPaperworkIds?: string[];
}): CardWarning[] {
    const out: CardWarning[] = [];
    if (pr.atRisk > 0) {
        out.push({
            code: 'party-at-risk',
            appointmentIds: pr.atRiskIds ?? [],
            text: `${pr.atRisk} load${pr.atRisk === 1 ? '' : 's'} overdue or marked no-show.`,
        });
    }
    if (pr.noPaperwork > 0) {
        out.push({
            code: 'party-no-paperwork',
            appointmentIds: pr.noPaperworkIds ?? [],
            /* ⭐ THE SLASHED FILE MOVED HERE from the corner badge - see `icon` on
               CardWarning. It is the one warning on these cards that is not about
               a deadline, and the glyph says so before the sentence is read. */
            icon: <NoPaperworkIcon className="mt-px h-3 w-3" />,
            text: `${pr.noPaperwork} load${pr.noPaperwork === 1 ? '' : 's'} with no paperwork filed.`,
        });
    }
    return out;
}

/**
 * ============================================================================
 * ⭐⭐⭐ ONE COUNT BADGE, AND A PAPERWORK ICON THAT MEANS "MISSING"
 * ============================================================================
 *
 * ---- ⛔⛤ THE PAPERWORK GLYPH WAS SAYING THE OPPOSITE OF WHAT IT MEANT ------
 *
 * > Pete, 2026-09-23: *"the paperwork warning is misleading. Looks like there are
 * > 2 documents on the driver card, not missing two. Put a circle with a slash
 * > thru it ontop of the paperwork icon."*
 *
 * ⛔⛔ HE IS RIGHT AND IT IS THE WORST KIND OF WRONG: a document icon with "2"
 * beside it reads as **two documents**, which is the exact inverse of "two loads
 * with nothing filed". Colouring it amber (2026-09-20) made it a WARNING without
 * making it legible — an alarming way to say the reassuring thing.
 *   ** AN ICON THAT NAMES THE SUBJECT DOES NOT STATE THE PREDICATE. `FileText`
 *      SAYS "PAPERWORK". IT DOES NOT SAY "PRESENT" OR "ABSENT", AND THE READER
 *      WILL ASSUME PRESENT. **
 *
 * ⭐ A SLASHED CIRCLE OVER IT IS UNAMBIGUOUS in a way no colour is — it is the
 * one symbol that means "not" without words, in every language a driver's
 * scheduler might read.
 */
function NoPaperworkIcon({ className }: { className?: string }) {
    return (
        /* ⚠️ A WRAPPER WITH THE ICON'S OWN BOX SIZE, so this drops into any slot a
           plain lucide icon fits. The overlay is absolutely positioned INSIDE it and
           cannot push its neighbours around. */
        <span className={'relative inline-flex shrink-0 ' + (className ?? 'h-3.5 w-3.5')}>
            <FileText className="h-full w-full" />
            {/* ⛔ NOT `opacity` OR A LIGHTER SHADE. "Faded" is a style; a slash is a
                statement. ⚠️ `strokeWidth` is raised because the Ban glyph is drawn at
                the same size as the file beneath it and a hairline would read as a
                rendering artefact rather than a mark. */}
            <Ban className="absolute inset-0 h-full w-full" strokeWidth={2.75} aria-hidden="true" />
        </span>
    );
}

/**
 * ---- ⭐⭐⭐ THE COUNT BADGE — ONE SHAPE FOR EVERY TOTAL ON THIS PANEL --------
 *
 * > Pete: *"these warning signs are different but mean the same thing? Driver
 * > checkins has the 2nd screenshot icons for warnings and client/carrier/
 * > consignee has the 3rd screenshot. Need to be consistent???"*
 *
 * ⛔⛤ THREE COPIES, TWO SHAPES. The driver card's badge and the panel header's
 * badge both drew `icon + filled amber circle`. The party cards drew
 * `icon + plain number`. Nothing shared a line of code, so "consistent" was a
 * thing somebody had to keep re-achieving by hand.
 *   ** THE SECOND COPY OF A VISUAL RULE IS WHERE IT STARTS TO DECAY, AND THE
 *      THIRD IS WHERE NOBODY CAN SAY WHICH ONE IS CANONICAL. **
 *
 * ⚠️ NO HOOKS, TOP LEVEL — same rule as `CardWarnings`, same reason (§1AV.254).
 */
function CountBadge({
    icon, count, title, onClick, ariaLabel, expanded,
}: {
    icon: React.ReactNode;
    count: number;
    title?: string;
    onClick?: () => void;
    ariaLabel?: string;
    expanded?: boolean;
}) {
    if (count <= 0) return null;
    const body = (
        <>
            {icon}
            {/* ⭐ THE FILLED CIRCLE, ALWAYS, INCLUDING AT ONE. Settled 2026-09-09:
                the number is how a scheduler knows the amber is not just "needs an
                appointment", and hiding it at 1 puts them back to guessing on the
                commonest card of all. */}
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full
                             bg-amber-600 px-1 text-[9px] font-bold leading-none text-white">
                {count}
            </span>
        </>
    );
    const cls = 'flex shrink-0 items-center gap-0.5 rounded px-0.5 text-amber-700';
    /* ⛔ A BUTTON ONLY WHERE SOMETHING HAPPENS — see the same rule on
       `CardWarnings`. The party glyphs report; they do not open anything. */
    return onClick ? (
        <button
            type="button" title={title} aria-label={ariaLabel} aria-expanded={expanded}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            onDoubleClick={(e) => e.stopPropagation()}
            className={cls + ' hover:bg-amber-100'}
        >{body}</button>
    ) : (
        <span title={title} className={cls}>{body}</span>
    );
}

export interface CardWarning {
    code: string;
    text: string;
    /** What opening this warning should do, when there is something to open. */
    onClick?: (e: React.MouseEvent) => void;
    title?: string;
    /**
     * ---- ⛔⛔ THE ONE LOUDNESS DIMENSION (2026-09-23) --------------------
     *
     * ⚠️ DECISION #269 AND PETE'S "MAKE THEM ALL THE SAME" BOTH APPLY, and they
     * are not actually in conflict once the complaint is read precisely. He
     * objected to warnings being ARBITRARILY different - one boxed, one bare, one
     * struck through - not to severity being visible. #269 says: *"every warning
     * drawn at the same weight - the loud one then means nothing."*
     *   ** A DISTINCTION NOBODY CAN DECODE IS NOT A DISTINCTION. A DISTINCTION
     *      DRAWN ONE WAY, EVERYWHERE, IS. **
     *
     * ⭐ SO THE GEOMETRY IS IDENTICAL - same border width, padding, type size,
     * numbering and position - and severity moves to colour weight alone. One
     * channel, applied by one renderer, rather than four call sites each deciding.
     *
     * ⛔ ONLY THE LICENCE SETS IT TODAY, from the SERVER's `blocking` flag. Nothing
     * here decides what is serious; `checkin-panel-guard` pins that the flag still
     * reaches the render, which is what it caught when this was refactored away.
     */
    severity?: 'blocking' | 'normal';
    /**
     * ⭐ THE GLYPH, WHEN THE TRIANGLE IS NOT THE RIGHT ONE (2026-09-23).
     *
     * ⛔ ADDED SO THE SLASHED-FILE ICON HAD SOMEWHERE TO GO once the party cards
     * stopped carrying a badge per warning TYPE. The information it was carrying -
     * "this is about paperwork, and it is MISSING" - belongs on the row that says
     * so in words, not on a counter in the corner.
     *   ** WHEN TWO INDICATORS SAY THE SAME THING, MOVE THE MEANING TO THE ONE
     *      THAT CAN ALSO EXPLAIN ITSELF. **
     */
    icon?: React.ReactNode;
    /**
     * ---- ⭐⭐⭐ THE LOADS THIS WARNING IS ABOUT (Pete, 2026-09-23) --------------
     *
     * > *"when you hover over them ... they need to highlight the appointmets with the
     * > warnings and then when you click on the warning, it only shows the appointments
     * > with the warnings in the calendar."*
     *
     * ⛔⛔ THE PARTY WARNINGS WERE INERT, AND INERT WAS THE BEST CASE. The box rendered
     * as a `<div>`, so the click fell through to the card root and **scoped the whole
     * screen to the party and expanded the card** — Pete: *"It expands the chevron now
     * when I try to click on the warning."* A control that does something unrelated is
     * worse than one that does nothing, because the user cannot tell it was not aimed.
     *   ** A REGION INSIDE A CLICKABLE CARD IS NEVER NEUTRAL. IT EITHER STOPS THE EVENT
     *      OR IT INHERITS A MEANING SOMEBODY ELSE CHOSE. **
     *
     * ⚠️ MY EARLIER REASONING WAS *"3 loads overdue is about a SET, and there is no
     * single record to open"*. The premise was right and the conclusion was wrong: the
     * answer to a set is a FILTER, not silence — and Pete had already specified it.
     *
     * ⛔ OPTIONAL, AND THE DRIVER CARD LEAVES IT UNSET. Its warnings name ONE record
     * each and open a dialog; nothing there needs a set.
     */
    appointmentIds?: string[];
    /** ⭐ The board is currently filtered to this warning — drawn as a pressed control. */
    active?: boolean;
}

/* ⭐ ONE CLASS STRING, USED BY EVERY WARNING ON EVERY CARD TYPE. It is the
   treatment Pete named - the blocking-licence box - promoted from a special case
   to the only case. ⚠️ If this string changes, all four card types change with it,
   which is the entire point of it being a constant. */
/* ⚠️ THE TWO STRINGS DIFFER IN COLOUR AND WEIGHT ONLY. Border WIDTH, padding and
   type size are shared, so a blocking warning and an ordinary one occupy exactly the
   same box and the list does not jitter when one becomes the other.
     ** SEVERITY MAY CHANGE HOW LOUD A THING IS. IT MAY NOT CHANGE WHERE IT SITS. ** */
/* ---- ⛔⛤ ~~WARN_BOX_BLOCKING~~ — ONE BOX AGAIN, 2026-09-23 ----------------

   For about an hour a blocking warning drew in a darker amber. Pete looked at a
   party card beside a driver card and asked *"The warning in the first screenshot
   and the second are formatted the same??? Looks like different colors?"*

   ⛔⛔ THAT IS THE WHOLE ARGUMENT AGAINST IT, IN ONE SENTENCE. He read the
   difference as INCONSISTENCY, not as severity - which is the exact complaint the
   uniform box was built to answer. A colour cannot say why it is different.
     ** IF A DISTINCTION HAS TO BE EXPLAINED TO BE SEEN AS A DISTINCTION, IT IS NOT
        COMMUNICATING - IT IS JUST VARIATION. **

   ⭐ SEVERITY NOW SAYS ITS OWN NAME. One box for everything; a blocking warning
   carries a word. ⚠️ #269 is still honoured - "the loud one" is still
   distinguishable - but by a label the reader can act on rather than a shade they
   have to decode. */
const WARN_BOX = 'flex w-full items-start gap-1.5 rounded border border-amber-300 '
    + 'bg-amber-50 px-1.5 py-1 text-left text-[10px] font-medium text-amber-800';

/**
 * ⚠️ `onHoverWarning` IS OPTIONAL AND THE DRIVER CARD DOES NOT PASS IT. One renderer,
 * two card types, and the capability appears only where the data supports it — which is
 * the same argument that put `icon` and `severity` on the warning rather than on the
 * component.
 * ⛔ IT REPORTS THE WARNING, NOT AN ID LIST, so the caller can label the preview with
 * the sentence the reader is pointing at.
 */
export function CardWarnings({ warnings, onHoverWarning }: {
    warnings: CardWarning[];
    onHoverWarning?: (w: CardWarning | null) => void;
}) {
    if (warnings.length === 0) return null;
    return (
        <div className="mt-1 flex flex-col gap-1">
            {warnings.map((w, i) => {
                const body = (
                    <>
                        {w.icon ?? <AlertTriangle className="mt-px h-3 w-3 shrink-0" />}
                        <span className="flex-1 [overflow-wrap:anywhere]">{w.text}</span>
                        {/* ⭐ THE COUNTER, UPPER RIGHT. ⚠️ `self-start` so it sits at the
                            TOP of a box whose text has wrapped to three lines, rather than
                            drifting to the middle. */}
                        {/**
                          * ---- ⛔⛤ THE COUNTER TAKES THE WARNING'S COLOUR ------------
                          *
                          * > Pete, 2026-09-23: *"the 1 and 2 in the actual panels are
                          * > yellow, not the color of the warning (for each warning)."*
                          *
                          * ⛔ IT WAS A PALE OUTLINE - `bg-amber-100` with a thin border -
                          * identical on every box however serious the box was, and washed
                          * out next to the SOLID filled circle the header badge uses. Two
                          * faults in one element: it did not match the badge it is meant to
                          * add up to, and it flattened the severity the box beside it was
                          * carrying.
                          *   ** A COUNTER THAT IGNORES WHAT IT IS COUNTING IS DECORATION
                          *      WEARING A NUMBER. **
                          *
                          * ⭐ SOLID, LIKE THE HEADER BADGE, so the circle in the corner and
                          * the circles in the boxes are one vocabulary - and darker on a
                          * blocking warning, so it carries the same severity its box does.
                          */}
                        {/**
                          * ---- ⛔⚤ ~~THE "BLOCKING" LABEL~~ - REMOVED 2026-09-23 --------
                          *
                          * > Pete: *"I am not sure what the BLOCKING accomplishes, please
                          * > remove. The scheduler can distinquish on their own from the
                          * > list whats important. You can still have a severity on the
                          * > warnings but we will use that in another reporting screen."*
                          *
                          * ⛔⛔ THIS IS THE **SECOND** SEVERITY CHANNEL TO BE REMOVED IN ONE
                          * EVENING, AND THE SECOND IS THE INFORMATIVE ONE. First a darker
                          * amber box, which he read as inconsistency; then this word, which
                          * he read as noise. The pattern is not that the drawing was wrong
                          * twice - it is that the SCREEN was the wrong place for it.
                          *   ** WHEN TWO DIFFERENT RENDERINGS OF ONE IDEA BOTH GET
                          *      REJECTED, THE IDEA IS NOT BADLY DRAWN. IT IS IN THE WRONG
                          *      ROOM. **
                          *
                          * ⭐ SO `severity` STAYS ON THE DATA and nothing draws it. #269's
                          * rule survives intact - every warning at the same weight - and the
                          * flag is there for the reporting screen Pete named, which is a
                          * place that can RANK and TOTAL it. A card cannot; it shows one
                          * driver, and a scheduler reading one card already knows which of
                          * three lines matters.
                          *
                          * ⚠️ `checkin-panel-guard` no longer asserts the word is drawn; it
                          * asserts the flag still REACHES the warning, and that nothing has
                          * quietly started drawing it again.
                          */}
                        {/* ⛔⛤ THE COUNTER NO LONGER CARRIES SEVERITY. It was briefly two
                            tones; severity is the label's job now, and a counter that also
                            shaded itself would be saying the same thing twice in a channel
                            that cannot explain itself. ⭐ Solid, matching the header badge. */}
                        <span className="ml-1 mt-px inline-flex h-4 w-4 shrink-0 self-start items-center
                                         justify-center rounded-full bg-amber-600 text-[9px] font-bold
                                         tabular-nums text-white">
                            {i + 1}
                        </span>
                    </>
                );
                /* ⛔ A BUTTON ONLY WHEN THERE IS SOMEWHERE TO GO. A div that looks
                   identical to a button is a control that does nothing when pressed,
                   and a scheduler learns to stop pressing all of them.
                     ** SAMENESS OF APPEARANCE MUST NOT OUTRUN SAMENESS OF
                        BEHAVIOUR. ** So the cursor and the hover tint differ; the
                   box, the spacing, the type and the counter do not. */
                /* ⭐ THE HOVER IS THE WARNING'S, NOT THE CARD'S. React dispatches
                   enter/leave only along the path between the old and new targets, so
                   moving from the card body onto this box fires THIS handler and does
                   not fire the card's leave — which is why the caller restores the
                   card's own preview on `null` rather than clearing it.
                     ** A NESTED HOVER REPLACES ITS PARENT'S, IT DOES NOT SUCCEED IT.
                        THE WAY BACK OUT HAS TO BE WRITTEN. ** */
                const hoverable = Boolean(onHoverWarning && w.appointmentIds && w.appointmentIds.length > 0);
                const hoverProps = hoverable
                    ? {
                        onMouseEnter: () => onHoverWarning?.(w),
                        onMouseLeave: () => onHoverWarning?.(null),
                    }
                    : {};
                /* ⛔⛔ ONE BOX STYLE STILL. `WARN_BOX` is untouched and there is no second
                   constant — the pressed state is a RING, which adds an outline without
                   competing with the box's own border, fill or type. ⚠️ Not a darker
                   background: that is the colour channel Pete read as inconsistency
                   three hours ago, and it would be indistinguishable from the hover. */
                const pressed = w.active ? ' ring-2 ring-inset ring-amber-600' : '';
                return w.onClick ? (
                    <button
                        key={w.code + i}
                        type="button"
                        title={w.title}
                        aria-pressed={w.active}
                        {...hoverProps}
                        /* ⛔ stopPropagation AND onDoubleClick, like every control on
                           these cards: the root opens the editor on double-click. */
                        onClick={(e) => { e.stopPropagation(); w.onClick?.(e); }}
                        onDoubleClick={(e) => e.stopPropagation()}
                        className={WARN_BOX + ' cursor-pointer hover:bg-amber-100' + pressed}
                    >{body}</button>
                ) : (
                    <div key={w.code + i} {...hoverProps} className={WARN_BOX + pressed}>{body}</div>
                );
            })}
        </div>
    );
}

function phoneTail(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 0) return null;
    return `x${digits.slice(-4)}`;
}

/**
 * ---- ⭐⭐ A TEAMS CALL LINK, OR NOTHING AT ALL ------------------------------
 *
 * > Pete, 2026-09-09: *"I think we pull up the text screen when you click this
 * > but then have the option to call the driver with a button via Teams?"*
 *
 * ⭐ TEXTING IS THE JOB; CALLING IS THE ESCAPE HATCH. The driver is not
 * answering, or the thing to say does not fit a composed message. So this is one
 * row at the BOTTOM of the texting menu, not a second entry point on the card.
 *
 * ---- ⛔⛔ IT RETURNS null RATHER THAN GUESS ---------------------------------
 *
 * ⚠️ Teams dials PSTN from an E.164 string — `4:+13129520484`. `driverPhoneEntered`
 * is whatever the gate typed, so the ONLY shapes accepted are the ones that can be
 * turned into E.164 without inventing anything:
 *   · 10 digits          -> +1 (Symbia is US: Denver, Reno, Houston, Nashville,
 *                                Brighton. There is no other country code to pick.)
 *   · 11 digits, 1-first -> +
 *   · already +          -> kept, 8..15 digits per E.164
 *
 * ⛔ ANY OTHER SHAPE GETS NO BUTTON, AND THE MENU SAYS WHY. A guessed country code
 * is a WRONG NUMBER DIALLED FROM A WAREHOUSE OFFICE — a real cost paid by a
 * stranger — and it would fail exactly the way this codebase keeps getting caught:
 * silently, looking like it worked. The full number is still selectable text in the
 * breakout above, so the scheduler always has something to dial by hand.
 *
 * ---- ⚠️ https://teams.microsoft.com, NOT msteams: -------------------------
 *
 * ⛔ The `msteams:` protocol handler is what produced Pete's *"Open Microsoft
 * Teams?"* prompt — and on a machine WITHOUT Teams a custom protocol fails to
 * nothing at all. The https form is a real URL: it opens the Teams web app, and
 * hands off to the desktop client when one is installed. ⭐ A link that degrades
 * to a working web page beats one that degrades to silence.
 */
export function teamsCallHref(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    const digits = trimmed.replace(/\D/g, '');

    let e164: string | null = null;
    if (trimmed.startsWith('+')) {
        // ⚠️ E.164 is at most 15 digits, and a country code plus a subscriber
        //    number is never under 8. Outside that it is not a phone number.
        if (digits.length >= 8 && digits.length <= 15) e164 = `+${digits}`;
    } else if (digits.length === 10) {
        e164 = `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
        e164 = `+${digits}`;
    }
    if (!e164) return null;

    // ⚠️ The `+` MUST be percent-encoded. Unencoded it is a SPACE in a query
    //    string, and Teams would receive `4: 13129520484` -- which is not a
    //    number it can dial, and not an error it reports.
    return `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(`4:${e164}`)}`;
}

function hhmm(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** "2h 14m" — the number that makes a stale labour session obvious. */
function elapsed(sinceIso: string, now: number): string {
    const t = new Date(sinceIso).getTime();
    if (!Number.isFinite(t)) return '';
    const mins = Math.max(0, Math.floor((now - t) / 60_000));
    const h = Math.floor(mins / 60);
    return h > 0 ? `${h}h ${mins % 60}m` : `${mins}m`;
}

/**
 * ⭐ A CORNER TICK MEANING "THIS HAS BEEN CAPTURED" (#143).
 *
 * Pete, 2026-08-20: *"we need some kinda of indicator on the card that Paperwork and
 * License were scanned, not the Needs Licence checkbox. Maybe a checkmark in the
 * Scan Paperwork and Scan License buttons in one of the corners."*
 *
 * ⛔ NOT THE "NEEDS LICENSE" FILTER, which answers a different question — that
 * narrows the WHOLE LIST to trucks still waiting, and tells you nothing about the
 * truck you are looking at. This is per-card, per-artifact state.
 *
 * ⚠️ ABSENCE IS SILENT. An empty corner means "not captured"; there is no red cross,
 * because most arrivals legitimately have no paperwork for most of their visit and a
 * panel of warnings is a panel nobody reads.
 */
/* ⛔⚤ ~~`checkInEventLabel`~~ — MOVED 2026-09-20 to `lib/checkInEvents.ts`, so the
   History rail can use it. It printed sentences here and the rail printed raw event
   codes; deleting the card's log without moving this would have been a downgrade
   wearing the shape of a cleanup. */

/**
 * ---- ⭐ ONE TOOL ICON, USED FOR ALL OF THEM ---------------------------------
 *
 * Pete, 2026-08-25, on a card carrying seven text buttons: *"This completely removes
 * all buttons and gives a clean, much need real estate look."*
 *
 * ⛔ AN ICON WITH NO NAME IS ONLY HONEST IF IT HAS A TOOLTIP AND A LABEL, and both come
 * from the same string here so they cannot drift apart. A strip of four unlabelled
 * glyphs is unusable to a screen reader and unguessable to a new scheduler; `title`
 * answers the second and `aria-label` the first.
 *
 * ⭐ `dot` MARKS "WE HAVE ONE ALREADY" — a licence on file, paperwork filed. It is the
 * same job the old text buttons did by renaming themselves, which is precisely what
 * made them unlearnable: the control a scheduler hunts for should not change its name
 * as data arrives.
 *
 * ⚠️ `warn` IS FOR THE STATE SOMEBODY MUST ACT ON — an outbound truck with no licence,
 * a driver who cannot be told his door because there is not one yet. ⛔ It is amber and
 * NOT red: neither is an error, and spending red here would blunt it where it counts.
 *
 * ⛔ IT STOPS THE CLICK. The card edits on double-click now, so an un-stopped press
 * would open the editor behind whatever the icon opened.
 */
/**
 * ---- ⭐ F110 — THE DETENTION TALLY BESIDE "DRIVER CHECK-INS" (2026-09-29) ------
 *
 * > Pete: *"change the circle i next to driver checkins. instead of directions, i want
 * > that to be our driver detention indicator matching the driver card total numbers
 * > of detentions for the day that are orange or red. The minute a detention becomes
 * > red out of any of them, this circle … becomes red too. When you hover it … say
 * > something like 4 near detention and 8 in detention"* · *"Maybe its the clock
 * > detention symbol too, not the circle like warning. So the clock symbol you have
 * > with a number next to it"*.
 *
 * ⛔⤴ ~~ProcessHelp — "How a check-in works" (2026-08-25)~~ — REPLACED at Pete's
 * request. The (i) explained the kiosk flow; its slot now carries the detention clock.
 *
 * ⭐ THE SAME CHIP AS THE CARDS: the Timer glyph and a number, ORANGE while every
 * truck on the clock is inside its free time, RED the moment any one is past it. The
 * number is how many on-site cards show a chip (orange + red) — the cards and the
 * tally are counted from the same rows by the same rule (`detentionState`), so they
 * cannot disagree. Grey 0 when nobody is on the clock.
 *
 * ⭐ HOVER (and focus, and click-to-pin — a dock office reads this on a touch screen)
 * opens the breakdown: "N near detention · M in detention", then each truck, longest
 * clock first.
 *
 * ⭐ F155 (Decision 329) — THE CLOCK COUNTS DOWN, here as on the board. Pete: *"the
 * detention clock should start at his appointment time and count down from there.
 * Should only be 1-hour … Turn orange at 30 and red at 15"* → *"Thats on the dock
 * scheduler app to"*. Above the orange lead the chip wears the ARRIVED status colour
 * (*"Same color as the status - arrived."*); orange inside it; red inside the red lead
 * and past zero, where it counts up "over". "Near detention" is now the orange and
 * red trucks that still have time; a green truck is on the clock but not near.
 * The tally is the WORST chip's colour; its number is every truck on the clock.
 */
/** The chip classes for a level — card, tally list and tally button share them. */
export function detentionChipTone(level: DetentionLevel): string {
    return isDetentionRed(level) ? 'bg-red-100 text-red-700'
        : level === 'warn' ? 'bg-orange-100 text-orange-800'
            : 'bg-status-arrived/15 text-status-arrived';
}
/**
 * ⛔ F110b — WHO IS ON THE DETENTION CLOCK. Pete: *"Make sure we are not measuring drivers
 * that show with no appointment assigned"* · *"has to have an approved or arrived
 * appointment, not requested either"*. One test, used by the card chip and the tally.
 */
/**
 * ⭐ F156 — WHAT "truck N" MEANS, ON HOVER. Pete: *"can't tell what truck 1, 2, 3 are
 * for (carrier, client????)"* → popup *"Keep it, add a hover"*. It is the CLIENT's Nth
 * truck through the gate today (`dock.vw_DriverCheckInSequence.ClientTruckNo`, 139);
 * the `#N` above it is the order across every client. One sentence, card and editor.
 */
export function clientTruckTitle(c: { clientTruckNo: number | null; clientCode: string | null; arrivalSeq: number | null }): string {
    const n = c.clientTruckNo ?? 0;
    const sfx = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
    const who = c.clientCode ? `${c.clientCode} truck` : "truck for this client";
    const overall = c.arrivalSeq !== null ? ` (arrival #${c.arrivalSeq} overall)` : '';
    return `${n}${sfx} ${who} today${overall}`;
}

/**
 * ⭐ F166 — THE CLIENT'S NAME BESIDE "truck N". Pete, on the empty space right of the
 * badge: *"Is there anything next to Truck 1 on the right side, if not, put the client
 * or carrier name here"* → popup *"Client name"* (the badge counts the CLIENT's trucks,
 * so the name says whose count it is).
 *
 * Master name first (`partyNames`, the Clients list), then the booking's own copy,
 * then the code. The booking copy is often blank (GEODIS read as GEOT, see
 * `partyNames`), so it is never the first choice. `null` when the arrival is not tied
 * to a client yet: the slot stays empty rather than guessing.
 */
export function clientDisplayName(
    c: { clientCode: string | null },
    appt: { clientCode: string | null; clientName: string | null } | null | undefined,
    partyNames: ReadonlyMap<string, string> | undefined,
): string | null {
    const code = c.clientCode ?? appt?.clientCode ?? null;
    if (!code) return null;
    // `||`, not `??`: a blank copy ("") must fall through like a missing one.
    return partyNames?.get(partyNameKey('client', code))
        || (appt && appt.clientCode === code ? appt.clientName : null)
        || code;
}

export function onDetentionClock(appt: { statusCode: string } | null | undefined): appt is { statusCode: string } {
    return !!appt && (appt.statusCode === 'APPROVED' || appt.statusCode === 'ARRIVED');
}

export interface DetentionTallyTruck {
    key: string;
    label: string;
    level: Exclude<DetentionLevel, 'off'>;
    clockMinutes: number;
    /** Minutes before detention (negative once over) — the list sorts on it. */
    minutesLeft: number;
    /** What the chip says: "45m left" / "12m over" (`detentionLabel`). */
    clockText: string;
}

export function DetentionSummary({ trucks, settings }: {
    trucks: readonly DetentionTallyTruck[];
    /** The facility's settings (migrations 224 + 229), for the definition line. Null = unknown. */
    settings: DetentionSettings | null;
}) {
    const [open, setOpen] = useState(false);
    const [pinned, setPinned] = useState(false);
    const ref = useRef<HTMLButtonElement>(null);
    const [box, setBox] = useState<{ left: number; top: number } | null>(null);

    const over = trucks.filter(t => t.level === 'over').length;
    const near = trucks.filter(t => t.level === 'warn' || t.level === 'urgent').length;
    const onTime = trucks.length - over - near;
    const worst: DetentionLevel | null = over > 0 ? 'over'
        : trucks.some(t => t.level === 'urgent') ? 'urgent'
            : near > 0 ? 'warn' : onTime > 0 ? 'ok' : null;
    const tone = worst === null ? 'bg-muted text-muted-foreground' : detentionChipTone(worst);
    const summary = `${onTime} with time left · ${near} near detention · ${over} in detention`;

    const place = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const W = 280;
        // ⚠️ Opens to the LEFT: this panel lives on the right edge of the screen.
        const left = Math.max(8, Math.min(r.left - W - 8, window.innerWidth - W - 8));
        const top = Math.min(r.bottom + 6, window.innerHeight - 320);
        setBox({ left, top });
    }, []);

    const show = () => { place(); setOpen(true); };
    const hide = () => { if (!pinned) setOpen(false); };

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { setPinned(false); setOpen(false); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    /* ⭐ F229 — Pete: *"The clock should show only 2 red alerts, when its only yellow only show
       that number. Or maybe you show yellow right next to it with the number"* · *"Don't need to
       see the green ones"*. The button shows the RED count (red lead + over) and the ORANGE count
       beside it, each only when it is not zero; a green truck is not counted or listed. */
    const red = trucks.filter(t => isDetentionRed(t.level)).length;
    const orange = trucks.filter(t => t.level === 'warn').length;
    const sorted = trucks.filter(t => t.level !== 'ok').sort((a, b) => a.minutesLeft - b.minutesLeft);

    return (
        <>
            <button
                ref={ref}
                type="button"
                title={summary}
                aria-label={`Detention: ${summary}`}
                aria-expanded={open}
                data-detention-tally={worst ?? 'none'}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={() => { setPinned(false); setOpen(false); }}
                onClick={() => {
                    if (pinned) { setPinned(false); setOpen(false); }
                    else { setPinned(true); show(); }
                }}
                className={`inline-flex shrink-0 items-center gap-1 rounded text-[11px] font-medium tabular-nums ${red + orange === 0 ? `px-1 ${tone}` : ''}`}
            >
                {red + orange === 0 ? (
                    <><Timer className="h-3 w-3" aria-hidden="true" />0</>
                ) : (<>
                    {red > 0 && (
                        <span className={`inline-flex items-center gap-0.5 rounded px-1 ${detentionChipTone('over')}`} data-detention-red={red}>
                            <Timer className="h-3 w-3" aria-hidden="true" />{red}
                        </span>
                    )}
                    {orange > 0 && (
                        <span className={`inline-flex items-center gap-0.5 rounded px-1 ${detentionChipTone('warn')}`} data-detention-orange={orange}>
                            <Timer className="h-3 w-3" aria-hidden="true" />{orange}
                        </span>
                    )}
                </>)}
            </button>

            {open && box && createPortal(
                <div
                    style={{ left: box.left, top: box.top, width: 280 }}
                    className="fixed z-50 rounded-lg border bg-background p-3 shadow-2xl"
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => { if (!pinned) setOpen(false); }}
                >
                    <div className="mb-1.5 text-xs font-semibold">Detention</div>
                    <div className="mb-2 flex flex-wrap gap-x-3 text-[11px]">
                        <span className="text-status-arrived">{onTime} with time left</span>
                        <span className="text-orange-800">{near} near detention</span>
                        <span className="text-red-700">{over} in detention</span>
                    </div>
                    {/**
                      * ⭐ THE DEFINITION, UNDER THE TWO LINES (Pete: *"Maybe the definition
                      * under the two lines when you hover that explains what they mean …
                      * but make it sound better"*; popup → the clock rule is unchanged,
                      * Decision 280). ⛔ The free time is the facility's setting, never a
                      * hard-coded "2 hours".
                      */}
                    <p className="mb-2 border-b pb-2 text-[10px] leading-snug text-muted-foreground" data-detention-definition="">
                        Each truck's clock starts at its appointment time — or when it arrives, if it came
                        late — and counts down the{settings ? ` ${formatMinutes(settings.freeMinutes)}` : ''} it
                        has to load or unload, including any time waiting for a door.{' '}
                        <span className="text-orange-800">Orange</span>
                        {settings && settings.warnMinutes > 0 ? ` with ${formatMinutes(settings.warnMinutes)} left` : ''},{' '}
                        <span className="text-red-700">red</span>
                        {settings && (settings.urgentMinutes ?? 0) > 0 ? ` with ${formatMinutes(settings.urgentMinutes ?? 0)} left` : ''}.{' '}
                        <span className="text-red-700">In detention</span>: the time is up and the clock
                        counts the minutes over — the time a carrier can bill.
                        {/* ⭐ F173 — say when some clients have their own time */}
                        {settings && settings.rules && settings.rules.length > 0 ? ' Some clients have their own time (Settings → Appointments).' : ''}
                    </p>
                    {sorted.length === 0 ? (
                        <div className="text-[11px] text-muted-foreground">No truck is near or in detention.</div>
                    ) : (
                        <ul className="max-h-64 space-y-0.5 overflow-y-auto">
                            {sorted.map(t => (
                                <li key={t.key} className="flex items-center gap-2 text-[11px]">
                                    <span className={`inline-flex shrink-0 items-center gap-0.5 rounded px-1 tabular-nums ${detentionChipTone(t.level)}`}>
                                        <Timer className="h-3 w-3" aria-hidden="true" />{t.clockText}
                                    </span>
                                    <span className="min-w-0 truncate">{t.label}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>,
                document.body,
            )}
        </>
    );
}

/**
 * ---- ⭐ THE LICENCE-PLATE GLYPH (#73, Pete: *"Or a license plate icon"*) -----
 *
 * ⛔ IT IS DRAWN HERE RATHER THAN IMPORTED. lucide-react has no plate, and the
 * near misses each say something else: `CreditCard` is a payment, `Container` is
 * the box this icon is partly about (so it would collide with the very field it
 * opens), `RectangleEllipsis` is a form control. A wrong glyph on a strip where
 * every other icon is literal is worse than an unfamiliar one.
 *
 * ⚠️ `currentColor` AND 3.5 UNITS, so it inherits `CardIcon`'s amber/muted
 * states exactly like the lucide icons beside it. A hard-coded colour here would
 * be the one icon in the strip that does not turn amber with the rest.
 *
 * ⚠️ `aria-hidden` — `CardIcon` already carries the label; a titled SVG would
 * read the name twice to a screen reader.
 */
function PlateGlyph({ className = 'h-3.5 w-3.5' }: { className?: string }) {
    return (
        <svg
            className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        >
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <path d="M7 11v2M11 11v2M15 11v2M19 11v2" />
        </svg>
    );
}

function CardIcon({
    label, onClick, onMouseEnter, expanded, disabled, children,
    dot = false, count = null, warn = false, destructive = false,
}: {
    label: string;
    /**
     * ⭐ THE EVENT IS PASSED THROUGH (2026-09-09). The "All options" icon opens a
     * menu and needs its own rectangle to place it — `getBoundingClientRect()` on
     * `currentTarget`.
     *
     * ⚠️ NOT THE MOUSE POSITION. A keyboard or touch activation has no cursor, and
     * a menu placed at (0, 0) is how that fails — quietly, and only for the people
     * who do not use a mouse.
     *
     * ⛔ EVERY EXISTING CALLER IGNORES IT. A handler that takes no argument is
     * assignable to one that is passed an extra, so nothing else changes.
     */
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    /**
     * ⭐ #289 — HOVER OPENS, CLICK PINS. Only the arrival-history icon uses this.
     *
     * ⛔ THE CARD ITSELF USED TO BE THE TRIGGER, and Pete's complaint is what that
     * cost: *"the driver-card hover expands the history and eats the rail."* A
     * rail of twenty cards where merely moving the mouse across one pushes the
     * other nineteen down is a list that will not hold still while it is read.
     */
    onMouseEnter?: () => void;
    /** Drawn pressed while its panel is open, so a pinned icon looks pinned. */
    expanded?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    dot?: boolean;
    /**
     * ⭐ A NUMBER IN PLACE OF THE DOT (2026-08-27). Pete: *"showing a count where the
     * green dot is on the eyeball of number of docs in the driver card."*
     *
     * ⛔ IT REPLACES THE DOT RATHER THAN SITTING BESIDE IT. Both would mean the same
     * thing twice in eight pixels — and the dot's only job was ever "there is
     * something here", which a number says better.
     *
     * ⚠️ CAPPED AT 9+. Two digits do not fit on a 12px icon corner, and the
     * difference between 12 and 13 documents is not a difference anybody acts on.
     */
    count?: number | null;
    warn?: boolean;
    destructive?: boolean;
}) {
    return (
        <button
            type="button"
            title={label}
            aria-label={label}
            aria-expanded={expanded}
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); onClick(e); }}
            onMouseEnter={onMouseEnter}
            className={'relative rounded p-1 disabled:opacity-30 '
                + (expanded ? 'bg-muted text-foreground ' : '')
                + (destructive
                    ? 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                    : warn
                        ? 'text-amber-700 hover:bg-amber-100'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground')}
        >
            {children}
            {count != null && count > 0 ? (
                <span
                    aria-hidden="true"
                    className="absolute -right-1 -top-1 min-w-[13px] rounded-full bg-emerald-600 px-[3px]
                               text-center text-[9px] font-bold leading-[13px] text-white"
                >
                    {count > 9 ? '9+' : count}
                </span>
            ) : dot ? (
                <span
                    aria-hidden="true"
                    className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-emerald-600"
                />
            ) : null}
        </button>
    );
}

/**
 * ⛔ `ScannedTick` LIVED HERE AND IS GONE. Its two callers — the licence button and the
 * paperwork button — became icons on 2026-08-25, and `CardIcon`'s `dot` does the same
 * job in the one place all four icons already share. ⚠️ Keeping it would have left a
 * SECOND way to draw "we have one of these", which is how two ticks end up meaning
 * subtly different things on the same card.
 */

/**
 * ---- ⭐⭐ WHICH CARDS ARE OPEN, AND WHY IT LIVES IN THE BROWSER --------------
 *
 * Pete, 2026-09-08: *"The driver card also has no drop arrow, only showing the 3
 * rows we have discussed"* — collapsed by default, **remembered per card**.
 *
 * ⛔⛔ THIS IS NOT CONFIGURATION AND MUST NEVER BECOME A TABLE. Migration 172's
 * header draws the line and it is the same line here:
 *
 *   · **AVAILABLE** — does this site use Labour at all? A fact about the SITE.
 *     DB-backed config, per facility.
 *   · **COLLAPSED** — have *I* folded this card right now? A fact about ONE
 *     PERSON at ONE BROWSER.  ⭐ **That is this.**
 *
 * ⚠️ Persisting it server-side would mean one scheduler folding an arrival folds
 * it for the supervisor standing at the next screen — during the exact minutes
 * both are looking at the same truck.
 *
 * ---- ⚠️ IT IS CAPPED, AND THAT IS NOT TIDYING -------------------------------
 *
 * ⛔ THE OBVIOUS IMPLEMENTATION GROWS WITHOUT BOUND. Every arrival ever opened
 * would keep its id forever — a few hundred a week per browser, in a store with a
 * hard quota, and the failure mode when that quota is hit is `setItem` THROWING
 * on a write that has nothing to do with the card being opened.
 *
 * ⭐ SO THE NEWEST 300 SURVIVE AND THE REST FALL OFF THE FRONT. A `Set` preserves
 * insertion order and re-adding moves an id to the end, so "newest" is free.
 *
 * ⚠️ AND EVERY ACCESS IS WRAPPED. Private-mode Safari and a browser set to block
 * site data both THROW on `localStorage` rather than returning null. An
 * unwrapped read here would take out the whole rail — the panel the screen exists
 * for — over a preference about a chevron.
 */
const OPEN_CARDS_KEY = 'dock.checkin.openCards.v1';

/**
 * ⭐⭐ THE ARRIVAL LIST'S CEILING WHILE A RAIL PANEL IS OPEN — a QUARTER OF THE
 * SCREEN, so the panel somebody just opened gets the other three quarters.
 *
 * > Pete, 2026-09-10: *"Think we need a scroll bar on the Driver Checkins after 3
 * > drivers so it doesn't encroch too much on the other panels"* … then, with four
 * > arrivals and one card expanded: *"none of the panels expand so you can see them.
 * > They need to expand to the 3/4 way up like we set before, so one driver panel is
 * > shone but rest below is the panel you opened."*
 *
 * ⛔ ~~`max-h-[16rem]`~~ — A CARD COUNT WAS THE WRONG UNIT. Three cards is a
 * different fraction of a 13" laptop than of a 27" monitor, and Pete's second
 * sentence is about the FRACTION, not the count. ⚠️ It also read as generous on a
 * short screen, which is exactly where the panels had least room.
 *
 * ⭐ MEASURED IN CHROMIUM at three viewport heights: the open panel's slot takes
 * 57% / 70% / 72% of the column at 900 / 760 / 640px, and the arrival list lands at
 * about three cards, two cards, two cards. The scarcer the space, the larger the
 * share the opened panel gets — which is the right way round.
 *
 * ⛔ A LITERAL CLASS, NOT AN INTERPOLATED ONE. Tailwind generates CSS by scanning
 * source text; `max-h-[${n}vh]` is never seen, never emitted, and the ceiling
 * silently does nothing — the same trap `STATUS_TONE` exists to avoid, and it fails
 * looking like a styling preference rather than a missing rule.
 */
/* ⚠️ MODULE-LEVEL, NOT `new Set()` IN THE DEFAULT — a fresh reference every render
   would defeat every memo downstream that depends on it. Same reasoning as
   `EMPTY_APPT_IDS` in GanttCalendar. */
const EMPTY_APPT_ID_SET: ReadonlySet<string> = new Set<string>();

const ARRIVAL_LIST_MAX = 'max-h-[25vh]';
const OPEN_CARDS_CAP = 300;

/**
 * ============================================================================
 * ⭐⭐ ONE RAIL COLUMN OR TWO — THE SCHEDULER'S OWN CHOICE (added 2026-09-10)
 * ============================================================================
 *
 * > Pete: *"we want two right-hand columns, making the calendar shift to the
 * > left. Driver Checkins will be the column right next to the calendar and then
 * > the next column will be all the other panels."* … *"If we can make the two
 * > column or one column an option they can set on the screen, that would be
 * > great."*
 *
 * ---- ⛔ WHAT IT COSTS, MEASURED BEFORE IT WAS BUILT ------------------------
 *
 * A second 340px column takes **356px** off the grid, and `hourWidth` is
 * `max(80, (gridWidth - 160) / hours)`. So at a 9-hour day:
 *
 *   1280 -> 360px short   1440 -> 200px short   1600 -> 40px short
 *   1920 -> 111px/hr ok   2560 -> 182px/hr ok
 *
 * ⚠️ BELOW ~1800px THE DAY STOPS FITTING and the grid scrolls sideways. That is
 * the trade Pete named himself on 2026-08-25 — *"you start to lose view of the
 * dock doors and all the sites have a lot of dock doors"* — which is why the
 * DEFAULT follows the window and the CHOICE overrides it. ⛔ Choosing two
 * columns on a laptop is allowed: it is a decision a person makes in the moment
 * with the consequence visible, not one this file makes for them.
 *
 * ---- ⭐ WHY IT IS `localStorage` AND NOT A USER PREFERENCE ------------------
 *
 * Same store as `openCards` two hundred lines up, and the same reasoning as the
 * scan folder: this is a fact about THE SCREEN somebody is sitting at, not about
 * the person. ⚠️ A scheduler on a 4K desk and a supervisor on a 13" laptop want
 * different answers under one login. ⛔ It follows that it does NOT travel
 * between machines, which is the intended behaviour and not an omission.
 *
 * ---- ⛔⛔ AND TWO COLUMNS DELETE A WHOLE CLASS OF BUG --------------------
 *
 * `railSlot.ts`, `slotProps`, `anyPanelOpen` and `ARRIVAL_LIST_MAX` all exist
 * for ONE reason: the arrival list and the four panels compete for one column's
 * height. ⭐ Side by side they do not compete, so the ceiling lifts and the
 * arrival list takes its own column. ⚠️ The machinery STAYS because one-column
 * mode still needs it — it is now conditional, not dead.
 */
const RAIL_COLUMNS_KEY = 'dock.checkin.railColumns.v1';

/** ⚠️ The width at which a second 340px column stops costing the day grid. */
const TWO_COLUMN_MIN_PX = 1800;

type RailColumns = 'one' | 'two';

function readRailColumns(): RailColumns {
    try {
        const raw = window.localStorage.getItem(RAIL_COLUMNS_KEY);
        if (raw === 'one' || raw === 'two') return raw;
    } catch {
        /* ⚠️ Private mode or blocked site data THROWS here rather than returning
           null — the same note as `readOpenCards`. Fall through to the default. */
    }
    /* ⭐ NO STORED CHOICE MEANS "DECIDE FOR ME", and the window is what decides.
       ⛔ Not a hardcoded 'one': a 4K desk should open in two columns without
       anybody being told the feature exists. */
    return typeof window !== 'undefined' && window.innerWidth >= TWO_COLUMN_MIN_PX
        ? 'two' : 'one';
}

function writeRailColumns(v: RailColumns): void {
    try { window.localStorage.setItem(RAIL_COLUMNS_KEY, v); } catch { /* see above */ }
}

function readOpenCards(): Set<string> {
    try {
        const raw = window.localStorage.getItem(OPEN_CARDS_KEY);
        if (!raw) return new Set();
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) return new Set();
        return new Set(parsed.filter((x): x is string => typeof x === 'string'));
    } catch {
        return new Set();
    }
}

function writeOpenCards(ids: Set<string>): void {
    try {
        window.localStorage.setItem(OPEN_CARDS_KEY, JSON.stringify([...ids]));
    } catch {
        /* ⚠️ Quota, private mode, or site data blocked. The chevron still works
           for this session; it just will not be remembered. Nothing to tell the
           scheduler about — and NOTHING to throw over. */
    }
}

export function CheckInPanel({
    facilityCode, date, appointments, boardApptIds, boardFiltered = false, collapseSignal = 0,
    getToken, onChanged, onCreateFromCheckIn, appointmentMenuItems, ordersScope, onCreateFromOrder, onMatchOrder, interactingRef,
    subject, onSubject, partyScope = null, onChooseParty, onClearParty, onOpenPartyRecord, partyNames,
    refreshKey = 0, highlightAppointmentId = null, onHoverAppointment, onChooseRecord,
    selectedAppointmentIds = EMPTY_APPT_ID_SET, onListChanged,
    railFocus = null, previewFocus = null, onHoverParty, onClearRailFocus,
    warningScopeKey = null, onChooseWarningScope,
    footer, onTextDoor, onTextPaperwork, onTextWork, onPaperwork, uploadToCheckIn, onMarkDeparted, onAttached,
    onAttachRequest,
    onDropPaperworkOnCheckIn, onDropRefused, onVoidedPaperwork, onSign, onPrint,
    suggestion, onSuggestionClose, onSuggestionView,
}: Props) {
    const [rows, setRows] = useState<CheckInDto[]>([]);
    /** ⭐ #150 — which card the dragged paperwork is currently over. */
    const [paperworkOver, setPaperworkOver] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    /** null = closed; otherwise the check-in whose menu is open. */
    const [menuFor, setMenuFor] = useState<string | null>(null);
    const [voidFor, setVoidFor] = useState<CheckInDto | null>(null);

    /**
     * ---- ⭐⭐ THE DRIVER CARD'S OWN MENU (#341, Pete 2026-09-09) -------------
     *
     * > *"Why can't you give me a dropdown too that has the same things as when
     * > you right click an appointment??????? So i have all options????"*
     *
     * ⚠️ ONE MENU AT A TIME, held at panel level rather than per card — the same
     * single-slot shape `smsFor` and `menuFor` use. Two open menus is a state
     * nobody can observe and every dismiss path would have to clear both.
     */
    const [cardMenu, setCardMenu] = useState<{ c: CheckInDto; x: number; y: number } | null>(null);
    /**
     * ---- ⭐⭐ THE PARTY CARD'S OWN OPEN SET AND ITS OWN MENU (Pete, 2026-09-20) --
     *
     * > *"There is no double-click on the client card. There are no icons for
     * > communication (even if no phone number). There is no ... in right hand
     * > corner for the menu."*
     *
     * ⛔ KEYED `kind:code`, NOT `code`. `TRAFFIX` can be a client at one site and a
     * consignee at another, and the three chips render three different lists from
     * one component. A bare code would open a carrier card because a client of the
     * same name was expanded.
     *   ** A KEY MUST BE UNIQUE ACROSS EVERY LIST THAT SHARES THE STORE, NOT ONLY
     *      WITHIN THE LIST YOU ARE LOOKING AT. **
     *
     * ⛔⛔ AND IT IS **NOT** PERSISTED, unlike `openCards`. An arrival card's fold
     * is about one truck and survives a reload usefully; a party card's fold is
     * about a question the scheduler is asking right now, and a rail that comes
     * back tomorrow with four clients expanded is a rail somebody has to tidy
     * before they can read it. ⚠️ Stated because the neighbouring state DOES
     * persist, and "why does one and not the other" is the next reader's question.
     */
    const [openParties, setOpenParties] = useState<Set<string>>(new Set());
    const [partyMenu, setPartyMenu] = useState<{ row: PartyRow; x: number; y: number } | null>(null);
    /* ⭐ F130: a single click on a card waits for a possible second one (lib/clickIntent). */
    const clickIntent = useClickIntent();

    /**
     * ---- ⭐ THE CARD'S DWELL, MATCHING THE GRID'S ---------------------------
     *
     * ⚠️ 350ms IS NOT A TASTE DECISION — it is the number `GanttCalendar` uses
     * for its hover card, and the tray follows both. Two different dwells would
     * mean the tray changed at a different moment depending on where the cursor
     * was, which reads as unreliability rather than as two settings.
     *
     * ⛔ CLEARED ON LEAVE **AND** ON UNMOUNT. A timer that outlives the card
     * fires `onHoverCardShown` for an arrival the scheduler has scrolled past.
     */
    const trayDwell = useRef<number | null>(null);
    const clearTrayDwell = useCallback(() => {
        if (trayDwell.current !== null) { window.clearTimeout(trayDwell.current); trayDwell.current = null; }
    }, []);
    useEffect(() => () => clearTrayDwell(), [clearTrayDwell]);

    /**
     * ---- ⭐⭐ WAS A 350ms HOVER DWELL; IS NOW A CLICK (decision 190, 2026-09-20) ----
     *
     * ⛔ THE TIMER IS GONE, NOT SHORTENED. A dwell is a *guess* that the cursor meant
     * to stop; a click is a statement. Pete: *"to actually only show the specifics for
     * them you have to 1 click."*
     * ⚠️ THE PAPERWORK-DRAG GUARD SURVIVES THE MOVE and is not vestigial — a click
     * that lands mid-drop still unmounts the row the scan was heading for.
     */
    const chooseCheckIn = useCallback((c: CheckInDto) => {
        if (!onChooseRecord) return;
        /* ⛔⛔ NOT WHILE A SCAN IS BEING CARRIED. Switching the tray unmounts the
           row in mid-air and the drop never lands — see `paperworkDragInProgress`.
           ⚠️ Both hover sources need this; the driver card is as droppable as the
           chip is, and a fix on one of two is how this month has gone. */
        if (paperworkDragInProgress()) return;
        clearTrayDwell();
        {
            /**
             * ---- ⛔⛔ ~~AN ARRIVAL WITH NO LOAD REPORTS NOTHING~~ — FIXED -----
             *
             * ⛔ IT USED TO `return` AFTER `onHoverCardShown(null)` when the man
             * had no `appointmentId`. That was right while paperwork was the only
             * consumer, and it became a bug the moment four more panels read this:
             *
             * > Pete, 2026-09-10: *"Comms can be sent to a driver after he checks
             * > in, no matter if he is assoicated to an appointment yet."*
             *
             * ⭐ SO EVERY CARD NOW PRODUCES A FOCUS. Whether a given panel can
             * scope it is that panel's question — `focusBlockedReason` answers it
             * in words, and `Calendar` still skips the paperwork tray when there
             * is no load, so nothing about paperwork changed.
             *
             * ⚠️ THE LABEL, THE PHONE FALLBACK AND THE LOAD NUMBERS ALL MOVED INTO
             * `focusFromCheckIn`, which is covered by `rail-focus-test`. They were
             * assembled inline here and there was no way to run them.
             */
            onChooseRecord(focusFromCheckIn(c));
        }
    }, [onChooseRecord, clearTrayDwell]);

    /**
     * ⛔⛔ LEAVING A CARD CANCELS A PENDING DWELL. IT DOES NOT CLEAR THE TRAY.
     *
     * > Pete, 2026-09-10: *"shows up but doesn't stay, no point."*
     *
     * ⚠️ THE TIMER STILL HAS TO BE KILLED — otherwise brushing a card and moving
     * on would switch the tray 350ms later, to a truck the cursor has left.
     * ⭐ But what has ALREADY been shown stays: the tray is a mode with its own
     * name and its own way back, not a tooltip that follows the mouse.
     */
    const endTrayDwell = useCallback(() => {
        clearTrayDwell();
    }, [clearTrayDwell]);
    /** The card being edited, and the draft. Null = nothing is being edited. */
    const [editFor, setEditFor] = useState<CheckInDto | null>(null);
    /**
     * ⭐ IS THE POPUP'S "ALL OPTIONS" LIST EXPANDED. ⛔ Reset whenever the popup
     * opens on a different arrival — an options list left open from the last
     * driver would be a menu of actions aimed at the wrong truck.
     */
    const [dialogOptions, setDialogOptions] = useState(false);
    /* ⛔ ~~`detailFor` — THE SEPARATE OPTIONS DIALOG~~ REMOVED 2026-09-10 (§0.3:
       a superseded thing is struck WHERE IT LIVED). It stood between the click
       and the fields; its list is now the (…) section of the edit popup, built
       from the same `cardMenuItems`. See `startEdit`. */
    /**
     * The arrival whose licence is being scanned. Null = the dialog is shut.
     *
     * ⚠️ IT JOINS THE POLL-SUPPRESSION LIST BELOW. The Zebra DS22 types into
     * whatever has focus, so a re-render mid-burst would scatter half a licence
     * into the page behind the dialog.
     */
    const [licenceFor, setLicenceFor] = useState<CheckInDto | null>(null);
    /**
     * ⭐ #73 — THE ARRIVAL WHOSE TRAILER / CONTAINER / PLATE IS BEING TYPED IN.
     *
     * ⚠️ THE ARRIVAL, NOT AN ID, deliberately matching `licenceFor` beside it:
     * the dialog seeds its three boxes from the row it was handed. ⛔ It re-seeds
     * on `checkInId` rather than on the object, because the rail polls and a
     * fresh object every few seconds would wipe what the scheduler is typing.
     */
    const [vehicleFor, setVehicleFor] = useState<CheckInDto | null>(null);
    const [paperworkFor, setPaperworkFor] = useState<CheckInDto | null>(null);
    /**
     * ⭐ THE ARRIVAL WHOSE TEXTING MENU IS OPEN. Two different messages used to sit on
     * the card as two buttons — "Text door" and "Paperwork ready" — and a scheduler
     * could not tell at a glance which sent what. One icon, one menu, each option named.
     */
    const [smsFor, setSmsFor] = useState<string | null>(null);

    /**
     * ---- ⭐⭐ THE AD-HOC MESSAGE (#340, Pete 2026-09-09) --------------------
     *
     * > *"I said we can text the driver period. Not a message telling me I can't
     * > do it because of the status."*
     *
     * ⚠️ ONE DRAFT AT A TIME, KEYED BY NOTHING. Only one card's texting panel is
     * open at once (`smsFor` is a single slot), so a per-card map would be state
     * nobody could ever observe two of. ⛔ It is CLEARED when the panel closes,
     * so a sentence typed for one driver can never be sent to the next.
     */
    const [adHocText, setAdHocText] = useState('');
    const [adHocBusy, setAdHocBusy] = useState(false);
    const [adHocError, setAdHocError] = useState<string | null>(null);

    /**
     * ---- ⭐⭐ A SEND THAT SUCCEEDS HAS TO SAY SO ---------------------------
     *
     * > Pete, 2026-09-10: *"after I sent the text, the whole texting box
     * > disappears with no message if it sent or not."*
     *
     * ⛔ THE PANEL HAD A FAILURE STATE AND NO SUCCESS STATE. Failure printed
     * "Not sent — …"; success closed the box and printed nothing, so the two
     * outcomes were told apart by ABSENCE. ⚠️ On the afternoon this was reported,
     * every send had been failing for a week — silence and a 500 look identical,
     * and the scheduler's next move after either is to send it again.
     *
     * ⚠️ IT LIVES ON THE CARD, NOT IN THE PANEL, because the panel is gone by the
     * time there is anything to say — closing on success is deliberate (see
     * `keepCardOpen`) and Pete has not asked for that to change.
     *
     * ⭐ AND IT IS TRANSIENT, NOT A RECORD. "Did my click work" is answered here;
     * "has this driver been texted" is `c.lastText` and the arrival history, both
     * of which survive a reload. ⛔ Conflating the two would put a tick on a card
     * that outlives the fact it is reporting.
     */
    const [sentNote, setSentNote] = useState<{ id: string; to: string | null } | null>(null);
    const sentTimer = useRef<number | null>(null);
    /* ⛔ CLEARED ON UNMOUNT — the same rule the hover dwell follows. A timer that
       outlives the list sets state on a component nobody is looking at. */
    useEffect(() => () => {
        if (sentTimer.current !== null) window.clearTimeout(sentTimer.current);
    }, []);
    const noteSent = useCallback((id: string, to: string | null) => {
        setSentNote({ id, to });
        if (sentTimer.current !== null) window.clearTimeout(sentTimer.current);
        sentTimer.current = window.setTimeout(() => setSentNote(null), 6000);
    }, []);
    /**
     * ⭐ THE ARRIVAL BEING MATCHED TO A LOAD. Opens by itself after a licence is stored
     * — Pete, 2026-08-25: *"Think this process needs to goto the scheduler screen right
     * after he scans the drivers license"* — and is reachable from the card at any time,
     * because a licence that will not read must not strand a truck (see the dialog).
     */
    const [matchFor, setMatchFor] = useState<CheckInDto | null>(null);

    /**
     * ⭐ THE ARRIVAL'S OWN HISTORY, BEHIND AN (i) ICON (#98 / #140 / #289).
     *
     * Pete, 2026-08-20: *"we need to have the same mouse hover over on the Driver
     * Check In cards to show the history/log for the scheduler."*
     *
     * ⛔ AND THEN, 2026-08-27: *"the driver-card hover expands the history and eats
     * the rail"* — he wants an **(i)** icon that reveals it on hover instead.
     *
     * ⚠️ THE ASK IS ABOUT THE TRIGGER, NOT THE PLACEMENT. The log still renders
     * INLINE under its card, for the reason written where it renders: this is a
     * narrow right rail, and a floating popover would either fall out of the
     * viewport or cover the next card — the one being compared against. What
     * changed is that it no longer opens by ACCIDENT.
     *
     * ⛔ THE OLD TRIGGER WAS THE WHOLE CARD, and that is what made it unusable.
     * Dragging paperwork onto card seven meant crossing cards one through six, each
     * of which grew by eighty pixels on the way past and pushed the target out from
     * under the cursor. The card is a drop target FIRST; it cannot also react to
     * being crossed.
     *
     * ---- ⭐ HOVER OPENS, CLICK PINS, AND LEAVING THE CARD CLOSES -------------
     *
     * ⚠️ THE CLOSE IS BOUND TO THE CARD, NOT TO THE ICON, and that is deliberate.
     * The log renders BELOW the icon, so closing on the icon's own mouseleave would
     * shut it the instant a scheduler moved down to read it — the classic
     * disappearing-menu bug. Leaving the card entirely is the honest "done here".
     *
     * ⭐ AND CLICKING PINS IT, because reading nine events while holding a mouse
     * still is a thing people should not have to do. A pinned card survives the
     * mouse leaving; Escape and a second click let it go.
     *
     * ⛔ A DIFFERENT LOG FROM THE APPOINTMENT'S. `dock.DriverCheckInEvent`
     * (migration 142) records what happened to the ARRIVAL — every edit, void,
     * pallet variance, license scan and match. `dock.AppointmentAudit` records what
     * happened to the BOOKING. They overlap and they are not the same question.
     *
     * ⚠️ `GET mgmt/checkins/{id}/history` HAS EXISTED SINCE #91 AND NOTHING CALLED
     * IT. Every one of those events has been written where no scheduler could see
     * it, which is the definition of an audit trail that is not doing its job.
     *
     * Cached per arrival and fetched after a delay, mirroring the appointment hover
     * card — a scroll past forty cards must not fire forty requests.
     */
    /**
     * ---- ⛔⚤ ~~THE ARRIVAL-HISTORY MACHINERY~~ — **DELETED 2026-09-20** ----------
     *
     * Removed with the inline log it served: `histFor`, `hist`, `histTimer`,
     * `beginHover`, `endHover`, `histPinned`, `toggleHistPin` and the Escape-unpins
     * effect. Pete: *"The Arrival History ... doesn't need to be on the card anymore
     * because single clicking the driver will show the history in the history panel."*
     *
     * ⚠️ `GET mgmt/checkins/{id}/history` IS UNTOUCHED AND STILL SERVED. The rail's
     * History panel is the one caller now. ⛔ Do not delete the endpoint on the
     * strength of this removal — the note this block carried is still true and is
     * still the reason that panel exists: *"every one of those events has been
     * written where no scheduler could see it, which is the definition of an audit
     * trail that is not doing its job."*
     *
     * ⭐ `noUnusedLocals` FOUND EVERY ORPHAN, IN PASSES. The render block came out
     * first and the compiler named what no longer had a reader — the only reliable
     * way to do this in a file of six thousand lines.
     *   ** A DELETION IS FINISHED WHEN THE COMPILER STOPS NAMING THINGS, NOT WHEN
     *      THE SCREEN LOOKS RIGHT. **
     */
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [voidReason, setVoidReason] = useState('');
    /**
     * ⛔ TWO INDEPENDENT CHECKBOXES, NOT ONE "on site only" TOGGLE.
     *
     * Pete, 2026-08-20: *"The On Site Only doesn't function right. Should add a
     * Departed with the check box so you can check both or one or neither."*
     *
     * ⚠️ AND "NEITHER" SHOWS EVERYTHING, NOT NOTHING. A scheduler who unticks both
     * would otherwise get a blank panel with no explanation — which is precisely
     * the "doesn't function right" complaint, arrived at from the other direction.
     * So an empty selection means "no presence filter", and the label says so out
     * loud rather than leaving them to work it out from an empty list.
     */
    const [showOnSite, setShowOnSite] = useState(true);
    const [showDeparted, setShowDeparted] = useState(false);
    /**
     * ⭐ FILTERS. By mid-morning a busy dock has thirty cards in this rail and
     * the scheduler is looking for one client's trucks, or for the queue of
     * drivers still waiting to have a licence taken.
     *
     * ⚠️ CLIENT AND CARRIER ARE DROPDOWNS, NOT CHECKBOXES, and the difference
     * is not cosmetic: "filter by client" is only half a sentence — it has to
     * name WHICH client. A checkbox can only say "on/off", so three of them
     * (All/Client/Carrier) would leave the scheduler with no way to say which
     * one he meant. Each dropdown carries its own "All" as the default, which is
     * the same idea expressed in a control that can actually hold the answer.
     *
     * '' means "all" for both. Deliberately not null — a select's value is a
     * string, and an empty string is the one value that cannot be a real code.
     */
    const [clientFilter, setClientFilter] = useState('');
    const [carrierFilter, setCarrierFilter] = useState('');
    /* ⭐ THE DRIVER CHIP'S OWN PICKER (2026-09-20) — one box per subject, naming that
       subject, so all four chips read the same way. Keyed on the NAME rather than the
       check-in id: it is what the box shows and what a scheduler says out loud. */
    const [driverFilter, setDriverFilter] = useState('');
    /** The work queue: trucks on site whose licence has not been captured. */
    const [needsLicenceOnly, setNeedsLicenceOnly] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    /** ⭐ F103 — the facility's detention settings, from the check-in list itself. */
    const [detention, setDetention] = useState<DetentionSettings | null>(null);
    const firstLoad = useRef(true);
    /**
     * ⭐ VOIDED ARRIVALS, OFF BY DEFAULT AND FETCHED ONLY WHEN ASKED FOR.
     *
     * Pete, 2026-08-25, on learning a voided arrival's paperwork survives but has
     * no route to it: *"Yes, show voided."* Documents keep their own VoidedUtc
     * (still null) and stay correctly linked — voiding the ARRIVAL removed the
     * only way to reach them, which is "technically stored" and not much else.
     *
     * ⛔ OFF BY DEFAULT because the panel's job is trucks that are HERE. A voided
     * row is a record to inspect, not work to do, and mixing the two would put
     * noise in the one list a supervisor scans at a glance.
     */
    const [showVoided, setShowVoided] = useState(false);
    const [voided, setVoided] = useState<VoidedCheckInDto[]>([]);

    /**
     * ⭐ WHICH CARD HAS ITS ATTACH ZONE OPEN — one at a time, by `checkInId`.
     *
     * ⛔ NOT A DROP ZONE ON EVERY CARD. The rail holds twenty arrivals and the
     * whole point of this panel's recent work is real estate; a dashed box on
     * each would cost more height than the cards themselves. ⚠️ So it is one
     * icon in the row that already exists, and the control appears under the
     * card that asked for it.
     */
    const [attachFor, setAttachFor] = useState<string | null>(null);

    /**
     * ---- ⭐⭐ THE PAPERCLIP GOES STRAIGHT TO THE FILE PICKER ------------------
     *
     * > Pete, 2026-09-08: *"When I add a file I want it to go directly back to the
     * > driver panel, not an intermediate popup. Same on the initial click - go
     * > directly to file explorer, pick a file(s), then back to the driver panel."*
     *
     * ⚠️ ~~THE INLINE DROP ZONE~~ and then ~~THE DIALOG~~ both went, in one day.
     * The dialog was the right answer to *"looks terrible"* and the wrong answer
     * to what he actually wanted, which is no surface at all: click, pick, done.
     *
     * ⭐ ONE INPUT FOR THE WHOLE PANEL, NOT ONE PER CARD. Twenty arrivals would
     * otherwise mean twenty hidden inputs; the card being attached to is a ref.
     *
     * ⛔ THE TARGET IS A REF, NOT STATE, AND THIS IS THE PART THAT WOULD BREAK.
     * `setAttachFor` has not applied by the time `.click()` opens the picker, and
     * the change handler fires later still — a state read there is a coin flip on
     * React's batching. ⭐ The ref is set and read synchronously. `attachFor` is
     * kept ONLY so the right card can draw the progress line.
     *
     * ⚠️ AND THE LOOP IS STILL `useFileUpload`. It carries #288's snapshot-before-
     * await, sequential uploads, and the count reported on failure as well as
     * success. Hand-rolling three lines here is how there were four copies of this
     * logic in the first place.
     */
    const paperworkInputRef = useRef<HTMLInputElement>(null);
    const attachTargetRef = useRef<string | null>(null);
    /**
 * ⛔ THE CARD'S LATEST-TEXT LINE, OFF (Pete, 2026-09-24): *"Door Text - 1 hr 59m can
 * be suppressed for right now. We have the communications in the right hand panels
 * now."* ⭐ A NAMED CONSTANT RATHER THAN A BARE `false &&`, because "for right now"
 * is a decision with an owner and a reason, and a literal `false` buried in JSX is
 * indistinguishable from a typo six months later.
 *   ** A FEATURE TURNED OFF DELIBERATELY SHOULD SAY SO IN A NAME. **
 */
const SHOW_LAST_TEXT_ON_CARD = false;

/** ⚠️ `rows` read from inside a stable callback — see `uploadPaperwork`. */
    const rowsRef = useRef<CheckInDto[]>([]);

    /* ⭐ WHICH CARDS THIS PERSON HAS OPENED. See the note above the component for
       why it is browser storage and why it is capped. */
    const [openCards, setOpenCards] = useState<Set<string>>(readOpenCards);
    /* ⭐ ONE COLUMN OR TWO — see the note on `RAIL_COLUMNS_KEY`. ⚠️ Read ONCE, in a
       lazy initialiser: reading `window.innerWidth` on every render would make the
       layout flip mid-resize under a choice the scheduler had already made. */
    const [railColumns, setRailColumns] = useState<RailColumns>(readRailColumns);
    const twoColumn = railColumns === 'two';
    /**
     * ⚠️ ONLY FOR THE TOOLTIP'S WORDING — it never blocks the choice. ⛔ Read once
     * on mount rather than on resize: a warning that appears and disappears while
     * somebody drags a window edge is noise, and this is advice, not state.
     */
    const [narrowForTwo] = useState(
        () => typeof window !== 'undefined' && window.innerWidth < TWO_COLUMN_MIN_PX);

    /**
     * ---- ⛔ CLOSING THE TEXT PANEL MUST NOT FOLD THE CARD (Pete, 2026-09-10) --
     *
     * > *"if you do anything from that popup, the driver expansion of the card,
     * > showing the text options minimizes again. Not a huge deal but kinda a
     * > pain if you are just checking out the text options."*
     *
     * ⛔ THE CAUSE IS THAT `smsFor` WAS DOING TWO JOBS. A card renders expanded
     * when `openCards.has(id) || smsFor === id` — so opening the text panel from
     * a COLLAPSED card expanded it as a side effect, and clearing `smsFor`
     * collapsed it again. The card was never actually open; it was borrowing the
     * text panel's state.
     *
     * ⭐ SO CLOSING PROMOTES THE BORROWED STATE INTO A REAL ONE: the card joins
     * `openCards` on the way out. ⚠️ Which is what the menu item at "Text the
     * driver" already did on the way IN — this is the same pairing, closed.
     */
    /**
     * ---- ⛔⛔⛔ THE COMMS PANEL NO LONGER CLOSES ITSELF (Pete, 2026-09-24) ----
     *
     * > *"after you send the text, the communication panel disappears. I think now
     * > you just have the chevron and the comm icons like phone and text just open
     * > the whole panel and close the whole panel."*
     *
     * ⛔⛤ ~~`closeSmsKeepingCardOpen`, which did `setSmsFor(null)`~~. It was written
     * when sending was a modal act that ought to tidy up after itself. In practice the
     * scheduler sends a text and then wants to see THAT IT WENT -- and the panel
     * carrying the confirmation removed itself half a second before the confirmation
     * arrived.
     *   ** A SURFACE THAT DISMISSES ITSELF ON SUCCESS TAKES THE EVIDENCE OF SUCCESS
     *      WITH IT. **
     *
     * ⭐ SO THE PANEL IS NOW CONTROLLED IN EXACTLY ONE WAY: the chevron and the comms
     * icons toggle it, and nothing else opens or closes it. One control, one
     * behaviour, which is what Pete described.
     * ⚠️ THE CARD-OPEN HALF SURVIVES and is still needed: the canned buttons open a
     * preview dialog, and a collapsed card behind that dialog is a card the sender
     * cannot check against.
     */
    const keepCardOpen = useCallback((id: string) => {
        setOpenCards((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    }, []);

    /**
     * ---- ⭐⭐ EXPANDING A CARD ALSO OPENS ITS HISTORY (Pete, 2026-09-09) --------
     *
     * > *"when you hit the carrot now and expand it, it should automatically show
     * > the (i) the history in the breakout. It should push the other driver panels
     * > below the breakout (not cover them up)."*
     *
     * ⭐ THE SECOND HALF WAS ALREADY TRUE, and it is recorded here rather than
     * quietly agreed with. The log renders INLINE inside the card, never in a
     * portal, so it has always pushed the cards below it down. The note on the log
     * itself argues why: a popover on a 340px rail would either overflow the
     * viewport or cover the next card — which is the one being compared against.
     * ⛔ Do not "improve" this into a floating panel.
     *
     * ⚠️ IT PINS RATHER THAN HOVERS. `beginHover` waits 350ms and lets a mouseleave
     * cancel it; a card opened deliberately must not close because the mouse moved
     * on. Pinning is exactly what `toggleHistPin` already means.
     *
     * ⛔ AND COLLAPSING UNPINS. `detailOpen` counts `histFor === id` as a reason to
     * stay open, so a pin left behind would make the chevron look broken — click to
     * collapse, and nothing happens.
     */
    /**
     * ---- ⭐⭐ THE PARTY CHEVRON'S TOGGLE, AND IT IS NOT CAPPED --------------
     *
     * ⛔ `toggleCard` BELOW ENFORCES `OPEN_CARDS_CAP` because an arrival card's
     * expansion is TALL — a history, a picker, an SMS panel — and twenty open at
     * once is a rail nobody can scroll. ⚠️ A party card's expansion is two small
     * boxes of a fixed height, and there are only ever as many party cards as the
     * facility has clients on one day.
     * ⛔⛔ SO THE CAP IS OMITTED ON PURPOSE, NOT FORGOTTEN. A cap that silently
     * closes the card you opened two clicks ago is a feature that has to earn its
     * confusion, and this list does not need it.
     *   ** A LIMIT COPIED FROM A NEIGHBOUR IS A LIMIT NOBODY MEASURED. **
     */
    const togglePartyCard = useCallback((key: string) => {
        setOpenParties((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }, []);

    const toggleCard = useCallback((id: string) => {
        /* ⚠️ READ BEFORE WRITING. The set updater below is the wrong place to decide
           this: React may invoke it twice, and pinning is NOT idempotent —
           `toggleHistPin` would pin and then immediately unpin. */
        const opening = !openCards.has(id);

        setOpenCards((prev) => {
            const next = new Set(prev);
            // ⚠️ DELETE-THEN-ADD ON RE-OPEN IS DELIBERATE: a Set keeps insertion
            //    order, so this is what makes the cap drop the OLDEST rather than
            //    an arbitrary one.
            if (next.has(id)) next.delete(id);
            else {
                next.add(id);
                while (next.size > OPEN_CARDS_CAP) {
                    const oldest = next.values().next().value;
                    if (oldest === undefined) break;
                    next.delete(oldest);
                }
            }
            writeOpenCards(next);
            return next;
        });

        /* ⛔⚤ ~~`if (opening) { if (histPinned !== id) toggleHistPin(id); return; }`~~
           — REMOVED 2026-09-20. Expanding a card used to open its history as well;
           the history is the rail's job now. ⚠️ The `return` went with it, so opening
           falls through to the same end as before — there is nothing left to clear on
           the way up. */
        if (opening) return;

        /**
         * ---- ⛔⛔ COLLAPSING MUST CLEAR **EVERY** REASON THE CARD IS OPEN -----
         *
         * > Pete, 2026-09-09: *"The carrot doesn't fire correctly in this
         * > instance. It should collapse everything to the driver card but
         * > instead it collapses the arrival history - only the circle i should
         * > do that"*
         *
         * ⛔⛔ `detailOpen` IS AN **OR OF FOUR** INDEPENDENT REASONS —
         * `openCards`, `histFor`, `menuFor`, `smsFor` — AND THIS FUNCTION USED TO
         * CLEAR TWO. So on a card with the attach picker open (`menuFor`), the
         * chevron removed the card from `openCards` and unpinned the history, and
         * `detailOpen` STAYED TRUE on the strength of `menuFor` alone.
         *
         * ⚠️ WHICH IS WHY IT LOOKED LIKE A HISTORY BUG. The one thing that
         * visibly went away was the history — because that was the reason the
         * chevron DID clear — so the control appeared to be doing a DIFFERENT
         * job badly rather than its own job partly. ⭐ Pete read it exactly
         * right: *"only the circle i should do that."*
         *
         * ⭐⭐ THE RULE: A CONTROL THAT CLAIMS TO CLOSE A THING MUST CLOSE EVERY
         * REASON IT IS OPEN. Anything less works on most cards and silently
         * fails on the ones mid-task — which are the cards a scheduler is
         * actually looking at.
         *
         * ⛔ SO ADDING A FIFTH INPUT TO `detailOpen` MEANS ADDING A LINE HERE.
         * ⚠️ Nothing will remind you. The new reason will simply hold the card
         * open against the chevron, on some cards, sometimes.
         *
         * ⚠️ EACH CLEAR IS GUARDED BY `prev === id`. These are SINGLE-SLOT
         * states — one card's menu at a time — so clearing unconditionally
         * would shut a menu the person just opened on a DIFFERENT card by
         * collapsing this one.
         *
         * ⚠️ `histFor` IS CLEARED SEPARATELY FROM THE PIN, and that is not
         * belt-and-braces. `toggleHistPin` only fires when `histPinned === id`;
         * a history opened by HOVER sets `histFor` with no pin at all, and would
         * otherwise survive the collapse until the mouse happened to leave.
         */
        /* ⛔⚤ ~~the two history clears~~ — REMOVED 2026-09-20 with the inline log.
           ⚠️ THE RULE ABOVE IS UNCHANGED AND STILL LOAD-BEARING: collapsing must clear
           EVERY reason the card is open. There are now THREE, not four. Adding a fourth
           back means adding a line here — the 2026-09-09 bug was exactly this list being
           shorter than the `detailOpen` it mirrors. */
        setMenuFor((prev) => (prev === id ? null : prev));
        setSmsFor((prev) => (prev === id ? null : prev));
    }, [openCards]);

    /**
     * ---- ⭐⭐ Ⓡ RETURNS THE SCREEN TO ITS RESTING STATE (decision 190 C) -----------
     *
     * ⭐ Pete, 2026-09-20: *"when you hit the circle R, you should close the open
     * chevrons on a particular driver panel so its a single, non-expanded line again."*
     *
     * ⛔⛔ IT CLEARS ALL **FOUR** REASONS A CARD IS OPEN, and that is the rule this
     * codebase learned the hard way on 2026-09-09, when the chevron cleared two:
     *   ** A CONTROL THAT CLAIMS TO CLOSE A THING MUST CLOSE EVERY REASON IT IS OPEN. **
     * ⚠️ And it presented as a HISTORY bug, because history was the one reason it DID
     * clear — *"only the circle i should do that."* A half-reset does not look like a
     * half-reset; it looks like a different feature misbehaving.
     *
     * ⛔ UNCONDITIONALLY, UNLIKE `collapseCard(id)`. Those `prev === id` guards exist
     * to protect a DIFFERENT card's single-slot menu — and there is no different card
     * when you are closing all of them.
     *   ** A PER-ITEM RESET AND AN ALL-ITEMS RESET ARE NOT THE SAME FUNCTION CALLED A
     *      DIFFERENT NUMBER OF TIMES. **
     *
     * ⭐ AND IT WRITES THROUGH TO `localStorage`. Pete chose Ⓡ to mean fresh
     * INCLUDING after a reload; the rejected alternative made the reset silently undo
     * itself on refresh.
     *   ** A RESET THAT A REFRESH UNDOES IS NOT A RESET. **
     *
     * ⚠️ IF A FIFTH REASON IS EVER ADDED TO `detailOpen`, IT MUST BE ADDED HERE TOO.
     * Nothing will remind you — it will simply hold a card open against Ⓡ, on some
     * cards, sometimes.
     */
    const collapseAllCards = useCallback(() => {
        setOpenCards((prev) => {
            if (prev.size === 0) return prev;
            const empty = new Set<string>();
            writeOpenCards(empty);
            return empty;
        });
        /**
         * ---- ⛔⛔ THE PARTY CARDS ARE STATE NOW, AND THAT COST HAD TO BE PAID ----
         *
         * ⚠️ §1AV.180 PUT AN ARGUMENT IN WRITING THAT THIS LINE RETIRES: the comms
         * section opened on `chosen`, and the note called that *"a dividend — Ⓡ
         * clears the party scope, so Ⓡ collapses these cards with no new state and
         * no entry in `collapseAllCards`."*
         *
         * ⛔ IT WAS A REAL DIVIDEND AND PETE SPENT IT DELIBERATELY. He asked for the
         * chevron twice; a chevron is a choice that must survive a click that is not
         * about it, and `chosen` cannot express "open but not scoped". ⚠️ So the
         * state arrived, and with it exactly the entry that note said we were being
         * spared. **That is not the note being wrong — it is the price being named
         * in advance and then paid on purpose.**
         *   ** A DESIGN THAT AVOIDS STATE IS BUYING SOMETHING WITH A CONSTRAINT.
         *      WHEN THE CONSTRAINT IS LIFTED, THE PURCHASE IS DUE. **
         */
        setOpenParties((prev) => (prev.size === 0 ? prev : new Set<string>()));
        setPartyMenu(null);
        /* ⛔⚤ ~~setHistPinned(null); setHistFor(null);~~ — REMOVED 2026-09-20.
           ⚠️ THREE REASONS NOW, NOT FOUR, and the warning below still applies in full:
           a fifth reason added to `detailOpen` must be added here too, and nothing will
           remind you. */
        setMenuFor(null);
        setSmsFor(null);
    }, []);

    /**
     * ---- ⭐⭐ ONE RESET, THREE WAYS IN (Pete, 2026-09-20, decision 194) ------------
     *
     * ⭐ Pete: *"I also like if you single click a panel a second time it resets
     * everything just like hitting the circle Ⓡ"*.
     *
     * ⛔⛔ "RESETS EVERYTHING" IS THREE CALLS, AND THE PARTY CARD WAS MAKING ONE. It
     * called `onClearParty()` alone — clearing the board scope while the rail focus and
     * every open arrival card stayed exactly where they were. A reset that resets a
     * third of the screen is worse than no reset, because the user believes it worked.
     *   ** IF TWO CONTROLS CLAIM TO DO THE SAME THING, THEY MUST BE THE SAME FUNCTION.
     *      TWO LISTS OF CALLS IS TWO BEHAVIOURS WEARING ONE NAME. **
     *
     * ⚠️ IT DOES **NOT** COLLAPSE THE RAILS — decision 194's reversal of 190 C, and
     * the whole difference between this and what Ⓡ did the day before.
     *
     * ⛔⛔ DECLARED **HERE**, DIRECTLY AFTER `collapseAllCards`, AND THAT IS NOT STYLE.
     * A `useCallback` dependency array is evaluated during RENDER, so naming
     * `collapseAllCards` from a `useCallback` written above it is a temporal dead zone
     * error — a crash on first render, not a lint warning. ⚠️ It WAS written 500 lines
     * higher first, and it was caught by READING IT BACK, not by a tool: `tsc` accepts a
     * forward reference inside a callback BODY, and the deps array sits in the same
     * expression, so nothing red appeared. The first sign would have been a white screen.
     *   ** THE COMPILER CHECKS THE CALL SITE, NOT THE MOMENT. A DEPS ARRAY IS A
     *      DIFFERENT MOMENT FROM THE FUNCTION IT DESCRIBES. **
     *   ** A DEPENDENCY ARRAY RUNS AT RENDER TIME. IT CAN ONLY NAME WHAT HAS ALREADY
     *      BEEN DECLARED. **
     */
    const resetAll = useCallback(() => {
        onClearRailFocus?.();
        onClearParty?.();
        collapseAllCards();
    }, [onClearRailFocus, onClearParty, collapseAllCards]);

    /**
     * ---- ⭐⭐ Ⓡ MOVED TO THE BOARD HEADER, AND THE CARDS STILL COLLAPSE --------
     *
     * > Pete, 2026-09-27: **"One Ⓡ in the board header, retire both."**
     *
     * ⛔ THE PAGE CANNOT CALL `collapseAllCards` — it is this panel's own state. So it
     * bumps a counter and this watches it, the same seam `refreshKey` and
     * `paperworkKey` already use to say "do your thing now" to a child.
     *   ** A PARENT ASKS A CHILD TO ACT BY CHANGING SOMETHING THE CHILD IS WATCHING.
     *      AN IMPERATIVE HANDLE IS FOR WHEN THAT IS IMPOSSIBLE. **
     *
     * ⛔⛔ `> 0` IS NOT TIDINESS. Effects run on mount and the page starts this at 0 —
     * without the test, every mount would slam the arrival cards shut before the
     * scheduler had touched anything, and it would read as the panel refusing to open.
     *   ** AN EFFECT THAT WATCHES A COUNTER FIRES ONCE BEFORE ANYBODY HAS COUNTED. **
     *
     * ⛔⛔ DECLARED **HERE**, AFTER `collapseAllCards`, for the reason the block above
     * spells out: a dependency array is evaluated during render, so naming it from
     * higher up the file is a temporal-dead-zone crash, not a lint warning. That rule
     * decided two declarations today — this one and `railFocus` in `Calendar.tsx`.
     */
    useEffect(() => {
        if (collapseSignal > 0) collapseAllCards();
    }, [collapseSignal, collapseAllCards]);

    /**
     * ⭐⭐ THE FILTER STRIP, BEHIND THE SEARCH ICON (Pete, 2026-09-08).
     *
     * > *"maybe just have the search icon next to the (i) and when you click the
     * > search icon, [the strip] drop down."*
     *
     * ⚠️ CLOSED BY DEFAULT — six controls at the top of a rail that has to stack
     * driver cards is the real estate this whole redesign is about.
     */
    const [showFilters, setShowFilters] = useState(false);

    /**
     * ---- ⭐⭐ WHICH RAIL CARDS THIS FACILITY USES (migration 172) -------------
     *
     * > Pete, 2026-09-08: *"Can you those cards (scanned papwerork, labor,
     * > communications and Driver Checkin) have an active/inactive flag in config
     * > screen?"* — and, on seeing the deployed rail, *"Scanned Paperwork is still
     * > Visible."*
     *
     * ⛔⛔ `null` MEANS "NOT ANSWERED YET", NOT "NO CARDS". Those two readings
     * produce the same screen — an empty rail — and only one of them is correct.
     * ⚠️ Every consumer goes through `panelOn`, which is the only place that
     * distinction is made; nothing else may read this state directly.
     *
     * ⭐ IT FAILS OPEN, DELIBERATELY, AND THE SERVER MAKES THE SAME CHOICE. A
     * config lookup that fails shows the FULL rail. The cost is a card somebody
     * meant to hide; the cost of the other direction is a scheduler missing Labor
     * mid-shift with nothing on screen or in a health check to say why.
     *
     * ⛔ THIS IS AVAILABILITY, NEVER PERMISSION. Failing open is right here and
     * catastrophic three feet away — a hidden card is tidiness, a hidden
     * authorization check is a breach.
     */
    const [railPanels, setRailPanels] = useState<RailPanelDto[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const r = await getRailPanels(await getToken(), facilityCode);
                if (!cancelled) setRailPanels(r.panels);
            } catch {
                // ⚠️ QUIET, AND `null` IS THE RIGHT RESTING STATE. `panelOn` reads
                //    null as "show everything", so a failure here is already the
                //    safe answer — there is nothing to tell a scheduler and nothing
                //    for them to do about it.
                if (!cancelled) setRailPanels(null);
            }
        })();
        return () => { cancelled = true; };
    }, [facilityCode, getToken]);

    /**
     * ⭐ THE ONLY READER OF `railPanels`.
     *
     * ⚠️ AN UNKNOWN CODE IS ON. A card added to the UI before its vocabulary row
     * exists should appear, not vanish — the alternative is a feature that ships
     * invisible and is debugged as a build problem.
     */
    /**
     * ---- ⭐⭐ WHICH RAIL CARD IS OPEN, AND WHAT THE RAIL DOES WITH IT --------
     *
     * > Pete, 2026-09-09: *"when you expand a panel like Labor, it should go all
     * > the way and bump up against the panel above, not white space … the opened
     * > panel went to the top of the list of the 3 panels (but still below the
     * > driver section)."*
     *
     * ⛔ THREE FAULTS, ONE MISSING FACT. The rail did not know which card was
     * open, so it could not (a) give that card the leftover space, (b) stop
     * squeezing the CLOSED cards until their headers were cut in half, or
     * (c) move the open one to the top.
     *
     * ⚠️ (b) WAS THE WORST AND THE LEAST OBVIOUS. Every wrapper was `shrink`, so
     * flexbox shared the shortfall across all three — which is why Scanned
     * Paperwork and Communications rendered as half a header each. ⭐ A collapsed
     * card is `shrink-0` now: its header is either fully there or the card is not.
     *
     * ⚠️ REPORTED, NOT OWNED — see `lib/railSlot.ts`. The paperwork card arrives
     * as an opaque `footer` from `Calendar` and this panel must not learn its type.
     */
    /**
     * ⛔⛔ A DRAFT MUST NOT SURVIVE THE CARD IT WAS TYPED ON. `smsFor` moving is
     * how the panel closes AND how it hops to another driver; either way the
     * half-typed sentence belongs to a truck that is no longer on screen, and
     * sending it to the next one is the kind of mistake nobody can take back.
     */
    useEffect(() => {
        setAdHocText('');
        setAdHocError(null);
    }, [smsFor]);

    /**
     * ⛔⛔ ONE CODE, NOT A SET — THE RAIL IS AN ACCORDION (Pete, 2026-09-10):
     * *"the other panels should close there chevrons and fall to the bottom."*
     * ⚠️ With a Set two panels could be open and `flex-1` split the column between
     * them, so neither reached the top. See `lib/railSlot.ts`.
     */
    const [openPanels, setOpenPanels] = useState<Set<string>>(() => new Set());

    /**
     * ---- ⭐⭐ THE TWO-PANEL CAP (decision 193, Pete 2026-09-20) -----------------
     *
     * ⭐ *"is it possible to only have two chevrons in the right panel open at one
     * time? One is ideal, two is the max … if they open a third, one of the other
     * minimizes, probably the one on the bottom."*
     *
     * ⛔⛔ THE POLICY IS NOT HERE. It is `nextRailSlots`, a pure function with
     * `rail-slot-test.cjs` over it — **because the 2026-09-10 accordion put this logic
     * in effects, raced, and was reverted within the day.** All this callback does is
     * call it and store both halves of the answer.
     *   ** LOGIC YOU CANNOT TEST IS LOGIC YOU ARE GUESSING ABOUT. **
     */
    const [evictedPanel, setEvictedPanel] = useState<string | null>(null);
    /**
     * ⛔⛔ A REF, NOT THE STATE, IS THE INPUT. The policy needs BOTH the open set and
     * the current eviction, and deriving one from a `setState` updater while writing
     * the other would be a side effect inside a reducer — React may call it twice in
     * StrictMode and would fire the eviction twice with it.
     *   ** A DECISION THAT WRITES TWO PIECES OF STATE CANNOT LIVE INSIDE EITHER. **
     * ⚠️ Kept in step immediately below, in the same synchronous call.
     */
    const slotsRef = useRef<{ open: ReadonlySet<string>; evicted: string | null }>(
        { open: new Set(), evicted: null });

    /* ⛔ THE SAME-SET BAIL-OUT IS STILL LOAD-BEARING. Without it this is an infinite
       loop — report, setState, render, report — presenting as a frozen tab rather
       than an error. `nextRailSlots` preserves it. */
    const reportSlot = useCallback((code: string, open: boolean) => {
        const prev = slotsRef.current;
        const next = nextRailSlots(prev.open, prev.evicted, code, open);
        if (next.open === prev.open && next.evicted === prev.evicted) return;
        slotsRef.current = next;
        setOpenPanels(next.open as Set<string>);
        setEvictedPanel(next.evicted);
    }, []);

    /* ⛔ MEMOISED. The provider value is an object; a fresh one every render would
       re-run `useRailSlot`'s effect in all five panels on every keystroke in the
       filter box — and that effect is the one that reports the slot.
       ⚠️ `evictedPanel` IS IN THE DEPS. It has to be — the panels read it through
       this object, so leaving it out would publish a stale eviction and the victim
       would never hear about it. */
    /**
     * ---- ⭐⭐ "A FRESH START ON A RECORD" (decision 190 C, Pete 2026-09-20) --------
     *
     * ⭐ *"you single click one of those, lets have the right side panel item all close
     * the chevrons like they are showing its a new, fresh start on a record."*
     *
     * ⛔⚤ EVERYTHING FROM HERE TO THE STRIKE BELOW DESCRIBES A MECHANISM THAT NO
     * LONGER EXISTS (decision 194 reversed 190 C on 2026-09-20). It is kept because the
     * REASONING is sound and the next person to want "collapse everything on a change"
     * should read why it was built this way and why Pete stopped wanting it.
     *
     * ⛔ KEYED ON `railFocus`, NOT ON THE CLICK HANDLERS. The scope can be set from
     * FOUR places — a grid chip, a driver card, a party card and Ⓡ — and hanging a
     * bump off each is four chances to miss one. The thing they all have in common is
     * that the rail's subject changed, and that is exactly what this prop says.
     *   ** WIRE THE EFFECT TO THE STATE THAT CHANGED, NOT TO EVERY DOOR IT CAME IN BY. **
     *
     * ⚠️ Ⓡ IS COVERED BY THE SAME LINE — it clears `railFocus`, which is a change, so
     * the rails collapse on a reset as well as on a selection. Pete asked for both.
     *
     * ⛔ THE MOUNT IS SKIPPED. Without the ref this bumps on first render, which is
     * harmless today (every rail starts closed) and would silently become a bug the
     * day one of them starts open.
     */
    /* ⛔⚤ ~~`collapseKey` / `firstFocus` / the bump effect~~ — **DELETED 2026-09-20.**
       Decision 190 C is reversed by decision 194 (§1AV.179). Pete: *"When scope changes,
       lets keep the panels we had open with the chevron still open so we can see the info
       change."* ⛔ The mechanism is gone from `lib/railSlot.ts` too — field, watcher and
       the context read that fed it — rather than left with no writer.
         ** A PANEL YOU OPENED IS A QUESTION YOU ASKED. CHANGING THE SUBJECT IS NOT AN
            ANSWER TO IT. **
       ⚠️ THE LONG NOTE THAT ARGUED FOR IT ("WIRE THE EFFECT TO THE STATE THAT CHANGED,
       NOT TO EVERY DOOR IT CAME IN BY") WENT WITH IT. That rule is still true and is not
       what was reversed — it simply has nothing left to govern here. */
    const railSlot = useMemo(
        () => ({ report: reportSlot, evicted: evictedPanel }),
        [reportSlot, evictedPanel]);

    const anyPanelOpen = openPanels.size > 0;
    /**
     * ⛔⛔ ~~`anyCardOpen`, WHICH LIFTED THE CEILING FOR AN EXPANDED CARD~~ — REMOVED
     * THE SAME DAY, BY PETE, AND THE REASONING IS WORTH KEEPING.
     *
     * ⚠️ I HELD BACK THE CEILING WHENEVER A DRIVER CARD WAS OPEN, on the argument
     * that an open card is the one thing that genuinely needs the height and that
     * clamping it would recreate 2026-09-08's *"It is now stuck here with no
     * options."* ⛔ **That argument was about a card open with NO panel open**, and I
     * applied it to the case Pete was actually looking at — a card open **and** a
     * panel open — where he wants the opposite: *"one driver panel is shone but rest
     * below is the panel you opened."*
     *
     * ⭐ SO THE PANEL WINS WHEN BOTH ARE OPEN, and the cost is real but small: an
     * expanded card is then reached by scrolling the arrival list. ⚠️ With NO panel
     * open the list is still `flex-1` and the card has the whole column, which is the
     * case the old argument was actually about. Same §4.14 shape as §4.14.4: a reason
     * that was true in one situation, applied to a second where it was not.
     */

    /**
     * ⭐ ORDER IS CSS, NOT A RE-SORTED TREE. `order: 0` on an open card and `1` on
     * a closed one puts the open one on top without moving anything in the JSX —
     * so the paperwork card keeps its position in the tree, keeps its identity,
     * and is not remounted (which would drop its scan-folder listing) merely
     * because somebody expanded Labor.
     *
     * ⚠️ AN OPEN CARD IS `flex-1`, A CLOSED ONE `shrink-0`. Two open cards share
     * the space rather than one winning — Pete described one open card, and the
     * rule that survives two is the one to write.
     */
    const slotProps = useCallback((code: string) => ({
        className: openPanels.has(code)
            ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
            : 'flex shrink-0 flex-col overflow-hidden',
        style: { order: openPanels.has(code) ? 0 : 1 },
    }), [openPanels]);

    const panelOn = useCallback((code: string): boolean => {
        if (railPanels === null) return true;
        const p = railPanels.find((x) => x.panelCode === code);
        return p ? p.isActive : true;
    }, [railPanels]);
    /* ⭐ HOISTED so column 2 can be gated on the same condition its contents were.
       ⚠️ An empty bordered column beside the arrivals reads as a card that failed to
       load, which is the same argument the struck `border-t` note makes. */
    /* ⭐ HISTORY joined the list 2026-09-10. ⚠️ `panelOn` defaults an UNKNOWN code
       to visible, so this renders before migration 183 gives it a dock.RailPanel
       row — the migration makes it SWITCHABLE in the toolbar, not present. */
    const anyPanelConfigured = panelOn('PAPERWORK') || panelOn('ORDERS')
        || panelOn('LABOUR') || panelOn('COMMS') || panelOn('HISTORY');

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const r = await getCheckIns(await getToken(), facilityCode, date, showVoided);
            setRows(r.checkIns);
            setDetention(r.detention ? {
                freeMinutes: r.detention.freeMinutes, warnMinutes: r.detention.warnMinutes,
                urgentMinutes: r.detention.urgentMinutes,
                rules: r.detention.rules ?? [],   // ⭐ F173: client free times (settingsFor)
            } : null);
            // ⚠️ Only replace when the server was actually asked. Otherwise closing
            // and reopening the section would flash an empty list before the refetch.
            if (showVoided) setVoided(r.voided ?? []);
            onListChanged?.(r.checkIns);
            setError(null);
        } catch (err) {
            // ⚠️ A background poll must never blank a panel the scheduler is
            // reading. Only a foreground load is allowed to show an error.
            if (!silent) setError(err instanceof Error ? err.message : 'Could not load arrivals.');
        } finally {
            if (!silent) setLoading(false);
            firstLoad.current = false;
        }
    }, [facilityCode, date, getToken, showVoided]);

    useEffect(() => { void load(); }, [load]);
    // Separate from the mount effect on purpose: this one fires on an explicit
    // user action, so it reloads even mid-interaction.
    useEffect(() => { if (refreshKey > 0) void load(true); }, [refreshKey, load]);

    /* ⭐ See the paperclip note above. This mirror exists so `uploadPaperwork`
       can stay stable while still seeing the CURRENT arrivals — the list reloads
       under it, and a closure over `rows` would attach to a stale object. */
    useEffect(() => { rowsRef.current = rows; }, [rows]);

    /**
     * ⚠️ THE ARRIVAL IS LOOKED UP PER FILE, NOT CAPTURED ONCE. `onDone` reloads
     * the list, so by the second file the object from the first is gone. The ID
     * is the stable thing; the row is not.
     *
     * ⛔ AND IT THROWS RATHER THAN RETURNING QUIETLY when the arrival has left the
     * screen. `useFileUpload` reports a throw AND how many files got through
     * before it; a silent return would count a file that was never sent.
     */
    const uploadPaperwork = useCallback(async (token: string, file: File) => {
        const id = attachTargetRef.current;
        if (!id) throw new Error('No arrival is selected.');
        const target = rowsRef.current.find((r) => r.checkInId === id);
        if (!target) throw new Error('That arrival is no longer on screen — reload and try again.');
        if (!uploadToCheckIn) throw new Error('This screen cannot attach paperwork.');
        return uploadToCheckIn(target, token, file);
    }, [uploadToCheckIn]);

    const paperwork = useFileUpload({
        getToken,
        upload: uploadPaperwork,
        onDone: async () => {
            // ⭐ THE COUNT ON THE EYE ICON IS SERVER-COMPUTED, so without this the
            //   scheduler attaches a file and the card still says nothing is filed
            //   — which reads as a failed upload.
            await load(true);
            onChanged?.();
        },
        failureMessage: 'Could not attach that file.',
    });

    /**
     * ---- ⭐⭐ STRAIGHT TO THE FILE PICKER, FROM EITHER ROUTE (2026-09-10) -----
     *
     * ⛔ THE `.click()` MUST BE SYNCHRONOUS INSIDE A USER GESTURE. A browser opens
     * a file picker only from a live one — which is why this is a plain function
     * called from a handler and not something that awaits anything first.
     *
     * ⚠️ THE TARGET GOES IN A REF **AND** IN STATE, on purpose. `setAttachFor`
     * will not have applied by the time the picker opens, so the ref is what the
     * change handler reads; the state is only so the card can draw its progress.
     *
     * ⛔⛔ IT LIVES **ABOVE** `MENU_DROP_EXACT`, NOT BESIDE `cardMenuItems`.
     * `arrival-menu-test.cjs` LIFTS the real `tidyArrivalMenu` by slicing this file
     * between those two markers and running it through `new Function` — so a TS
     * annotation placed inside that range is a syntax error in the TEST, not in the
     * build. ⚠️ That is exactly how this landed the first time, and the test caught
     * it. The boundary is a contract; move the code, never the marker.
     *
     * ⭐ EXTRACTED BECAUSE THE PAPERCLIP LEFT THE CARD. Pete moved the icons into
     * the (…), and the ONE task the menu could not already do was this one-click
     * upload — "Paperwork (sign, print, attach)…" opens the dialog, which has a
     * drop zone, but that is two steps. ⛔ One function, two callers, rather than a
     * second copy of the ref-then-click dance.
     */
    const openPaperworkPicker = useCallback((c: CheckInDto) => {
        attachTargetRef.current = c.checkInId;
        setAttachFor(c.checkInId);
        paperwork.reset();
        paperworkInputRef.current?.click();
    }, [paperwork]);


    // Same discipline as the calendar's own refresh: never poll while the user
    // is mid-action, or a re-render yanks a menu out from under them.
    useEffect(() => {
        const t = window.setInterval(() => {
            setNow(Date.now());
            // ⚠️ editFor JOINS THIS LIST. A poll mid-edit reloads rows underneath a
            // half-typed correction; the draft survives but the card behind it may
            // not, and a scheduler cannot tell which truck he is still editing.
            /* ⚠️ `vehicleFor` JOINS THE LIST for the same reason `editFor` did:
               the dialog holds three boxes seeded from a row, and a poll that
               replaces that row mid-type is how a scheduler loses a number a
               driver just read out over the phone. */
            if (!interactingRef.current && !menuFor && !voidFor && !editFor && !licenceFor
                && !vehicleFor && !paperworkFor) void load(true);
        }, REFRESH_MS);
        return () => window.clearInterval(t);
    }, [load, menuFor, voidFor, editFor, licenceFor, vehicleFor, paperworkFor, interactingRef]);

    // A separate, cheap ticker so elapsed times move without re-fetching.
    useEffect(() => {
        const t = window.setInterval(() => setNow(Date.now()), 30_000);
        return () => window.clearInterval(t);
    }, []);

    /**
     * ⚠️ "On site" means arrived and not yet departed. By mid-afternoon a busy
     * dock has forty arrivals and most are gone; a panel that lists them all is
     * a scroll, and the ones needing action are buried in it.
     */
    /** Today's appointments by id — the panel needs status, not just existence. */
    const apptById = useMemo(
        () => new Map(appointments.map(a => [String(a.appointmentId), a])),
        [appointments],
    );

    /**
     * ⭐ WHAT IS HAPPENING TO THIS TRUCK, in one word, beside the driver's name.
     *
     * Pete, 2026-08-20: *"lets put a dash next to Driver Name at top and write the
     * status so Pete Lombardo - Arrived, Pete Lombardo - Departed. Pete Lombardo -
     * Unloading"*.
     *
     * ⚠️ THE LABOUR CLOCK WINS over the appointment status, because it is the more
     * specific truth: a truck being unloaded is also "arrived", and "Unloading" is
     * the fact a scheduler is actually looking for.
     *
     * ⛔ AND AN UNMARKED APPOINTMENT SAYS "On site", NOT "Arrived". A check-in means
     * the man is through the gate; ARRIVED is a specific appointment status that may
     * not have been set yet, and printing it anyway would put a status on screen that
     * the appointment does not have.
     */
    const statusWord = useMemo(() => {
        const byId = apptById;
        return (c: CheckInDto): string | null => {
            if (c.work) return c.work.code === 'LOADING' ? 'Loading' : 'Unloading';
            if (c.appointmentId === null) return null;   // the card already shouts "No appointment"
            const st = byId.get(c.appointmentId)?.statusCode;
            if (st === 'DEPARTED') return 'Departed';
            if (st === 'ARRIVED')  return 'Arrived';
            if (st === 'NOSHOW')   return 'No-show';
            if (st === 'CANCELLED') return 'Cancelled';
            return 'On site';
        };
    }, [apptById]);

    /**
     * ⭐⭐ THE COLOUR FOR EACH STATUS WORD — LITERAL CLASSES IN A LOOKUP TABLE.
     *
     * Pete, 2026-08-27: *"the color of the text should be the color of the status."*
     *
     * ⛔⛔ THIS CANNOT BE `text-status-${code.toLowerCase()}` AND THAT IS THE WHOLE
     * REASON THE TABLE EXISTS. Tailwind generates CSS by scanning SOURCE TEXT for
     * class names; a class assembled at runtime is never seen, never emitted, and
     * the word renders in whatever colour it inherited. ⚠️ It would look like a
     * styling preference rather than a missing stylesheet — the kind of bug that
     * survives review because the screen is not obviously broken.
     *
     * ⭐ THE VALUES ARE THE ADMIN'S OWN PALETTE. `text-status-arrived` resolves to
     * `rgb(var(--status-arrived))`, the variable Status Colours (migration 135)
     * sets on <html> — so the card, the chip and the wall board are one colour and
     * changing it in one place changes it everywhere.
     *
     * ⚠️ WORK BEATS STATUS AND GETS EMERALD, matching `LabourRow`'s working tint
     * rather than a status colour: "Unloading" is a labour fact, and there is no
     * `dock.StatusColor` row for it to borrow. ⛔ Not amber — amber on this panel
     * already means "unmatched", and one colour must not mean two things.
     *
     * ⚠️ "On site" TAKES THE ARRIVED COLOUR ON PURPOSE. It is the same physical
     * truth (through the gate) worded honestly for an appointment whose status was
     * never set — see `statusWord`. Giving it a colour of its own would invent a
     * seventh status on a card.
     */
    const STATUS_TONE: Record<string, string> = {
        Loading:     'text-emerald-700',
        Unloading:   'text-emerald-700',
        Arrived:     'text-status-arrived',
        'On site':   'text-status-arrived',
        Departed:    'text-status-departed',
        'No-show':   'text-status-noshow',
        Cancelled:   'text-status-cancelled',
    };
    /** ⚠️ Falls back to the body colour — a NEW word stays readable, just untinted. */
    const statusTone = (word: string | null): string =>
        (word && STATUS_TONE[word]) || 'text-foreground';

    const departed = useMemo(() => {
        const byId = apptById;
        return (c: CheckInDto) =>
            c.appointmentId !== null && byId.get(c.appointmentId)?.statusCode === 'DEPARTED';
    }, [appointments]);

    /**
     * ⭐ F110 — THE DETENTION TALLY: every on-site card of the day that shows a chip, by
     * the card's own rule (`detentionState`, the same inputs as the card). ⛔ Counted
     * from ALL of today's rows, not the filtered view: the tally is the dock's state,
     * not the panel's.
     */
    const detentionTally = useMemo((): DetentionTallyTruck[] => {
        const out: DetentionTallyTruck[] = [];
        for (const c of rows) {
            if (departed(c)) continue;
            /* ⛔ F110b — Pete: *"Make sure we are not measuring drivers that show with no
               appointment assigned in this check"* · *"has to have an approved or arrived
               appointment, not requested either"*. Otherwise no clock — here AND on the card
               (`onDetentionClock`), so the tally and the cards count the same trucks. */
            const appt = c.appointmentId ? apptById.get(c.appointmentId) : undefined;
            if (!onDetentionClock(appt)) continue;
            const st = detentionState({
                arrivalIso: appt?.actualArrivalUtc ?? c.checkedInUtc,
                scheduledStartIso: appt?.scheduledStartUtc ?? null,
                timed: !!appt && appt.schedulingMode !== 'DayOnly',
                departureIso: appt?.actualDepartureUtc ?? null,
            }, settingsFor(detention, appt?.clientCode, appt?.direction), now);   // ⭐ F173: the client's free time
            if (!st || st.level === 'off') continue;
            out.push({
                key: c.checkInId,
                label: [c.arrivalSeq !== null ? `#${c.arrivalSeq}` : null, c.driverName || 'Driver',
                        c.appointmentNumber].filter(Boolean).join(' · '),
                level: st.level,
                clockMinutes: st.clockMinutes,
                minutesLeft: st.minutesLeft ?? 0,
                clockText: detentionLabel(st),
            });
        }
        return out;
    }, [rows, departed, apptById, detention, now]);

    /**
     * The options are built from the WHOLE day, never from the filtered set.
     *
     * ⚠️ Building them from `visible` would make each dropdown erase the other's
     * choices the moment one was used — pick a carrier and every client but his
     * vanishes from the client list, so backing out of a filter becomes
     * impossible without a reload.
     */
    /* ⛔⚤ ~~`clientOptions`~~ — REMOVED 2026-09-20 with the `All clients` dropdown, as
       `carrierOptions` went with `All carriers`. ⚠️ Both FILTERS survive; only their option
       lists went, because only the lists had a single reader.
         ** DELETE WHAT FED THE THING YOU DELETED, AND NOTHING ELSE. ** */
    /* ⛔⚤ ~~`carrierOptions`~~ — REMOVED 2026-09-20 with the `All carriers` dropdown it
       populated. ⚠️ `carrierFilter` ITSELF SURVIVES — the search block and the Week/Month
       views still read it — so only the option list went, not the filter.
         ** DELETE WHAT FED THE THING YOU DELETED, AND NOTHING ELSE. THE STATE HAD OTHER
            READERS; THE OPTIONS HAD ONE. **
       ⭐ `noUnusedLocals` is what drew the line in exactly the right place. */

    const driverOptions = useMemo(
        () => [...new Set(rows.map(r => r.driverName?.trim()).filter((v): v is string => !!v))].sort(),
        [rows],
    );
    const filtersActive = clientFilter !== '' || carrierFilter !== '' || driverFilter !== '' || needsLicenceOnly;

    /**
     * ---- ⛔⛔ A FILTER YOU CANNOT SEE IS A FILTER YOU DO NOT KNOW IS ON --------
     *
     * ⚠️ THE STRIP IS NOW HIDDEN BY DEFAULT, and that changes what these controls
     * are. While they were always on screen, their own state WAS the indicator: a
     * ticked "Departed" with nothing departed explains its own empty rail. Fold
     * them away and the same state becomes invisible — trucks are missing from
     * the panel and NOTHING on screen says why.
     *
     * ⭐⭐ THIS IS THE SESSION'S RECURRING SHAPE AGAIN: the wrong answer is not an
     * error, it is a rail that looks calm. Nobody investigates a quiet screen.
     *
     * ---- ⚠️ AND `filtersActive` IS THE WRONG PREDICATE FOR IT ------------------
     *
     * ⛔ IT DOES NOT COUNT ON-SITE / DEPARTED. Measured 2026-09-08 by reading it:
     * it tests client, carrier and needs-licence only — so the header's amber
     * "filtered" chip is ABSENT for exactly the case most likely to empty the
     * rail, somebody leaving "Departed" ticked on its own.
     *
     * ⭐ SO THE DOT USES A WIDER TEST rather than widening `filtersActive` itself.
     * That constant also drives the "Clear filters" button and the empty-state
     * text, and changing what it means would change both of those silently —
     * a second behaviour change smuggled inside a layout one.
     *
     * ⚠️ DEFAULTS ARE `showOnSite = true`, `showDeparted = false`. Off-default is
     * therefore `!showOnSite || showDeparted`, NOT "either is false".
     */
    /**
     * ⛔⛤ ~~filtersActive || !showOnSite || showDeparted~~ — WIDENED 2026-09-25.
     * The board's filters now hide driver cards too, and the whole point of this
     * constant is that an empty rail explains itself. Leaving the board out would
     * recreate, with a new control, the exact fault the comment above records.
     *   ** EVERY FILTER THAT CAN EMPTY THE RAIL MUST REACH THE THING THAT SAYS THE
     *      RAIL IS FILTERED. **
     */
    const anyFilterOffDefault = filtersActive || !showOnSite || showDeparted || boardFiltered;

    /**
     * ---- ⭐⭐⭐ THE CARDS FOLLOW THE FOCUS TOO (Pete, 2026-09-25) --------------
     *
     * > *"when i single click the appointment, the panels change but the driver,
     * > client, carrier, consignee don't change to just the one driver, one carrier"*
     * > -> *"Should probably do the same on hover, then off hover ALL loads again
     * > just like the panels"*.
     *
     * ⛔ THE SAME EXPRESSION THE PANELS ALREADY USE, not a second rule.
     * `previewFocus ?? railFocus ?? partyScope` is exactly what `effFocus ?? effParty`
     * collapses to — Calendar's `paperworkFocus` records why in full, and writing it
     * out here is following that precedent rather than inventing one.
     *
     * ⚠️ THIS IS A DIFFERENT AXIS FROM `boardApptIds` AND BOTH APPLY. The board bound
     * is a persistent filter somebody set; this is a momentary "show me that one".
     *
     * ---- ⛔⛔ AND AN UNMATCHED ARRIVAL IS TREATED OPPOSITELY BY THE TWO ---------
     *
     * He SURVIVES the board bound and is HIDDEN by a focus, and the difference is
     * not an inconsistency:
     *   - a FILTER is a state you can leave on and forget. Hiding a truck nobody has
     *     matched behind one is how it gets forgotten at the gate.
     *   - a FOCUS is a gesture you are making right now, and Pete asked for *"just
     *     the one driver"*. Padding that answer with every unmatched arrival is
     *     refusing to answer it.
     *     ** WHETHER A HIDDEN THING IS DANGEROUS DEPENDS ON HOW LONG IT STAYS
     *        HIDDEN, NOT ON WHAT HID IT. **
     */
    const [ownHover, setOwnHover] = useState(false);
    /**
     * ⭐ ONE EXPRESSION, BOTH LISTS. `previewFocus ?? railFocus ?? partyScope` is what
     * the panels use; dropping the preview while the cursor is on this panel's own
     * cards is the only difference, and it applies to both.
     */
    const scopeFocus = ownHover
        ? (railFocus ?? partyScope)
        : (previewFocus ?? railFocus ?? partyScope);

    const driverScope = useMemo(() => {
        /* ⛔ `scopeFocus`, NOT `previewFocus ?? railFocus ?? partyScope`. This list
           raised the hover, so it must not read it — see `ownHover` just above. */
        const f = scopeFocus;
        if (!f) return null;
        return {
            ids: new Set<string>(f.appointmentIds ?? []),
            /* ⭐ THE ONLY HANDLE ON AN UNMATCHED DRIVER — `railFocus.ts` says so at
               length. A focus built from his card carries no appointment, so without
               this, clicking him would hide him. */
            checkInId: f.checkInId != null ? String(f.checkInId) : null,
        };
    }, [scopeFocus]);

    /**
     * ---- ⛔⛔ A HOVER OVER A CARD MUST NOT SCOPE THAT CARD'S OWN LIST ---------
     *
     * ⚠️ THIS IS A FEEDBACK LOOP, NOT A PREFERENCE, AND THIS CODEBASE HAS ALREADY
     * HIT IT ONCE. Hovering a party card calls `onHoverParty`, which becomes
     * `previewFocus`. Scope the party cards by that and: the list narrows to the one
     * card -> the card under the cursor moves -> the hover ends -> the list restores
     * -> the card is back under the cursor. It flickers forever.
     *
     * `Calendar.tsx` solved the identical problem for rail ROWS and wrote down why:
     * *"A RAIL ROW MARKS; IT DOES NOT SCOPE ... which breaks the loop, because the
     * panel stops rebuilding the row the cursor is sitting on."*
     *   ** A LIST MAY BE SCOPED BY A HOVER OVER ANYTHING EXCEPT ITSELF. **
     *
     * ⭐ THE PANELS ARE UNAFFECTED and still take the preview in full — they are not
     * the surface the cursor is on, so there is no loop to break. Only this one list
     * drops its own hover.
     *
     * ⚠️ DRIVER CARDS NEED NO SUCH GUARD: hovering one produces a non-grid origin,
     * which `previewFocus` already resolves to null for exactly this reason.
     */
    /**
     * ⛔⛤⛤ ~~`partyCardHover`~~ — **WIDENED 2026-09-25, AFTER IT SHIPPED AND FLICKERED.**
     *
     * > Pete, minutes after the deploy: *"the driver check in panel is flickering big
     * > time when i hover the mouse over it"*.
     *
     * ⛔⛔ THE FLAG WAS SET ON THE PARTY CARDS ONLY, because the note above reasoned
     * that driver cards were safe: hovering one yields a non-grid origin, which
     * `previewFocus` resolves to null. **THAT IS THE WRONG PATH.** Driver cards do not
     * go through `hoverApptId` at all — they call `onHoverParty(focusFromCheckIn(c))`,
     * and `previewFocus` returns `hoverPartyFocus` FIRST, before any origin test:
     *
     *     if (hoverPartyFocus) return hoverPartyFocus;    <-- driver cards land here
     *     if (!hoverApptId) return null;
     *     if (hoverApptOrigin !== 'grid') return null;    <-- the guard I cited
     *
     * So the driver list scoped itself on its own hover and flickered exactly as the
     * party list would have.
     *   ** A SAFETY ARGUMENT ABOUT ONE CODE PATH IS NOT AN ARGUMENT ABOUT A SURFACE
     *      UNTIL YOU HAVE CHECKED WHICH PATH THAT SURFACE ACTUALLY TAKES. **
     *
     * ⭐ SO IT IS ONE FLAG FOR EVERY HOVER THIS PANEL RAISES, set at all four of its
     * `onHoverParty` boundaries, and ONE scope derived from it. Two lists cannot
     * disagree about a rule they read from the same expression.
     *   ** A LIST MAY BE SCOPED BY A HOVER OVER ANYTHING EXCEPT ITSELF — AND "ITSELF"
     *      MEANS EVERY LIST IN THE PANEL THAT RAISED THE HOVER. **
     */
    /* ⛔ `ownHover` AND `scopeFocus` ARE DECLARED ABOVE `driverScope`, NOT HERE.
       ⛔⛤ THEY WERE HERE, AND THE BUILD REFUSED IT 2026-09-25: TS2448, `driverScope`
       reads `scopeFocus` ~60 lines before this. `const` is block-scoped and does not
       hoist, so the two consumers of one expression must both sit below it.
         ** A VALUE SHARED BY TWO CONSUMERS BELONGS ABOVE THE FIRST OF THEM, NOT
            BESIDE THE SECOND. ** */
    const partyCardScope = useMemo(
        () => (scopeFocus ? new Set<string>(scopeFocus.appointmentIds ?? []) : null),
        [scopeFocus],
    );

    /**
     * ---- ⛔⛔ AND AN UNMATCHED ARRIVAL SURVIVES ALL OF IT (Pete, 2026-09-25) ----
     *
     * A driver with no appointment has no status and no work kind, so a strict
     * mirror of the board hides him the moment any filter is on. Pete chose that he
     * stays, and it is the SAME exemption `filterAppts` already gives a door block:
     *   ** A TRUCK IN THE YARD WITH NO APPOINTMENT IS THE YARD'S STATE, NOT AN
     *      APPOINTMENT'S. HIDING IT SHOWS A YARD EMPTIER THAN IT IS. **
     * ⚠️ AND HE IS THE ONE CARD THAT NEEDS SOMEBODY TO ACT. The others are being
     * watched; this one is waiting to be matched.
     */
    const visible = useMemo(
        () => rows.filter(r =>
            (r.appointmentId === null
                || r.appointmentId === undefined
                || !boardApptIds
                || boardApptIds.has(String(r.appointmentId)))
            /* ⛔ THE FOCUS, STRICT — see `driverScope` for why this one does NOT
               exempt an unmatched arrival the way the board bound above does. */
            && (!driverScope
                || (driverScope.checkInId !== null && String(r.checkInId) === driverScope.checkInId)
                || (r.appointmentId !== null && r.appointmentId !== undefined
                    && driverScope.ids.has(String(r.appointmentId))))
            &&
            // Neither ticked = no presence filter at all. See the state comment.
            ((showOnSite && showDeparted) || (!showOnSite && !showDeparted)
                || (showOnSite ? !departed(r) : departed(r)))
            // ⚠️ A walk-up has no client until it is attached, so a client filter
            // hides it. That is correct — it is not that client's truck yet — but
            // it is why "needs an appointment" is counted over `rows`, not here.
            && (clientFilter === '' || r.clientCode === clientFilter)
            && (carrierFilter === '' || r.carrierCode === carrierFilter)
            && (driverFilter === '' || r.driverName?.trim() === driverFilter)
            && (!needsLicenceOnly || !r.hasDriverIdentity)),
        /* ⚠️ `boardApptIds` IS A DEPENDENCY. Omitting it is the classic stale-memo
           bug and it would fail QUIETLY: the rail simply would not move when the
           board's filters changed, which is the very complaint being fixed. */
        [rows, boardApptIds, driverScope, showOnSite, showDeparted, departed, clientFilter, carrierFilter, driverFilter, needsLicenceOnly],
    );

    /**
     * ⚠️ COUNTED SEPARATELY FROM `visible`, and this is a real bug avoided rather
     * than tidiness. The "hiding N departed" line used to be
     * `rows.length - visible.length`; with a client filter on, that same
     * subtraction would report trucks hidden by the FILTER as departed — and a
     * scheduler reading "hiding 6 departed" on a morning when nothing has
     * departed has been told something false about his yard.
     */
    const departedHidden = useMemo(
        () => (showOnSite && !showDeparted ? rows.filter(r => departed(r)).length : 0),
        [rows, departed, showOnSite, showDeparted],
    );
    /** How many have gone — for the Departed checkbox's own count. */
    const departedCount = useMemo(
        () => rows.filter(r => departed(r)).length, [rows, departed],
    );

    /**
     * ⚠️ THREE GROUPS, NOT TWO. Inbound, outbound, and "needs direction" — a
     * walk-up that has not told us and has no appointment to derive it from has
     * to live somewhere honest. Guessing "inbound because most trucks are" would
     * be a lie a supervisor acts on.
     *
     * ONE SCROLLING LIST with sticky headers rather than separate panels: the
     * arrival number is a single facility-wide gate sequence, so splitting the
     * list would show 1, 4, 7 in one column and 2, 3, 5 in the other and look
     * broken. And a narrow right rail with two scroll areas shows fewer trucks.
     */
    const groups = useMemo(() => {
        const inb = visible.filter(c => c.direction === 'INBOUND');
        const out = visible.filter(c => c.direction === 'OUTBOUND');
        const unk = visible.filter(c => c.direction !== 'INBOUND' && c.direction !== 'OUTBOUND');
        return [
            { key: 'in',  label: 'Inbound',         rows: inb },
            { key: 'out', label: 'Outbound',        rows: out },
            { key: 'unk', label: 'Needs Appointment', rows: unk },
        ].filter(g => g.rows.length > 0);
    }, [visible]);

    /**
     * ---- ⭐⭐ WHEN THE SUBJECT IS A PARTY, THE PANEL LISTS PARTIES --------------
     *
     * ⭐ Pete, 2026-09-19: *"clicking on client, consignee and carrier do nothing
     * yet"*. ⛔ `subject` reached only the heading until now; this is the body.
     *
     *   ** THE SUBJECT SAYS WHAT A CARD *IS*; A FILTER SAYS WHICH CARDS SHOW. **
     *
     * ⚠️ BUILT FROM `appointments`, NOT FROM `rows`. The arrivals list holds trucks
     * that have CHECKED IN; a client with four loads booked and none arrived is still
     * a client with work today, and building the list from check-ins would hide
     * exactly the clients a scheduler most wants to look at in the morning.
     *
     * ⛔ `Date.now()` IS READ HERE AND PASSED IN. `partyRows` takes the clock as an
     * argument so its "overdue" boundary is testable; reading it inside would make
     * the one thing worth asserting impossible to assert.
     */
    const partyKind = partyKindFor(subject);
    /**
     * ---- ⭐⭐ THE BOARD'S APPOINTMENTS, WHICH IS WHAT THE CARDS COUNT ----------
     *
     * ⛔ ONE DERIVATION, USED BY THE CARDS AND THE RAIL PANELS BOTH. Filtering at
     * each consumer would be the same rule written three times, and the first
     * refinement would land in one of them.
     *
     * ⚠️ `undefined` MEANS NO BOUND — see the prop's docstring. `?? appointments`
     * rather than `?? []`.
     */
    const boardAppts = useMemo(
        () => (boardApptIds
            ? appointments.filter(a => boardApptIds.has(String(a.appointmentId)))
            : appointments),
        [appointments, boardApptIds],
    );
    /**
     * ⛔ BUILT FROM `boardAppts`, NOT `appointments`. Pete, 2026-09-25 — the cards
     * are a reading of the board, so a client with no appointment left on it has no
     * card, and the counts on the cards that remain are the board's counts.
     * ⚠️ THE CONSEQUENCE HE ACCEPTED: `partyScope` is one of the board's filters, so
     * choosing a client leaves ONE card. Clearing is the toolbar pill, not a card —
     * which is why that pill exists outside this list.
     */
    const parties = useMemo(
        () => {
            if (!partyKind) return [];
            /* ⛔ BOTH BOUNDS, IN ORDER: the board's persistent filters first
               (`boardAppts`), then the momentary focus. */
            const scoped = partyCardScope
                ? boardAppts.filter(a => partyCardScope.has(String(a.appointmentId)))
                : boardAppts;
            return partyRows(partyKind, scoped, Date.now(), partyNames);
        },
        [partyKind, boardAppts, partyCardScope, partyNames],
    );

    /**
     * ⭐ The picker's value: the chosen party, but only while the SUBJECT still matches.
     * ⛔ Reading `partyScope.partyCode` alone would show a carrier code in the client
     * dropdown for the moment between switching chips and the scope being cleared.
     */
    const pickerValue = partyScope && partyScope.kind === partyKind
        ? partyScope.partyCode ?? '' : '';

    /**
     * ---- ⭐⭐ THE HEADER HOLDS TWO SCOPES AT ONCE (Pete, 2026-09-19) --------------
     *
     * ⛔⛔ THE PAGE HANDS THIS PANEL `railFocus ?? partyScope` — ONE COLLAPSED VALUE.
     * That collapse is right for the four rails on the right (they scope to the
     * narrowest thing), and wrong for a HEADING, because a heading that renders only
     * the narrower of two live scopes stops naming the other one. Pete hit both ends
     * of it within ten minutes: the hovered load erased `Client · HELLMAN` **and the
     * R with it**, and the `Driver` chip could not get `Driver Check-Ins` back.
     *   ** A TRANSIENT SCOPE MUST NOT ERASE THE STANDING ONE THAT STILL GOVERNS. **
     *
     * ⭐ SO THE HEADER READS BOTH PROPS, not the collapsed one:
     *     line 1  `panelHeadline(subject, partyScope)`  -- the panel, and whose
     *     line 2  `panelTitle(subject, narrowerFocus)`  -- what is being pointed at
     *
     * ⛔ `sameFocus`, NOT `!==`. With nothing hovered the page passes the SAME object
     * in both props, so an identity test happens to work — today. See that function.
     */
    /**
     * ---- ⛔⛔⚤ AND LINE 1 MAY ONLY NAME A SCOPE THAT **BELONGS TO THIS SUBJECT** ----
     *
     * ⭐ Pete, 2026-09-20, with a screenshot: *"This is an error - showing the appt -
     * driver not appt - client"*. The heading read **`Clients · A00000345 · PETER
     * LOMBARDO`** — the panel word from the CLIENTS chip, the name from a DRIVER scope.
     *
     * ⛔⛔ **THIS WAS CAUSED BY DECISION 194, HOURS EARLIER, AND THE CAUSE IS WORTH
     * NAMING.** `partyScope` used to hold ONLY a client / carrier / consignee — its name
     * says so. 194 made a driver card write it too, so the board could filter to that
     * driver (Pete's *"it doesn't change the schedule to show ... for that driver"*).
     * Every reader of that state inherited a case it was never written for, and this
     * heading is the one that says the wrong thing out loud.
     *   ** WIDENING WHAT A STATE CAN HOLD IS A CHANGE TO EVERY READER OF IT. THE
     *      COMPILER DOES NOT SEE IT, BECAUSE THE TYPE DID NOT CHANGE — ONLY WHICH OF
     *      ITS CASES NOW OCCUR. **
     *   ** A NAME THAT DESCRIBES A NARROWER THING THAN THE VARIABLE HOLDS IS THE BUG
     *      REPORT ARRIVING EARLY. `partyScope` IS NO LONGER ONLY A PARTY. **
     *
     * ⛔⚤ ~~THE FIX IS NOT TO CLEAR THE SCOPE WHEN THE CHIP CHANGES~~ — **STRUCK THE
     * SAME HOUR.** That is exactly what Pete then asked for: *"Since nothing was
     * selected on client, just should say Client or similar but **needs to clear the
     * driver**"*. §1AV.174's *"the chips keep the scope"* is reversed; the clear lives
     * in `chooseSubject` in `pages/Calendar.tsx`, where the subject has one writer.
     *
     * ⛔⛔ **AND THIS GUARD STAYS ANYWAY — IT IS NOT DEAD CODE.** A `useCallback` that
     * clears state runs on the CLICK; this heading renders in the SAME PASS. There is
     * one commit in which `subject` is already the new one and `partyScope` is still
     * the old one, and without `ownScope` line 1 renders `Clients · PETER LOMBARDO`
     * for that frame — the reported bug, briefly, on every chip change.
     *   ** A CLEAR IS AN EVENT; A HEADING IS A FUNCTION OF STATE. THE EVENT CANNOT
     *      PROTECT THE FRAME BEFORE IT. **
     * ⚠️ So the clear is the BEHAVIOUR and this is the INVARIANT: line 1 is
     * structurally incapable of naming a scope that is not this subject's, whatever
     * any future caller forgets to clear.
     *
     * | chip | scope | line 1 | line 2 |
     * |---|---|---|---|
     * | Drivers | that driver | `Driver Check-Ins · PETER LOMBARDO` | — |
     * | Clients | TRAFFIX | `Clients · TRAFFIX` | — |
     * | Clients | **a driver** | `Clients` | `Driver · PETER L. / Appt A00000345` |
     * | Clients | TRAFFIX + a load clicked | `Clients · TRAFFIX` | `Load · A00000346` |
     *
     * ⚠️ `partyKindFor` RETURNS NULL FOR THE DRIVER SUBJECT, so the `?? 'driver'`
     * is the mapping, not a fallback for a missing value.
     */
    const subjectScopeKind: RailFocus['kind'] = partyKindFor(subject) ?? 'driver';
    /**
     * ⭐⭐ THE PREVIEW SUBSTITUTES FOR THE COMMITTED VALUE, AND ONLY FOR **DISPLAY**.
     * ⛔ `partyScope` itself is untouched — it still drives which card reads as `chosen`,
     * what the dropdown shows, and (in the page) the board filter. A hover that lit a card
     * up as selected would be claiming something the next click has not done yet.
     *   ** A PREVIEW MAY CHANGE WHAT A PANEL SAYS. IT MAY NOT CHANGE WHAT A CONTROL
     *      CLAIMS ABOUT ITSELF. **
     */
    const effParty = previewFocus && previewFocus.kind !== 'appointment' ? previewFocus : partyScope;
    const effFocus = previewFocus ?? railFocus;

    /**
     * ============================================================================
     * ⭐⭐⭐ THE THREE RAILS GET THE TWO GESTURES (Pete, 2026-09-27)
     * ============================================================================
     *
     * > *"Labor is not working on the hover or the single click btw. Hover should
     * > narrow down the calendar, single click makes it stay. I thought that is how we
     * > have it on all the panels?"* → asked how far to take it: **"All three
     * > together."**
     *
     * ⛔⛔ HE WAS RIGHT TWICE AND WRONG ONCE, AND THE WRONG ONE IS THE USEFUL PART.
     * Hover-narrows + click-latches existed on the PARTY CHIPS and the DRIVER CARDS —
     * both of which raise a whole `RailFocus` — and on NONE of Labour, Communications
     * or Orders. Those three got the cheap version: `onHoverAppointment(id, 'rail')`,
     * which rings the matching chip and deliberately does NOT re-scope. Labour and
     * Communications have no click handler at all; Orders reports nothing, its hover
     * being pure CSS.
     *
     * ---- ⛔⛔ WHY THEY GOT THE CHEAP VERSION, WHICH IS THE THING TO PRESERVE ----
     *
     * A rail row that re-scopes the board also re-scopes ITS OWN LIST. The list
     * rebuilds, the row under the cursor is replaced, the browser fires `mouseenter`
     * on whatever lands there, and round it goes — the loop Pete hit on 2026-09-21
     * and which cost a day.
     *   ** A HOVER THAT RE-SCOPES THE LIST CONTAINING THE HOVERED ROW IS A LOOP BY
     *      CONSTRUCTION. **
     *
     * ⭐ `ownHover` IS THE ANSWER AND IT ALREADY EXISTED — twenty lines up, for this
     * panel's own cards: *"this list raised the hover, so it must not read it."*
     * `railHover` is that idea with a NAME attached, because three lists now need it
     * and each must suppress only its own.
     *
     * ---- ⭐⭐ WHY THE FOCUS IS BUILT **HERE** AND NOT IN THE PANELS ------------
     *
     * A Labour row knows an `appointmentId`; a Communications row knows one too. NONE
     * of them holds an `AppointmentDto`, which `focusFromAppointment` needs for the
     * number, the identifiers and the label. ⛔ The alternative — three panels each
     * assembling a nine-field object literal — is exactly what `railFocus.ts` records
     * as the defect that made the driver focuses untestable.
     *   ** THE COMPONENT THAT HOLDS THE DATA BUILDS THE VALUE. A CHILD THAT ONLY HAS
     *      AN ID SHOULD SEND AN ID. **
     * ⭐ So the panels keep sending ids, and this one turn them into focuses — which
     * also means the label, the driver join and the identifiers are computed once
     * instead of three times with three chances to differ.
     */
    const [railHover, setRailHover] = useState<'orders' | 'labour' | 'comms' | null>(null);
    /**
     * ⛔ THE HOVERING RAIL READS THE COMMITTED SCOPE; EVERY OTHER RAIL READS THE
     * PREVIEW. Identical in shape to `scopeFocus` above, and for the identical reason.
     */
    const railScope = (who: 'orders' | 'labour' | 'comms') =>
        railHover === who ? (railFocus ?? partyScope) : (effFocus ?? effParty);

    /**
     * ⭐ ONE ID → ONE FOCUS, with the label the rest of the app already uses.
     * ⚠️ Returns null for an id this day does not hold — a row can outlive a reload —
     * and a null focus clears the preview, which is the honest outcome.
     */
    const focusForApptId = (id: string | null): RailFocus | null => {
        if (!id) return null;
        const a = appointments.find(x => String(x.appointmentId) === String(id));
        if (!a) return null;
        /* ⭐ THE SAME LABEL SHAPE `Calendar.tsx` composes for a chip click — number
           first, then door and time, because "A00000311" is the one fact on screen
           that does not help somebody identify a truck. */
        const when = a.scheduledStartUtc
            ? new Date(a.scheduledStartUtc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
            : null;
        const detail = [a.dockDoorCode, when].filter(Boolean).join(' · ');
        return focusFromAppointment(a, detail ? `${a.appointmentNumber} · ${detail}` : a.appointmentNumber);
    };

    /**
     * ⭐ Hover: flag which rail raised it, then raise it as a PREVIEW for everyone else.
     *
     * ---- ⛔⛔ NOT A `useCallback`, AND THAT IS NOT AN OVERSIGHT ---------------
     *
     * `paperwork-tray-guard` ABORTED THIS DEPLOY over the first draft, which was
     * memoised. Its rule: **a memoised hover writer is how the appointment hover
     * froze three times.** The deps were complete, so it was not stale that day —
     * but it was the banned shape on the exact path that has failed three times.
     *
     * ⭐ AND THE MEMOISATION BOUGHT NOTHING, WHICH IS WHAT SETTLED IT. Measured,
     * not assumed: none of LabourPanel, CommunicationsPanel or OrdersPanel is
     * `React.memo`'d, and both call sites wrap this in an INLINE ARROW anyway —
     * so a fresh function reached the child on every render regardless.
     *   ** A `useCallback` WHOSE CONSUMER IS NOT MEMOISED IS A DEPENDENCY ARRAY
     *      YOU HAVE TO KEEP CORRECT FOREVER IN EXCHANGE FOR NOTHING. **
     *
     * ⚠️ THE GUARD WAS RIGHT AND THE CODE WAS WRONG, which is the outcome worth
     * recording: the temptation on a red guard is to retarget it.
     */
    const railHoverAppt = (who: 'orders' | 'labour' | 'comms', id: string | null) => {
        const f = focusForApptId(id);
        setRailHover(f ? who : null);
        onHoverParty?.(f);
    };
    /**
     * ⭐ Click: LATCH it. ⛔ And clear `railHover` in the same breath — the preview is
     * over, the committed scope now covers this row, and leaving the flag set would
     * leave this rail reading a stale exception to its own scope.
     */
    /* ⛔ PLAIN, like its hover twin above and for the same measured reason. */
    const railChooseAppt = (id: string | null) => {
        const f = focusForApptId(id);
        if (!f) return;
        setRailHover(null);
        onChooseRecord?.(f);
    };

    /**
     * ---- ⭐⭐ AN **ORDER** ROW IS NOT AN APPOINTMENT ROW ----------------------
     *
     * > Pete: *"when you hover an appointment or hover the WMS orders record, it needs
     * > to highlight appointment or order record associated to it."*
     *
     * ⛔⛔ AN ORDER CAN BE ON SEVERAL TRUCKS — *"an order can obviously have multiple
     * trucks to fulfill it but only 1 truck per appointment"* — so it cannot go through
     * `focusForApptId`, which answers for exactly one. `focusFromBookedOrder` covers all
     * of them and deliberately leaves the singular fields null when there is more than
     * one, so nothing downstream names one truck while scoping two.
     *
     * ⚠️ AN UNBOOKED ORDER STILL RAISES A FOCUS, and it is deliberately not scopeable.
     * The rails then say WHY they are empty instead of going blank — which is the whole
     * of `focusBlockedReason`. ⛔ Returning null here would look like a dead row.
     *   ** A GESTURE THAT CANNOT SUCCEED MUST STILL BE SEEN TO HAVE HAPPENED. **
     */
    const railHoverOrder = (
        orderNumber: string | null,
        bookedOn: readonly { appointmentId: string; appointmentNumber: string | null }[],
    ) => {
        const f = orderNumber ? focusFromBookedOrder(orderNumber, bookedOn) : null;
        setRailHover(f ? 'orders' : null);
        onHoverParty?.(f);
    };
    const railChooseOrder = (
        orderNumber: string,
        bookedOn: readonly { appointmentId: string; appointmentNumber: string | null }[],
    ) => {
        setRailHover(null);
        onChooseRecord?.(focusFromBookedOrder(orderNumber, bookedOn));
    };
    /**
     * ---- ⭐ THE PREVIEW, AS A SET, SO THE TWO VIEWS KEEP AGREEING ------------
     *
     * The grid gained `previewAppointmentIds` on 2026-09-21 so that hovering a
     * client outlines all of its loads rather than one. ⛔ This list draws the
     * SAME two rings from the SAME sources — an existing asserted property:
     * *"the arrivals list draws both too, so the two views agree"* — so it has
     * to follow, or hovering a client would mark the board and not the rail.
     *
     * ⭐ NO NEW PROP. `previewFocus` is already here and already carries
     * `appointmentIds`; the grid needed one only because it is a sibling of
     * this panel, not a child of it.
     *
     * ⚠️ NO TWO-TONE TREATMENT HERE, and that is deliberate. The grid needed a
     * light inner hairline because a chip is filled with a SATURATED STATUS
     * COLOUR the customer can change. These cards sit on `bg-muted/30` or
     * `bg-amber-50`, so a single dark ring at 40% reads perfectly well.
     *   ** THE SAME MARK NEEDS DIFFERENT CONSTRUCTION ON DIFFERENT BACKGROUNDS;
     *      COPYING IT WHOLESALE WOULD ADD A HAIRLINE NOTHING HERE NEEDS. **
     */
    const previewApptIds = useMemo(
        () => new Set<string>(previewFocus?.appointmentIds ?? []),
        [previewFocus],
    );
    const ownScope = effParty && effParty.kind === subjectScopeKind ? effParty : null;
    /* ⭐ D2 / Decision 239 — a scope HELD from another chip. Line 1 names it after the
       list word (`Carriers · for client TRAFFIX`), so it is not repeated on line 2. */
    const crossScope = effParty && effParty.kind !== subjectScopeKind && effParty.kind !== 'appointment'
        ? effParty : null;

    /* ⛔⚤ ~~`partyScope && !sameFocus(railFocus, partyScope)`~~ — AMENDED 2026-09-20.
       Two changes, and the first is the subtler:
         1. It compares against `ownScope`, so a scope belonging to another subject
            falls THROUGH to line 2 instead of being swallowed by line 1.
         2. The leading `partyScope &&` is gone. It meant line 2 never rendered unless a
            PARTY was standing — so a driver clicked with no party was named on neither
            line, and decision 30 says he must be named (*"Driver Check-Ins should change
            to Driver - Pete L. / Appt xxxxx"*). ⚠️ That was invisible before 194,
            because line 1 could not name a driver either.
       ⛔ `sameFocus`, NOT `!==` — with nothing narrower the page passes the SAME object
       in both props, so an identity test happens to work today and by luck. */
    const narrowerFocus = !sameFocus(effFocus, ownScope ?? crossScope) ? effFocus : null;

    /* ⛔⤴ ~~`const anyScope = railFocus ?? partyScope;`~~ — ORPHANED 2026-09-27 with Ⓡ.
       It existed only to decide whether that button rendered and what its tooltip named.
       ⭐ THE REASONING MOVED WITH THE BUTTON, not away: `Calendar.tsx` now computes
       `scopeForReset` as the same `railFocus ?? partyScope` and for the same two
       reasons, which are worth keeping legible here because this is where they were
       learned:
         ⛔⛔ Ⓡ IS OFFERED FOR **ANYTHING SCOPED**, not the state that happens to be
         named in the condition. ⭐ `railFocus` first so the tooltip names the narrowest
         thing the scheduler is looking at; `partyScope` is the fallback that keeps the
         control on screen on a day where the chosen party has no appointments — Pete's
         *"lost the circle R button and no appointments on the date - so stuck"*. */

    /**
     * ---- ⭐⭐ DECISION 4: PICKING ONE NARROWS THE LIST TO ONE CARD ---------------
     *
     * *"narrows to ONE card when that subject is picked from the dropdown, so panel,
     * grid and rail always describe the same scope."*
     * ⛔ THE GRID IS ALREADY NARROWED by `filterAppts` — if the panel kept showing all
     * the other clients, the two halves of the screen would describe different scopes
     * and the card list would invite a click that changes nothing visible.
     */
    const shownParties = pickerValue
        ? parties.filter(pp => pp.code === pickerValue)
        : parties;

    /**
     * ---- ⛔⛔ A SUPPRESSED FILTER IS **CLEARED**, NOT JUST HIDDEN ----------------
     *
     * The driver dropdowns and `Needs license` vanish on a party subject. If one of
     * them was narrowing the arrivals list, hiding it leaves a filter in force with no
     * control on screen to release it — and switching back to Drivers then shows a
     * mysteriously short list.
     *   ** NEVER HIDE A CONTROL THAT IS CURRENTLY DOING SOMETHING. ** (§1AV.170, and
     *   the `hideStatuses` `!checked` rule it produced.)
     * ⚠️ Unconditional rather than `if (partyKind)`: coming BACK to Drivers must also
     * start clean, or the list is filtered by a choice made before the detour.
     */
    useEffect(() => {
        setClientFilter('');
        setCarrierFilter('');
        setNeedsLicenceOnly(false);
    }, [subject]);

    /**
     * ---- ⭐⭐ A PARTY CARD'S DWELL LATCHES THE RAIL, LIKE A DRIVER CARD'S --------
     *
     * ⛔ §1AR (2026-09-10, from Pete): *"hovering SETS the scope and only ALL clears
     * it. Anything else makes the rail change under a scheduler who looked away."*
     * ⚠️ SO THERE IS NO `onMouseLeave` COUNTERPART. Releasing on mouse-out is the
     * behaviour that section exists to forbid, and the `ALL` control every panel
     * carries is what the latch is for.
     *
     * ⭐ 350ms — THE SAME DWELL as `beginHover` and the grid's hover card, because it
     * is the same gesture. Pete, 2026-09-10: *"if the user is going really fast over
     * appointments, it doesnt fire?"* ⛔ A second, nearby delay would be a second
     * feel for one interaction.
     *
     * ⛔ IT GOES THROUGH `onHoverCardShown`, the seam the driver cards already use.
     * A party focus carries `appointmentId: null`, so the page's handler sets the
     * rail focus and returns before touching the paperwork target — which is correct
     * and needed no branch.
     */
    /**
     * ---- ⛔⛔ THE DWELL MOVES THE **CALENDAR** TOO (Pete, 2026-09-19) ------------
     *
     * ⭐ ***"You have to select the client or the carrier for it to change the
     * calendar, its not consistent because when you hover it changes the right side
     * panels. It should change the calendar too"***
     *
     * ⛔⛤ AND HE IS RIGHT ABOUT THE INCONSISTENCY, WHICH WAS MINE. I had hover drive
     * the RAIL and click drive the RAIL PLUS THE CALENDAR — two gestures with
     * different reach, and nothing on screen said which did what. A scheduler who
     * hovers, sees three panels change, and then finds the grid unmoved has been
     * taught that the feature is unreliable rather than that he used the wrong verb.
     *
     *   ** IF ONE GESTURE ALREADY CHANGES MOST OF THE SCREEN, THE PART IT LEAVES
     *      ALONE READS AS BROKEN, NOT AS SCOPED. **
     *
     * ✅ SO THE DWELL SETS THE PARTY SCOPE — the same call the click makes. §1AR's
     * latch rule is unchanged and now covers the grid as well: hovering SETS it, and
     * only Reset (or the toolbar pill's ×) clears it.
     *
     * ⚠️ CLICK IS STILL NOT REDUNDANT. It is immediate rather than 350ms, and it is
     * the only way to TOGGLE the chosen party off from the card itself.
     */
    /* ⛔⚤ ~~beginPartyDwell / endPartyDwell~~ — DELETED 2026-09-20, decision 190.
       They latched the party scope on a 350ms hover. ⭐ THE CLICK PATH THEY DUPLICATED
       IS UNTOUCHED and is now the only trigger, which is the whole change: the dwell
       was a second, invisible way to do what the click did deliberately.
       ** WHEN TWO TRIGGERS DO ONE THING AND ONLY ONE IS VISIBLE, DELETE THE OTHER. **
       ⚠️ `partyDwell` (the ref) went with them — a timer nobody sets is a cleanup
       nobody needs, and `noUnusedLocals` is what found all three. */

    /**
     * ⭐ Truck counts by direction, over what is actually on screen.
     * ⛔⛔ AND THEY FOLLOW THE SUBJECT. §1AV.171: *"a label on a panel that does not
     * actually scope is the header asserting something false"* — a header counting
     * trucks over a body listing clients is the same fault. On a party subject these
     * count the day's LOADS, and the bold total says `Loads` rather than `Trucks`.
     */
    /**
     * ---- ⛔⛤ THE HEADER COUNTED THE WHOLE DAY WHILE THE TITLE NAMED ONE PARTY ---
     *
     * ⭐ Pete: *"The in out and Loads are not updating when you select individual
     * client, carrier, etc"* — `Client · HELLMAN` over `In 4 · Out 1 · Loads 5`,
     * with Hellman's own card directly beneath saying `Trucks 2 · In 1 · Out 1`.
     *
     * ⛔⛔ THE COUNTS WERE OVER `appointments`, THE UNFILTERED DAY. The heading, the
     * counts and the card list were three statements about three different sets,
     * stacked in 340px.
     *   ** A COUNT SITTING UNDER A HEADING IS READ AS A COUNT **OF** THAT HEADING.
     *      IF IT IS NOT, IT IS NOT A NEUTRAL NUMBER — IT IS A WRONG ONE. **
     * ⚠️ Same family as `departedHidden`, which exists because
     * `rows.length - visible.length` reported filtered trucks as departed.
     *
     * ⛔ NARROWED WITH `apptMatchesParty`, the SAME predicate `filterAppts` uses for
     * the grid, so the header cannot disagree with what is drawn.
     */
    const countedAppts = partyKind && pickerValue
        ? appointments.filter(a => apptMatchesParty(a, partyKind, pickerValue))
        : appointments;

    const inCount = partyKind
        ? countedAppts.filter(a => a.direction === 'INBOUND').length
        : visible.filter(c => c.direction === 'INBOUND').length;
    const outCount = partyKind
        ? countedAppts.filter(a => a.direction === 'OUTBOUND').length
        : visible.filter(c => c.direction === 'OUTBOUND').length;
    /**
     * ---- ⭐⭐ EVERY WARNING ACROSS THE ARRIVALS ON SCREEN --------------------
     *
     * ⚠️ ~~`unmatchedCount`~~ counted ONE warning kind, over ALL rows. Pete,
     * 2026-09-09: *"Probably should show the other warning for consistency
     * right?"* — so all three are counted, from `arrivalWarnings`, which is also
     * what each card's badge reads. ⛔ The header and the rail cannot disagree.
     *
     * ⭐ IT COUNTS `visible`, NOT `rows`, and that is a deliberate change. Every
     * other number in this header — In, Out, Trucks — describes what is ON
     * SCREEN, and the amber "filtered" chip beside them already says when that is
     * a narrowed view. A warning count on a different denominator from the counts
     * next to it is the kind of quiet disagreement nobody reconciles.
     *
     * ⚠️ ORDER IS FIXED, NOT INSERTION ORDER. A summary whose lines reshuffle as
     * trucks arrive is one a scheduler has to re-read every time.
     */
    const [showWarnings, setShowWarnings] = useState(false);

    const warningSummary = useMemo(() => {
        const ORDER: ArrivalWarningCode[] = ['no-appointment', 'licence', 'no-licence-outbound'];
        const counts = new Map<ArrivalWarningCode, number>();
        for (const r of visible) {
            for (const w of arrivalWarnings(r)) counts.set(w.code, (counts.get(w.code) ?? 0) + 1);
        }
        const lines = ORDER
            .filter((code) => (counts.get(code) ?? 0) > 0)
            .map((code) => ({ code, label: WARNING_LABEL[code](counts.get(code) ?? 0) }));
        let total = 0;
        for (const n of counts.values()) total += n;
        return { total, lines };
    }, [visible]);

    /**
     * ---- ⭐⭐ THE PARTY TABS GET THE SAME TOTALS (Pete, 2026-09-23) ----------
     *
     * > *"driver checkin top panel has the same icons totalled in the upper right
     * > hand corner. Clients/carriers/consignees does not."*
     *
     * ⛔ `warningSummary` ABOVE COUNTS `visible`, WHICH IS THE ARRIVAL ROWS. On a
     * party tab those are not what is on screen, so the header went blank — not
     * because the parties had no warnings, but because nothing had ever summed
     * theirs.
     *   ** A SUMMARY IS OF WHATEVER IS ON SCREEN. WHEN THE SCREEN CAN SHOW TWO
     *      KINDS OF ROW, ONE SUMMARY IS A SUMMARY OF THE OTHER ONE. **
     *
     * ⚠️ IT SUMS `shownParties`, NOT `parties` — the list after filtering, so the
     * total describes what the reader can actually count. The driver summary makes
     * the same choice (`visible`), and two totals computed from different
     * populations would be worse than one missing total.
     *
     * ⛔ TWO SEPARATE NUMBERS, NOT ONE ADDED TOGETHER. "Overdue" and "no paperwork
     * filed" are different problems with different cures; a single 7 would be a
     * number nobody can act on, and the party CARDS already show them apart.
     */
    const partySummary = useMemo(() => {
        /* ⛔⛤ WAS TWO TOTALS OF LOADS (atRisk, noPaperwork). It is now ONE total of
           WARNINGS, so the header badge counts what the cards' badges count and what the
           numbered boxes count.
             ** A SUMMARY MUST COUNT THE UNIT ITS ROWS COUNT, OR IT IS A DIFFERENT
                QUESTION ANSWERED IN THE SAME CORNER. ** */
        let total = 0;
        const lines: string[] = [];
        for (const pr of shownParties) {
            const pw = partyWarnings(pr);
            total += pw.length;
            /* ⚠️ THE TOOLTIP NAMES THE PARTY - "34 warnings" across seventeen clients
               is a number nobody can act on without knowing whose. */
            if (pw.length > 0) lines.push(pr.name + ': ' + pw.map(w => w.text).join(' '));
        }
        return { total, lines };
    }, [shownParties]);

    /** Appointments a walk-up could plausibly be attached to. */
    const attachable = useMemo(
        () => appointments
            .filter(a => a.statusCode !== 'CANCELLED' && a.statusCode !== 'DEPARTED' && a.direction !== 'BLOCK')
            .sort((a, b) => a.scheduledStartUtc.localeCompare(b.scheduledStartUtc)),
        [appointments],
    );

    /**
     * The substitute-driver question, pending an answer.
     *
     * ⚠️ NOT window.confirm(). A native dialog is the BROWSER's chrome, not the
     * product's: it cannot be styled, it announces "dockscheduler.symbia.com
     * says", it freezes the whole tab, and it is visually identical to the
     * prompts users are trained to dismiss without reading. This app ships
     * ConfirmDialog for exactly this. See arch §4.
     */
    const [attachAsk, setAttachAsk] = useState<
        { c: CheckInDto; appointmentId: string; apptNumber: string; names: string } | null
    >(null);

    async function doAttach(c: CheckInDto, appointmentId: string) {
        const appt = attachable.find(a => String(a.appointmentId) === appointmentId);
        // ⚠️ A SUBSTITUTE DRIVER IS THE NORMAL CASE, not an error — the booking
        // says Ruiz, Vega turns up. So an appointment that already lists a driver
        // is allowed, with a confirmation naming who is on it, rather than being
        // blocked outright.
        if (appt && appt.drivers && appt.drivers.length > 0) {
            setAttachAsk({
                c, appointmentId,
                apptNumber: appt.appointmentNumber,
                names: appt.drivers.map(d => d.name).filter(Boolean).join(', ') || 'a driver',
            });
            return;
        }
        await attachNow(c, appointmentId);
    }

    async function attachNow(c: CheckInDto, appointmentId: string) {
        setAttachAsk(null);
        /**
         * ---- ⭐⭐⭐ HAND IT TO THE ONE FLOW (2026-09-23) --------------------
         *
         * ⛔ EVERYTHING AFTER THIS BLOCK IS THE FALLBACK, not the normal path. See
         * the note on `onAttachRequest`: the handler owns the substitute-driver
         * question, the move confirmation, BOTH 409 prompts, the pallet variance,
         * the shape hints and the door text.
         *
         * ⚠️ NO `await load()` HERE, AND THAT IS NOT AN OMISSION. The handler may
         * return having only OPENED a dialog — the attach happens later, when the
         * scheduler answers. Reloading now would refresh the list before anything
         * changed and leave it stale afterwards. `Calendar` bumps `refreshKey` when
         * the attach actually lands, which is the signal this panel already
         * reloads on.
         *   ** A REFRESH FIRED WHEN THE FUNCTION RETURNS, NOT WHEN THE WORK
         *      FINISHES, IS A REFRESH AIMED AT THE WRONG MOMENT. **
         *
         * ⛔ `menuFor` IS CLEARED IMMEDIATELY. The menu sits over the grid; leaving
         * it open under a conflict dialog is a scheduler answering a prompt through
         * a hole in a popup.
         */
        if (onAttachRequest) {
            const appt = attachable.find(a => String(a.appointmentId) === appointmentId);
            if (appt) {
                setMenuFor(null);
                /* ⭐ THE LOAD HE IS ALREADY ON, so the handler can ask its move
                   question. ⚠️ Omitting it would make every re-attach from the menu
                   a silent move, which is the exact thing that question exists to
                   prevent — and the drag path has always supplied it. */
                const from = c.appointmentId
                    ? appointments.find(a => String(a.appointmentId) === String(c.appointmentId))
                    : null;
                await onAttachRequest({
                    checkInId: c.checkInId,
                    appt,
                    fromAppointmentNumber: from?.appointmentNumber ?? null,
                });
                return;
            }
            /* ⚠️ FALLS THROUGH when the appointment is not in `attachable` — it may
               be on another day or filtered off the board. The direct call below
               still works, minus the prompts, which is strictly what happened
               before this change rather than a new gap. */
        }
        setBusy(c.checkInId);
        interactingRef.current = true;
        try {
            await attachCheckIn(await getToken(), c.checkInId, appointmentId);
            setMenuFor(null);
            await load();
            onChanged();
            // ⚠️ AFTER the reload, so the prompt reads the door the appointment
            // actually has rather than the one this card was showing a moment ago.
            onAttached?.(appointmentId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not attach that arrival.');
        } finally {
            setBusy(null);
            interactingRef.current = false;
        }
    }

    /**
     * Save a corrected card.
     *
     * ⚠️ SENDS ONLY WHAT CHANGED. The endpoint treats an absent key as "leave it
     * alone" and an explicit null as "clear it", so posting the whole draft every
     * time would rewrite fields nobody touched — and write a history line for
     * each of them, which would make the log useless exactly when it matters.
     */
    async function saveEdit() {
        if (!editFor) return;
        const before: Record<string, string> = {
            driverName: editFor.driverName ?? '',
            driverPhone: editFor.driverPhoneEntered ?? '',
            carrierCode: editFor.carrierCode ?? '',
            poNumber: editFor.poNumber ?? '',
            /* ⛔⛤ ADDED 2026-09-23 - see driverCheckInRepository's COLS. The BOL was
               capturable at the gate and correctable nowhere. */
            bolNumber: editFor.bolNumber ?? '',
            referenceNumber: editFor.referenceNumber ?? '',
            containerNumber: editFor.containerNumber ?? '',
            trailerNumber: editFor.trailerNumber ?? '',
            /* ⛔⛤ ABSENT UNTIL 2026-09-24, while the form drew the box. A field missing
               from this map is not read, not compared and NEVER SENT -- so the control
               looked live and did nothing. ** A FORM CONTROL WITH NO ENTRY IN THE SAVE
               MAP IS A DECORATION THAT ACCEPTS TYPING. ** */
            trailerPlate: editFor.trailerPlate ?? '',
        };
        const changes: Record<string, string | null> = {};
        for (const k of Object.keys(before)) {
            const next = (draft[k] ?? '').trim();
            if (next !== before[k]) changes[k] = next === '' ? null : next;
        }
        if (Object.keys(changes).length === 0) { setEditFor(null); return; }

        setBusy(editFor.checkInId);
        interactingRef.current = true;
        try {
            await editCheckIn(await getToken(), editFor.checkInId, changes);
            setEditFor(null);
            await load();
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not save that change.');
        } finally {
            setBusy(null);
            interactingRef.current = false;
        }
    }

    /**
     * ---- ⭐⭐ EVERY OPTION FOR ONE ARRIVAL, IN ONE LIST (#341) --------------
     *
     * > Pete, 2026-09-09: *"Why can't you give me a dropdown too that has the
     * > same things as when you right click an appointment??????? So i have all
     * > options????"*
     *
     * ⭐ TWO SECTIONS, AND ONLY THE FIRST IS DEFINED HERE:
     *
     *   1. THE ARRIVAL's own actions — the things that exist because a truck is
     *      standing in the yard, whether or not it is matched to a load. This
     *      panel owns every one of these handlers.
     *   2. THE APPOINTMENT's menu, appended verbatim from `appointmentMenuItems`
     *      — the identical list a right-click on the grid produces, built by
     *      `contextMenuItemsFor` in Calendar.
     *
     * ⛔⛔ SECTION 2 IS NEVER RE-LISTED HERE. Its entries (View details, Text
     * directions, Text delivery pin, Change status) are built from handlers that
     * live only in Calendar, and a copy would drift the first time either gained
     * an entry — producing exactly the complaint above, again.
     *
     * ⚠️ AN UNMATCHED ARRIVAL GETS SECTION 1 ONLY, and that is not a refusal:
     * there is no appointment, so there is no appointment menu to show. The
     * actions that MAKE one — Attach, Find load, New appointment — are in
     * section 1 and are the top of the list on exactly those cards.
     */
/**
 * ============================================================================
 * ⭐⭐ THE ARRIVAL MENU IS TWO MENUS GLUED TOGETHER — THIS IS THE SEAM
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"This needs to be cleaned up. Text Driver stays at the
 * > top. Text Driver his door, Text driver - unloading started, Text Driver -
 * > paperwork ready should all be removed... Mark departed is listed twice. All
 * > status items should be together at the bottom... Sign Paperwork and Print
 * > Paperwork should be removed. Paperwork.... takes care of those two."*
 *
 * ---- ⛔ WHY THE LIST WAS A MESS AND WHY IT IS FIXED **HERE** ---------------
 *
 * `cardMenuItems` builds the ARRIVAL's own actions and then appends the LOAD's
 * menu verbatim — which is deliberate and worth keeping: the appointment owns
 * its actions, and copying them would give a scheduler two menus that drift.
 * ⚠️ But the two lists were written independently, so they both offer "Mark
 * departed", the load's menu carries three one-tap texts that "Text the
 * driver…" already opens a picker for, and the paperwork actions appear three
 * times between them.
 *
 * ⛔ SO THE TIDY HAPPENS AT THE SEAM, NOT IN EITHER SOURCE. Removing "Text
 * driver his door" from `appointmentMenuItems` would remove it from the
 * APPOINTMENT's own right-click menu too, where it is the fastest route there
 * is. Nothing here changes what a load's menu offers a load.
 *
 * ---- ⚠️ IT MATCHES ON LABELS, WHICH IS A REAL WEAKNESS -------------------
 *
 * `MenuItem` has a label, an icon and a handler — no id — so the label is the
 * only thing to key on. ⛔ A rename in `Calendar` would silently stop filtering
 * and the duplicates would quietly return. ⚠️ That is why every string below
 * is in ONE named list and `checkin-panel-guard.cjs` asserts each one still
 * appears in `Calendar.tsx`: the rename fails a deploy instead of a demo.
 */

/** ⛔ Superseded by "Paperwork…", which opens the dossier these two act on. */
const MENU_DROP_EXACT = [
    'Sign paperwork',
    'Print paperwork',
];

/**
 * ⛔ The one-tap texts. "Text the driver…" at the top opens a picker that
 * contains all of them, so these are the same actions listed twice — once as a
 * menu and once inline. Pete: *"They are accessed when you hit Text Driver at
 * the top."*
 */
const MENU_DROP_PREFIX = [
    'Text driver',
];

/**
 * ⭐ EVERYTHING THAT CHANGES WHAT STATE THE TRUCK OR THE LOAD IS IN, in Pete's
 * order, pulled to the bottom together. ⚠️ Order matters and is his: the two
 * that end a visit, then the two that undo or re-state it.
 */
const MENU_STATUS_ORDER = [
    'Mark departed',
    'Void this arrival',
    'Cancel appointment',
    'Change status',
];

const startsWithAny = (label: string, list: string[]) =>
    list.some(p => label.toLowerCase().startsWith(p.toLowerCase()));

/**
 * ⭐ SAYS WHAT IT SWALLOWED. Pete, 2026-09-10: *"Put a (sign, print, attach)
 * next to Paperwork."*
 *
 * ⛔ THIS IS THE OTHER HALF OF DROPPING "Sign paperwork" AND "Print paperwork".
 * Removing two entries makes a menu shorter; it also makes those two actions
 * look GONE. ⚠️ Naming them on the entry that now carries them is what keeps
 * the tidy from reading as a removal.
 *
 * ⚠️ RENAMED AT THE SEAM, not in `Calendar`. The appointment's own right-click
 * menu still says "Paperwork…", because nothing was folded into it there.
 */
const PAPERWORK_LABEL = 'Paperwork (sign, print, attach)…';

function tidyArrivalMenu(items: MenuItem[]): MenuItem[] {
    const kept: MenuItem[] = [];
    const status: MenuItem[] = [];
    /** ⛔ Kills the SECOND "Mark departed" — the arrival's and the load's. */
    const seen = new Set<string>();

    for (const it of items) {
        if (MENU_DROP_EXACT.includes(it.label)) continue;
        if (startsWithAny(it.label, MENU_DROP_PREFIX)) continue;
        /* ⚠️ First wins. The arrival's own entry is built first and acts on the
           arrival, which is what this menu is about. */
        if (seen.has(it.label)) continue;
        seen.add(it.label);
        const entry = it.label.toLowerCase().startsWith('paperwork')
            ? { ...it, label: PAPERWORK_LABEL }
            : it;
        (startsWithAny(entry.label, MENU_STATUS_ORDER) ? status : kept).push(entry);
    }

    /* ⭐ Sorted into Pete's order, and anything status-like that is not in the
       list keeps its relative position after the ones that are. */
    status.sort((a, b) => {
        const rank = (l: string) => {
            const i = MENU_STATUS_ORDER.findIndex(
                p => l.toLowerCase().startsWith(p.toLowerCase()));
            return i < 0 ? MENU_STATUS_ORDER.length : i;
        };
        return rank(a.label) - rank(b.label);
    });
    return [...kept, ...status];
}

    /**
     * ⭐ THE PARTY CARD'S MENU ITEMS. See the render site for why every disabled
     * row states its blocker inside its own label.
     * ⚠️ `partyScope` IS IN THE DEPS AND HAS TO BE: the first item flips between
     * "scope" and "clear" on it, and a menu built from a stale scope would offer
     * to scope the board to the thing it is already scoped to.
     */
    const partyMenuItems = useCallback((pr: PartyRow): MenuItem[] => {
        const isChosen = partyScope?.partyCode === pr.code && partyScope?.kind === pr.kind;
        /* ⛔ THE SAME TWO SENTENCES THE COMMS SECTION USES, and they differ by kind
           for the reason recorded there: a carrier is ONE ENDPOINT away and a client
           is a §3B MIGRATION away, and one shared vague phrase would hide which of
           those two problems you actually have. */
        const blocked = pr.kind === 'carrier'
            ? 'not on this screen yet'
            : `no ${pr.kind} contacts stored yet`;
        return [
            {
                label: isChosen ? 'Clear the scope' : `Scope the board to ${pr.name}`,
                icon: <Filter className="h-3.5 w-3.5" />,
                onClick: () => {
                    if (isChosen) { resetAll(); return; }
                    onChooseParty?.(pr.kind, pr.code, pr.name, pr.appointments);
                },
            },
            {
                label: `Text the ${pr.kind} — ${blocked}`,
                icon: <MessageSquare className="h-3.5 w-3.5" />,
                onClick: () => { /* blocked */ },
                disabled: true,
            },
            {
                label: `Email Schedule — ${blocked}`,
                icon: <Mail className="h-3.5 w-3.5" />,
                onClick: () => { /* blocked */ },
                disabled: true,
            },
            {
                /* ⛔ DECISION 190 E. Named rather than left off the list: an absent
                   item reads as a thing nobody wanted, a disabled one as a thing
                   somebody is building. */
                label: `Open the ${pr.kind} record — no ${pr.kind} screen yet`,
                icon: <PenLine className="h-3.5 w-3.5" />,
                onClick: () => { /* blocked */ },
                disabled: true,
            },
        ];
    }, [partyScope, onChooseParty, resetAll]);

    const cardMenuItems = useCallback((c: CheckInDto): MenuItem[] => {
        const unmatched = c.appointmentId === null;
        const items: MenuItem[] = [];

        /* ⭐ WHAT AN UNMATCHED TRUCK NEEDS COMES FIRST. On a matched card these
           three are meaningless, so they are absent rather than disabled — the
           same rule Calendar's menu follows for consignee pins and admin. */
        if (unmatched) {
            items.push(
                /**
                  * ⛔⛔ ~~`onClick: () => setMenuFor(c.checkInId)`~~ — 2026-09-25.
                  * Pete: *"attach to a load does nothing."*
                  *
                  * ⚠️ IT RELIED ON `menuFor` TO FORCE THE CARD OPEN, through the OR in
                  * `detailOpen`. That is the borrowed-state pattern retired from
                  * `partyOpen` the same day: when the picker is ALREADY open - from the
                  * Attach button on the card - this set the state to the value it
                  * already held, so nothing re-rendered and the click was spent.
                  *   ** SETTING A STATE TO WHAT IT ALREADY IS IS INDISTINGUISHABLE
                  *      FROM A DEAD CONTROL. **
                  *
                  * ⭐ ITS SIBLING ALREADY DID THIS PROPERLY. "Text the driver..." writes
                  * `openCards` and THEN sets its panel, so the card is genuinely open
                  * rather than borrowing. This now matches it.
                  *   ** TWO MENU ITEMS THAT OPEN THE SAME CARD SHOULD NOT OPEN IT TWO
                  *      DIFFERENT WAYS. **
                  */
                { label: 'Attach to an appointment…', icon: <Paperclip className="h-3.5 w-3.5" />,
                  onClick: () => {
                      setOpenCards(prev => new Set(prev).add(c.checkInId));
                      setMenuFor(c.checkInId);
                  } },
                { label: 'Find his load…', icon: <Search className="h-3.5 w-3.5" />,
                  onClick: () => setMatchFor(c) },
                { label: 'New appointment…', icon: <PenLine className="h-3.5 w-3.5" />,
                  onClick: () => onCreateFromCheckIn(c) },
            );
        }

        /* ⛔ TEXT AND CALL ARE ON EVERY CARD, MATCHED OR NOT (#340). They need a
           number, not an appointment. This is the whole point of the arrival text route. */
        items.push(
            { label: 'Text the driver…', icon: <MessageSquare className="h-3.5 w-3.5" />,
              onClick: () => { setOpenCards(prev => new Set(prev).add(c.checkInId)); setSmsFor(c.checkInId); } },
        );
        const tel = teamsCallHref(c.driverPhoneEntered);
        if (tel) {
            items.push({
                label: `Call ${c.driverPhoneEntered} in Teams`,
                icon: <PhoneCall className="h-3.5 w-3.5" />,
                /* ⚠️ window.open, not an <a> — a menu item is a button. `noopener`
                   so the Dock tab cannot be reached by the opened page. */
                onClick: () => { window.open(tel, '_blank', 'noopener,noreferrer'); },
            });
        }

        items.push(
            { label: 'Scan or view his license…', icon: <ScanLine className="h-3.5 w-3.5" />,
              onClick: () => setLicenceFor(c) },
            /* ⭐ #73 — ON EVERY CARD, NOT ONLY THE ONES THAT OWE A NUMBER. The
               ICON is conditional (it carries a warning state); this row is the
               way to correct a wrong trailer number or add a plate to an arrival
               that answered properly at the gate. ⚠️ The plate is not on the edit
               popup at all — its `before` map never listed one — so without this
               row there is no route to it. */
            { label: 'Trailer, container or plate…', icon: <PlateGlyph />,
              onClick: () => setVehicleFor(c) },
            { label: 'Edit this arrival…', icon: <PenLine className="h-3.5 w-3.5" />,
              onClick: () => startEdit(c) },
        );

        /* ⚠️ THE ARRIVAL'S PAPERWORK, ONLY WHEN THERE IS NO APPOINTMENT MENU
           COMING. Section 2 carries Paperwork / Sign / Print for a matched load,
           and two entries doing nearly the same thing is how a scheduler learns
           to distrust a menu. */
        if (unmatched) {
            if (onPaperwork) items.push({ label: 'Paperwork…', icon: <Eye className="h-3.5 w-3.5" />, onClick: () => onPaperwork(c) });
            /**
             * ---- ⛔⚤ ~~'Add paperwork from this machine…'~~ — REMOVED 2026-09-25 ----
             *
             * > Pete: *"Paperwork and Add Paperwork should be combined into Paperwork
             * > screen - redundant."*
             *
             * ⚠️ THE ROW ABOVE ALREADY WARNED ABOUT THIS SHAPE — "two entries doing
             * nearly the same thing is how a scheduler learns to distrust a menu" —
             * and then this was added three lines under it. Both were right in
             * isolation: it genuinely was the one paperwork task that skipped the
             * dialog. It still read as a duplicate, because a menu is judged on what
             * its labels look like, not on what its handlers do.
             *   ** A DISTINCTION THAT ONLY EXISTS IN THE HANDLER IS NOT A DISTINCTION
             *      THE READER CAN SEE. **
             *
             * ⛔⛔ THE COST, NAMED RATHER THAN DISCOVERED LATER. The paperclip
             * CardIcon that calls the same function is gated on `inDialog`, so on the
             * RAIL this menu item was the only one-click route to a file picker.
             * Uploading from a rail card is now: open Paperwork, use its drop zone.
             * Two steps where there was one — which is exactly the cost the
             * 2026-09-10 note weighed when it added this, and Pete has now weighed it
             * the other way with the menu in front of him.
             *   ** THE PERSON LOOKING AT THE SCREEN GETS TO OVERRULE THE NOTE WRITTEN
             *      BY THE PERSON WHO WAS LOOKING AT THE CODE. **
             *
             * ⚠️ `openPaperworkPicker` IS NOT ORPHANED — the paperclip icon still
             * calls it. §0.3: recorded, not deleted, so restoring this is one line.
             */
            if (onSign)      items.push({ label: 'Sign paperwork', icon: <PenLine className="h-3.5 w-3.5" />, onClick: () => onSign(c) });
            if (onPrint)     items.push({ label: 'Print paperwork', icon: <Printer className="h-3.5 w-3.5" />, onClick: () => onPrint(c) });
        }

        if (onMarkDeparted && !unmatched && !departed(c)) {
            items.push({ label: 'Mark departed', icon: <DoorOpen className="h-3.5 w-3.5" />,
                         onClick: () => onMarkDeparted(c) });
        }

        items.push({
            label: 'Void this arrival…', icon: <Trash2 className="h-3.5 w-3.5" />,
            destructive: true,
            onClick: () => { setVoidFor(c); setVoidReason(''); },
        });

        /* ⭐⭐ AND THEN THE APPOINTMENT'S ENTIRE MENU, UNCHANGED. */
        if (!unmatched && appointmentMenuItems) {
            items.push(...appointmentMenuItems(String(c.appointmentId)));
        }
        return tidyArrivalMenu(items);
    }, [appointmentMenuItems, onCreateFromCheckIn, onPaperwork, onSign, onPrint,
        onMarkDeparted]);

    /**
     * ---- ⭐⭐ ONE POPUP, NOT TWO (Pete, 2026-09-10) -------------------------
     *
     * > *"Clicking the driver card should go directly into a popup edit screen
     * > with the same ICONs and '...' on that page."*
     *
     * ⛔ THERE WERE TWO DIALOGS AND THAT WAS THE COMPLAINT. A click opened an
     * OPTIONS list; Edit on it opened a SECOND dialog with the fields. Pete's
     * screenshot of the result shows the cost plainly — the second dialog even
     * printed its own title twice, once in the header and once in the body,
     * because it had been written to stand alone and then given a header.
     *
     * ⭐ NOW THE CLICK LANDS ON THE FIELDS, and the options are still all there
     * — as the tool strip along the top and the (…) that expands the same
     * `cardMenuItems` list the right-click menu uses. Nothing was removed; one
     * step was.
     *
     * ⚠️ `setDialogOptions(false)` IS NOT TIDINESS. Without it the list stays
     * expanded from the last arrival, and every item in it acts on THIS one.
     */
    function startEdit(c: CheckInDto) {
        setEditFor(c);
        setDialogOptions(false);
        setDraft({
            driverName: c.driverName ?? '',
            driverPhone: c.driverPhoneEntered ?? '',
            carrierCode: c.carrierCode ?? '',
            poNumber: c.poNumber ?? '',
            /**
             * ⛔⛤ `bolNumber` WAS MISSING HERE AND PRESENT EVERYWHERE ELSE. The form
             * RENDERS a BOL box and `saveEdit`'s `before` map LISTS it, so the box drew
             * empty on an arrival that had a BOL -- and saving anything else on that
             * form wrote NULL over it.
             *   ** A FIELD THE FORM DRAWS AND THE SAVE COMPARES MUST BE SEEDED. TWO OF
             *      THE THREE IS NOT A PARTIAL FEATURE, IT IS A DELETE. **
             */
            bolNumber: c.bolNumber ?? '',
            referenceNumber: c.referenceNumber ?? '',
            containerNumber: c.containerNumber ?? '',
            trailerNumber: c.trailerNumber ?? '',
            /* ⚠️ AND `trailerPlate` WAS THE MIRROR FAULT: rendered, seeded nowhere, and
               absent from `before` -- so typing in it did nothing at all. Seeded here;
               `before` gains it too. */
            trailerPlate: c.trailerPlate ?? '',
        });
    }

    async function doVoid() {
        if (!voidFor || voidReason.trim().length < 3) return;
        setBusy(voidFor.checkInId);
        interactingRef.current = true;
        try {
            await voidCheckIn(await getToken(), voidFor.checkInId, voidReason.trim());
            setVoidFor(null);
            setVoidReason('');
            await load();
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not void that arrival.');
        } finally {
            setBusy(null);
            interactingRef.current = false;
        }
    }


    /**
     * ============================================================================
     * ⭐⭐ THE ARRIVAL'S TOOL STRIP — ONE DEFINITION, TWO PLACES IT APPEARS
     * ============================================================================
     *
     * > Pete, 2026-09-10: *"Clicking the driver card should go directly into a
     * > popup edit screen with the same ICONs and '...' on that page."*
     *
     * ---- ⛔⛔ WHY IT IS EXTRACTED AND NOT COPIED ------------------------------
     *
     * The popup needs the SAME strip the card carries. ⚠️ Pasting a second copy
     * of eight icons — each with its own enable rule, warning colour and count
     * badge — is how the two quietly stop agreeing: somebody adds an icon to the
     * card, nobody adds it to the popup, and the popup is the one a scheduler
     * reached by clicking rather than by aiming. That is §4.14.1 restated, and it
     * has already shipped green once in this rail.
     *
     * ⭐ SO THERE IS ONE FUNCTION. An icon added here appears in both places
     * because there is no second place to forget.
     *
     * ---- ⚠️ WHAT `inDialog` CHANGES, AND WHAT IT MUST NOT --------------------
     *
     * ⛔ IT DOES NOT REMOVE ICONS. "Same ICONs" is the request, so the strip is
     * the same strip. The one thing it changes is the ARRIVAL HISTORY: the (i)
     * expands the history INSIDE THE CARD, which is behind the modal — so from
     * the popup it closes the popup and pins the history, landing the scheduler
     * exactly where the (…) menu's own history item lands them.
     *
     * ⚠️ NOT A `useCallback`. It closes over a dozen pieces of state and the deps
     * array would be the thing that goes stale — an icon disabled against a
     * `busy` from two renders ago is worse than an unmemoised function.
     */
    const arrivalIcons = (c: CheckInDto, inDialog = false) => {
    /* ⛔ DERIVED HERE, NOT PASSED IN. It used to be computed by the card's map;
       taking it as an argument would let the two call sites disagree about what
       "unmatched" means, which is the drift this extraction exists to stop. */
    const unmatched = c.appointmentId === null;
    return (
        <div
        className="mt-1 flex items-center justify-end gap-0.5"
        onDoubleClick={(e) => e.stopPropagation()}
        >
        {/* ⭐ F166: the client's name, on the card only (the editor names the client
            itself). `mr-auto` pushes the icons right; with no name the row's
            `justify-end` still keeps them right. Truncates, full name on hover. */}
        {!inDialog && (() => {
            const name = clientDisplayName(c, c.appointmentId ? apptById.get(c.appointmentId) : null, partyNames);
            return name
                ? <span className="card-client mr-auto min-w-0 truncate text-[11px] font-medium text-muted-foreground" title={name}>{name}</span>
                : null;
        })()}
        {/* ⚠️ ~~THE CHEVRON WAS HERE~~ and moved into the card's
            LEFT COLUMN — see the note beside it there. This row
            is the tool strip again, right-aligned, nothing else. */}
        {/**
          * ⛔⛔ NO LONGER GATED ON `!unmatched`, AND THE OLD GATE
          * WAS BACKWARDS.
          *
          * ⚠️ It hid the paperwork viewer on arrivals with no
          * appointment — which is EXACTLY where the #221 flow
          * puts paperwork, because the documents are what find
          * the load. Pete, 2026-08-25, after dropping documents
          * on an unmatched driver: *"it doesn't have the eye icon
          * like I asked to view paperwork?"*
          *
          * ⭐ THE GATE WAS A SYMPTOM. Opening it needed an
          * appointment because the dialog only knew how to load a
          * LOAD's dossier. It now takes an arrival too, so the
          * icon can simply always be here.
          *
          * ⚠️ AND THE COUNT WAS LYING UNDERNEATH IT — the server
          * counted documents only through the appointment, so an
          * unmatched card reported zero however much paperwork was
          * filed on it. Fixed in the same pass; without that the
          * dot would still never light.
          */}
        {/**
          * ---- ⭐⭐ THE CARD SHOWS TWO ICONS. THE POPUP SHOWS ALL OF THEM. ------
          *
          * > Pete, 2026-09-10: *"I want to see what the driver card will look like
          * > with the icons invisible for all of them except the drivers license
          * > scan and the .... cookie crum. The other ones make invisible and put
          * > there task in the cookie crumb menu if its not in there already."*
          *
          * ⛔⛔ `inDialog` IS THE GATE, AND IT MUST STAY THAT WAY. This strip is
          * ONE function used at BOTH the card and the arrival popup — Pete asked
          * for the popup to carry *"the same ICONs and '...' on that page"*, and
          * `paperwork-tray-guard` asserts it is defined once and used twice.
          * ⚠️ Hiding these outright would empty the popup he asked to have them on.
          *
          * ⭐ ON THE CARD THE MENU IS THE ROUTE. Every hidden icon's task is in the
          * (…) — checked mechanically, not by eye, in `arrival-menu-test`.
          *
          * ⚠️ THE LICENCE SCAN STAYS BECAUSE IT CARRIES A WARNING STATE. It turns
          * amber when the licence is missing or not commercial (§315), and a
          * warning behind a menu is a warning nobody sees.
          */}
        {inDialog && onPaperwork && (
        <CardIcon
            label={(c.paperworkCount ?? 0) > 0
                ? `Paperwork — ${c.paperworkCount} on file`
                : 'Paperwork — nothing filed yet'}
            onClick={() => onPaperwork(c)}
            disabled={busy !== null}
            /* ⭐ THE COUNT, NOT THE DOT (2026-08-27) — see CardIcon.
               ⚠️ The licence icon below KEEPS its dot: there is only
               ever one licence, so a "1" there would be noise. */
            count={c.paperworkCount ?? 0}
        >
            <Eye className="h-3.5 w-3.5" />
        </CardIcon>
        )}
        {/**
          * ⭐ ATTACH, NEXT TO VIEW. The eye says what is already
          * filed; this puts something new on. ⚠️ Rendered only
          * when the page supplied `uploadToCheckIn` — a panel
          * with nowhere to send a file must not offer a button
          * that does nothing.
          */}
        {inDialog && uploadToCheckIn && (
        <CardIcon
            label="Attach paperwork to this arrival"
            /**
              * ⭐⭐ STRAIGHT TO THE FILE PICKER. Pete, 2026-09-08:
              * *"go directly to file explorer, pick a file(s),
              * then back to the driver panel."*
              *
              * ⛔ THE `.click()` IS SYNCHRONOUS INSIDE THIS HANDLER
              * AND THAT IS THE WHOLE TRICK. A browser opens a file
              * picker only from a live user gesture. The dialog
              * version had to mount a component first and hope the
              * gesture was still warm; this cannot fail that way,
              * in any browser.
              *
              * ⚠️ THE TARGET GOES IN A REF **AND** IN STATE, on
              * purpose. `setAttachFor` will not have applied by the
              * time the picker opens, so the ref is what the change
              * handler reads; the state is only so THIS card knows
              * to render the progress line.
              */
            /* ⭐ THE SHARED ACTION — see `openPaperworkPicker`. The (…) menu item
               calls the SAME function, so the icon and the menu cannot drift about
               what "attach paperwork" does. */
            onClick={() => openPaperworkPicker(c)}
            disabled={busy !== null || paperwork.busy}
        >
            <Paperclip className="h-3.5 w-3.5" />
        </CardIcon>
        )}
        {/**
          * ⭐ SIGN AND PRINT, STRAIGHT FROM THE CARD (#245).
          * Pete: *"If there is only 1 doc associated to the
          * driver, he should be able to hit a sign icon next to
          * his name and pop the screen. Only show that
          * intermediate screen if you need to edit, print, etc."*
          *
          * ⚠️ SHOWN ONLY WHEN THERE IS PAPERWORK. A sign icon on
          * a card with nothing filed is a button whose only
          * possible outcome is a disappointment.
          */}
        {inDialog && onSign && (c.paperworkCount ?? 0) > 0 && (
        <CardIcon
            label="Sign the paperwork"
            onClick={() => onSign(c)}
            disabled={busy !== null}
        >
            <PenLine className="h-3.5 w-3.5" />
        </CardIcon>
        )}
        {inDialog && onPrint && (c.paperworkCount ?? 0) > 0 && (
        <CardIcon
            label="Print the paperwork"
            onClick={() => onPrint(c)}
            disabled={busy !== null}
        >
            <Printer className="h-3.5 w-3.5" />
        </CardIcon>
        )}
        {/* ⚠️ AMBER WHEN AN OUTBOUND TRUCK HAS NO LICENCE.
        That is the one case that matters (design §3) —
        an outbound is TAKING goods away.

        ⛔ AND NOW ALSO WHEN THE LICENCE WE DO HAVE IS WRONG
        (#315). This icon used to warn for the OPPOSITE thing
        only: an expired licence, or a car licence, or a
        different man's name all set `hasDriverIdentity` and
        left the icon in its calm state — so a licence on file
        but bad was the QUIETEST thing on the card.

        ⭐ THE LABEL CARRIES THE SENTENCE, so hovering the icon
        answers "what is wrong with it" without opening
        anything. Same string the card shows below; one
        source, not two. */}
        <CardIcon
        label={c.licenceWarning
            ? `${c.licenceWarning.text} Open to view or replace.`
            : c.hasDriverIdentity
                ? 'License on file — open to view or replace'
                : 'Scan the driver license'}
        onClick={() => setLicenceFor(c)}
        disabled={busy !== null}
        dot={c.hasDriverIdentity}
        warn={!!c.licenceWarning
            || (c.direction === 'OUTBOUND' && !c.hasDriverIdentity)}
        >
        <ScanLine className="h-3.5 w-3.5" />
        </CardIcon>
        {/**
          * ---- ⭐⭐ #73 — THE NUMBER HE OWES, BESIDE THE LICENCE -------------
          *
          * > Pete, 2026-09-23: *"lets add a icon next to the driversid scan icon
          * > like 123 where a screen pops in dockscheduler and the scheduler can
          * > enter either trailer, container or license plate for the driver"*
          *
          * ⛔⛔ IT IS PLACED AFTER THE LICENCE ICON, AND THAT IS NOT COSMETIC.
          * `arrival-menu-test` proves the licence scan is not hidden by looking
          * for a dialog-only gate within five hundred characters ABOVE it, so a
          * conditional block inserted before it would close that gap and fail a
          * guard that is about something else entirely.
          *
          * ⚠️ AND THE GUARD'S PATTERN IS DESCRIBED HERE RATHER THAN QUOTED.
          * The first draft of this note pasted the regex in, and the pasted text
          * MATCHED ITSELF — the comment explaining the rule broke the rule.
          *   ** A COMMENT QUOTING A GUARD'S PATTERN IS INDISTINGUISHABLE FROM
          *      THE THING THE PATTERN FORBIDS. **
          *   ** A GUARD THAT MATCHES ON PROXIMITY IS A GUARD THE NEXT EDIT CAN
          *      TRIP WITHOUT TOUCHING WHAT IT GUARDS. **
          *
          * ⭐ ON THE CARD ONLY WHEN THERE IS SOMETHING TO DO, and always in the
          * popup — the same rule the licence scan is kept by: *"THE LICENCE SCAN
          * STAYS BECAUSE IT CARRIES A WARNING STATE… a warning behind a menu is a
          * warning nobody sees."* A permanent new icon on twenty cards for an
          * action almost none of them need is the other half of that mistake.
          *
          * ⚠️ `=== true` — the flag is a tri-state and null means this site
          * never asked.
          *
          * ⚠️ AND ITS TASK IS IN THE (…) REGARDLESS, so hiding it on a card
          * that does not need it hides nothing: 'Trailer, container or plate…'.
          */}
        {/**
          * ---- ⛔⚤ ~~CONDITIONAL~~ - ALWAYS DRAWN, 2026-09-23 -------------------
          *
          * > Pete: *"why is the plate icon conditional? We will always want to see the
          * > trailer, container, license plate."*
          *
          * ⛔⛔ THE ARGUMENT ABOVE WAS FOR THE WRONG KIND OF ICON. It read: *"on the
          * card only when there is something to do ... a permanent new icon on twenty
          * cards for an action almost none of them need."* That reasoning is sound for a
          * control whose only purpose is to FIX something - and it is borrowed from the
          * licence scan, which is exactly such a control. This one is not. It opens the
          * truck's trailer, container and plate numbers, which a scheduler wants to READ
          * far more often than they want to correct.
          *   ** AN ICON THAT OPENS A RECORD IS NOT AN ICON THAT COMPLETES A TASK. HIDING
          *      IT WHEN NOTHING IS OWED HIDES THE RECORD, NOT THE CHORE. **
          *
          * ⚠️ AND IT HID MORE THAN INTENDED. `vehicleNumberPending` is a TRI-STATE and
          * the test was `=== true`; null - a site that never asked - drew nothing either,
          * so on a facility with the prompt switched off the numbers had no door at all.
          *   ** A `=== true` TEST ON A TRI-STATE MAKES "NO" AND "NEVER ASKED" THE SAME
          *      ANSWER, AND ONLY ONE OF THEM DESERVES A BLANK. **
          *
          * ⭐ `warn` IS UNCHANGED, so the icon still turns amber when he owes one. The
          * loudness was always the right channel for "something to do"; the presence was
          * not.
          */}
        <CardIcon
            label={c.vehicleNumberPending === true
                ? 'He owes a trailer, container or plate number — enter it'
                : 'Trailer, container or plate number'}
            onClick={() => setVehicleFor(c)}
            disabled={busy !== null}
            warn={c.vehicleNumberPending === true}
        >
            <PlateGlyph />
        </CardIcon>
        {/**
          * ⭐ #289 — THE ARRIVAL LOG, ON ITS OWN CONTROL.
          *
          * Pete: *"the driver-card hover expands the history
          * and eats the rail"* — he asked for an (i) icon that
          * reveals it on hover instead, and this is it.
          *
          * ⚠️ HOVER STILL OPENS IT, so nothing got slower for
          * the scheduler who liked the old behaviour; what
          * changed is that it now takes AIMING at a twenty-pixel
          * target rather than merely passing over the card.
          */}
        {/* ⛔⚤ ~~THE (i) ARRIVAL-HISTORY ICON~~ — REMOVED 2026-09-20 with the inline log
           it opened. The History RAIL now answers this, scoped by a single click. */}
        {/**
          * ---- ⭐⭐ THE COMMS ICONS, ON THE ROW WITH EVERY OTHER ACTION -----------
          *
          * > Pete, 2026-09-24: *"we need to move the Phone and Text Icon on Drivers
          * > Checkin panel to the line with the other icons (driver scan and license
          * > plate) but keep x4945 on the same line."*
          *
          * ⛔⛤ ~~`inDialog &&`~~ — DROPPED. The text icon already existed here and
          * rendered ONLY inside the dialog, so the rail card -- the surface a scheduler
          * actually works from -- had no comms icon at all and the only way in was the
          * phone number itself.
          *
          * ⚠️ BOTH ICONS DO THE SAME THING, AND THAT IS DELIBERATE. The panel they
          * open holds the call button and the text composer together; splitting them
          * into two destinations would mean guessing which half the scheduler wanted.
          * Pete: *"the comm icons like phone and text just open the whole panel and
          * close the whole panel."*
          *   ** TWO DOORS INTO ONE ROOM IS NOT A DUPLICATE CONTROL. IT IS TWO PEOPLE'S
          *      IDEA OF WHERE THE DOOR IS. **
          *
          * ⛔ A PHONE-ONLY ARRIVAL STILL GETS THEM. `!unmatched` gates only the
          * standard messages, which need a load; a scheduler can always ring a driver.
          */}
        {c.driverPhoneEntered && (
        <CardIcon
            label="Call or text the driver"
            onClick={() => setSmsFor(smsFor === c.checkInId ? null : c.checkInId)}
            disabled={busy !== null}
        >
            <Phone className="h-3.5 w-3.5" />
        </CardIcon>
        )}
        {(onTextDoor || onTextPaperwork) && !unmatched && (
        <CardIcon
            label="Text the driver"
            onClick={() => setSmsFor(smsFor === c.checkInId ? null : c.checkInId)}
            disabled={busy !== null}
            /* ⛔ ~~`warn={!c.doorCode}`~~ — REMOVED 2026-09-28 (F95). Pete: *"why is the
               text icon orange on two and grey on another. Should all be gray."* It turned
               amber whenever the truck had no door yet, as a nudge to text one — but the
               card already says "No door yet" in words, and one icon changing colour for a
               reason nobody could see read as a fault. The licence and trailer icons keep
               their amber: those are problems WITH that icon's own subject. */
        >
            <MessageSquare className="h-3.5 w-3.5" />
        </CardIcon>
        )}
        {inDialog && (
        <CardIcon
        label="Void this arrival"
        onClick={() => { setVoidFor(c); setVoidReason(''); }}
        disabled={busy !== null}
        destructive
        >
        <Trash2 className="h-3.5 w-3.5" />
        </CardIcon>
        )}
        {/**
          * ---- ⭐⭐ EVERY OPTION, LAST IN THE STRIP (#341) --------
          *
          * > Pete, 2026-09-09: *"Why can't you give me a dropdown
          * > too that has the same things as when you right click
          * > an appointment??????? So i have all options????"*
          *
          * ⛔ THE CARD IS ALSO RIGHT-CLICKABLE, and this button
          * exists because that is not discoverable. Nothing on a
          * card advertises a right-click, so a scheduler who never
          * tries it never finds half the options — which is the
          * complaint, restated.
          *
          * ⚠️ LAST, AFTER THE BIN, ON PURPOSE. The icons before it
          * are single actions; this one opens everything, including
          * those. A menu in the middle of a tool strip reads as one
          * more tool.
          *
          * ⭐ IT USES THE BUTTON'S OWN RECTANGLE, not the mouse
          * position — a keyboard or touch activation has no cursor,
          * and a menu at (0,0) is how that fails.
          */}
        <CardIcon
        label="All options"
        onClick={(e) => {
            /**
             * ⛔⛔ INSIDE THE POPUP IT EXPANDS A LIST, IT DOES NOT FLOAT A MENU.
             *
             * `ContextMenu` is `position: fixed`, and a modal dialog sets
             * `pointer-events: none` on everything outside its own tree — so a
             * menu floated from here would DRAW over the dialog and refuse every
             * click. ⚠️ That failure looks like a dead button, not like a
             * layering bug, which is the kind that survives a demo.
             *
             * ⭐ SAME ITEMS EITHER WAY. Both routes render `cardMenuItems(c)`;
             * only the container differs.
             */
            if (inDialog) { setDialogOptions(o => !o); return; }
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setCardMenu({ c, x: r.left, y: r.bottom + 2 });
        }}
        expanded={inDialog ? dialogOptions : undefined}
        >
        <MoreHorizontal className="h-3.5 w-3.5" />
        </CardIcon>
        </div>
    );
    };

    return (
        /**
         * ⛔⛔ THE HEIGHT IS PINNED TO THE VIEWPORT, AND HERE IS WHY IT HAS TO BE.
         *
         * ⚠️ `html`, `body` AND `#root` CARRY NO HEIGHT IN index.css. So `h-full` on
         * App's shell is `height: 100%` of an AUTO-height parent, which CSS resolves
         * to `auto` — and every `flex-1 min-h-0` beneath it, all the way down to this
         * rail, silently degrades to "grow to fit the content". Nothing is ever taller
         * than its container, so nothing ever scrolls; this <aside>'s `overflow-hidden`
         * then quietly CLIPS whatever ran past the bottom of the window.
         *
         * ⭐ THAT IS THE WHOLE OF PETE'S *"still no scroll"* — and it is why adding
         * `min-h-0` and inner scroll boxes changed nothing. They were correct and they
         * were operating inside a box with no ceiling to press against.
         *
         * ⛔ THE REAL FIX IS THREE LINES IN index.css (`html, body, #root { height:
         * 100% }`) AND IT IS DELIBERATELY NOT DONE HERE. That reaches every page in the
         * application at once, on production, with no way to check the admin screens
         * from a sandbox. It is logged as its own item with the evidence.
         *
         * ⚠️ SO THIS RAIL GIVES ITSELF A CEILING THE ANCESTOR CHAIN CANNOT TAKE AWAY.
         * `100vh` minus the app header, the toolbar and the page padding — deliberately
         * a generous subtraction, because a rail that ends slightly early merely looks
         * short, while one that ends slightly late goes back to clipping.
         */
        /**
         * ---- ⭐⭐ ONE COLUMN OR TWO, AND ONE-COLUMN IS BYTE-FOR-BYTE TODAY ------
         *
         * ⛔⛔ `contents` IS THE WHOLE TRICK, AND IT IS NOT A CLEVERNESS. In
         * one-column mode both boxes are `display: contents`, which removes them
         * from layout entirely — their children become direct children of this
         * <aside>, so the rendered box tree is EXACTLY what shipped before this
         * change. ⚠️ That matters more than elegance: one-column mode is what Pete
         * is using today, its height-sharing took three rounds and a Chromium
         * measurement to get right (§1O.6), and a refactor that "should be
         * equivalent" is how that gets quietly undone.
         *
         * ⭐ IN TWO-COLUMN MODE each box becomes its own 340px card and the
         * <aside> is just the row that holds them. Heights stop competing, so
         * `ARRIVAL_LIST_MAX` and the whole slot dance switch off — see their notes.
         *
         * ⛔ NOT TWO SEPARATE RENDER BRANCHES. One tree, one set of props, no
         * conditional mounting — so toggling never unmounts a panel and never
         * resets an open card, a filter or the paperwork tray's view.
         */
        <aside className={twoColumn
            ? 'flex shrink-0 gap-4 max-h-[calc(100vh-10rem)]'
            : `w-[340px] shrink-0 flex flex-col max-h-[calc(100vh-10rem)] border ${FRAME_BORDER} rounded-lg bg-card overflow-hidden`}>
        {/* ---- COLUMN 1: the arrivals ---- */}
        <div className={twoColumn
            ? `w-[340px] shrink-0 flex min-h-0 flex-col border ${FRAME_BORDER} rounded-lg bg-card overflow-hidden`
            : 'contents'}>
            {/* ⚠️ bg-muted/50 IS THE SAME TOKEN THE GRID'S DATE STRIP USES. Not a
                lookalike grey — the two headers sit side by side at the same height,
                so anything approximate reads as a mistake rather than a pairing. */}
            <div className={`px-3 py-2 ${SECTION_HEADER}`}>
                <div className="flex items-baseline justify-between gap-2">
                    {/* "Arrivals" leaned inbound; an outbound driver collecting a
                        load checks in at the same gate through the same process. */}
                    <span className="flex items-baseline gap-1">
                        {/**
                          * ---- ⭐⭐ Ⓡ LIVES IN THE UPPER-LEFT CORNER, AND NEVER MOVES ----------
                          *
                          * ⭐ Pete, 2026-09-20: *"lets move the circle R to the upper left corner of
                          * the panel so it never moves."*
                          *
                          * ⛔⛔ IT USED TO SIT AFTER THE HEADING, so its x-position was a function of
                          * the heading's LENGTH — `Clients` put it in one place, `Driver · Peter L.`
                          * in another, and the one control a scheduler reaches for under pressure
                          * moved every time the thing it undoes changed.
                          *   ** A CONTROL WHOSE POSITION DEPENDS ON THE TEXT BESIDE IT IS A CONTROL
                          *      YOU HAVE TO LOOK FOR. **
                          *
                          * ⛔⛔⛔ AND ITS CONDITION WIDENED FROM `railFocus` TO **ANY SCOPE AT ALL**.
                          * ⭐ Pete: *"lost the circle R button and no appointments on the date - so
                          * stuck."* ⚠️ **THE TRAP PREDICTED WHEN THE TOOLBAR Ⓡ WAS REMOVED, ARRIVING
                          * FROM AN ANGLE NOBODY PREDICTED** — not *"you switched to Week"* but *"the
                          * day went empty"*. A party scope with no loads that day leaves `railFocus`
                          * null while `partyScope` still filters the board, so the only exit
                          * un-rendered itself precisely when it was the only way out.
                          *   ** A RESET MUST BE VISIBLE WHENEVER ANYTHING NEEDS RESETTING — NOT
                          *      WHENEVER THE STATE YOU HAPPENED TO NAME IS SET. **
                          *
                          * ⭐⭐ THE ORIGINAL REASONING FOLLOWS AND IS UNCHANGED — only the position and
                          * the condition moved. ⚠️ Its *"BESIDE THE ⓘ"* and *"RENDERED ONLY WHEN
                          * SOMETHING IS SCOPED"* are both now read through the two rules above.
                          *
                          * ---- ⭐⭐ THE `R` RESET, BESIDE THE ⓘ (Pete, 2026-09-19) -----------
                          *
                          * ⭐ ***"put a little circle R with a tool tip next to the circle i …
                          * Clicking this will reset both the driver panel and the right side
                          * panel … Lets make it pulse and stand out when a specific client,
                          * carrier or consignee is selected so the user notices it instantly."***
                          *
                          * ⛔⛔ THIS REVERSES A DECISION OF 2026-09-10, AND THE REVERSAL IS
                          * PETE'S. That day: *"All icons move there except the circle i. that
                          * should stay next to Driver Check-ins"* — the rule being that the ⓘ is
                          * punctuation on a heading while anything that CHANGES something lives
                          * on the controls row. ⛔ An `R` that resets the scope is unambiguously
                          * a control, so this row is no longer controls-free.
                          * ⚠️ `checkin-panel-guard` asserted exactly that ("the title row carries
                          * the heading and the (i), and nothing else") and has been amended
                          * rather than deleted — the rule it protected was *the title must not
                          * become a toolbar*, and one conditional glyph is not a toolbar.
                          *
                          * ⭐ WHY HERE AND NOT WITH THE OTHER ICONS: it undoes what the CHIPS
                          * and the TITLE describe, and both are in this row. ** AN UNDO BELONGS
                          * BESIDE THE THING IT UNDOES, NOT WITH THE CONTROLS THAT DO OTHER JOBS. **
                          *
                          * ⛔ RENDERED ONLY WHEN SOMETHING IS SCOPED — a reset that would do
                          * nothing must not be on screen. The pulse is the other half of Pete's
                          * ask: a filter nobody notices is a filter that gets blamed on the data.
                          * ⚠️ `motion-safe:` — a pulsing control is exactly what
                          * `prefers-reduced-motion` exists for; the amber ring carries the signal
                          * without it.
                          */}
                        {/**
                          * ---- ⛔⤴ ~~Ⓡ LIVED HERE~~ — MOVED TO THE BOARD HEADER 2026-09-27
                          *
                          * > Pete: *"think we should put the circle R to the right of XX
                          * > Appointments instead of Driver Check-INs"* → **"One Ⓡ in the
                          * > board header, retire both."**
                          *
                          * ⭐ THERE WERE TWO, AND THE OTHER ONE CLEARED LESS. `Calendar.tsx`
                          * had a toolbar Ⓡ for Week/Month calling `clearParty` alone, which
                          * left `railFocus` latched; this one called `resetAll` and cleared
                          * all three plus the cards. They were never adjacent, so nothing
                          * revealed the difference. Both are gone; the board header draws
                          * one Ⓡ in all three views, with this one's behaviour.
                          *
                          * ⛔ THE CARDS STILL COLLAPSE — via `collapseSignal`, which this
                          * panel watches. The page cannot call `collapseAllCards`, so it
                          * bumps a counter, the same seam `refreshKey` already uses.
                          *
                          * ⚠️ `resetAll` IS NOT ORPHANED and must not be deleted: the SECOND
                          * CLICK ON A PARTY CARD still calls it (decision 194, *"if you
                          * single click a panel a second time it resets everything just like
                          * hitting the circle Ⓡ"*). Only the button is gone.
                          *
                          * ⛔⛤ ~~`anyScope` IS STILL READ below for the scope sentence on
                          * the title~~ — **WRONG, AND CORRECTED 2026-09-27.** It was read
                          * by NOTHING but this button, and `focusScopeLabel` was imported
                          * for nothing but this tooltip. Both are gone; see their sites.
                          *
                          * ⚠️ HOW THE FALSE CLAIM WAS MADE, because it is the interesting
                          * part: I checked with a regex that strips `/* … *\/` and counted
                          * what was left. At that moment this very comment was MALFORMED —
                          * the verbatim record below carried the button's own nested
                          * comment delimiters — so the stripper ended the comment early and
                          * counted the record's own text as live code. It reported four
                          * uses. `tsc` reported one, which was the declaration.
                          *   ** A CHECK THAT PARSES THE FILE LOOSELY AGREES WITH WHATEVER
                          *      THE FILE IS BROKEN INTO — AND A BROKEN FILE IS EXACTLY WHEN
                          *      YOU ARE ASKING IT. **
                          *
                          * ⛔ §0.3 — IT READ, VERBATIM, AND IN FULL:
                          *
                          *     ~~                        {anyScope && (~~
                          *     ~~                            <button~~
                          *     ~~                                type="button"~~
                          *     ~~                                /\* ⛔⚤ ~~A single click collapses the RAILS ... Ⓡ does both.~~~~
                          *     ~~                                   — **STRUCK 2026-09-20, decision 194.** Pete reversed 190 C~~
                          *     ~~                                   the same day: *"when we hit the circle R, it should collaspe~~
                          *     ~~                                   the driver, client, carrier, consignee cards like it does now,~~
                          *     ~~                                   but not collaspe the right side panels because those populate~~
                          *     ~~                                   with ALL and still good to see."*~~
                          *     ~~                                   ⭐ SO Ⓡ TOUCHES THE ARRIVAL CARDS AND NOTHING ELSE COLLAPSES~~
                          *     ~~                                   ANYTHING. The rails stay open and re-populate in place.~~
                          *     ~~                                   ⚠️ Still the only gesture that touches the cards — clicking a~~
                          *     ~~                                   driver card to scope to him must not shut it under the cursor. *\/~~
                          *     ~~                                onClick={resetAll}~~
                          *     ~~                                title={`Showing only ${anyScope.label} ${focusScopeLabel(anyScope)} — click to reset this panel and the panels on the right to everything`}~~
                          *     ~~                                aria-label={`Reset the ${anyScope.label} scope`}~~
                          *     ~~                                /\**~~
                          *     ~~                                  * ---- ⭐ BIGGER, AND THE SAME SIZE AS ITS TWIN (2026-09-22)~~
                          *     ~~                                  *~~
                          *     ~~                                  * > Pete: *"the pulsating circle R needs to be bigger so it~~
                          *     ~~                                  * > stands out"*~~
                          *     ~~                                  *~~
                          *     ~~                                  * ⛔⛤ ~~h-4 w-4 … text-[9px]~~ — 16px with a 9px glyph.~~
                          *     ~~                                  * ⭐ NOW h-6 w-6 / text-xs, which is NOT a guess: it is~~
                          *     ~~                                  * byte-for-byte the size `Calendar.tsx` already gives the~~
                          *     ~~                                  * OTHER Ⓡ button. The two are the same control, rendered in~~
                          *     ~~                                  * two places and deliberately never on screen together (day~~
                          *     ~~                                  * view vs the rest) — so a scheduler switching views was~~
                          *     ~~                                  * watching one control change size.~~
                          *     ~~                                  *   ** TWO SIZES FOR ONE CONTROL IS NOT A STYLE CHOICE, IT~~
                          *     ~~                                  *      IS A DRIFT NOBODY CAN SEE BECAUSE THE TWO ARE NEVER~~
                          *     ~~                                  *      ADJACENT. **~~
                          *     ~~                                  * ⚠️ 24px IS TALLER THAN THIS HEADER'S OTHER GLYPHS (h-3.5).~~
                          *     ~~                                  * The row is `items-center` so it centres rather than~~
                          *     ~~                                  * clipping, but it WILL set the row height. Said out loud~~
                          *     ~~                                  * because it is the one visible cost, and it is the cost~~
                          *     ~~                                  * Pete asked to pay.~~
                          *     ~~                                  *\/~~
                          *     ~~                                className="mr-1 inline-flex h-6 w-6 shrink-0 items-center justify-center~~
                          *     ~~                                           rounded-full border border-amber-500 bg-amber-100 text-xs~~
                          *     ~~                                           font-bold leading-none text-amber-800 ring-1 ring-amber-400/60~~
                          *     ~~                                           transition-colors hover:bg-amber-200 motion-safe:animate-pulse"~~
                          *     ~~                            >~~
                          *     ~~                                R~~
                          *     ~~                            </button>~~
                          *     ~~                        )}~~
                          */}

                        {/* ⛔⛔ THE TITLE FOLLOWS THE SUBJECT, and "Check-Ins" is a DRIVER
                            word — a client does not check in. Pete cut my "Clients Today"
                            to "Clients": the panel only ever shows one day and the date is
                            in the toolbar directly above.
                            ** A WORD THAT IS TRUE OF EVERY STATE OF A SCREEN CARRIES NO
                               INFORMATION IN ANY OF THEM. **
                            ⚠️ The chip says `Drivers` while this says `Driver Check-Ins`,
                            on purpose — see lib/panelSubject.ts. */}
                        {/* ⛔⛔ `panelHeadline(subject, partyScope)`, NOT `panelTitle(subject,
                            railFocus)`. The panel word comes FIRST and is always present, so the
                            `Driver` chip puts `Driver Check-Ins` back at the front of this line
                            even while HELLMAN is still scoped — Pete, 2026-09-19:
                            *"The Title doesn't go back to Driver Check-In when you select the
                            driver option in the scope"*. The party is APPENDED, never
                            substituted. What is being POINTED at moved to the line below. */}
                        {/* ⭐ TWO SPANS, ONE COMPUTATION. Pete: *"Make P.Lombardo not bold
                            and uncapitalized font."* ⛔ `panelHeadline` still produces the single
                            string for the tooltip and the guards, so the two renderings cannot
                            drift apart.
                              ** WHEN TWO PARTS OF ONE SENTENCE ARE STYLED DIFFERENTLY, RETURN THE
                                 PARTS — NOT TWO FUNCTIONS THAT MUST AGREE. ** */}
                        <h2 className="truncate text-sm" title={panelHeadline(subject, ownScope, crossScope)}>
                            <span className="font-medium">{panelHeadlineParts(subject, ownScope, crossScope).word}</span>
                            {panelHeadlineParts(subject, ownScope, crossScope).who && (
                                <span className="font-normal"> · {panelHeadlineParts(subject, ownScope, crossScope).who}</span>
                            )}
                        </h2>
                        {/* ⭐ F110 — the detention tally, in the (i)'s old slot. */}
                        <DetentionSummary trucks={detentionTally} settings={detention} />
                    </span>
                    {/* ⚠️ TRUCKS, NOT APPOINTMENTS. An unmatched check-in is a truck
                        on site with no appointment at all, so these deliberately do
                        not agree with the appointment counts in the date strip —
                        "4 trucks, 2 on the schedule" is a real state, not a bug. */}
                    {/* ⭐ IN AND OUT ARE ALWAYS SHOWN, INCLUDING AT ZERO (Pete,
                        2026-08-19). They used to disappear below one, which meant
                        "Out" being absent could mean either "none outbound" or
                        "the panel forgot" — and a supervisor cannot tell those
                        apart at a glance. A zero is information; a gap is not. */}
                    <span className="flex items-baseline gap-2 text-xs">
                        <span className="whitespace-nowrap">
                            <span className="text-muted-foreground">In </span>
                            <span className="font-medium tabular-nums">{inCount}</span>
                        </span>
                        <span className="whitespace-nowrap">
                            <span className="text-muted-foreground">Out </span>
                            <span className="font-medium tabular-nums">{outCount}</span>
                        </span>
                        {/* The total is the number anyone glancing at the rail
                            actually wants, so it carries the weight and In/Out
                            step back to medium to let it. */}
                        <span className="whitespace-nowrap">
                            {/* ⛔ THE WORD FOLLOWS THE SUBJECT. "Trucks 12" over a list of
                                four clients counts something that is not on screen. */}
                            <span className="font-bold">{partyKind ? 'Appointments ' : 'Trucks '}</span>
                            <span className="text-sm font-bold tabular-nums">
                                {partyKind ? countedAppts.length : visible.length}
                            </span>
                        </span>
                        {/* ⚠️ THE COUNTS DESCRIBE WHAT IS ON SCREEN, so a live filter
                            has to say so. Without this, "Trucks 3" with a client
                            filter on reads as three trucks in the yard. */}
                        {filtersActive && (
                            <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-800">
                                filtered
                            </span>
                        )}
                    </span>
                </div>
                {/**
                  * ---- ⭐⭐ LINE 2: WHAT IS BEING POINTED AT (Pete, 2026-09-19) ----------
                  *
                  * ⭐ *"this should go under that first line so you still see the client,
                  * carrier, consignee name so you can distinguish"* — then, on the size:
                  * *"should be smaller text size and not bolded too"*.
                  *
                  * ⛔ RENDERED ONLY WHEN THERE IS A SECOND THING TO SAY. With a party
                  * scoped and nothing hovered, `narrowerFocus` is null and this line is
                  * absent — because the page collapses `railFocus ?? partyScope`, drawing
                  * it unconditionally would print `Client · HELLMAN` directly beneath
                  * `Driver Check-Ins · HELLMAN`. ** THE SAME SENTENCE TWICE IS NOT MORE
                  * INFORMATION; IT IS A BUG THAT LOOKS LIKE A LAYOUT. **
                  *
                  * ⛔⛔ `text-[11px] font-normal`, AND THE 11 IS NOT A GUESS. It is the
                  * status-bar size Pete settled on this evening after four wrong rounds
                  * (`Limits` measured 11px w400 in his own console dump). ⚠️ AND THIS IS A
                  * <div>, so it is NOT hit by `index.css`'s base-layer
                  * `input, select, button, textarea { font-size: 14px }` — the rule that
                  * silently beat four attempts to resize the chips.
                  *   ** READING THE JSX TELLS YOU WHAT THE CLASS SAYS. IT CANNOT TELL YOU
                  *      WHAT THE CASCADE DID. **
                  *
                  * ⚠️ `truncate` — a load label is `A00000346 · DD122 · 1:00 PM / Appt
                  * A00000346` and the rail is 340px. Wrapping would push the chips row
                  * down and move every control under it on a mere hover.
                  */}
                {narrowerFocus && (
                    <div
                        className="truncate text-[11px] font-normal text-muted-foreground"
                        title={panelTitle(subject, narrowerFocus)}
                    >
                        {panelTitle(subject, narrowerFocus)}
                    </div>
                )}
                {/**
                  * ---- ⭐⭐ THE SUBJECT CHIPS SHARE THE CONTROLS ROW (Pete, 2026-09-19) --
                  *
                  * > *"Or maybe just like the status bar but have Drivers, Clients
                  * > Carriers as the check boxes underneath the Driver Check-in"* … then,
                  * > looking at it: *"That will allow the icons to go on the same line as
                  * > well, saving valuable real estate."*
                  *
                  * ⛔⤴ ~~"NOT THE CONTROLS ROW BELOW. Its order is load-bearing … three
                  * word-chips would wrap at laptop width and push it off that axis."~~
                  * ~~** A ROW WHOSE ORDER IS LOAD-BEARING IS FULL, WHATEVER SPACE IS LEFT
                  * IN IT. **~~ STRUCK 2026-09-19 (§0.3). It shipped as its own row for one
                  * evening and cost ~24px for four words.
                  *
                  * ⚠️ THE OLD RULE WAS DEFENDING THE RIGHT CONSTRAINT AND MEASURING THE
                  * WRONG THING. Pete's 2026-09-10 instruction — *"the triangle and warning
                  * should align with each driver so should be far right"* — constrains the
                  * ⚠ to the HARD RIGHT EDGE. It does not constrain what else is on the row;
                  * it constrains what is to the **right of the ⚠**, which is nothing.
                  *
                  *   ** "THIS ROW IS FULL" WAS A CLAIM ABOUT WIDTH. THE RULE IT CITED WAS
                  *      ABOUT ORDER. A CONTROL PINNED TO AN EDGE IS NOT CROWDED BY ANYTHING
                  *      THAT ENTERS FROM THE OTHER EDGE. **
                  *
                  * ✅ SO: `justify-between`, chips left, and the three controls WRAPPED IN
                  * THEIR OWN BOX so they stay one right-hand group in the order Pete set.
                  * ⛔ THE WRAPPER IS NOT OPTIONAL. Without it, `justify-between` distributes
                  * FOUR children across 340px and the ⚠ keeps its edge only by accident of
                  * the columns/search icons being last — and the moment `warningSummary` is
                  * zero the whole thing re-spaces.
                  *
                  * ⚠️ AND NOT THE TITLE ROW EITHER: that row already lost once, on
                  * 2026-09-10, when five things competed for 340px and the title wrapped.
                  */}
                <div className="mt-1 flex items-center justify-between gap-2">
                <SubjectChips
                    subject={subject}
                    onSubject={onSubject}
                />
                {/**
                  * ---- ⭐⭐ THE CONTROLS GET THEIR OWN ROW (Pete, 2026-09-10) ---------
                  *
                  * > *"This panel is alittle messy now. I think all the icons should move
                  * > to the next line … To the far right, like all the other icons. All
                  * > icons move there except the circle i. that should stay next to
                  * > Driver Check-ins."*
                  *
                  * ⛔ THE TITLE ROW HAD FIVE THINGS COMPETING FOR 340px and lost: "Driver
                  * Check-Ins" wrapped to two lines with the icons crowding the break.
                  * ⚠️ Every control added here since 2026-09-08 was added to that ONE row,
                  * and the row never got a say — this is the bill for that.
                  *
                  * ⭐ THE (i) STAYS WITH THE TITLE because it explains the TITLE — it is
                  * punctuation on a heading, not a control. Everything that CHANGES
                  * something moves here, right-aligned, which is where every other rail
                  * card keeps its controls.
                  *
                  * ⚠️ `items-center`, NOT `items-baseline`. There is no text on this row
                  * for a baseline to align to, and the icons carry their own `self-center`
                  * from when they sat beside a heading — harmless, but this is what
                  * actually centres them now.
                  */}
                {/* ⛔ `shrink-0` — the chips wrap, the controls never do. Given a
                     narrow panel flexbox would otherwise shrink whichever child it
                     can, and these three are icons with nothing to give. */}
                <div className="flex shrink-0 items-center gap-1">
                        {/**
                          * ---- ⭐⭐ ONE COLUMN OR TWO, SET ON THE SCREEN (2026-09-10) ----
                          *
                          * > Pete: *"If we can make the two column or one column an option
                          * > they can set on the screen, that would be great."*
                          *
                          * ⭐ BESIDE THE (i) AND THE SEARCH ICON, because this row is
                          * already where this panel keeps the controls that change how it
                          * is SHOWN rather than what it shows.
                          *
                          * ⛔ THE TITLE CARRIES THE COST, NOT JUST THE STATE. On a narrow
                          * window two columns push the day grid into horizontal scrolling
                          * (356px, measured — see `RAIL_COLUMNS_KEY`), and a control that
                          * does something expensive should say so BEFORE it is pressed.
                          * ⚠️ It is still allowed: a person can decide the trade in the
                          * moment, and refusing would be this file overruling them.
                          */}
                        <button
                            type="button"
                            onClick={() => {
                                const next: RailColumns = twoColumn ? 'one' : 'two';
                                setRailColumns(next);
                                writeRailColumns(next);
                            }}
                            /* ⛔ ~~`aria-pressed`~~ REMOVED WITH THE ICON'S MEANING. Pressed
                               state describes a TOGGLE showing what it IS; this icon now
                               shows what it WILL DO, and the two readings contradict each
                               other for anyone using a screen reader. The `sr-only` text
                               says the action, like the tooltip. */
                            title={twoColumn
                                ? 'Two columns — arrivals beside the other panels. Click for one column and a wider calendar.'
                                : (narrowForTwo
                                    ? 'One column. Click for two — ⚠ this screen is narrow, so the calendar will scroll sideways.'
                                    : 'One column. Click for two — arrivals beside the other panels.')}
                            className="shrink-0 self-center rounded p-0.5 text-muted-foreground
                                       transition-colors hover:bg-muted hover:text-foreground"
                        >
                            {/**
                              * ⛔ THE ICON IS THE DESTINATION, NOT THE STATE (Pete,
                              * 2026-09-10): *"the new icon is backwards. You have it show
                              * two pages when it should be one page and one page when it
                              * should be two - showing you what it will do after you click
                              * it."*
                              *
                              * ⚠️ THE FIRST CUT SHOWED THE CURRENT LAYOUT, which is the
                              * other legitimate convention — and it is the wrong one HERE
                              * because the layout is already visible on screen. An icon
                              * restating what somebody can see, in place of telling them
                              * what the button does, spends the only glance they give it.
                              */}
                            {twoColumn
                                ? <Square className="h-3.5 w-3.5" />
                                : <Columns2 className="h-3.5 w-3.5" />}
                            <span className="sr-only">
                                {twoColumn ? 'Switch to one column' : 'Switch to two columns'}
                            </span>
                        </button>
                        {/**
                          * ⛔⛔ ~~THE 1-2-3-4 BUTTON~~ REVERTED 2026-09-10, THE SAME DAY.
                          *
                          * > Pete: *"now they don't work. none of them expand. Put it
                          * > back."*
                          *
                          * ⚠️ THE BUTTON WAS NOT THE FAULT — it set a cap the accordion
                          * enforced, and the accordion closed every panel on the way OPEN
                          * (the render/commit race written up in `lib/railSlot.ts`). ⛔ But
                          * a control whose only effect is broken is worse than no control,
                          * so it went WITH the mechanism rather than being left inert.
                          *
                          * ⭐ IT COMES BACK WITH THE ACCORDION, once the report is sent
                          * synchronously from the click instead of from an effect.
                          */}
                        {/**
                          * ⭐⭐ THE SEARCH ICON, NEXT TO THE (i) — Pete, 2026-09-08:
                          * *"maybe just have the search icon next to the (i) and when
                          * you click the search icon, [the filter strip] drop down."*
                          *
                          * ⭐ A TOGGLE, NOT A CHEVRON, AND HE CHOSE THAT. The other rail
                          * cards fold their whole body behind a chevron; this one folds
                          * only its CONTROLS and keeps the driver cards — which is the
                          * one thing on this panel nobody wants hidden.
                          *
                          * ⛔⛔ THE DOT IS NOT DECORATION. It is the entire reason it is
                          * safe to hide these controls at all: see `anyFilterOffDefault`.
                          * A rail that is short because of a filter and a rail that is
                          * short because the yard is empty must never look the same.
                          *
                          * ⚠️ AND IT USES THE WIDER PREDICATE, NOT `filtersActive` — the
                          * amber "filtered" chip to the right does not fire for a lone
                          * "Departed" tick, which is the likeliest way to empty this
                          * panel by accident.
                          */}
                        <button
                            type="button"
                            onClick={() => setShowFilters((v) => !v)}
                            aria-expanded={showFilters}
                            aria-label={showFilters ? 'Hide search and filters' : 'Show search and filters'}
                            title={anyFilterOffDefault && !showFilters
                                ? 'Filters are on — open to see or clear them'
                                : showFilters ? 'Hide search and filters' : 'Search and filter'}
                            className="relative self-center rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                            <Search className="h-3.5 w-3.5" />
                            {anyFilterOffDefault && (
                                <span
                                    aria-hidden="true"
                                    className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-amber-500"
                                />
                            )}
                        </button>
                        {/**
                          * ---- ⭐⭐ THE WARNING IS LAST, SO IT LINES UP WITH THE CARDS ----
                          *
                          * > Pete, 2026-09-19: *"the triange and warning should align with
                          * > each driver so should be far right. The two column and search
                          * > icon should be to the left of the warning so its consistent"*
                          *
                          * ⭐ Every arrival card carries its own amber triangle at ITS far
                          * right. With this one last, the panel total sits in the SAME
                          * COLUMN as all of them, and warnings read as one vertical line
                          * down the rail instead of a count that floats off on its own.
                          *
                          * ⛔ SO THE ORDER IS LOAD-BEARING, NOT COSMETIC. Putting anything
                          * after this breaks the alignment it exists for — a new control
                          * goes BEFORE the warning, beside the columns and search icons.
                          *
                          * ⭐ It still reads the same way as the search icon beside it: an
                          * icon that reveals a strip, with a marker when there is something
                          * behind it. ⚠️ ABSENT ENTIRELY at zero — a permanently visible
                          * "0 warnings" is the wallpaper #269 warns about, and its absence
                          * is also why the alignment only matters on the days it appears.
                          */}
                        {/* ⭐ THE ARRIVALS' TOTAL — unchanged in behaviour, now drawn by
                            the shared badge so it cannot drift from the cards below it. */}
                        {!partyKind && (
                            <CountBadge
                                icon={<AlertTriangle className="h-3.5 w-3.5" />}
                                count={warningSummary.total}
                                expanded={showWarnings}
                                ariaLabel={`${warningSummary.total} warning${warningSummary.total === 1 ? '' : 's'} across these arrivals`}
                                title={showWarnings ? 'Hide the warnings' : warningSummary.lines.map(l => l.label).join(' · ')}
                                onClick={() => setShowWarnings((v) => !v)}
                            />
                        )}
                        {/**
                          * ⭐⭐ AND THE SAME TWO TOTALS ON A PARTY TAB, in the same corner,
                          * in the same shape as the cards beneath them.
                          *
                          * ⛔ NOT CLICKABLE. The arrivals badge toggles a warnings strip that
                          * summarises `arrivalWarnings` by code; there is no equivalent strip
                          * for parties, and a badge that looked identical but did nothing
                          * when pressed would teach the reader to stop pressing the one that
                          * works.
                          * 🔨 When the party strip exists, these gain the same onClick.
                          */}
                        {partyKind && (
                            <CountBadge
                                icon={<AlertTriangle className="h-3.5 w-3.5" />}
                                count={partySummary.total}
                                title={partySummary.lines.join('\n')}
                            />
                        )}
                </div>
                </div>
                {/**
                  * ---- ⭐⭐ WARNINGS FOLD AWAY BEHIND THEIR OWN ICON (Pete, 2026-09-09) -----
                  *
                  * > *"Its a little redundant with the triangle warning and not sure the
                  * > scheduler will utilize the information and it takes up valuable space"*
                  * > … *"Maybe have the warning symbol also next to the search icon in Driver
                  * > Checkins and it can expand if the scheduler wants?"*
                  *
                  * ⭐ HIS SECOND MESSAGE IS THE BETTER ANSWER AND IT REPLACED THE FIRST. He
                  * had asked for a config flag to hide this line; a flag would have been a
                  * setting invented to work around a layout problem. ⛔ An icon that costs
                  * nothing when there is nothing to say removes the problem instead, so the
                  * setting is not needed — and a setting nobody needs is one more control to
                  * explain, and eventually wallpaper (#269).
                  *
                  * ⚠️ ~~"1 needs an appointment" ON ITS OWN LINE, ALWAYS~~ — one warning kind
                  * had a permanent row and the other two had none, which is why Pete asked
                  * for consistency. All three are counted here now.
                  *
                  * ⛔ THE COUNT STAYS VISIBLE EVEN FOLDED. Hiding the NUMBER would make an
                  * empty rail and a rail full of problems look identical — the same argument
                  * as the search icon's amber dot, three feet to the left.
                  */}
                {warningSummary.total > 0 && showWarnings && (
                    <div className="mt-1 space-y-0.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                        {warningSummary.lines.map((l) => (
                            <div key={l.code}>{l.label}</div>
                        ))}
                    </div>
                )}
                {/* ---- filters ------------------------------------------------
                    Two dropdowns and a queue toggle. Kept on one row each because
                    this is a narrow rail and every line spent on controls is a
                    truck the scheduler cannot see.

                    ⭐ AND NOW FOLDED AWAY ENTIRELY unless asked for — the same
                    argument carried to its end. ⚠️ The WARNING SUMMARY above
                    deliberately stays OUTSIDE this: it is an alert about trucks, not
                    a control, and it has its own icon and its own toggle. */}
                {showFilters && (
                <>
                {/**
                  * ---- ⛔⛔ DECISION 5 — KEEP THE MATCHING PICKER, HIDE THE OTHERS -----
                  *
                  * ⭐ Pete INVERTED his own first answer within the hour, and his is right:
                  * ***"the search buttons for client and carrier should still be present,
                  * just one or the other depending on if the client or carrier is ticked.
                  * Then you could select a single client for example and just see there
                  * appointments in the schedule"***
                  *
                  * ⛔⛔ AND ON A PARTY SUBJECT IT IS NOT A LIST FILTER — IT IS A
                  * SINGLE-SUBJECT PICKER. Choosing one narrows **the card list AND the
                  * schedule**, which is the one thing the card list cannot do for itself.
                  * ⚠️ I had ruled it out as redundant: *"the card list already IS the client
                  * list"*. True of the PANEL, false of the GRID.
                  *   ** A CONTROL THAT LOOKS REDUNDANT AGAINST ONE CONSUMER MAY BE THE ONLY
                  *      WAY TO REACH A SECOND ONE. **
                  *
                  * ⭐ DRIVERS KEEPS BOTH, EXACTLY AS TODAY — there they are cross-filters
                  * over the arrivals list, which is a different job and stays unchanged.
                  */}
                <div className="mt-1.5 flex gap-1.5">
                    {partyKind === null ? (
                        /**
                          * ---- ⭐⭐⭐ ONE BOX, `All drivers` — THE FOUR CHIPS NOW MATCH -------
                          *
                          * ⭐ Pete, 2026-09-20: *"I think we make it say all drivers for now like
                          * the other panels. Lets keep it consistent."* · *"Technically, to match
                          * clients and carriers, it should be one box with all drivers."*
                          *
                          * ⛔⚤ THIS REPLACES **BOTH** CROSS-FILTERS — ~~`All clients`~~ and
                          * ~~`All carriers`~~ — and reverses decision 5 / 22's *"Drivers keeps
                          * exactly today's two."* ⚠️ That decision was taken the day BEFORE the
                          * other three subjects had chips of their own.
                          *
                          * ⛔⛔ AND THE COST IS REAL AND WAS NAMED BEFORE IT WAS PAID. Those two
                          * boxes were the only way to ask *"which drivers are FedEx's"* — a
                          * question about PEOPLE, where the Carrier chip asks about LOADS.
                          *   ** TWO CONTROLS THAT FILTER THE SAME DATA ARE NOT REDUNDANT IF THEY
                          *      ANSWER DIFFERENT QUESTIONS. **
                          * ⭐ Pete weighed that against one rule across four chips and chose the
                          * rule: *"We can change it later if necessary."* ⬜ And the capability is
                          * partly restored from the other direction — an appointment focus now
                          * carries the driver's name, so clicking a carrier's load NAMES the man
                          * on line 2 from any chip.
                          *
                          * ⚠️ `clientFilter` / `carrierFilter` STATE SURVIVES — the search block
                          * and the Week/Month views still read them. Only the two controls went.
                          */
                        <select
                            value={driverFilter}
                            onChange={(e) => setDriverFilter(e.target.value)}
                            className="min-w-0 flex-1 rounded border bg-background px-1 py-0.5 text-[11px]"
                            aria-label="Show one driver"
                        >
                            <option value="">All drivers</option>
                            {driverOptions.map(d => <option key={d} value={d}>{d}</option>)}
                            {/* ⚠️ A CHOSEN VALUE NO LONGER IN THE DAY'S DATA STILL HAS TO RENDER.
                                His truck departing (or a poll landing) would otherwise drop the
                                option, and a select whose value is not among its options silently
                                displays the FIRST one — so the box would read "All drivers" while
                                the list stayed filtered to a man nobody can see. */}
                            {driverFilter !== '' && !driverOptions.includes(driverFilter) && (
                                <option value={driverFilter}>{driverFilter} (gone today)</option>
                            )}
                        </select>
                    ) : (
                        <select
                            value={pickerValue}
                            onChange={(e) => {
                                const code = e.target.value;
                                if (!code) { onClearParty?.(); return; }
                                const row = parties.find(pp => pp.code === code);
                                /* ⛔ NO SYNTHETIC FALLBACK. Choosing a party the day no longer
                                   contains would scope the grid to nothing; the stale option
                                   below exists so the box can DISPLAY it, not so it can be
                                   re-selected into a scope with no appointments behind it. */
                                if (row) onChooseParty?.(row.kind, row.code, row.name, row.appointments);
                            }}
                            className="min-w-0 flex-1 rounded border bg-background px-1 py-0.5 text-[11px]"
                            aria-label={`Show one ${partyKind}`}
                        >
                            <option value="">All {SUBJECT_WORDS[subject].title.toLowerCase()}</option>
                            {parties.map(pp => (
                                <option key={pp.code} value={pp.code}>{pp.name}</option>
                            ))}
                            {/* ⚠️ Same stale-value rule as the driver dropdowns above — a scope
                                latched on Monday must still NAME itself on Tuesday. */}
                            {pickerValue !== '' && !parties.some(pp => pp.code === pickerValue) && (
                                <option value={pickerValue}>{partyScope?.label ?? pickerValue} (none today)</option>
                            )}
                        </select>
                    )}
                </div>

                {/* ⚠️ THE COUNTS ARE THE POINT. "On site" means "its appointment has
                    not DEPARTED", so on a day when nothing has departed yet a lone
                    checkbox changes nothing and reads as broken — which is how this
                    control earned its complaint. Each box now carries its own number,
                    and an empty selection says what it is doing. */}
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <label className="flex items-center gap-1.5">
                        <input
                            type="checkbox" checked={showOnSite}
                            onChange={(e) => setShowOnSite(e.target.checked)}
                        />
                        On site
                        {departedHidden > 0 && (
                            <span className="text-muted-foreground">· hiding {departedHidden}</span>
                        )}
                    </label>
                    <label className="flex items-center gap-1.5">
                        <input
                            type="checkbox" checked={showDeparted}
                            onChange={(e) => setShowDeparted(e.target.checked)}
                        />
                        Departed
                        <span className="text-muted-foreground">· {departedCount}</span>
                    </label>
                    {/**
                      * ⭐ "Needs license" JOINED THIS ROW ON 2026-08-27. Pete: *"Lets get
                      * the Needs License Checkbox on the same line as Onsite and Departed
                      * too."* It had a row to itself and spent a line of a rail that is
                      * already short — and the rail's height is what pushes the paperwork,
                      * labour and communications sliders off the screen.
                      *
                      * ⚠️ IT IS A DIFFERENT KIND OF FILTER FROM THE OTHER TWO and sits last
                      * for that reason. On site / Departed choose WHICH CARDS EXIST here;
                      * this one narrows whatever they left. ⛔ They COMPOUND — ticking this
                      * with "On site" alone means "on site AND no licence", which is the
                      * useful combination and also the one most easily misread, so the
                      * count next to it reports what is actually visible.
                      *
                      * ⚠️ THE ROW WRAPS (`flex-wrap`), so on a narrow rail this drops to a
                      * second line by itself rather than squashing the other two.
                      */}
                    {/* ⛔ DECISION 6 — SUPPRESSED FOR CLIENT / CARRIER / CONSIGNEE. A
                        licence is a DRIVER document; a checkbox that can never match
                        anything on screen is worse than an absent one.
                        ** NEVER SHOW A CONTROL THAT CANNOT DO ANYTHING. ** (the other
                        reading of §1AV.170's never-hide-a-live-control rule)
                        ⚠️ `needsLicenceOnly` is CLEARED by the subject effect above, so it
                        cannot still be filtering while invisible. */}
                    <label className={`items-center gap-1.5 ${partyKind ? 'hidden' : 'flex'}`}>
                        <input
                            type="checkbox"
                            checked={needsLicenceOnly}
                            onChange={(e) => setNeedsLicenceOnly(e.target.checked)}
                        />
                        {/* ⚠️ "LICENCE", NOT "LICENCE AND PAPERWORK". Paperwork capture
                            is not built yet (#92 blob plumbing), so a checkbox
                            promising to find drivers whose PAPERWORK is missing would
                            quietly return the licence queue and be believed. It joins
                            this filter the day #92 lands, and the label changes then. */}
                        Needs license
                        {needsLicenceOnly && (
                            <span className="text-muted-foreground">· {visible.length} waiting</span>
                        )}
                    </label>
                    {!showOnSite && !showDeparted && (
                        <span className="italic">showing everything</span>
                    )}
                </div>

                {filtersActive && (
                    <button
                        type="button"
                        onClick={() => { setClientFilter(''); setCarrierFilter(''); setNeedsLicenceOnly(false); setShowOnSite(true); setShowDeparted(false); }}
                        className="mt-1 self-start text-[11px] underline text-muted-foreground"
                    >
                        Clear filters
                    </button>
                )}
                </>
                )}
            </div>

            {error && (
                <div className="px-3 py-2 text-xs text-destructive border-b bg-destructive/5">{error}</div>
            )}

            {/**
              * ⛔⛔ `min-h-0` IS LOAD-BEARING, NOT TIDYING (2026-08-27).
              *
              * ⚠️ A flex child with `overflow-y-auto` inside a `flex flex-col` parent
              * has a default `min-height: auto`, which means IT REFUSES TO SHRINK BELOW
              * ITS CONTENT. So this box grew to fit every driver card, never scrolled,
              * and pushed everything after it — the paperwork tray, Labor,
              * Communications — past the bottom of an `overflow-hidden` <aside>, where
              * they were clipped with no scrollbar anywhere.
              *
              * ⭐ THAT IS THE WHOLE OF PETE'S *"no scroll on the Scanned Paperwork"*.
              * The list inside the panel was never the problem: the PANEL was off the
              * bottom of its own column.
              */}
            {/* ⭐ THE DRIVER LIST STOPS BEING GREEDY WHEN A CARD IS OPEN. `flex-1`
                took every spare pixel and left the white gap Pete saw above the
                panels; `shrink` sizes it to its cards and hands the rest to the
                open card. ⚠️ `min-h-[6rem]` so a long arrival list can never be
                squeezed to nothing — the panel this screen exists for must always
                show at least a card and a half. */}
            {/**
              * ---- ⭐⭐ AND IT STOPS GROWING AT ABOUT THREE CARDS (2026-09-10) -----
              *
              * > Pete: *"Think we need a scroll bar on the Driver Checkins after 3
              * > drivers so it doesn't encroch too much on the other panels"*
              *
              * ⛔ `overflow-y-auto` WAS ALREADY HERE AND WAS NOT ENOUGH — the same
              * lesson as §1O.6, one panel over. `shrink` only shrinks when the COLUMN
              * overflows, so until the whole `<aside>` was full the list took every
              * pixel its cards asked for and the tray, Orders, Labor and Comms were
              * pushed down. ⚠️ A scrollbar that appears only once everything else has
              * already been squeezed is not the scrollbar he is asking for.
              *
              * ⭐ SO THERE IS A CEILING, AND IT IS EXPRESSED IN CARDS. A collapsed
              * arrival card is ~76px including its gap; `ARRIVAL_LIST_MAX` is three of
              * them plus a sliver of the fourth, which is what makes it obvious the
              * list continues rather than looking like it ended at three.
              *
              * ⛔⛔ ~~AND IT IS RELEASED THE MOMENT A CARD IS EXPANDED~~ — REMOVED BY
              * PETE THE SAME DAY. See the struck note on `anyCardOpen` above for the
              * argument and why it did not apply to the case he was looking at. ⭐ When
              * a card and a panel are both open, THE PANEL WINS.
              *
              * ⚠️ THE CEILING ONLY APPLIES WHEN A RAIL PANEL IS OPEN. With every panel
              * folded there is nothing below to protect and the list should use the
              * column, which is what `flex-1` already does — and that is the case the
              * removed argument was really about.
              */}
            {/* ⛔ THE CEILING IS A ONE-COLUMN RULE. In two columns the arrivals have a
                column to themselves, nothing below to protect, and capping them at
                25vh would waste three quarters of it. */}
            <div className={anyPanelOpen && !twoColumn
                ? `min-h-[6rem] shrink overflow-y-auto ${ARRIVAL_LIST_MAX}`
                : 'min-h-0 flex-1 overflow-y-auto'}>
                {/**
                  * ---- ⭐⭐ THE PARTY LIST (decision 188, increment 3) -----------------
                  *
                  * ⛔ FIRST IN THE CHAIN, so a party subject never falls through to the
                  * arrivals branches — their loading state, their empty-state wording and
                  * their filters are all about trucks that have checked in.
                  *
                  * ⚠️ NO `loading` BRANCH HERE ON PURPOSE. These rows are derived from
                  * `appointments`, a prop the page already has; there is nothing to wait
                  * for, and a spinner over data that is already present is a lie about
                  * where the delay is.
                  */}
                {partyKind ? (
                    shownParties.length === 0 ? (
                        <div className="p-3 text-xs text-muted-foreground">
                            {/* ⛔ NAMES THE SUBJECT. "Nothing scheduled" would be the same
                                sentence on all three chips and answer none of them. */}
                            No {SUBJECT_WORDS[subject].chip.toLowerCase()} has appointments scheduled today.
                        </div>
                    ) : (
                        shownParties.map((pr) => {
                            const chosen = partyScope?.partyCode === pr.code
                                && partyScope?.kind === pr.kind;
                            const inb = pr.appointments.filter(a => a.direction === 'INBOUND').length;
                            const outb = pr.appointments.filter(a => a.direction === 'OUTBOUND').length;
                            const partyKey = `${pr.kind}:${pr.code}`;
                            /**
                             * ---- ⛔⚤ ~~`chosen || openParties.has(partyKey)`~~ — RETIRED 2026-09-25
                             *
                             * ⭐⭐ THE 2026-09-20 NOTE SAID THIS WAS "correct rather than a bug".
                             * Pete overruled it from the floor: *"the chevron doesn't collapse the
                             * screen now"*. ⛔ RETIRED EXPLICITLY RATHER THAN EDITED AWAY, because
                             * the old reasoning was sound and someone will re-derive it otherwise.
                             *
                             * ⚠️ WHAT THE OLD REASONING GOT RIGHT: scoping to a party IS a request
                             * to see its detail, so a single click must still open the card. That
                             * is preserved — the click handlers below now ADD to `openParties`.
                             *
                             * ⛔ WHAT IT GOT WRONG: it expressed that by OR-ing the circumstance
                             * into the state, which makes the control powerless whenever the
                             * circumstance holds. The chevron wrote `openParties` and the OR put
                             * it straight back.
                             *   ** A STATE THAT IS THE OR OF A CHOICE AND A CIRCUMSTANCE IS A
                             *      CONTROL THAT STOPS WORKING WHENEVER THE CIRCUMSTANCE HOLDS. **
                             *
                             * ⚠️ THIS IS THE THIRD TIME IN THIS FILE. `smsFor` did it (see the note
                             * above `keepCardOpen`), `detailOpen` still does it with `menuFor` and
                             * `smsFor`, and this was the second. Fixing one call site did not fix
                             * the pattern.
                             *   ** A RULE FIXED AT ONE CALL SITE IS NOT A RULE. **
                             *
                             * ⭐ THE SCOPE IS UNAFFECTED. Collapsing a chosen card leaves the board
                             * filtered — which is what Pete chose when asked directly.
                             */
                            const partyOpen = openParties.has(partyKey);
                            /**
                             * ---- ⛔⛔⚤ ~~`<button>`~~ → A ROLE-BUTTON DIV, 2026-09-20 ---------
                             *
                             * ⭐ Pete: *"There is no double-cllik on the client card. There are
                             * no icons for communication ... There is no ... in right hand
                             * corner for the menu."*
                             *
                             * ⛔⛔ **A `<button>` MAY NOT CONTAIN A `<button>`.** It is invalid
                             * HTML, and every control Pete asked for here is one. ⚠️ The comms
                             * section shipped on 2026-09-20 was ALREADY nesting buttons this
                             * way — browsers tolerate it, so nothing complained, which is
                             * exactly why it survived a deploy.
                             *   ** "IT RENDERS" IS NOT "IT IS LEGAL". THE BROWSER FORGIVES
                             *      WHAT THE SPEC FORBIDS, AND THE NEXT CONTROL IS THE ONE
                             *      THAT BREAKS. **
                             *
                             * ⭐⭐ AND IT MAKES THE TWO CARDS ONE SHAPE. The DRIVER card has
                             * always been a div with an `el.closest(...)` guard, precisely so
                             * its icon strip works. Decision 13 said a party card is the driver
                             * card with slots re-purposed; this is the last place that was
                             * still untrue.
                             *
                             * ⚠️ KEYBOARD PARITY IS NOT OPTIONAL when you give up a real
                             * `<button>`: `role`, `tabIndex` and Enter/Space are what a
                             * `<button>` was providing for free.
                             */
                            return (
                                <div
                                    key={pr.code}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter' && e.key !== ' ') return;
                                        if ((e.target as HTMLElement) !== e.currentTarget) return;
                                        e.preventDefault();
                                        if (chosen) { resetAll(); return; }
                                        /* ⭐ CHOOSING A PARTY STILL OPENS ITS CARD (2026-09-25).
                                           The OR that used to do this is retired above; opening is
                                           now a WRITE, so the chevron can undo it. */
                                        setOpenParties((prev) => new Set(prev).add(partyKey));
                                        onChooseParty?.(pr.kind, pr.code, pr.name, pr.appointments);
                                    }}
                                    aria-pressed={chosen}
                                    /* ⭐⭐ HOVER **PREVIEWS** (Pete, 2026-09-20). ⛔ It calls
                                       `onHoverParty`, NEVER `onChooseParty` — the distinction
                                       decision 190 exists for. Nothing here survives the cursor
                                       leaving, so the rail cannot change under a scheduler who
                                       looked away, which is what §1AR was protecting. */
                                    /* ⭐ THE LOCAL FLAG IS THE LOOP BREAKER — see
                                       `partyCardScope`. It is set HERE, on the card's own
                                       enter/leave, so the warning-hover handler nested
                                       inside this card is covered by it too: the cursor
                                       cannot reach a warning without having entered the
                                       card first.
                                         ** A GUARD PLACED AT THE OUTER BOUNDARY COVERS
                                            EVERY HANDLER INSIDE IT. ONE PLACED AT EACH
                                            HANDLER IS A LIST THAT GOES STALE. ** */
                                    onMouseEnter={() => {
                                        setOwnHover(true);
                                        onHoverParty?.(
                                            focusFromParty(pr.kind, pr.code, pr.name, pr.appointments));
                                    }}
                                    onMouseLeave={() => {
                                        setOwnHover(false);
                                        onHoverParty?.(null);
                                    }}
                                    /* ⛔⚤ ~~onMouseEnter={() => beginPartyDwell(pr)}
                                       onMouseLeave={endPartyDwell}~~ — REMOVED 2026-09-20,
                                       decision 190. Hovering a party card no longer scopes
                                       anything; the onClick below already did the same job
                                       deliberately, and the dwell was a second, invisible
                                       trigger for it. */
                                    onClick={(e) => {
                                        /* ⛔⛔ DESCENDANT CONTROLS DO THEIR OWN JOB. The card now
                                           carries a chevron, two comms icons and a … menu; without
                                           this every one of them would ALSO re-scope the whole
                                           screen behind the thing it opened — the same guard the
                                           driver card has carried since its icon strip landed.
                                             ** AN INDICATOR INSIDE A CONTROL INHERITS THAT
                                                CONTROL'S CLICK. ** */
                                        if ((e.target as HTMLElement).closest('button,select,input,textarea,a,label')) return;
                                        /* ⭐ F130 (Decision 308): the single click WAITS for a second
                                           one; a double-click opens the party's record in Settings. */
                                        clickIntent.click(`party:${pr.kind}:${pr.code}`, () => {
                                        /* ⭐ CLICKING THE CHOSEN ONE CLEARS IT. Without this the
                                           only way out on Day would be the toolbar pill, at the
                                           other end of the screen from the thing that set it.
                                           ** THE WAY OUT SHOULD BE WHERE THE WAY IN WAS. ** */
                                        /* ⛔⚤ ~~`onClearParty?.()`~~ — WIDENED 2026-09-20.
                                           Pete: *"if you single click a panel a second time it
                                           resets everything just like hitting the circle Ⓡ"*.
                                           Clearing the party alone left the rail focus and every
                                           open card standing. */
                                        if (chosen) { resetAll(); return; }
                                        /* ⭐ CHOOSING A PARTY STILL OPENS ITS CARD (2026-09-25).
                                           The OR that used to do this is retired above; opening is
                                           now a WRITE, so the chevron can undo it. */
                                        setOpenParties((prev) => new Set(prev).add(partyKey));
                                        onChooseParty?.(pr.kind, pr.code, pr.name, pr.appointments);
                                        }, () => onOpenPartyRecord?.(pr.kind, pr.code));
                                    }}
                                    onDoubleClick={(e) => {
                                        if ((e.target as HTMLElement).closest('button,select,input,textarea,a,label')) return;
                                        clickIntent.dbl(`party:${pr.kind}:${pr.code}`, () => onOpenPartyRecord?.(pr.kind, pr.code));
                                    }}
                                    /**
                                      * ---- ⛔⛤ THE MISSING BACKGROUND (Pete, 2026-09-23) --------
                                      *
                                      * > *"the background color of the panels should all be that
                                      * > light gray like it is on driver panels and communication
                                      * > panels. Client Carrier and Consignee don't have that
                                      * > background color."*
                                      *
                                      * ⛔⛔ AND THE NOTE DIRECTLY BELOW THIS ONE SAID SO ALREADY.
                                      * Decision 13: *"THIS IS THE DRIVER CARD WITH SLOTS
                                      * RE-PURPOSED, NOT A NEW CARD SHAPE."* The left rail column,
                                      * the two-column split, the name row and the facts row were
                                      * all matched to the driver card deliberately — and the
                                      * SURFACE they sit on was never matched at all. The unchosen
                                      * state carried `rounded border` with no border colour and no
                                      * fill, so these cards floated on the rail while every card
                                      * around them sat on `bg-muted/30`.
                                      *   ** A RULE THAT NAMES THE PARTS IT COVERS WILL BE READ AS
                                      *      NOT COVERING THE PART IT FORGOT TO NAME. **
                                      *
                                      * ⚠️ `border-border bg-muted/30` IS COPIED FROM THE DRIVER
                                      * CARD, not re-chosen. A second grey that merely looks close
                                      * is the same inconsistency one shade further on.
                                      *
                                      * ---- ⭐⭐ AND THE BORDER, WHICH IS THE HALF THAT WAS
                                      *      ACTUALLY BEING FELT --------------------------------
                                      *
                                      * > Pete, second look: *"I think it has to do with the border
                                      * > actually. The border is not lighting up around the box
                                      * > when you mouse over it."*
                                      *
                                      * ⛔ HE IS RIGHT, AND IT IS THE SHARPER DIAGNOSIS. The card
                                      * had `hover:bg-accent` and NOTHING for the border, so the
                                      * fill shifted a shade and the outline stayed dead — which
                                      * reads as "nothing happened" however correct the resting
                                      * colour is. `CommunicationsPanel`'s row, which is the
                                      * comparison he was making, lights BOTH:
                                      * `hover:border-primary/50 hover:bg-primary/5`.
                                      *   ** A HOVER THAT CHANGES ONLY THE FILL IS READ AS NO
                                      *      HOVER AT ALL. THE EDGE IS WHAT THE EYE TRACKS. **
                                      *
                                      * ⚠️ `transition-colors` WAS ALREADY THERE and now has a
                                      * second property to animate, which is what makes the edge
                                      * arrive rather than snap.
                                      *
                                      * ⛔ THE CHOSEN STATE IS UNTOUCHED. `border-primary
                                      * bg-primary/10` is how a party says it is the active scope,
                                      * and it has to keep beating both the resting fill and the
                                      * hover edge.
                                      */
                                    className={`mx-2 my-1.5 block w-[calc(100%-1rem)] rounded border px-2.5 py-2
                                                text-left text-xs transition-colors
                                                ${chosen ? 'border-primary bg-primary/10'
                                                         : 'border-border bg-muted/30 hover:border-primary/50 hover:bg-accent'}`}
                                >
                                    {/**
                                      * ---- ⛔⛔ DECISION 13: THIS IS THE DRIVER CARD WITH SLOTS
                                      *      RE-PURPOSED, NOT A NEW CARD SHAPE ---------------------
                                      *
                                      * ⛔⛤ IT SHIPPED ONCE AS A NEW SHAPE — a name, two glyphs and a
                                      * counts line — and Pete rejected it: the layout had been
                                      * SPECIFIED and I invented one instead.
                                      *   ** A SPEC THAT LISTS WHICH SLOTS CHANGE IS DESCRIBING THE
                                      *      EXISTING LAYOUT, NOT INVITING A NEW ONE. **
                                      *
                                      * ⚠️ So the left rail column, the two-column split, the name
                                      * row and the facts row all match the driver card exactly —
                                      * a scheduler switching chips must not have to re-learn where
                                      * to look.
                                      */}
                                    <div className="flex items-start gap-2">
                                        {/* ⭐ THE `#25` / `arrival` SLOT BECOMES `n` / `expected`
                                            — total trucks in + out for this party today.
                                            ⛔⚤ ~~`#{pr.appointments.length}`~~ — THE HASH IS GONE
                                            (Pete, 2026-09-20): *"Client and Carrier and Consigned
                                            #1 Expected on left column needs to drop the # sign."*
                                            ⭐⭐ AND THE REASON IS NOT TIDINESS. On a driver card `#1`
                                            is an ORDINAL — the first arrival of the day. On a party
                                            card the same slot holds a COUNT. `#2 expected` reads as
                                            *the second expected*, which is a different and wrong
                                            fact — and it reads that way precisely BECAUSE the slot
                                            was re-purposed rather than redrawn.
                                              ** RE-USING A SLOT INHERITS ITS PUNCTUATION, AND
                                                 PUNCTUATION CARRIES MEANING. ** */}
                                        {/* ⛔ `w-12`, THE SAME FIXED WIDTH THE DRIVER CARD'S ARRIVAL COLUMN
                                            NOW CARRIES (2026-09-24). Pete: *"the changes that we make in the
                                            driver panel, if they apply to the client/carrier/consignee panels
                                            we need to make sure they are all consistent."*
                                            ⚠️ IT IS ALSO WHAT LINES THE CHEVRON UP. Both chevrons hang off
                                            the bottom of this column; while one column sized to `expected` and
                                            the other to a `truck N` badge, the two chevrons sat at different
                                            x on the two card kinds — which is the misalignment Pete saw.
                                              ** TWO COLUMNS THAT SIZE TO THEIR OWN CONTENT ARE TWO WIDTHS, AND
                                                 EVERYTHING HANGING OFF THEM INHERITS THE DIFFERENCE. ** */}
                                        <div className="w-12 shrink-0 text-center">
                                            <div className="font-semibold text-sm leading-none tabular-nums">
                                                {pr.appointments.length}
                                            </div>
                                            <div className="mt-0.5 text-[10px] text-muted-foreground">expected</div>
                                            {/* ⛔ NO TRUCK BADGE, NO STATUS BADGE. Decision 13:
                                                "status suppressed". A party has no single status —
                                                its appointments have several, and they are counted below. */}
                                            {/**
                                              * ---- ⭐⭐⭐ THE CHEVRON, IN THE LEFT COLUMN (Pete, 2026-09-20)
                                              *
                                              * ⛔⛔ I REFUSED THIS ONCE AND THE REFUSAL WAS WRONG. On
                                              * 2026-09-20 I argued a chevron inside the card would
                                              * inherit the card's click — citing `OrdersPanel`, where
                                              * folding the panel was how you read the scope label. Pete
                                              * asked again; the citation does not hold. ⚠️ **THAT FAULT
                                              * WAS A LABEL** — a non-interactive span with nowhere to
                                              * put a handler. A real `<button>` with `stopPropagation`,
                                              * inside a card that is now a div with a `closest(...)`
                                              * guard, is a different thing entirely.
                                              *   ** A PRECEDENT APPLIES TO THE SHAPE IT WAS ABOUT. AN
                                              *      INDICATOR AND A CONTROL ARE NOT THE SAME SHAPE. **
                                              *
                                              * ⚠️ SAME COLUMN, SAME CLASSES, SAME `-ml-1` AS THE DRIVER
                                              * CARD'S. Decision 13 says this card is the driver card
                                              * with slots re-purposed; a chevron half a column away
                                              * from the other chevron is how that stops being true.
                                              *
                                              * ⛔ IT STOPS BOTH EVENTS, for the driver card's reason:
                                              * `onClick` does not cover `dblclick`, which fires on the
                                              * parent as a separate event.
                                              */}
                                            <div className="mt-1 flex justify-start">
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); togglePartyCard(partyKey); }}
                                                    onDoubleClick={(e) => e.stopPropagation()}
                                                    aria-expanded={partyOpen}
                                                    aria-label={partyOpen ? `Collapse ${pr.name}` : `Expand ${pr.name}`}
                                                    title={partyOpen ? 'Collapse' : 'Expand'}
                                                    className="-ml-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                                >
                                                    {partyOpen
                                                        ? <ChevronDown className="h-4 w-4" />
                                                        : <ChevronRight className="h-4 w-4" />}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            {/* ---- name row: the driver-name slot ---- */}
                                            <div className="flex items-start justify-between gap-2">
                                                {/* ⛔ `flex-1 min-w-0` SO THE NAME ABSORBS THE SLACK, exactly as
                                                    the driver card's does. Without it the In/Out pair and the
                                                    warnings would sit wherever the party name happened to end —
                                                    ADMRDL and a long consignee name would disagree. Same one-class
                                                    fix, same reason, same hour. */}
                                                <span className="min-w-0 flex-1 truncate font-medium">{pr.name}</span>
                                                {/**
                                                  * ⭐⭐ IN / OUT, ON THE NAME ROW (Pete, 2026-09-24).
                                                  *
                                                  * > *"On the client card/Carrier, the In 1 Out 0 can move to same
                                                  * > line as ADMRDL but to the far right - but to the left of the
                                                  * > warnings."*
                                                  *
                                                  * ⚠️ ORDER IS THE INSTRUCTION: after the name, BEFORE the warning
                                                  * badges. The badges stay hard right because a warning is the one
                                                  * thing on this card somebody scans a column of cards for, and
                                                  * anything placed after it pushes it off that column.
                                                  * ⛔ RENDERED AT ZERO, like the counts below — the In/Out rule
                                                  * this card already follows: a zero is information, a gap is not.
                                                  */}
                                                <span className="shrink-0 whitespace-nowrap text-[11px] font-normal text-muted-foreground">
                                                    In <span className="tabular-nums">{inb}</span>
                                                    {' · '}Out <span className="tabular-nums">{outb}</span>
                                                </span>
                                                {/* ⭐ WARNINGS KEPT (decision 13), hard right — the
                                                    same axis as every driver card's ⚠. */}
                                                <span className="flex shrink-0 items-center gap-1.5">
                                                    {/* ⭐ THE SAME BADGE THE DRIVER CARD AND THE PANEL
                                                        HEADER DRAW. Was `icon + plain number`; the other
                                                        two were `icon + filled circle`, and nothing shared
                                                        code so they drifted. */}
                                                    {/**
                                                      * ---- ⛔⛤ ONE BADGE, COUNTING WARNINGS (2026-09-23)
                                                      *
                                                      * > Pete: *"Why is this duplicated??? It should be one
                                                      * > number in a circle counting all the warnings."*
                                                      *
                                                      * ⛔⛔ AND THE DUPLICATION WAS THE SMALLER HALF: the two
                                                      * badges counted LOADS while the circles in the boxes
                                                      * below counted WARNINGS. A card with 2 overdue loads
                                                      * and 2 unfiled ones showed "2", "2", "1" and "2" - four
                                                      * numbers, two meanings, nothing saying which was which.
                                                      *   ** TWO COUNTERS OF DIFFERENT THINGS, DRAWN THE SAME
                                                      *      WAY, ARE WORSE THAN ONE COUNTER AND A LIST. **
                                                      *
                                                      * ⭐ ONE BADGE = `partyWarnings(pr).length`, exactly as the
                                                      * driver card's is `arrivalWarnings(c).length`. It agrees
                                                      * with the numbered boxes BY CONSTRUCTION: the last circle
                                                      * in the list equals the badge.
                                                      * ⚠️ The per-type detail is not lost - it moved into the
                                                      * boxes, where it is a sentence instead of a glyph.
                                                      */}
                                                    {(() => {
                                                        const pw = partyWarnings(pr);
                                                        return (
                                                            <CountBadge
                                                                icon={<AlertTriangle className="h-3.5 w-3.5" />}
                                                                count={pw.length}
                                                                title={pw.map(w => w.text).join('\n')}
                                                            />
                                                        );
                                                    })()}
                                                    {/* ⭐ Decision 24, the half the payload supports.
                                                        ⛔ "Nothing filed" — NOT "unsigned".
                                                        `AppointmentDto` carries a COUNT, not a
                                                        signature state, and a glyph claiming more
                                                        than the data holds gets trusted. */}
                                                    {/* ⛔⚤ ~~`text-muted-foreground`~~ — AMBER, 2026-09-20. Pete: *"Two loads
                                                        with no paperwork warnings should be orange to follow the standard
                                                        for warnings."*
                                                        ⭐⭐ IT SAT BESIDE AN AMBER TRIANGLE AND RENDERED GREY, so two warnings
                                                        about the same party were drawn as a warning and a statistic. The muted
                                                        colour was inherited from the neutral COUNTS BELOW it, not chosen for
                                                        this glyph.
                                                          ** WHEN A NEW INDICATOR BORROWS A CLASS, IT BORROWS AN ARGUMENT
                                                             SOMEBODY ELSE MADE ABOUT A DIFFERENT THING. **
                                                        ⚠️ `text-amber-700` MATCHES THE TRIANGLE EXACTLY, not merely "an
                                                        orange" — two shades of warning is the same fault one step smaller. */}
                                                    {/* ⛔⛤ `FileText` ALONE SAID "2 DOCUMENTS". It now
                                                        carries the slash — see `NoPaperworkIcon`. */}
                                                    {/* ⛔⛤ ~~THE SECOND BADGE~~ - folded into the one above.
                                                        The slashed file now leads its own warning row. */}
                                                </span>
                                            </div>

                                            {/* ---- facts row: `Trucks` + the three buckets ----
                                                ⭐ Decision 19: on-site = Arrived, complete =
                                                Departed, remaining = Requested + Approved. The same
                                                seven statuses the status bar shows, so the numbers
                                                reconcile by eye. ⛔ Rendered at ZERO as well — the
                                                In/Out rule: a zero is information, a gap is not.

                                                ⛔⚤ ~~"in progress"~~ → **"on site"**, 2026-09-24.
                                                This number is `statusCode === 'ARRIVED'` — through the
                                                gate — and it has never meant that anybody is working
                                                the truck. Pete asked for REAL labour progress on this
                                                card (see the labour row below), and the two could not
                                                both be called "in progress".
                                                  ** WHEN A NEW FACT ARRIVES WEARING AN OLD FACT'S
                                                     WORD, RENAME THE OLD ONE. **
                                                ⭐ "On site" is not a new coinage: `statusWord` already
                                                returns it for ARRIVED on the driver card, so the two
                                                card kinds now say the same word for the same fact. */}
                                            <div className="mt-0.5 text-[11px] text-muted-foreground">
                                                <span className="font-medium text-foreground">Trucks </span>
                                                <span className="font-medium tabular-nums text-foreground">
                                                    {pr.appointments.length}
                                                </span>
                                                <span className="whitespace-nowrap">
                                                    {' · '}<span className="tabular-nums">{pr.arrived}</span> on site
                                                </span>
                                                <span className="whitespace-nowrap">
                                                    {' · '}<span className="tabular-nums">{pr.departed}</span> complete
                                                </span>
                                                <span className="whitespace-nowrap">
                                                    {' · '}<span className="tabular-nums">{pr.remaining}</span> remaining
                                                </span>
                                            </div>
                                            {/* ⛔⛤ ~~THE IN / OUT ROW~~ — MOVED ONTO THE NAME ROW, 2026-09-24.
                                                ⚠️ ITS ORIGINAL REASON IS UNCHANGED and still worth keeping: the
                                                `#n expected` above is the two added together, and a scheduler
                                                needs the split. Only its position moved — it now sits at the
                                                right-hand end of the name row, left of the warning badges, where
                                                Pete asked for it. That also buys a whole line back on a card
                                                that had four of them. */}
                                            {/**
                                              * ---- ⭐⭐⭐ LABOUR, THE SAME WAY THE DRIVER CARD SAYS IT ----
                                              *
                                              * > Pete, 2026-09-24: *"We need to show on the carrier/client/
                                              * > consignee that labor is in progress too, just like the driver
                                              * > panel."* → *"Same as on the driver card - consistency."*
                                              *
                                              * ⭐ SAME GLYPH, SAME SPIN, SAME EMERALD as the word beside the
                                              * driver's name ~1,000 lines above — the same `WorkTruckMark`
                                              * component with the same five props, not classes matched by eye.
                                              *   ** TWO CARDS THAT LOOK ALIKE BECAUSE SOMEBODY MATCHED THE
                                              *      CLASSES WILL STOP LOOKING ALIKE. TWO THAT SHARE A
                                              *      RENDERER CANNOT. ** (This card's warnings row already
                                              *      makes that argument; this is the same argument again.)
                                              *
                                              * ⚠️ THE UNIT IS LOADS, NOT A VERB. The driver card describes ONE
                                              * truck, so it can say "Unloading"; a party card covers eleven
                                              * going both ways and "Loading" would be false for half of them.
                                              * So it counts: *"2 being worked"*. ⛔ Not "2 Loading/Unloading" —
                                              * a slash is not a word anybody reads at 11px.
                                              *
                                              * ⛔⛔ AND PAUSED IS DRAWN, NOT LEFT BLANK. `laborActive` is
                                              * `State = 'Active'`, so it goes false the instant somebody presses
                                              * Pause; a row that only knew `working` would show NOTHING for a
                                              * party whose every load is paused — indistinguishable from a party
                                              * nobody has started. That is the exact fault `PauseMark.tsx` was
                                              * created for:
                                              *   ** ABSENCE IS NOT A STATE. IT IS THE ABSENCE OF EVERY STATE,
                                              *      AND A READER CANNOT TELL "NOBODY EVER STARTED" FROM
                                              *      "SOMEBODY STOPPED". **
                                              *
                                              * ⚠️ AMBER FOR PAUSED IS SAFE **HERE**. `STATUS_TONE`'s note
                                              * forbids amber for the WORKING word because amber means
                                              * "unmatched" on the driver cards — but paused is not working, it
                                              * is a thing a scheduler should look at, which is what amber says
                                              * on this panel everywhere else (the triangle, the slashed file).
                                              *
                                              * ⛔ THE WHOLE ROW IS ABSENT WHEN BOTH ARE ZERO, unlike the counts
                                              * above it. "0 being worked" is the normal state of most parties
                                              * most of the day; printing it on every card would bury the two
                                              * cards that say something.
                                              *   ** A ZERO IS INFORMATION WHEN ITS SIBLINGS ARE NOT ZERO. A
                                              *      COLUMN OF ZEROES IS FURNITURE. **
                                              */}
                                            {(pr.working > 0 || pr.paused > 0) && (
                                                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
                                                    {pr.working > 0 && (
                                                        <span className="inline-flex items-center gap-1 font-semibold text-emerald-700"
                                                              title="Being worked — loading/unloading in progress">
                                                            <WorkTruckMark
                                                                wrapClassName="relative inline-flex h-3.5 w-3.5 shrink-0"
                                                                ringClassName="absolute inset-0 h-full w-full animate-spin motion-reduce:[animation-duration:8s]"
                                                                truckClassName="absolute left-[23%] top-[23%] h-[54%] w-[54%]"
                                                                ringStyle={{ animationDuration: `${WORK_MARK_SPIN_SECONDS}s` }}
                                                                ringStrokeWidth={4.5}
                                                                truckStrokeWidth={2.6}
                                                            />
                                                            <span className="tabular-nums">{pr.working}</span>
                                                            {' being worked'}
                                                        </span>
                                                    )}
                                                    {pr.paused > 0 && (
                                                        <span className="inline-flex items-center gap-1 font-semibold text-amber-700"
                                                              title="Paused — a worker is assigned and nobody is on it right now">
                                                            {/* ⚠️ SAME FOOTPRINT AS THE TRUCK (r=19, 0 0 48 48), so a
                                                                load moving between the two does not nudge this row. */}
                                                            <PauseMark
                                                                wrapClassName="relative inline-flex h-3.5 w-3.5 shrink-0"
                                                                markClassName="absolute inset-0 h-full w-full"
                                                                ringStrokeWidth={4.5}
                                                            />
                                                            <span className="tabular-nums">{pr.paused}</span>
                                                            {' paused'}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {/**
                                              * ---- ⭐⭐⭐ THE SAME WARNINGS, THE SAME SHAPE ------------
                                              *
                                              * > Pete, 2026-09-23: *"This layout needs to be consistent
                                              * > across driver, client carrier, consignee which it is not."*
                                              *
                                              * ⭐ SAME COMPONENT AS THE DRIVER CARD, not a copy of its
                                              * classes - which is the only version of "consistent" that
                                              * survives the next change to either card.
                                              *   ** TWO CARDS THAT LOOK ALIKE BECAUSE SOMEBODY MATCHED
                                              *      THE CLASSES WILL STOP LOOKING ALIKE. TWO THAT SHARE A
                                              *      RENDERER CANNOT. **
                                              *
                                              * ⚠️ PLACED WHERE THE DRIVER CARD PUTS ITS WARNINGS: after
                                              * the facts, before the tool strip and the buttons.
                                              *
                                              * ⛔⚤ ~~NO onClick ON EITHER~~ - REVERSED 2026-09-23, AND
                                              * THE REVERSAL IS PETE'S ORIGINAL SPEC. I wrote here that
                                              * *"there is no single record to open"* and stopped, which
                                              * turned out to be half an argument: the answer to a set is
                                              * a FILTER. Worse, the inert `<div>` did not even stay inert
                                              * - the click reached the card root and scoped the screen.
                                              *   ** "THERE IS NOTHING TO OPEN" IS A REASON TO ASK WHAT
                                              *      ELSE A SET AFFORDS, NOT A REASON TO SHIP A DEAD
                                              *      CONTROL INSIDE A LIVE ONE. **
                                              *
                                              * ⭐⭐ HOVER PREVIEWS, CLICK LATCHES. The two gestures the
                                              * card itself already uses, one level in: hovering the card
                                              * previews the whole party, hovering a warning narrows the
                                              * preview to the loads that warning is about, and leaving it
                                              * restores the party - the cursor is still on the card, so
                                              * clearing outright would read as a flicker.
                                              *
                                              * ⚠️ A WARNING WITH NO IDS STAYS A `<div>`. `partyWarnings`
                                              * supplies them from `PartyRow`, so that is currently
                                              * unreachable - and it is the honest fallback if a future
                                              * warning is about the party rather than about loads.
                                              */}
                                            {/**
                                              * ---- ⛔⚤ BACK BEHIND THE CHEVRON (Pete, 2026-09-23) ------
                                              *
                                              * > *"why are warnings now showing without expanding the
                                              * > chevron? We didn't ask to change that."*
                                              *
                                              * ⛔⛔ AND HE CAUGHT IT IN THE CHANGE THAT WAS SUPPOSED TO
                                              * MAKE THESE TWO CARDS AGREE. The driver card's `CardWarnings`
                                              * sits INSIDE `detailOpen` - measured, line ~7261 inside the
                                              * block opened at ~6490 - so its sentences are collapsed
                                              * detail. The party card rendered the identical component in
                                              * the ALWAYS-VISIBLE body. Same component, same classes,
                                              * opposite disclosure.
                                              *   ** COPYING A COMPONENT COPIES ITS APPEARANCE. IT DOES NOT
                                              *      COPY WHERE THE ORIGINAL SITS, AND "CONSISTENT" IS
                                              *      MOSTLY ABOUT WHERE THINGS SIT. **
                                              *
                                              * ⭐ THE RULE, NOW TRUE ON BOTH: **the COUNT is the collapsed
                                              * state, the SENTENCES are the expanded one.** The corner
                                              * `CountBadge` still says how many without a click - which is
                                              * what a scheduler scanning the rail needs - and this file's
                                              * own note on `partyWarnings` already claimed exactly that:
                                              * *"a count at the top, the detail in the body."*
                                              *
                                              * ⚠️ WHICH MEANS THE WARNING FILTER LIVES BEHIND THE CHEVRON
                                              * TOO, deliberately. Filtering the whole board is not a thing
                                              * to be one stray click away from on a collapsed card.
                                              */}
                                            {/**
                                              * ---- ⭐⭐⭐ THE TOOL STRIP: COMMS ICONS AND THE `…` MENU --
                                              *
                                              * > Pete, 2026-09-20: *"There are no icons for communication
                                              * > (even if no phone number). There is no ... in right hand
                                              * > corner for the menu."*
                                              *
                                              * ⭐⭐ *"EVEN IF NO PHONE NUMBER"* IS THE INSTRUCTION, AND IT
                                              * REVERSES THE DRIVER CARD'S RULE ON PURPOSE. There, an icon
                                              * is hidden when it cannot act — *"a sign icon on a card with
                                              * nothing filed is a button whose only possible outcome is a
                                              * disappointment."* ⛔ Here the absence IS the fact worth
                                              * showing: **97 clients exist and not one has a contact**
                                              * (§1AV.158), so a rail of cards with no comms icons says
                                              * "this feature does not exist", while a rail of greyed ones
                                              * says "nobody has been given a number yet" — which is the
                                              * true statement and the actionable one.
                                              *   ** HIDING A CONTROL HIDES THE GAP. DISABLING IT WITH ITS
                                              *      REASON PUBLISHES THE GAP. WHICH IS RIGHT DEPENDS ON
                                              *      WHETHER THE GAP IS SOMEBODY'S JOB. **
                                              *
                                              * ⭐ SAME `CardIcon`, SAME CONTAINER CLASSES AS `arrivalIcons`.
                                              * ⛔ NOT the same FUNCTION, and that is measured rather than
                                              * lazy: `arrivalIcons` takes a `CheckInDto` and closes over a
                                              * dozen arrival-only pieces of state (licence, void, SMS,
                                              * paperwork upload). A party has none of them. Sharing the
                                              * shape without sharing the body is the correct half of that
                                              * trade — and the classes ARE the shape.
                                              *
                                              * ⚠️ `justify-end` PUTS THE `…` IN THE RIGHT-HAND CORNER,
                                              * which is where Pete asked for it and where the driver
                                              * card's already is.
                                              */}
                                            <div
                                                className="mt-1 flex items-center justify-end gap-0.5"
                                                onDoubleClick={(e) => e.stopPropagation()}
                                            >
                                                <CardIcon
                                                    label={`Text the ${pr.kind}`}
                                                    onClick={() => { /* blocked — see the section below */ }}
                                                    disabled
                                                >
                                                    <MessageSquare className="h-3.5 w-3.5" />
                                                </CardIcon>
                                                <CardIcon
                                                    label={`Email the ${pr.kind}`}
                                                    onClick={() => { /* blocked — see the section below */ }}
                                                    disabled
                                                >
                                                    <Mail className="h-3.5 w-3.5" />
                                                </CardIcon>
                                                {/* ⭐ LAST, AFTER THE SINGLE ACTIONS — the driver strip's
                                                    rule, for its reason: this one opens everything,
                                                    including those, and a menu in the middle of a strip
                                                    reads as one more tool.
                                                    ⭐ ITS OWN RECTANGLE, NOT THE MOUSE POSITION: a
                                                    keyboard or touch activation has no cursor, and a menu
                                                    at (0,0) is how that fails for exactly the people who
                                                    will not report it. */}
                                                <CardIcon
                                                    label="All options"
                                                    onClick={(e) => {
                                                        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                        setPartyMenu({ row: pr, x: r.left, y: r.bottom + 2 });
                                                    }}
                                                >
                                                    <MoreHorizontal className="h-3.5 w-3.5" />
                                                </CardIcon>
                                            </div>

                                            {/* ⭐ THE WARNINGS SIT UNDER THE ICON ROW (Pete, 2026-09-25). Moved
                                                from above the tool strip, where they pushed the icons down the
                                                card and read as part of the header rather than the body.
                                                ⛔ SAME RULE ON THE DRIVER CARD: first thing under the icons,
                                                above the first content box. One rule, two card types, because
                                                a scheduler reads both in the same sweep. */}
                                            {partyOpen && (
                                                <CardWarnings
                                                    onHoverWarning={(w) => {
                                                        /* ⛔ NEVER `null` HERE. The cursor is still inside the
                                                           card; the card's own leave handler is what clears. */
                                                        if (!w || !w.appointmentIds || w.appointmentIds.length === 0) {
                                                            onHoverParty?.(focusFromParty(
                                                                pr.kind, pr.code, pr.name, pr.appointments));
                                                            return;
                                                        }
                                                        const ids = new Set(w.appointmentIds);
                                                        const subset = pr.appointments.filter(
                                                            a => ids.has(String(a.appointmentId)));
                                                        /* ⚠️ THE LABEL IS OVERRIDDEN so the panel headers say
                                                           what is being previewed. `focusFromParty` names the
                                                           party, which would be a true statement about the
                                                           wrong set. */
                                                        onHoverParty?.({
                                                            ...focusFromParty(pr.kind, pr.code, pr.name, subset),
                                                            label: `${pr.name} — ${w.text}`,
                                                        });
                                                    }}
                                                    warnings={partyWarnings(pr).map((w) => {
                                                        const ids = w.appointmentIds;
                                                        if (!ids || ids.length === 0) return w;
                                                        const key = `${partyKey}|${w.code}`;
                                                        const on = warningScopeKey === key;
                                                        return {
                                                            ...w,
                                                            active: on,
                                                            title: on
                                                                ? 'Showing only these appointments on the schedule — click to show them all again'
                                                                : 'Show only these appointments on the schedule',
                                                            /* ⛔ A TOGGLE, NOT A SET. Clicking the pressed one
                                                               clears it; the card's own second click and the
                                                               Ⓡ button both clear it too. */
                                                            onClick: () => onChooseWarningScope?.(
                                                                on ? null : key, ids, `${pr.name} — ${w.text}`),
                                                        };
                                                    })}
                                                />
                                            )}
                                            {/**
                                              * ---- ⬜⬜ THE LAST TWO SLOTS OF DECISION 13 ARE BLOCKED,
                                              *      AND ARE ABSENT RATHER THAN FAKED ----------------
                                              *
                                              * Decision 13 also specifies **"default contact in the
                                              * time/TBD slot"** and a **"NEW email section"**.
                                              *
                                              * ⛔ NEITHER CAN BE BUILT YET, AND BOTH ARE BLOCKED ON
                                              * THINGS THE DOCUMENTS ALREADY NAME:
                                              *   · the contact needs `dock.ClientContact` /
                                              *     `dock.ConsigneeContact`, which DO NOT EXIST
                                              *     (decision 23, the contact gap). `CarrierContact`
                                              *     exists but reaches no DTO on this screen.
                                              *   · the email section is decision 189, whose transport
                                              *     and carrier-first scope are agreed and unbuilt.
                                              *
                                              * ⛔⛔ SO THEY ARE OMITTED, NOT STUBBED. A contact line
                                              * reading "—" on every card, or an Email button that
                                              * does nothing, is precisely what decision 6's reasoning
                                              * forbids:
                                              *   ** NEVER SHOW A CONTROL THAT CANNOT DO ANYTHING. **
                                              * ⚠️ Recorded in OPEN-ITEMS against decisions 13/23/189
                                              * so the gap is visible rather than discovered later.
                                              */}
                                        </div>
                                    </div>

                                    {/* ⛔⛔ DECISION 21 — THE COUNTS DO NOT SUM, AND THE PANEL SAYS SO.
                                        `dock.AppointmentConsignee` is a JOIN: one appointment bound for three
                                        consignees appears on three cards. A scheduler adding these up
                                        against `Appointments N` above would find them wrong and reasonably
                                        conclude the screen is broken. */}
                                    {chosen && pr.kind === 'consignee' && (
                                        <div className="mt-1 text-[10px] text-muted-foreground">
                                            An appointment with several consignees is counted under each of them,
                                            so these totals overlap.
                                        </div>
                                    )}

                                    {/**
                                      * ---- ⭐⭐ THE COMMUNICATIONS SECTION (decision 13's last slot) ----
                                      *
                                      * ⭐ Pete, 2026-09-20: *"Need the communication icons on the client,
                                      * carrier, consignee panels. These will have canned emails like Email
                                      * Schedule. This puts the schedule in the email body and a link to the
                                      * client's/carriers portals for them to login."* — decision 189.
                                      *
                                      * ⭐⭐⭐ IT OPENS ON `chosen`, AND THAT IS NOT LAZINESS ABOUT A CHEVRON.
                                      * The card is a `<button>` whose click scopes the whole screen, so a
                                      * chevron INSIDE it would inherit that click — the exact fault found in
                                      * `OrdersPanel` on 2026-09-19, where the scope label lived inside the
                                      * collapse button and folding the panel was how you read it.
                                      *   ** AN INDICATOR INSIDE A CONTROL INHERITS THAT CONTROL'S CLICK. **
                                      * ⭐ AND IT PAYS A SECOND DIVIDEND: Ⓡ clears the party scope, so Ⓡ
                                      * collapses these cards with no new state and no entry in
                                      * `collapseAllCards` — which is exactly what Pete asked Ⓡ to do.
                                      *
                                      * ⛔⛔ EVERY CONTROL HERE IS DISABLED, AND EACH SAYS WHY ON ITSELF.
                                      * That is Pete's own ruling, twice: decision 185 (*"Leave the line but
                                      * have it grey'd out so you can't select it"*) and again on 2026-09-20
                                      * when asked what an Email button should do with nowhere to send.
                                      * ⚠️ IT IS THE OPPOSITE OF A STUB. The rule this seems to break —
                                      * *"never show a control that cannot do anything"* — is about controls
                                      * that LOOK live; a disabled control carrying its own reason is how the
                                      * missing thing becomes visible instead of discovered at send time.
                                      *   ** A PROMISE THE SCREEN MAKES AND THE SERVER BREAKS IS THE FAILURE.
                                      *      A CONTROL THAT STATES ITS OWN BLOCKER IS THE FIX. **
                                      *
                                      * ⛔ THE BLOCKERS ARE REAL, MEASURED AND DIFFERENT PER KIND:
                                      *   · client / consignee — §1AV.158: **97 clients exist and not one has
                                      *     a contact of any kind.** No email column on `dbo.BusinessUnits`
                                      *     nor on any `Client*` table; `dock.ClientContact` and
                                      *     `dock.ConsigneeContact` do not exist (decision 23). §3B migration.
                                      *   · carrier — `dock.CarrierContact` DOES exist and backs the carrier
                                      *     portal login, but it reaches no DTO on this screen. Frontend has
                                      *     nothing to send to until an endpoint serves it.
                                      * ⬜ So the carrier half ships first (decision 26, carriers before
                                      * clients before consignees) and it is a BACKEND task, not this file.
                                      */}
                                    {partyOpen && (
                                        <div className="mt-2 space-y-1.5 border-t pt-2">
                                            {(() => {
                                                /* ⛔ ONE SENTENCE PER KIND, NOT A SHARED VAGUE ONE. "Not
                                                   available" on all three would hide that a carrier is one
                                                   endpoint away and a client is a migration away. */
                                                const blocked = pr.kind === 'carrier'
                                                    ? 'Carrier contacts exist but are not on this screen yet'
                                                    : `No contacts are stored for ${pr.kind}s yet`;
                                                const who = pr.kind === 'client' ? 'client'
                                                    : pr.kind === 'carrier' ? 'carrier' : 'consignee';
                                                return (
                                                    <>
                                                        <div className="rounded border bg-background p-1 shadow-sm">
                                                            <div className="px-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                                Text the {who}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                disabled
                                                                title={blocked}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="flex w-full items-center gap-1.5 rounded px-2 py-1
                                                                           text-left text-[11px] opacity-40"
                                                            >
                                                                <MessageSquare className="h-3 w-3 shrink-0" />
                                                                Send a standard message
                                                            </button>
                                                            <div className="px-2 pb-1 text-[10px] text-muted-foreground">
                                                                {blocked}.
                                                            </div>
                                                        </div>

                                                        {/* ⭐⭐ DECISION 189, NAMED ON THE BUTTON. *"Lets put
                                                            the schedule in the email body as well as the link."*
                                                            ⛔ The transport cannot attach files — one function,
                                                            `notifyViaPipeline`, contract `{subject, body}` — which
                                                            is why it is a TABLE IN THE BODY plus a portal link,
                                                            and why that needs no Pipeline change at all.
                                                              ** THE ATTACHMENT HAD BEEN MISTAKEN FOR THE
                                                                 REQUIREMENT. ** */}
                                                        <div className="rounded border bg-background p-1 shadow-sm">
                                                            <div className="px-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                                Email the {who}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                disabled
                                                                title={blocked}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="flex w-full items-center gap-1.5 rounded px-2 py-1
                                                                           text-left text-[11px] opacity-40"
                                                            >
                                                                <Mail className="h-3 w-3 shrink-0" />
                                                                Email Schedule — today's appointments + a portal link
                                                            </button>
                                                            <div className="px-2 pb-1 text-[10px] text-muted-foreground">
                                                                {blocked}.
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )
                ) : loading && firstLoad.current ? (
                    <div className="p-3 text-xs text-muted-foreground">Loading arrivals…</div>
                ) : visible.length === 0 ? (
                    <div className="p-3 text-xs text-muted-foreground">
                        {/* ⚠️ AN EMPTY LIST HAS TO SAY WHY. "No drivers have checked
                            in today" is a lie when six have and a filter is hiding
                            them, and it is the message a scheduler would act on. */}
                        {rows.length === 0
                            ? 'No drivers have checked in today.'
                            : filtersActive
                                ? needsLicenceOnly && clientFilter === '' && carrierFilter === ''
                                    ? 'Every truck on site has a license on file.'
                                    : 'No trucks match these filters.'
                                : 'Every truck that arrived today has departed.'}
                    </div>
                ) : (
                    groups.map((g) => (
                    <div key={g.key}>
                        {/* Sticky so the group a card belongs to is still legible
                            after scrolling past its header — the whole point of
                            grouping is lost if the label scrolls away. Hidden
                            entirely when there is only one group, because a
                            header over the only list is noise. */}
                        {groups.length > 1 && (
                            <div className={`sticky top-0 z-10 flex items-center justify-between
                                            bg-muted px-3 py-1 text-[11px] font-semibold ${SUBSECTION_HEADER}`}>
                                {/* ⭐ F71: the tag in FRONT of the word, so the header
                                    reads as the same symbol the cards under it carry. The
                                    "Needs Appointment" group has no direction and no tag. */}
                                <span className="flex items-center gap-1.5">
                                    {g.key === 'in' && <DirectionTag direction="INBOUND" />}
                                    {g.key === 'out' && <DirectionTag direction="OUTBOUND" />}
                                    <span>{g.label}</span>
                                </span>
                                <span className="tabular-nums text-muted-foreground">{g.rows.length}</span>
                            </div>
                        )}
                        {g.rows.map((c) => {
                        const unmatched = c.appointmentId === null;

                        /**
                          * ---- ⭐⭐ IS THIS CARD'S DETAIL SHOWING? -------------------
                          *
                          * ⛔⛔ THE CHEVRON IS NOT THE ONLY THING THAT OPENS IT, AND THAT
                          * IS THE WHOLE POINT OF COMPUTING IT HERE.
                          *
                          * ⚠️ The tool strip has controls whose OUTPUT renders inside this
                          * region — the (i) history and the appointment picker both draw
                          * below. On a folded card, clicking one of those would put the
                          * result in the one place on the screen nobody can see, and the
                          * icon would read as broken. ⭐ Same failure `PaperworkSliver`
                          * already carries a `setOpen(true)` to avoid.
                          *
                          * ⭐ SO THE OPEN CONDITION IS DECLARATIVE RATHER THAN A SET OF
                          * `setOpenCards` CALLS AT EACH CLICK SITE. A call site can be
                          * forgotten when the next inline panel is added; this cannot —
                          * the failure would be a compile error about an unknown name.
                          *
                          * ⚠️ AND IT IS DELIBERATELY NOT STICKY: closing the history
                          * returns the card to whatever the chevron said. The forced open
                          * is a consequence of the thing being shown, not a change to
                          * what this person chose.
                          */
                        const detailOpen = openCards.has(c.checkInId)
                            || menuFor === c.checkInId
                            /* ⭐ ADDED 2026-09-09. The texting menu moved INTO the detail
                               (Pete: *"I don't think we have room for the note in the
                               collapsed panel but it would go nicely on the expansion"*),
                               so opening it from a collapsed card has to open the card —
                               otherwise the phone is a control that visibly does nothing. */
                            || smsFor === c.checkInId;
                        return (
                            <div
                                key={c.checkInId}
                                // ⚠️ Unmatched is amber, not red. It is not an error — it is a
                                // truck waiting for a human, which is the panel's whole job.
                                /**
                                 * ⭐ A DEPARTED ARRIVAL IS STRUCK THROUGH, like a cancelled
                                 * appointment (2026-08-27). Pete: *"can we put the light gray
                                 * lines thru them like we do for cancelled appointments. That
                                 * gives us a visual that they are done."*
                                 *
                                 * ⚠️ THE SAME MARK MEANS THE SAME THING EVERYWHERE — a chip on
                                 * the grid and a card in the rail are two views of one truck, and
                                 * a scheduler should not have to learn two vocabularies.
                                 *
                                 * ⛔ DIMMED AS WELL AS STRUCK, and the card stays fully readable:
                                 * a departed truck is still evidence — its paperwork, texts and
                                 * labor are all reached from here — so it recedes rather than
                                 * being hidden.
                                 */
                                /**
                                 * ⭐⭐ EACH CARD IS ITS OWN BOX NOW (2026-08-27). Pete: *"the way
                                 * have the Labor slider with a very slight grey background and a
                                 * border around each one, make that happen for driver cards…
                                 * easier to distinguish between different cards."*
                                 *
                                 * ⚠️ IT WAS A FLUSH LIST WITH A HAIRLINE BETWEEN ROWS, and a card
                                 * here can run to six or seven lines — name, phone, appointment,
                                 * door, last text, buttons. At that height one border between two
                                 * of them stops reading as a boundary, and the eye joins the
                                 * bottom of one card to the top of the next.
                                 *
                                 * ⭐ SAME SHAPE AS `LabourRow`: `rounded border` + a very light
                                 * fill. Three rails sit in one column — paperwork, labour,
                                 * communications — and a card should look like a card in all of
                                 * them.
                                 *
                                 * ⛔⛔ THE SURFACE IS ONE STRING, NOT FOUR CLASSES STACKED.
                                 * Tailwind resolves `bg-amber-50` against `bg-muted/30` by
                                 * STYLESHEET ORDER, not by which appears later in this template —
                                 * so appending a state background to a base one is a coin toss
                                 * that renders correctly until the day the build reorders. Every
                                 * state picks its border AND its fill together, exactly once.
                                 */
                                className={`mx-2 my-1.5 rounded border px-2.5 py-2 text-xs transition-colors
                                    ${paperworkOver === c.checkInId
                                        // ⭐ SKY, THE SAME COLOUR THE GRID USES FOR PAPERWORK
                                        // (#150). One colour per outcome across both
                                        // surfaces, so the gesture reads the same wherever
                                        // it is aimed.
                                        ? 'border-sky-400 bg-sky-50 ring-2 ring-inset ring-sky-500'
                                        : unmatched
                                            // ⚠️ Unmatched is amber, not red. It is not an error — it
                                            // is a truck waiting for a human, which is the panel's
                                            // whole job.
                                            /* ---- ⭐⭐ GREY FILL, AMBER EDGE (Pete, 2026-09-23) -----
                                               > *"I like the greyish background with the yellow warnings
                                               > as opposed to both yellow warnings and yellow background -
                                               > background is too much."*

                                               ⛔⛤ THIS REVERSES A DECISION OF HIS OWN, 2026-09-09: *"the
                                               amber fill stays as the scan-a-stack signal"* - an unmatched
                                               card was meant to be findable down a long rail. Recorded as a
                                               REVERSAL, not a tidy-up, so that if the rail becomes hard to
                                               scan this is the line to undo.

                                               ⭐ THE BORDER SURVIVES AND THE FILL GOES. He objected to the
                                               BACKGROUND specifically, and the edge keeps some of the
                                               scan signal the fill was carrying.
                                                 ** WHEN REVERSING A DECISION, KEEP WHATEVER PART OF IT THE
                                                    NEW INSTRUCTION DID NOT ACTUALLY OBJECT TO. ** */
                                            ? 'border-amber-300 bg-muted/30'
                                            : 'border-border bg-muted/30'}
                                    ${departed(c) ? 'opacity-60 [&_.card-title]:line-through' : ''}
                                    ${c.appointmentId && selectedAppointmentIds.has(c.appointmentId)
                                        ? 'ring-2 ring-inset ring-slate-900'
                                        : c.appointmentId && (highlightAppointmentId === c.appointmentId
                                                              || previewApptIds.has(c.appointmentId))
                                            ? 'ring-2 ring-inset ring-slate-900/40' : ''}
                                    hover:bg-accent`}
                                /**
                                 * ---- ⭐ DOUBLE-CLICK THE CARD TO EDIT ------------------
                                 *
                                 * Pete, 2026-08-25: *"you can remove the EDIT button and just
                                 * doubleclick on the driver card to edit. This completely
                                 * removes all buttons and gives a clean, much need real estate
                                 * look."*
                                 *
                                 * ⚠️ AND A DOUBLE-CLICK ALONE WOULD HAVE MADE EDITING
                                 * UNREACHABLE WITHOUT A MOUSE. So the card is focusable and
                                 * Enter does the same thing — a gesture with no keyboard
                                 * equivalent is not a shortcut, it is a wall.
                                 *
                                 * ⛔ THE ICONS STOP THE EVENT THEMSELVES. Without that, a
                                 * double-click landing on the bin would open the editor behind
                                 * the confirm dialog.
                                 */
                                role="button"
                                tabIndex={0}
                                /**
                                 * ---- ⭐⭐ SINGLE CLICK OPENS THE ARRIVAL (2026-09-10) --
                                 *
                                 * > Pete: *"the single click on the driver panel should
                                 * > bring up the edit driver, consistent with the
                                 * > appointment single click"* … *"The popup initially
                                 * > should maybe give all the options that it has on the
                                 * > panel. Then from there, they can edit?"*
                                 *
                                 * ⭐ SO IT OPENS THE ARRIVAL'S OPTIONS, NOT THE FORM —
                                 * which is exactly what clicking an appointment does:
                                 * `AppointmentDetailDialog` shows the load with its
                                 * action buttons, and Edit is one of them.
                                 *
                                 * ⛔ ~~onDoubleClick → startEdit~~ REPLACED. A
                                 * double-click fires two single clicks first, so keeping
                                 * both would open the options and then the form on top of
                                 * it. ⚠️ And a double-click was never discoverable: nothing
                                 * on a card said it was there.
                                 *
                                 * ⚠️ THE GUARD SURVIVES THE MOVE. A click that lands on a
                                 * button, a field or a link belongs to that control — the
                                 * icon strip is all descendant buttons, so without this
                                 * every icon would also open the dialog behind itself.
                                 */
                                /**
                                 * ---- ⭐⭐ ONE CLICK SCOPES (decision 190, 2026-09-20) ----
                                 *
                                 * ⛔ THIS USED TO CALL `startEdit(c)`. Pete: *"I think the
                                 * driver panel has a one click to bring up driver detail, we
                                 * will change this to doubleclick."*
                                 *
                                 * ⚠️ THE DESCENDANT-CONTROL GUARD SURVIVES THE CHANGE and
                                 * matters more now, not less: the icon strip is all buttons,
                                 * and without this every icon press would also re-scope the
                                 * whole board behind the thing it opened.
                                 */
                                onClick={(e) => {
                                    const el = e.target as HTMLElement;
                                    if (el.closest('button,select,input,textarea,a,label')) return;
                                    if (busy !== null) return;
                                    /**
                                     * ⭐ A SECOND CLICK ON THE CHOSEN DRIVER RESETS
                                     * EVERYTHING, exactly as it does on a party card
                                     * (Pete, 2026-09-20, decision 194).
                                     *   ** THE WAY OUT SHOULD BE WHERE THE WAY IN WAS. **
                                     *
                                     * ⚠️ COMPARED BY `checkInId`, NOT BY THE FOCUS OBJECT.
                                     * Every render rebuilds the focus, so reference
                                     * equality would be false forever and the second
                                     * click would simply re-scope to the same man.
                                     *   ** A PREDICATE THAT IS TRUE BY REFERENCE IS TRUE
                                     *      BY LUCK. COMPARE THE VALUES. **
                                     *
                                     * ⛔ HERE, NOT INSIDE `chooseCheckIn`. That callback is
                                     * declared long before `resetAll`, and naming it in a
                                     * dependency array up there is a temporal dead zone
                                     * error. JSX runs after every declaration in the body.
                                     */
                                    /* ⭐ F130 (Decision 308): the single click WAITS for a second
                                       one. A double-click opens the check-in and leaves the scope
                                       alone; only a lone click scopes (or clears the chosen one). */
                                    clickIntent.click(`driver:${c.checkInId}`, () => {
                                        if (railFocus?.kind === 'driver'
                                            && railFocus.checkInId === c.checkInId) {
                                            resetAll();
                                            return;
                                        }
                                        chooseCheckIn(c);
                                    }, () => startEdit(c));
                                }}
                                /**
                                 * ---- ⭐⭐ DOUBLE CLICK OPENS (decision 190/191) ----------
                                 *
                                 * ⛔ THE SINGLE FIRES FIRST, TWICE, so the board is already
                                 * scoped to this driver when the popup opens. Accepted
                                 * deliberately — see §1AV.175. ⚠️ Which also RETIRES the
                                 * 2026-09-10 objection that killed double-click-to-edit
                                 * (*"it would open the options and then the form on top of
                                 * it"*): the single click no longer opens anything.
                                 * ⭐ AND `title="Double-click to edit"` — on this card since
                                 * 2026-09-10 and WRONG the whole time — is true again.
                                 */
                                onDoubleClick={(e) => {
                                    const el = e.target as HTMLElement;
                                    if (el.closest('button,select,input,textarea,a,label')) return;
                                    if (busy !== null) return;
                                    clickIntent.dbl(`driver:${c.checkInId}`, () => startEdit(c));
                                }}
                                /**
                                 * ---- ⭐⭐ RIGHT-CLICK, LIKE AN APPOINTMENT (#341) ----
                                 *
                                 * > Pete: *"Why can't you give me a dropdown too that has
                                 * > the same things as when you right click an
                                 * > appointment??????? So i have all options????"*
                                 *
                                 * ⚠️ AND THE (...) BUTTON IN THE TOOL STRIP DOES THE SAME.
                                 * Right-click alone is not discoverable — nothing on a card
                                 * advertises it, and a scheduler who never tries it simply
                                 * never finds half the options.
                                 *
                                 * ⛔ preventDefault, or the BROWSER menu opens over ours.
                                 */
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setCardMenu({ c, x: e.clientX, y: e.clientY });
                                }}
                                onKeyDown={(e) => {
                                    /* ⭐ ENTER DOES WHAT THE CLICK DOES. A click has no
                                       keyboard equivalent otherwise, and this used to open
                                       the editor — so it moves with it, or the two paths
                                       to the same card would disagree. */
                                    if (e.key === 'Enter' && e.target === e.currentTarget && busy === null) {
                                        e.preventDefault();
                                        startEdit(c);
                                    }
                                }}
                                title="Double-click to edit"
                                /* ⛔ #289 — THE CARD NO LONGER OPENS THE HISTORY. It used to
                                   call beginHover here, so crossing the rail with a mouse
                                   expanded every card on the way and shoved the one being
                                   aimed at out from under the cursor. The (i) icon below
                                   opens it now; the card only ever CLOSES it.

                                   ⚠️ onHoverAppointment STAYS. That is a different feature —
                                   it highlights the matching chip on the calendar and
                                   changes nothing about this rail's layout. */
                                /* ⭐ TWO THINGS ON ONE GESTURE, ON PURPOSE: the grid
                                   outlines the block IMMEDIATELY, and the paperwork tray
                                   follows only after the dwell. See `onHoverCardShown`. */
                                /* ⛔⚤ `beginTrayDwell(c)` REMOVED from the hover (decision
                                   190). The cursor still raises the matching chip on the
                                   grid — that is a highlight — but it no longer scopes. */
                                onMouseEnter={() => {
                                    onHoverAppointment?.(c.appointmentId, 'rail');
                                    /* ⭐ AND THE PANELS PREVIEW HIM — same rule as a party card.
                                       ⚠️ `focusFromCheckIn`, so a driver with no load still
                                       previews: Communications and History can scope him by
                                       phone and check-in id, which is the whole reason that
                                       constructor stopped returning null on 2026-09-10. */
                                    /* ⛔ THE LOOP BREAKER, 2026-09-25. This card is in the
                                       list `driverScope` filters, so the hover it raises must
                                       not reach that scope. Missing here is what flickered. */
                                    setOwnHover(true);
                                    onHoverParty?.(focusFromCheckIn(c));
                                }}
                                onMouseLeave={() => {
                                    onHoverAppointment?.(null, 'rail');
                                    endTrayDwell();
                                    setOwnHover(false);
                                    onHoverParty?.(null);
                                    /* ⛔⚤ ~~endHover(c.checkInId)~~ — the hover that
                                       opened the inline history is gone (2026-09-20). */
                                }}
                                /**
                                 * ---- ⭐ EDIT IS NOW A DOUBLE-CLICK, AND SINGLE-CLICK IS GONE
                                 *
                                 * Pete, 2026-08-25: *"you can remove the EDIT button and just
                                 * doubleclick on the driver card to edit."*
                                 *
                                 * ⚠️ SINGLE-CLICK ALREADY OPENED THE EDITOR (#141a) — so
                                 * removing the Edit button lost nothing, but LEAVING single-click
                                 * would have. With the button gone this card is a drop target for
                                 * paperwork, carries four icons, and is the thing a scheduler
                                 * clicks merely to look at. A modal that opens on a stray click,
                                 * mid-drag or mid-glance, is worse than one extra click.
                                 *
                                 * ⛔ THE `closest` CHECK IS STILL LOAD-BEARING and now guards
                                 * MORE than it did: every icon in the strip is a descendant
                                 * button, so without it a double-click that lands on the bin
                                 * would open the editor behind the confirm dialog.
                                 *
                                 * ⭐ AND ENTER DOES THE SAME THING (see the handler above) —
                                 * a double-click has no keyboard equivalent, so on its own it
                                 * would have made editing mouse-only.
                                 */
                                /**
                                 * ---- ⭐⭐ A MATCHED ARRIVAL DRAGS TOO (Pete, 2026-09-10)
                                 *
                                 * > *"the driver I am testing is attached to an appointment
                                 * > already but it should still allow me to drag him to
                                 * > another appointment"*
                                 *
                                 * ⛔ IT USED TO BE `draggable={unmatched}`, which read the
                                 * rule as "an arrival belongs to one load, so once it has
                                 * one there is nothing to do". ⚠️ That is true of where the
                                 * arrival ENDS UP and false about whether it can be
                                 * CORRECTED — a scheduler who attaches to the wrong load
                                 * had no way back except the menu.
                                 *
                                 * ⛔⛔ ONE TRUCK IS STILL ONE APPOINTMENT. The drop MOVES
                                 * the arrival; it never copies it, and the confirm dialog
                                 * says so in those words. See `attachCheckIn`'s note on the
                                 * 2026-08-19 rule and the A00000122 incident behind it.
                                 */
                                draggable
                                onDragStart={(e) => {
                                    // The grid's drop targets read this MIME type. Shared
                                    // constant, not a literal -- see lib/dnd.ts.
                                    e.dataTransfer.setData(CHECKIN_DND_MIME, c.checkInId);
                                    /* ⭐ ONLY WHEN HE IS ALREADY ON A LOAD. Absent means
                                       unmatched, which is the ordinary case and needs no
                                       warning — see CHECKIN_FROM_APPT_MIME. */
                                    if (c.appointmentNumber) {
                                        e.dataTransfer.setData(CHECKIN_FROM_APPT_MIME, c.appointmentNumber);
                                    }
                                    e.dataTransfer.effectAllowed = 'link';
                                }}
                                /**
                                 * ---- ⭐ PAPERWORK LANDS ON THE DRIVER (#150) ----
                                 *
                                 * Pete, 2026-08-25: *"drag the document to, or attach the
                                 * document to the driver or an appointment."*
                                 *
                                 * ⭐ AND THIS IS THE HALF THAT MATTERS FOR A WALK-IN. He has
                                 * no booking, so there is no chip on the grid to aim at —
                                 * his card is the only thing his bill of lading can be put
                                 * on. `fileDocument` links the appointment too when he has
                                 * one, and backfills it later when the scheduler makes him
                                 * one.
                                 *
                                 * ⚠️ EVERY CARD ACCEPTS IT, matched or not. Refusing on an
                                 * already-matched arrival would push the commonest case —
                                 * "here is the paperwork for the truck in front of me" —
                                 * into a dialog, to do exactly the same thing.
                                 */
                                onDragOver={(e) => {
                                    if (!isPaperworkDrag(e.dataTransfer)) return;
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'copy';
                                    setPaperworkOver(c.checkInId);
                                }}
                                onDragLeave={() => setPaperworkOver(
                                    (h) => (h === c.checkInId ? null : h))}
                                onDrop={(e) => {
                                    if (!isPaperworkDrag(e.dataTransfer)) return;
                                    e.preventDefault();
                                    setPaperworkOver(null);
                                    const payload = readPaperworkDrag(e.dataTransfer);
                                    // ⚠️ Reported, not ignored — a drop that quietly does
                                    // nothing looks exactly like a broken feature.
                                    if (!payload) {
                                        onDropRefused?.('That drag carried nothing to file.');
                                        return;
                                    }
                                    onDropPaperworkOnCheckIn?.({ payload: { items: payload }, checkIn: c });
                                }}
                            >
                                <div className="flex items-start gap-2">
                                    {/**
                                      * ⛔⛔ `w-12` IS NEW AND IT IS LOAD-BEARING (2026-09-24).
                                      *
                                      * This column held `#2 / arrival` and sometimes a `truck 2` badge, and
                                      * sized itself to whichever was widest -- so `#2` and `#12` produced
                                      * columns of DIFFERENT widths and every card's content started at a
                                      * slightly different x. Nobody had complained, but it is the same fault
                                      * Pete named on the status word an hour earlier, one column to the left.
                                      *
                                      * ⭐ AND IT IS WHAT MAKES THE FULL-WIDTH COMMS BOXES POSSIBLE. Those
                                      * boxes escape this column with a negative margin, and a negative margin
                                      * against a column that sizes to its content is a guess that is wrong on
                                      * most cards.
                                      *   ** YOU CANNOT REACH PAST A THING WHOSE WIDTH YOU DO NOT KNOW. **
                                      * ⚠️ 3rem holds `arrival` at 10px and the `truck N` badge; the badge
                                      * wraps rather than widening the column, which is the right trade -- the
                                      * arrival number is what this column is for.
                                      */}
                                    <div className="w-12 shrink-0 text-center">
                                        <div className="font-semibold text-sm leading-none">
                                            #{c.arrivalSeq ?? '—'}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5">arrival</div>
                                        {c.clientTruckNo !== null && (
                                            (() => {
                                                // ⚠️ THE TRUCK BADGE IS THE STATUS COLOUR, SOLID,
                                                // WITH WHITE TEXT -- the same treatment the block on
                                                // the grid gets, not a tint of it.
                                                //
                                                // It was blue always, then briefly an 18% wash of
                                                // the status colour. Both were wrong for the same
                                                // reason: a scheduler glancing between the panel and
                                                // the grid has to see ONE colour for one truck. A
                                                // pale green badge beside a solid green block reads
                                                // as a different state, not the same one.
                                                //
                                                // Slate when the arrival is attached to nothing --
                                                // there is genuinely no status to mirror then, and
                                                // in practice the badge is absent anyway because the
                                                // truck number stays NULL until the client is known.
                                                const st = c.appointmentId
                                                    ? apptById.get(c.appointmentId)?.statusCode ?? null : null;
                                                const v = st ? `--status-${st.toLowerCase()}` : null;
                                                return (
                                                    <div
                                                        className="mt-1 rounded px-1 py-0.5 text-[10px] font-semibold"
                                                        title={clientTruckTitle(c)}
                                                        style={v
                                                            ? { backgroundColor: `rgb(var(${v}))`, color: '#fff' }
                                                            : { backgroundColor: '#64748b', color: '#fff' }}
                                                    >
                                                        truck {c.clientTruckNo}
                                                    </div>
                                                );
                                            })()
                                        )}
                                        {/**
                                          * ---- ⭐⭐ THE CARD'S CHEVRON, IN THE LEFT COLUMN ---------
                                          *
                                          * > Pete, 2026-09-08: *"the driver card needs to have its own
                                          * > drop down arrow so it just shows the Truck arrival number
                                          * > row"* … *"should be in the lower left hand corner"* …
                                          * > *"needs to be aligned vertically with the other drop
                                          * > arrows for Scanned Paperwork, Labor, Communications."*
                                          *
                                          * ⚠️ THREE POSITIONS IN ONE AFTERNOON, and only the last one
                                          * satisfies all three sentences:
                                          *   1. ~~title row, before IN/OUT~~ — pushed the driver's
                                          *      name right on every card, and the name is the thing a
                                          *      scheduler scans a stack FOR.
                                          *   2. ~~tool-strip row, `mr-auto`~~ — lower-left of the card,
                                          *      but still inside the RIGHT-HAND column, so it sat
                                          *      indented past the arrival number and lined up with
                                          *      nothing.
                                          *   3. ⭐ HERE: the left column, under the arrival number.
                                          *      This is the leftmost content the card has, so it is
                                          *      the only place it can share a vertical line with the
                                          *      rail cards' chevrons.
                                          *
                                          * ⚠️ `justify-start` OVERRIDES THE COLUMN'S `text-center`.
                                          * The number and the truck badge are centred in this column
                                          * on purpose; the chevron must not be, or it drifts right by
                                          * half the column and the alignment is lost again.
                                          *
                                          * ⛔ IT STOPS BOTH EVENTS. The card edits on DOUBLE-click, so
                                          * an un-stopped double tap here would toggle twice AND open
                                          * the editor behind it — `onClick` does not cover dblclick,
                                          * which is a separate event on the parent.
                                          */}
                                        <div className="mt-1 flex justify-start">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); toggleCard(c.checkInId); }}
                                                onDoubleClick={(e) => e.stopPropagation()}
                                                aria-expanded={detailOpen}
                                                aria-label={detailOpen ? 'Collapse this arrival' : 'Expand this arrival'}
                                                title={detailOpen ? 'Collapse' : 'Expand'}
                                                className="-ml-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                            >
                                                {detailOpen
                                                    ? <ChevronDown className="h-4 w-4" />
                                                    : <ChevronRight className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5">
                                            {/* ⚠️ IN / OUT SITS BESIDE THE NAME, NOT IN THE
                                                DETAIL LINE. It decides which supervisor the
                                                truck belongs to, so it has to survive a
                                                glance down a list of twenty cards. Absent
                                                when we do not know -- an unmatched walk-up
                                                has not told us, and guessing "IN" because
                                                most trucks are inbound would be a lie a
                                                scheduler would act on. */}
                                            {/* ⭐ F70 (2026-09-28): Pete — *"use the calendar version
                                                of the icons in the driver card."* The dark slate / indigo
                                                tag is gone; this is the calendar chip's own component. */}
                                            {c.direction && (
                                                <DirectionTag direction={c.direction === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND'} />
                                            )}
                                            {/* ⚠️ `card-title` IS THE STRIKE-THROUGH TARGET for a
                                                departed arrival — see the card's className. Only
                                                the NAME is ruled through, never the whole card:
                                                striking the times and the paperwork links would
                                                make evidence look retracted. */}
                                            {/**
                                              * ⛔ THE STATUS CAME OFF THIS LINE ON 2026-08-27.
                                              *
                                              * Pete: *"we should put the status - in front of it
                                              * and remove the '- status' from the right of the
                                              * drivers name. This will be a better spot."*
                                              *
                                              * ⚠️ IT WAS HIS OWN EARLIER LAYOUT (*"Pete Lombardo -
                                              * Arrived"*, 08-20) and it stopped working when the
                                              * name line got long: the status sat INSIDE the
                                              * truncating span, so on any real driver name it was
                                              * the first thing cut. ⭐ The most important word on
                                              * the card was the one most likely to be missing.
                                              *
                                              * ⭐ IT NOW LEADS THE LINE BELOW, where it is first
                                              * rather than last and nothing truncates it.
                                              */}
                                            {/**
                                              * ⛔⛔ ~~THE PHONE STANDS IN FOR THE NAME~~ — REVERSED THE
                                              * SAME DAY IT SHIPPED, once Pete saw it on a real card.
                                              *
                                              * > *"Cell phone is showing twice even after the scan has
                                              * > the drivername. After review, don't have the driver
                                              * > cell number at all after the OUT icon. Just leave it
                                              * > blank if no driver."*
                                              *
                                              * ⚠️ STRUCK WHERE IT LIVES RATHER THAN DELETED (§0.3), and
                                              * the reasoning below is kept ON PURPOSE: the problem it
                                              * names is real and did not go away. A stack of un-named
                                              * arrivals IS hard to tell apart.
                                              *
                                              * ⭐ WHAT CHANGED IS THE ANSWER, NOT THE PROBLEM. The
                                              * number already renders in the facts row one line down —
                                              * so the stand-in was never adding information, only a
                                              * second copy of it. ⛔ THE COST OF THAT COPY WAS NOT
                                              * COSMETIC: it occupied roughly ninety pixels of the facts
                                              * row, and the check-in time was the only element on that
                                              * row still free to shrink. The time collapsed to "2..."
                                              * — see the fix below.
                                              *
                                              * ⚠️ AND A DUPLICATE IS NOT A NEUTRAL REDUNDANCY. Two
                                              * renderings of one field is two things that can disagree
                                              * — a reader who sees them differ has no way to tell which
                                              * is stale.
                                              *
                                              * ---- ~~superseded, retained for the reasoning~~ --------
                                              *
                                              * ⭐⭐ THE PHONE STANDS IN FOR THE NAME UNTIL THERE IS
                                              * ONE (Pete, 2026-09-08).
                                              *
                                              * > *"The phone number needs to be where the driver name
                                              * > is during the initial checkin, if there is no driver
                                              * > name and then it needs to move back to where it is
                                              * > now when a driver name is added to the record. So
                                              * > when rolled up, you always either see the cell
                                              * > number or the driver name."*
                                              *
                                              * ⛔ THE POINT IS THAT A ROLLED-UP CARD IS NEVER
                                              * ANONYMOUS. At initial check-in a driver has given a
                                              * number and not yet a name, and a stack of cards all
                                              * reading "Driver not named" is a stack nobody can tell
                                              * apart — which is precisely when the rail is fullest.
                                              *
                                              * ⭐ IT SWAPS LIVE, with no interaction: the moment a
                                              * name reaches the record this renders the name and the
                                              * number returns to the line below, where it still is.
                                              * ⚠️ The number is NOT removed from that line — Pete:
                                              * *"Both, when a name exists."*
                                              *
                                              * ⚠️ "Driver not named" SURVIVES for the arrival that
                                              * gave neither. It is not a fallback for the phone; it
                                              * is the third case, and it still has to say something.
                                              */}
                                            {/**
                                              * ---- ⭐⭐⭐ THE NAME ABSORBS THE SLACK (Pete, 2026-09-24) ----
                                              *
                                              * > *"the status in the driver panel always should be in the same
                                              * > spot how it is in #13. That leaves space for the warning icons
                                              * > to the right and driver name to the left. Driver name, if too
                                              * > long, should get cutoff by the status so its all consistent."*
                                              *
                                              * ⛔⛔ `flex-1 min-w-0` IS THE WHOLE FIX AND IT IS ONE CLASS.
                                              * Without it this div sized to its CONTENT, so the status sat
                                              * wherever the name happened to end -- far left on ANDMED, far
                                              * right on HAITHAM MOHAMMAD. Two cards, two positions, and nothing
                                              * in the markup deciding either.
                                              *   ** A SIBLING THAT SIZES TO ITS CONTENT DECIDES WHERE EVERY
                                              *      SIBLING AFTER IT LANDS. **
                                              * ⭐ Now the name always takes the remaining width, so the status
                                              * starts at a constant x and a long name truncates INTO it, which
                                              * is exactly the behaviour Pete described.
                                              * ⚠️ `min-w-0` IS NOT OPTIONAL WITH `flex-1`: a flex item's default
                                              * min-width is its content, so `truncate` cannot take effect and the
                                              * row overflows instead of ellipsising.
                                              */}
                                            <div className="card-title min-w-0 flex-1 font-medium truncate">
                                                {/* ⚠️ ~~BLANK~~ — Pete asked for blank at 15:10 and for
                                                    words back an hour later, having seen it: *"I want
                                                    to add back in 'Awaiting Driver Name' next to the
                                                    IN button."*
                                                    ⭐ THE REASON IS WORTH KEEPING because it is not a
                                                    reversal, it is a third answer. The number was
                                                    wrong here (it is one row down already), but so was
                                                    nothing: a title row holding only an IN badge reads
                                                    as a card that failed to load. ⚠️ The placeholder
                                                    says a name is COMING, which is the true state of a
                                                    driver who has checked in and not yet been carded —
                                                    "Driver not named" would accuse the record. */}
                                                {/**
                                                  * ⛔⛔ THE PLACEHOLDER KEEPS THE NAME'S WEIGHT AND
                                                  * COLOUR. ~~`font-normal italic text-muted-foreground`~~
                                                  * lasted one deploy.
                                                  *
                                                  * > Pete: *"I want the coloring and formatting back of
                                                  * > the text… Can you not see the different in the look
                                                  * > of the two?"*
                                                  *
                                                  * ⚠️ HE IS RIGHT AND THE REASON IS ARITHMETIC, NOT
                                                  * TASTE. Count the dark text on an UNMATCHED card:
                                                  * `statusWord` returns null for one (the card already
                                                  * shouts "No appointment"), there is no door yet, and
                                                  * the truck badge needs a client. ⭐ THE TITLE IS THE
                                                  * ONLY WEIGHTED TEXT LEFT ON IT. Muting that one
                                                  * drained the card to flat grey — every remaining
                                                  * character was `text-muted-foreground`.
                                                  *
                                                  * ⛔ SO A PLACEHOLDER IS NOT AUTOMATICALLY SECONDARY.
                                                  * The reflex is to grey out what is "not real data";
                                                  * on a card where it is the last thing with contrast,
                                                  * that reflex removes the card's whole visual anchor.
                                                  *
                                                  * ⚠️ IT INHERITS from `card-title font-medium` on the
                                                  * parent rather than restating them — that class is
                                                  * also the strike-through target for a departed
                                                  * arrival, and a nested span with its own weight would
                                                  * survive the strike looking untouched.
                                                  */}
                                                {c.driverName || 'Awaiting driver name'}
                                            </div>
                                            {/**
                                              * ---- ⭐⭐ THE STATUS, BACK BESIDE THE NAME (2026-09-09) --------
                                              *
                                              * > Pete: *"The status could move to the right of driver name and
                                              * > always be on top (Onsite, etc). That should free up space and
                                              * > make it look better."*
                                              *
                                              * ⛔⛔ THIS IS THE THIRD POSITION FOR THE STATUS AND THE SECOND
                                              * TIME IT HAS BEEN HERE. Read the struck note above before moving
                                              * it again — the August failure was real and this is not a repeat
                                              * of it.
                                              *
                                              * ⚠️ IN AUGUST IT SAT **INSIDE** THE TRUNCATING NAME SPAN, so on any
                                              * real driver name the status was the FIRST thing cut — the most
                                              * important word on the card was the one most likely to be missing.
                                              *
                                              * ⭐ IT IS A `shrink-0` SIBLING NOW, not a child. The name truncates
                                              * and the status cannot, which is the correct priority: a clipped
                                              * name still identifies a driver, a missing status does not identify
                                              * anything. ⛔ **Never put it back inside the `card-title` div.**
                                              *
                                              * ⭐ AND IT PAYS FOR ITSELF TWICE. Leaving the facts row frees ~60px
                                              * there, which is what stopped a phone number wrapping across three
                                              * lines; and the status is now on the row that is ALWAYS visible, so
                                              * a collapsed card still says what the truck is doing.
                                              */}
                                            {/**
                                              * ⛔⛔ THE SLOT IS RENDERED EVEN WITH NO STATUS, and that is the
                                              * half of Pete's request that is easy to miss. An unmatched arrival
                                              * has no status word; if the span simply vanished, the warning icons
                                              * after it would slide left and the row would be inconsistent again
                                              * for exactly the cards that carry the most warnings.
                                              *   ** RESERVING A SLOT IS WHAT MAKES A POSITION CONSTANT. HIDING AN
                                              *      EMPTY ONE MAKES IT CONSTANT ONLY WHILE IT IS FULL. **
                                              * ⚠️ WIDTH SIZED TO THE LONGEST WORD PLUS THE MARK: "Unloading" and
                                              * "Cancelled" are the longest of the seven, and Loading/Unloading are
                                              * the two that also carry the orbiting truck. ⛔ Not `min-w`: a
                                              * minimum that one word exceeds is not a fixed column.
                                              * ⚠️ `justify-end` PINS THE WORD TO THE SLOT'S RIGHT EDGE, so the
                                              * gap between name and status varies with the word's length rather
                                              * than the status moving.
                                              */}
                                            <span className="flex w-[6.25rem] shrink-0 justify-end" aria-hidden={!statusWord(c)}>
                                            {statusWord(c) && (
                                                <span className={`flex shrink-0 items-center gap-1 whitespace-nowrap font-semibold ${statusTone(statusWord(c))}`}>
                                                    {/**
                                                      * ⭐ THE TURNING TRUCK, WHILE IT IS BEING WORKED (#292).
                                                      *
                                                      * Pete: *"In the driver card, it says loading…. Can we
                                                      * add the Truck with Circle Icon to it so its consistent
                                                      * with the appointment."*
                                                      *
                                                      * ⭐ THE SAME GLYPH AND THE SAME 2.4s ROTATION as the
                                                      * appointment chip and the wall board, so one symbol
                                                      * means one thing on all three surfaces.
                                                      *
                                                      * ⛔ NOT `LaborPulse`. That renders WHITE for saturated
                                                      * tiles; this card is a pale grey box and a white truck
                                                      * on it is an invisible truck. `currentColor` picks up
                                                      * the emerald the word is already wearing.
                                                      *
                                                      * ⚠️ ONLY WHILE WORK IS ACTUALLY RUNNING — `c.work` is
                                                      * what makes `statusWord` say Loading or Unloading, so a
                                                      * spinning mark beside "Departed" is impossible here.
                                                      */}
                                                    {c.work && (
                                                        <WorkTruckMark
                                                            wrapClassName="relative inline-flex h-3.5 w-3.5 shrink-0"
                                                            ringClassName="absolute inset-0 h-full w-full animate-spin motion-reduce:[animation-duration:8s]"
                                                            truckClassName="absolute left-[23%] top-[23%] h-[54%] w-[54%]"
                                                            ringStyle={{ animationDuration: `${WORK_MARK_SPIN_SECONDS}s` }}
                                                            ringStrokeWidth={4.5}
                                                            truckStrokeWidth={2.6}
                                                        />
                                                    )}
                                                    {statusWord(c)}
                                                </span>
                                            )}
                                            </span>
                                            {/**
                                              * ---- ⭐⭐ WARNINGS, TOP-RIGHT, ALWAYS VISIBLE (2026-09-09) -----
                                              *
                                              * > Pete: *"the little warning symbol for no commerical license (and
                                              * > maybe others in the future), should show in the top panel in the
                                              * > very far right, upper corner notifing the scheduler that there is
                                              * > a warning."*
                                              *
                                              * ⛔⛔ THE LICENCE WARNING USED TO EXIST **ONLY** INSIDE THE DETAIL
                                              * REGION, which now collapses by default. So the card that most needs
                                              * a second look was the card giving no sign it needed one — a warning
                                              * visible only to somebody who already opened the card is not a
                                              * warning, it is a confirmation.
                                              *
                                              * ⭐ IT IS A LIST, NOT A FIELD, BECAUSE PETE SAID "AND MAYBE OTHERS".
                                              * A second warning added later appends here; it does not need this
                                              * markup rewritten, and it cannot be added as a sibling that quietly
                                              * pushes the first one off the row.
                                              *
                                              * ⚠️ THE FULL TEXT STAYS IN THE DETAIL BELOW. This mark says THAT
                                              * there is a problem and the amber body says WHAT — a truncated
                                              * warning on a 340px rail would be worse than none, because a warning
                                              * cut in half reads as a different warning.
                                              *
                                              * ⭐ `ml-auto` PINS IT RIGHT. It is the last child of the title row,
                                              * so one class puts it in the corner and leaves the status sitting
                                              * beside the name where Pete asked for it.
                                              *
                                              * ⛔ CLICKING OPENS THE LICENCE, not a tooltip-only dead icon. The
                                              * warning names a thing the scheduler has to go and look at, and the
                                              * shortest path to looking at it is the point.
                                              */}
                                            {(() => {
                                                /* ⭐ ONE DEFINITION — see `arrivalWarnings`. The header
                                                   summary counts the same list, so the badge and the
                                                   count cannot disagree. */
                                                const warnings = arrivalWarnings(c).map(w => w.text);
                                                if (warnings.length === 0) return null;

                                                /**
                                                 * ---- ⛔⛔ ~~THE CLICK GOES TO THE MOST ACTIONABLE ONE~~ ----
                                                 *      REVERSED SAME DAY. Pete, 2026-09-09:
                                                 *
                                                 * > *"The warning icon should only list the warnings when you
                                                 * > hover over it."*
                                                 *
                                                 * ⚠️ WHAT IT USED TO DO, and why it had to stop: it picked ONE
                                                 * warning — licence if there was one, otherwise the load picker
                                                 * — and jumped straight into that dialog. On a card wearing two
                                                 * warnings that is a guess, made silently, that throws a modal
                                                 * over the rail. A scheduler who clicked because she wanted to
                                                 * know WHICH TWO got the answer to a question she did not ask,
                                                 * and lost the card behind it.
                                                 *
                                                 * ⭐⭐ SO THE ICON IS A SIGNPOST AND THE BREAKOUT IS WHERE YOU
                                                 * ACT. Hover lists every warning; click OPENS THE CARD, where —
                                                 * as of the same message — each warning row is itself the
                                                 * control for its own fix:
                                                 *   · "Not a commercial licence…" -> the licence scan
                                                 *   · "No appointment"            -> the new-appointment dialog
                                                 * ⭐ Two warnings, two destinations, chosen by the person who
                                                 * can see both. That is the thing one click could never do.
                                                 *
                                                 * ---- ⛔ IT IS `toggleCard`, NOT "open" ---------------------
                                                 *
                                                 * ⚠️ An icon that opens but cannot close is a control that does
                                                 * NOTHING on its second press — the failure this file keeps
                                                 * finding, green and silent. It is the same call the chevron
                                                 * makes, so the two agree, and `toggleCard` carries the history
                                                 * pin/unpin with it.
                                                 *
                                                 * ⛔ DO NOT RE-ADD A DIALOG HERE. `setLicenceFor` and
                                                 * `setMatchFor` are still reachable — the tool strip's licence
                                                 * icon and "Find load…" — and the breakout rows above. This
                                                 * icon's job is to say HOW MANY and WHICH, nothing else.
                                                 */
                                                return (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); toggleCard(c.checkInId); }}
                                                        onDoubleClick={(e) => e.stopPropagation()}
                                                        /* ⚠️ NEWLINES, NOT SPACES. Pete asked it to "list" the
                                                           warnings; three full sentences run together on one
                                                           line is a paragraph, and the second warning is the
                                                           one that gets missed. A title attribute honours \n. */
                                                        title={warnings.join('\n')}
                                                        aria-label={`${warnings.length} warning${warnings.length === 1 ? '' : 's'}: ${warnings.join(' ')}`}
                                                        /* ⚠️ `ml-auto` KEEPS IT ON THE RIGHT EDGE and is why this
                                                           one is still hand-drawn rather than a `CountBadge`: the
                                                           badge owns its own layout classes and this site needs a
                                                           positioning one the others must not inherit. ⛔ THE
                                                           INNER SHAPE IS IDENTICAL, deliberately — if the circle
                                                           below ever diverges from `CountBadge`, that is the bug. */
                                                        className="ml-auto flex shrink-0 items-center gap-0.5 rounded px-0.5 text-amber-700 hover:bg-amber-100"
                                                    >
                                                        <AlertTriangle className="h-4 w-4" />
                                                        {/* ⭐⭐ THE COUNT IS ALWAYS SHOWN, AND THAT REVERSES THIS
                                                            MORNING'S "only above one".

                                                            ⚠️ ~~A "1" beside a single warning is noise~~ — it is not,
                                                            once "no appointment" is one of the things being counted.
                                                            Pete's point exactly: the number is how a scheduler knows
                                                            the yellow is *"just not needs an appointment"*. Hiding it
                                                            at one puts the reader back to guessing on the commonest
                                                            card of all.

                                                            ⭐ A FILLED CIRCLE, RIGHT OF THE ICON — the same shape the
                                                            eye already uses for its paperwork count, so one badge
                                                            means one thing on this card. */}
                                                        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-600 px-1 text-[9px] font-bold leading-none text-white">
                                                            {warnings.length}
                                                        </span>
                                                    </button>
                                                );
                                            })()}
                                        </div>

                                        {/**
                                          * ⭐ THE TEXTING MENU, INLINE UNDER THE NAME. Same
                                          * reasoning as the history log below: this panel is a
                                          * narrow right rail, and a floating popover would either
                                          * overflow the viewport or cover the next card — which is
                                          * the one the scheduler is comparing against.
                                          *
                                          * ⚠️ EACH OPTION SAYS WHAT IT SENDS. Three items in this
                                          * menu once opened with the words "Text driver" and a
                                          * scheduler could not tell a map link from a dock door
                                          * from "your paperwork is ready" (Pete, 2026-08-20:
                                          * *"otherwise scheduler won't know"*).
                                          */}
                                        {/**
                                          * ---- ⭐ THE TOOLS SIT ON THE TIME LINE, NOT THE NAME LINE
                                          *
                                          * Pete, 2026-08-25: *"it lists the driver .... then the icons,
                                          * what is covered up? That needs to go on the next line so its
                                          * not covered."*
                                          *
                                          * ⛔ HE COULD NOT READ THE DRIVER'S NAME. Four icons beside it
                                          * ate the width, and the name — the single most important
                                          * thing on the card — truncated to "Pete Lombardo Driver …".
                                          *
                                          * ⭐ SO THEY MOVED DOWN ONE LINE RATHER THAN ONTO A NEW ONE.
                                          * The time-and-carrier line is short and never fills the card,
                                          * so the strip costs nothing here — where its own row would
                                          * have given back the vertical space this whole redesign was
                                          * about reclaiming.
                                          */}
                                        <div className="flex items-center gap-2">
                                            {/**
                                              * ⛔ THE CELL NUMBER BELONGS HERE. Pete, 2026-08-25:
                                              * *"doesn't show his cell phone number now which is
                                              * still important to see"*.
                                              *
                                              * ⚠️ IT WAS DISPLACED BY HIS OWN EARLIER FIX — the icon
                                              * strip moved onto this line to stop the driver's name
                                              * truncating, and the phone lost its place in the move.
                                              *
                                              * ⭐ IT EARNS THE SPACE. Migration 144 calls the phone
                                              * the real-world lookup key, and it is how a scheduler
                                              * actually reaches the man standing in the lobby. The
                                              * carrier is the one that gives way when the line is
                                              * tight: it is already on the load.
                                              */}
                                            {/**
                                              * ⛔⛔ THE PHONE CAME OFF THIS LINE ON 2026-08-27 AND WENT
                                              * TO ITS OWN ROW BELOW — it did NOT come off the card.
                                              *
                                              * Pete: *"Because of all the icons on the driver card, We
                                              * will have to drop either the time or the cell phone
                                              * number to the next line."*
                                              *
                                              * ⭐ THE PHONE IS THE ONE THAT MOVES, NOT THE TIME, AND
                                              * THE REASON IS TRUNCATION, NOT LENGTH. `hhmm` is five
                                              * characters and cannot be cut into something wrong.
                                              * A phone number cut short — "(616) 555-12…" — still
                                              * LOOKS like a phone number, and somebody will dial it.
                                              * ⚠️ A half-identifier that reads as whole is the same
                                              * failure as a truncated container number.
                                              *
                                              * ⚠️ SO THE ROW BELOW DOES NOT TRUNCATE EITHER. It wraps.
                                              * The card grows by a line on a narrow rail; that is the
                                              * price of a number that is either right or absent.
                                              *
                                              * ⭐ TIME AND CARRIER STAY HERE because they are short,
                                              * and because this line has to hold SOMETHING: it is the
                                              * icon strip's row, and an empty left side would let the
                                              * tools drift under the driver's name.
                                              */}
                                            {/**
                                              * ⛔⛔ THE TIME HAS ITS OWN SLOT AND NEVER TRUNCATES (2026-09-08, second pass).
                                              *
                                              * ⚠️ IT SHIPPED BROKEN THAT MORNING AND THE SCREENSHOT IS THE RECORD: the card
                                              * read **"2:55 ..."**, and on a card whose status word had grown from "Arrived"
                                              * to "Loading", **"2..."**.
                                              *
                                              * ⭐⭐ THE CAUSE IS WORTH MORE THAN THE FIX. This span carried `flex-1 truncate`
                                              * from a time when it was the ONLY text on the line, with fixed-width icons
                                              * beside it — correct then. The regroup moved status, cell number and door onto
                                              * this row, and every one of them is `shrink-0` or fixed for good reasons of its
                                              * own. ⛔ THAT LEFT EXACTLY ONE ELEMENT ABLE TO YIELD, SO IT YIELDED ALL OF IT.
                                              *
                                              * ⚠️ NOBODY WROTE "SHRINK THE TIME". It is what `flex-1 truncate` MEANS once it
                                              * is the last flexible thing in a row — the property was unchanged and its
                                              * consequence inverted. ⭐ A layout rule is a statement about a row, not about
                                              * an element, and it has to be re-read every time the row gains a member.
                                              *
                                              * ⛔ AND IT DEGRADED IN THE WORST DIRECTION: "2..." is not a shortened time, it
                                              * is no time at all, while still occupying the place a reader looks for one.
                                              * The comment below already argued a half-identifier that reads as whole is the
                                              * real danger — it was applied to the phone and not to its own line.
                                              *
                                              * ⭐ THE CARRIER IS THE ONE THAT GIVES WAY, as the note below always said it
                                              * should: it is short, it is on the appointment one line down, and a clipped
                                              * carrier code costs nothing. ⚠️ ITS SPAN RENDERS EVEN WHEN EMPTY — it holds
                                              * the `flex-1` that keeps status, number and door in the same place on every
                                              * card, and a row that reshapes itself per card is unreadable in a stack.
                                              */}
                                            <span className="shrink-0 text-muted-foreground">
                                                {hhmm(c.checkedInUtc)}
                                            </span>
                                            {/**
                                              * ⭐ F103 — THE DETENTION CLOCK (Decision 280). Pete: *"2 hour turn
                                              * is called detention … We definitely need to track that on trucks"*.
                                              *
                                              * ⭐ F109 (2026-09-29) — Pete: *"can you make it orange under 2 hours
                                              * and Red at 2 hours and beyond?"* ⛔⤴ ~~shown only inside the amber
                                              * lead~~: the chip is now on EVERY on-site card, ORANGE while the truck
                                              * is inside its free time and RED from the free time on. (The amber
                                              * lead setting no longer changes the colour.) ⭐ Only while the truck
                                              * is on site: a departed truck's detention is a report number.
                                              *
                                              * ⭐ F155 (Decision 329) — ⛔⤴ ~~orange for the whole free time, counting
                                              * up~~: it COUNTS DOWN the time left — arrived colour, orange at the
                                              * orange lead, red at the red lead, then "Xm over" in red.
                                              */}
                                            {(() => {
                                                if (departed(c)) return null;
                                                const appt = c.appointmentId ? apptById.get(c.appointmentId) : undefined;
                                                /* ⛔ F110b — only an Approved or Arrived appointment is on the clock (the tally uses the same test). */
                                                if (!appt || !onDetentionClock(appt)) return null;
                                                /* ⭐ F173 — this truck's client (and direction) free time */
                                                const truckDet = settingsFor(detention, appt.clientCode, appt.direction);
                                                const st = detentionState({
                                                    arrivalIso: appt?.actualArrivalUtc ?? c.checkedInUtc,
                                                    scheduledStartIso: appt?.scheduledStartUtc ?? null,
                                                    timed: !!appt && appt.schedulingMode !== 'DayOnly',
                                                    departureIso: appt?.actualDepartureUtc ?? null,
                                                }, truckDet, now);
                                                if (!st || st.level === 'off') return null;
                                                const over = st.level === 'over';
                                                const title = over
                                                    ? `In detention ${formatMinutes(st.overMinutes)} — clock started ${hhmm(st.clockStartIso)}, time to load or unload ${formatMinutes(truckDet?.freeMinutes ?? 0)}`
                                                    : `Detention in ${formatMinutes(st.minutesLeft ?? 0)} — clock started ${hhmm(st.clockStartIso)}, time to load or unload ${formatMinutes(truckDet?.freeMinutes ?? 0)}`;
                                                return (
                                                    <span
                                                        title={title}
                                                        data-detention={st.level}
                                                        /* ⭐ F159 — Pete: *"DD4x is off the page"*. "1h 00m left" pushed the door
                                                           off the card. Minutes only ("60m left") keeps it the width "49m left"
                                                           always was; and the chip may give way a little (flexShrink 0.25) before
                                                           the door is ever cut, as a last resort for long free times. */
                                                        className={`inline-flex min-w-0 items-center gap-0.5 overflow-hidden rounded px-1 text-[11px] font-medium tabular-nums ${detentionChipTone(st.level)}`}
                                                        style={{ flexShrink: 0.25 }}
                                                    >
                                                        <Timer className="h-3 w-3 shrink-0" aria-hidden="true" />
                                                        <span className="truncate">{detentionLabel(st, { minutesOnly: true })}</span>
                                                    </span>
                                                );
                                            })()}
                                            {/**
                                              * ⛔⛔ THE CARRIER IS `shrink`, NOT `flex-1`, AND THE FIRST GO GOT THIS
                                              * WRONG IN THE OPPOSITE DIRECTION.
                                              *
                                              * ⚠️ Pete, on the deployed card: *"What happen to all the formatting?"* —
                                              * an arrival with NO carrier rendered an EMPTY `flex-1` span, which
                                              * happily ate every spare pixel and threw the status, the number and the
                                              * door to the far right. ⭐ The row had not lost its formatting; it had
                                              * an invisible element holding the middle of it open.
                                              *
                                              * ⭐⭐ AND THAT IS THE SAME MISTAKE AS THE TRUNCATED TIME, ONE STEP ON:
                                              * a flex row gives its slack to whatever will take it, so the question is
                                              * never "what should stretch" but "what happens when the thing that
                                              * stretches is EMPTY". `flex-1` on optional content is a hole.
                                              *
                                              * ⚠️ IT RENDERS ONLY WHEN THERE IS A CARRIER. An always-present span is
                                              * what created the hole; `shrink` + `truncate` lets a long code give way
                                              * without any of them reserving space that may hold nothing.
                                              */}
                                            {c.carrierCode && (
                                                <span className="min-w-0 shrink truncate text-muted-foreground">
                                                    · {c.carrierCode}
                                                </span>
                                            )}
                                            {/**
                                              * ⭐⭐ THE FACTS ON ONE LINE, THE TOOLS ON THE NEXT (Pete, 2026-09-08).
                                              *
                                              * > *"maybe move the icons to the bottom row of the collapsed panel and the
                                              * > Arrived, cell number and door to where the icons are now - would look cleaner."*
                                              *
                                              * ⭐ THIS ROW NOW READS AS FACTS AND THE ONE BELOW AS ACTIONS. Status, number
                                              * and door take the space the icon strip vacated — where the eye already went.
                                              *
                                              * ⚠️ THE TIME KEEPS THE LEFT EDGE and its `flex-1`. Everything hoisted here sits
                                              * to its RIGHT, exactly where the icons were, so the line keeps its old shape.
                                              *
                                              * ⛔ EACH PIECE KEEPS ITS OWN CONDITIONAL. Neither status nor number is always
                                              * present, and the row must never render an orphan separator.
                                              */}
                                            {/**
                                              * ⛔⛔ THE CELL NUMBER CAME BACK OFF ITS OWN LINE (2026-09-08), REVERSING
                                              * 2026-08-27. ~~"THE CELL NUMBER, ON ITS OWN LINE"~~ is struck rather than
                                              * deleted: the reasoning was sound and only the constraint changed. A line was
                                              * spent to stop the number crowding the name; the icon strip leaving this row
                                              * freed the width to hold both.
                                              *
                                              * ⭐ STILL TRUE, AND STILL THE REASONS: migration 144 calls the phone the
                                              * real-world lookup key, and it is how a scheduler reaches the man in the lobby.
                                              * `[overflow-wrap:anywhere]` and NO `truncate` — a number cut short still reads
                                              * as a number, and this one gets dialled. The icon is the label; spelling out
                                              * "Cell" would cost more width than it saves.
                                              *
                                              * ⚠️ NOTHING RENDERS WHEN THERE IS NO PHONE — an empty slot with a handset on it
                                              * says we are missing something we are not.
                                              *
                                              * ⛔⛔ ~~AND IT IS NO LONGER THE ONLY PLACE THE NUMBER APPEARS~~ — IT IS AGAIN,
                                              * AND THAT IS NOW THE POINT. The stand-in above lasted one morning; Pete saw
                                              * the number rendered twice on one card and had it taken off the title line.
                                              *
                                              * ⭐ SO THIS IS THE ONLY PLACE THE CELL NUMBER APPEARS ON A COLLAPSED CARD.
                                              * ⚠️ ANYTHING THAT REMOVES IT FROM HERE REMOVES IT FROM THE CARD — there is no
                                              * longer a second copy to fall back on, and migration 144 calls this field the
                                              * real-world lookup key. Read that before deciding this row is crowded.
                                              */}
                                                {/**
                                                  * ⭐⭐ THE STATUS LEADS, IN THE STATUS'S OWN COLOUR
                                                  * (2026-08-27). Pete: *"the status Arrived,
                                                  * Approved, etc. Should be highlighted or bolded to
                                                  * stand out"* … *"And the color of the text should
                                                  * be the color of the status."*
                                                  *
                                                  * ⭐ THE SAME COLOUR THE TILE USES. These are the
                                                  * `--status-*` variables the admin sets in Status
                                                  * Colours (migration 135) and every board already
                                                  * paints with — so a card and its chip agree, and
                                                  * re-tinting one re-tints both.
                                                  *
                                                  * ⛔ THE CLASSES ARE LITERAL, NEVER BUILT. Tailwind
                                                  * scans source text, so `text-status-${x}` compiles
                                                  * to a class that is never generated — the word
                                                  * would render in the inherited colour and look
                                                  * merely wrong rather than broken. See STATUS_TONE.
                                                  */}
                                                {/* ⚠️ ~~THE STATUS LED THIS ROW~~ — it moved UP to the title row
                                                    on 2026-09-09; the note beside it there explains why that is
                                                    safe now and was not in August. ⭐ This row is time · carrier ·
                                                    number · door, and the ~60px the status gave back is what stops
                                                    a phone number wrapping mid-digit across three lines. */}
                                                {c.driverPhoneEntered && (
                                                    <>
                                                        {/**
                                                          * ---- ⭐ PHONE **AND** TEXT, SIDE BY SIDE (2026-09-10) ----
                                                          *
                                                          * > Pete: *"The phone icon next to x0484 should change to a
                                                          * > phone and SMS icon somehow (maybe a communciation
                                                          * > icon??)"* … then, on seeing the single-glyph version:
                                                          * > *"Maybe you just add the Text Icon we have right next to
                                                          * > the phone icon x0484."*
                                                          *
                                                          * ⛔ ~~A SINGLE `MessagesSquare`~~ — my first answer, and it
                                                          * traded one wrong noun for another: it dropped the CALL,
                                                          * which is a real thing you can do with this number (the
                                                          * (…) dials it in Teams). ⭐ Two glyphs say both, and the
                                                          * only reason I hesitated — crowding — went away when the
                                                          * tool strip left this card an hour ago.
                                                          *
                                                          * ⛔⛤ ~~BOTH ARE DECORATION, `aria-hidden`. The CHIP is the
                                                          * control … Making a 12px glyph clickable beside a button
                                                          * that already does the job is two targets for one action
                                                          * and a smaller one to hit.~~
                                                          * ⛔⛤ **SUPERSEDED 2026-09-18 — see the block below.** The
                                                          * reasoning was right about two MORE buttons and wrong as a
                                                          * reason to leave the glyphs dead: they now live INSIDE the
                                                          * one button, which is a single larger target, and while
                                                          * they were dead a click on them fell through to the card
                                                          * and opened the editor.
                                                          */}
                                                        {/* ⭐ THE LAST FOUR ONLY — see `phoneTail`. ⚠️ `shrink-0` now,
                                                            not `min-w-0 [overflow-wrap:anywhere]`: five characters
                                                            never need to wrap, and the old pair is what broke a real
                                                            number across THREE lines on a narrow rail.
                                                            ⛔ `title` carries the whole number so a hover still
                                                            answers "which number is that" without expanding. */}
                                                        {/* ⭐⭐ THE NUMBER IS THE WAY TO TEXT HIM (Pete, 2026-09-09:
                                                            *"When you click on the phone, it shouldn't be an edit of
                                                            the card, it should be a comm popup that allows you to send
                                                            the driver a text"*).
                                                            ⛔ IT WAS PLAIN TEXT, so a click fell through to the CARD —
                                                            which edits on double-click. Clicking a phone number and
                                                            getting an editor is the card answering a question nobody
                                                            asked.
                                                            ⚠️ `stopPropagation` on both events, for that same reason. */}
                                                        <button
                                                            type="button"
                                                            /* ⛔ TOGGLES, 2026-09-24. It used to only ever OPEN, so
                                                               clicking the number a second time did nothing and the
                                                               only way back was the chevron. Pete: *"the comm icons
                                                               like phone and text just open the whole panel and close
                                                               the whole panel."* Same expression as the icon-row
                                                               button, so the two controls cannot diverge. */
                                                            onClick={(e) => { e.stopPropagation(); setSmsFor(smsFor === c.checkInId ? null : c.checkInId); }}
                                                            onDoubleClick={(e) => e.stopPropagation()}
                                                            title={unmatched
                                                                ? `${c.driverPhoneEntered} — match this arrival to an appointment before texting`
                                                                : `${c.driverPhoneEntered} — call or text this driver`}
                                                            className="inline-flex shrink-0 items-center gap-1 rounded px-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                                        >
                                                            {/**
                                                              * ---- ⛔⛤ THE ICONS ARE INSIDE THE BUTTON NOW (2026-09-18) ----
                                                              *
                                                              * > Pete: *"you only get the communiction option when you click
                                                              * > right over the phone number x1234. Should be over the text
                                                              * > icon and phone icon as well."*
                                                              *
                                                              * ⛔⛤ THE COMMENT ABOVE ARGUED AGAINST EXACTLY THIS, AND ITS
                                                              * ARGUMENT WAS SOUND ABOUT THE WRONG SHAPE: *"making a 12px
                                                              * glyph clickable beside a button that already does the job is
                                                              * two targets for one action and a smaller one to hit."* ⭐ True
                                                              * of TWO MORE BUTTONS. Not true of ONE BUTTON THAT CONTAINS ALL
                                                              * THREE — that is one target for one action, roughly three
                                                              * times larger than what was there.
                                                              *
                                                              *   ** THE OBJECTION WAS TO A SECOND CONTROL, NOT TO A BIGGER
                                                              *      ONE. IT READ AS A REASON THE GLYPHS MUST STAY DEAD. **
                                                              *
                                                              * ⛔⛔ AND LEAVING THEM DEAD WAS NOT NEUTRAL. The card root's
                                                              * onClick ignores a click only when it lands on
                                                              * `button,select,input,textarea,a,label` — a bare `aria-hidden`
                                                              * <svg> matches none of those, so a click on the phone icon fell
                                                              * THROUGH to the card and opened the EDIT dialog.
                                                              *
                                                              *   ** DECORATION THAT SITS INSIDE A CLICKABLE PARENT IS NOT
                                                              *      INERT. IT INHERITS THE PARENT'S ACTION, AND THE PARENT'S
                                                              *      ACTION IS THE ONE THING IT MUST NOT DO. **
                                                              *
                                                              * ⭐ They stay `aria-hidden` — decoration inside the control, not
                                                              * two more things for a screen reader to announce. The button
                                                              * still carries the whole number in its `title`.
                                                              */}
                                                            {/* ⛔⛤ ~~<Phone /> AND <MessageSquare /> WERE HERE~~ — MOVED
                                                                TO THE ICON ROW, 2026-09-24. Pete: *"we need to move the
                                                                Phone and Text Icon on Drivers Checkin panel to the line
                                                                with the other icons (driver scan and license plate) but
                                                                keep x4945 on the same line."*
                                                                ⭐ THE FACTS LINE IS FOR FACTS AND THE ICON ROW IS FOR
                                                                ACTIONS, and these two were the only actions living among
                                                                the facts. ⚠️ THE NUMBER ITSELF STAYS AND STAYS CLICKABLE
                                                                — it is a fact AND a control, which is why it is the one
                                                                that keeps its place. The dotted underline is what says so.
                                                                ⚠️ 2026-09-18's argument for putting them here is preserved
                                                                and is NOT being reversed on its merits: it said three
                                                                targets for one action is worse than one. Still true. The
                                                                icon row is that one target, on the row that already holds
                                                                every other action. */}
                                                            {/* ⭐ THE UNDERLINE STAYS ON THE DIGITS ONLY. It is the
                                                                affordance that says "this is a control", and dragging it
                                                                under two glyphs makes it read as a mistake rather than a
                                                                cue. ⚠️ `hover:bg-muted` on the BUTTON is what tells you
                                                                the whole strip is one target. */}
                                                            <span className="tabular-nums underline decoration-dotted">
                                                                {phoneTail(c.driverPhoneEntered)}
                                                            </span>
                                                        </button>
                                                    </>
                                                )}
                                            {/**
                                              * ⭐ THE DOOR, WITHOUT THE WORD "DOOR" (Pete: *"You might have to drop the
                                              * word 'Door'"*). `DD124` is already unmistakably a door.
                                              *
                                              * ⛔⛔ A FIXED SLOT, NOT A CONDITIONAL ELEMENT, AND THAT IS THE POINT. Pete:
                                              * *"leave the space empty"* rather than collapse the gap. In a flex row an
                                              * absent child takes no width, so every card without a door would pull its
                                              * neighbours left and the column would stop lining up — which matters exactly
                                              * when it is hardest, scanning fifteen stacked arrivals.
                                              *
                                              * ⚠️ "NO DOOR YET" IS NOT SHOWN HERE. It stays in the detail below, in amber,
                                              * because it is a state somebody must ACT on rather than a fact to read.
                                              */}
                                            {/**
                                              * ⭐⭐ ONE SPACER, AND IT IS THE ONLY ELASTIC THING ON THE ROW.
                                              *
                                              * ⭐ EVERYTHING BEFORE IT PACKS LEFT — time, carrier, status, number all
                                              * sit together and read as one phrase. The DOOR alone goes right, in its
                                              * fixed slot, so the door column lines up down a stack of arrivals.
                                              *
                                              * ⚠️ AN EXPLICIT SPACER RATHER THAN `ml-auto` ON THE DOOR. The door is
                                              * rendered even when empty (see below), and `ml-auto` on an empty span
                                              * would still push — but it puts the row's layout rule INSIDE the door,
                                              * where the next person to touch the door will move it by accident. The
                                              * gap is its own element because the gap is its own decision.
                                              */}
                                            <span className="flex-1" aria-hidden="true" />
                                            <span className="w-14 shrink-0 text-right font-medium">
                                                {c.doorCode ?? ''}
                                            </span>
                                        </div>
                                            {/**
                                              * ---- ⭐ THE TOOL STRIP ------------------------
                                              *
                                              * Pete, 2026-08-25: *"Maybe you have the delete
                                              * button next to the driver (right side like
                                              * others)… That gives us alot more real estate."*
                                              *
                                              * ⚠️ IT STARTED ON THE NAME LINE AND HAD TO MOVE.
                                              * Four icons there ate the width and truncated the
                                              * driver's name — the most important thing on the
                                              * card — to "Pete Lombardo Driver …". It sits on the
                                              * time-and-carrier line instead: short text, always
                                              * room, and no row spent.
                                              *
                                              * ⭐ FOUR TOOLS, AND THEY ARE ALL THE SAME KIND OF
                                              * THING: look at the paperwork, take the licence, text
                                              * him, bin the arrival. None of them changes where the
                                              * truck is or what the load is doing — which is exactly
                                              * why they can be nameless icons.
                                              *
                                              * ⛔ THE ACTIONS THAT CHANGE STATE STAYED AS WORDS.
                                              * "Mark Departed", "Attach…" and "New appointment" are
                                              * the card's JOB at that moment, not tools on it, and a
                                              * nameless icon that departs a truck is a misclick
                                              * waiting for a busy morning. Real estate is worth a
                                              * lot; it is not worth that.
                                              *
                                              * ⚠️ EVERY ICON STOPS THE EVENT. The card now edits on
                                              * double-click, so an un-stopped click here would open
                                              * the editor behind whatever the icon opened.
                                              */}
                                            {/* ⭐ RIGHT-ALIGNED, LIKE LABOR'S REFRESH (Pete, 2026-09-08:
                                                *"these icons should be right loaded, not left"*).
                                                ⚠️ THE STRIP USED TO GET THIS FREE from `ml-auto`, which
                                                worked while it shared a row with the time. Moving it to
                                                its OWN row made `ml-auto` meaningless — a sole flex
                                                child has no sibling to be pushed away from — so the
                                                alignment silently became left. `justify-end` is the
                                                row-level way to say the same thing.
                                                ⛔⛔ AND IT WENT LEFT A SECOND TIME, 2026-09-09. The chevron
                                                briefly lived in this row carrying `mr-auto`, which did the
                                                same job; when it moved to the left column that class went
                                                with it and NOTHING replaced it. ⚠️ Worse, the deploy guard
                                                had been updated to assert the class as it then was — so it
                                                passed, having recorded the regression rather than caught
                                                it. ⭐ A guard must assert what SHOULD be true, not be
                                                re-pointed at whatever the file currently says.
                                                ⭐ AND IT MATTERS BEYOND TASTE: every rail card puts its
                                                controls on the right edge, so a strip on the left reads
                                                as a different kind of thing. */}
                                            {arrivalIcons(c)}

                                        {/**
                                          * ---- ⭐⭐ WHAT THE ATTACH ACTUALLY DID -------------------
                                          *
                                          * ⛔ THIS IS NOW THE ONLY FEEDBACK THERE IS. With the dialog
                                          * gone, the picker closes and the scheduler is looking at the
                                          * rail again — if nothing here changed, "uploaded nothing" and
                                          * "uploaded everything" are the same screen. ⚠️ That exact
                                          * indistinguishability is #288, and it cost two days.
                                          *
                                          * ⭐ THE COUNT IS THE POINT, not the word. Pete: *"any type of
                                          * indication failed or success"* — and "2 files added" is a
                                          * different fact from "1 file added" when two were picked.
                                          *
                                          * ⛔ `added` AND `error` RENDER TOGETHER, DELIBERATELY. A run
                                          * that got three of five through sets both, and showing only
                                          * the error would send somebody to re-drop all five and end up
                                          * with eight documents.
                                          *
                                          * ⚠️ OUTSIDE THE COLLAPSIBLE. The paperclip works on a folded
                                          * card, so its result must be visible on one.
                                          */}
                                        {attachFor === c.checkInId
                                            && (paperwork.busy || paperwork.added !== null || paperwork.error) && (
                                            <div className="mt-1 text-[11px]">
                                                {paperwork.busy && (
                                                    <span className="text-muted-foreground">Attaching…</span>
                                                )}
                                                {!paperwork.busy && paperwork.added !== null && (
                                                    <span className="font-medium text-emerald-700">
                                                        {paperwork.added} file{paperwork.added === 1 ? '' : 's'} added
                                                    </span>
                                                )}
                                                {!paperwork.busy && paperwork.error && (
                                                    <span className={paperwork.added !== null ? 'ml-1 text-destructive' : 'text-destructive'}>
                                                        {paperwork.error}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/**
                                          * ---- ⭐⭐ EVERYTHING BELOW HERE IS THE CARD'S DETAIL ------
                                          *
                                          * ⛔ COLLAPSED BY DEFAULT (Pete, 2026-09-08). Above this line
                                          * are the three rows he specified and nothing else: the
                                          * arrival row, the facts row, the tool strip.
                                          *
                                          * ⚠️ ~~THE ATTACH DROP ZONE USED TO BE THE FIRST THING HERE~~
                                          * and it moved into a dialog the same day — Pete: *"when you
                                          * select the paperclip, should be a popup screen, not inline
                                          * below (looks terrible)."*
                                          *
                                          * ⭐ THAT REMOVED A BUG THIS COLLAPSE WOULD OTHERWISE HAVE
                                          * CREATED, which is worth writing down because the two
                                          * changes look unrelated: a drop zone rendered INSIDE this
                                          * region would have opened invisibly on a folded card, and
                                          * the paperclip would have read as a dead icon. `histFor` and
                                          * `menuFor` still render here, which is why `detailOpen`
                                          * accounts for them rather than trusting the chevron alone.
                                          *
                                          * ⚠️ A FRAGMENT, not a wrapper `div`. This branch has many
                                          * siblings and several of them carry their own `mt-*`; an
                                          * extra box here would change the card's spacing on every
                                          * arrival, which is a layout change nobody asked for hiding
                                          * inside a behaviour change.
                                          */}
                                        {detailOpen && (
                                        <>
                                        {/* ⭐ WARNINGS FIRST IN THE EXPANDED BODY (Pete, 2026-09-25): *"The
                                            show first, top when the chevron is expanded"*. They were below
                                            the DRIVER ENTERED box, so the one thing that changes what you do
                                            sat under the things that merely describe the appointment.
                                            ⛔ ABOVE THE IDENTITY BOX TOO, which means a matched card now opens
                                            with its exceptions, not its facts. Flagged to Pete on the day.
                                            ⚠️ Mirrors the party card, where the same block moved under the
                                            icon row in the same change. */}
                                        {/**
                                          * ---- ⭐⭐⭐ EVERY WARNING, ONE SHAPE, NUMBERED ---------------
                                          *
                                          * ⛔ THE FOUR HAND-PLACED BLOCKS ARE GONE. This is the whole of
                                          * the driver card's warnings: no-appointment, licence,
                                          * vehicle-number, vehicle-mismatch, paperwork-pending and
                                          * no-licence-outbound, in `arrivalWarnings` order, boxed
                                          * identically and numbered 1..N.
                                          *
                                          * ⚠️ THE CLICKS ARE ATTACHED HERE, NOT IN `arrivalWarnings`. That
                                          * function is pure and is also read by the header summary, which
                                          * has no dialogs to open - handing it callbacks would make a
                                          * counting function depend on a rendering context.
                                          *   ** THE LIST OF WHAT IS WRONG AND THE LIST OF WHAT TO DO
                                          *      ABOUT IT ARE THE SAME LENGTH, NOT THE SAME THING. **
                                          *
                                          * ⛔ A WARNING WITH NO DIALOG GETS NO onClick and renders as a
                                          * div. `no-licence-outbound` has nowhere to go: the cure is a
                                          * licence that does not exist yet.
                                          */}
                                        <CardWarnings
                                            warnings={arrivalWarnings(c).map(w => {
                                                if (w.code === 'no-appointment') {
                                                    return { ...w, title: 'Create an appointment for this arrival',
                                                             onClick: () => onCreateFromCheckIn(c) };
                                                }
                                                if (w.code === 'licence') {
                                                    /* ⛔⛔ THE SERVER'S FLAG, NOT A JUDGEMENT MADE HERE.
                                                       `checkin-panel-guard` asserts this reference exists -
                                                       *"the blocking flag changes how loudly it is drawn"* -
                                                       and it caught its removal when the warnings were first
                                                       unified. The guard was right. */
                                                    return { ...w, title: 'Open the license scan',
                                                             severity: c.licenceWarning?.blocking ? 'blocking' : 'normal',
                                                             onClick: () => setLicenceFor(c) };
                                                }
                                                /* ⚠️ BOTH BOX WARNINGS OPEN THE SAME DIALOG, and it edits
                                                   the TRUCK's numbers. A disagreement has three possible
                                                   cures (stale booking, mistyped number, wrong truck) and
                                                   nothing here knows which - so the control goes to the one
                                                   record this panel owns and the text NAMES the other. */
                                                if (w.code === 'vehicle-number' || w.code === 'vehicle-mismatch') {
                                                    return { ...w, title: "Open this truck's trailer, container and plate numbers",
                                                             onClick: () => setVehicleFor(c) };
                                                }
                                                /**
                                                 * ---- ⛔⛤ THE ONE WARNING THAT OPENED NOTHING ------
                                                 *
                                                 * > Pete, 2026-09-23: *"how the others open the proper
                                                 * > screen to fix the warning when you click on them but
                                                 * > the last warning doesn''t open anything."*
                                                 *
                                                 * ⚠️ AND THE MISSING CLICK WAS A SYMPTOM, NOT THE BUG.
                                                 * There was nowhere to send him: `BolNumber` was absent
                                                 * from the repository''s COLS, the route''s Edit zod, and
                                                 * `editCheckIn`''s signature, so no screen could have
                                                 * fixed what this warning is about. All four are now
                                                 * closed and this opens the arrival editor, which holds
                                                 * PO, BOL and Reference together - the three fields the
                                                 * sentence names.
                                                 *   ** A CONTROL WITH NOWHERE TO GO IS USUALLY A
                                                 *      MISSING DESTINATION, NOT A MISSING HANDLER. **
                                                 */
                                                /**
                                                 * ---- ⛔⛔⛤⛤ THIS CALLED `setEditFor` AND IT COULD
                                                 *      ERASE THE CARD (2026-09-24) --------------------
                                                 *
                                                 * > Pete: *"I clicked the bottom warning and it pulls up a
                                                 * > different screen from the one above it. The first one
                                                 * > has none of the information populated."*
                                                 *
                                                 * ⛔⛔ `setEditFor` OPENS THE EDITOR. `startEdit` OPENS IT
                                                 * **AND SEEDS THE DRAFT.** Every other route called the
                                                 * second; this one called the first, so the form opened
                                                 * BLANK -- and `saveEdit` writes every field whose draft
                                                 * differs from the record. Pressing Save wrote NULL over
                                                 * the driver name, phone, carrier, PO, BOL, reference,
                                                 * container and trailer.
                                                 *   ** AN EDIT FORM THAT OPENS EMPTY IS NOT A FORM WITH
                                                 *      NOTHING IN IT. IT IS A FORM ABOUT TO ERASE WHAT
                                                 *      IS THERE. **
                                                 *   ** AND A SETTER THAT OPENS A DIALOG WITHOUT SEEDING
                                                 *      IT IS ONE CALL SITE AWAY FROM DATA LOSS, EVERY
                                                 *      TIME. THE SEEDING BELONGS INSIDE THE OPENER. **
                                                 *
                                                 * ✅ It now goes to the FOCUSED dialog, which reads the
                                                 * arrival on every render and copies nothing into state it
                                                 * has not seeded -- so it cannot have this class of bug.
                                                 */
                                                if (w.code === 'paperwork-pending') {
                                                    return { ...w, title: 'Enter his PO, BOL or reference number',
                                                             onClick: () => setPaperworkFor(c) };
                                                }
                                                return w;
                                            })}
                                        />
                                        {/**
                                          * ---- ⭐⭐ THE LOAD DETAILS (Pete, 2026-09-23) ----------------
                                          *
                                          * > *"You will need to see those three fields on the driver card
                                          * > when you hit the chevron so you can spot check against the
                                          * > appointment … there is a star or something next to the item
                                          * > (following the warning) with a note saying this XxX does not
                                          * > match XXX on the appointment record (to save space)."*
                                          *
                                          * ⭐ HE CHOSE THE MARKER OVER A SECOND COLUMN, and the space
                                          * argument is the smaller half of why it is right: two columns
                                          * make a scheduler compare them himself, every row, including
                                          * the ones that agree. A marker puts the work where the
                                          * disagreement is.
                                          *   ** A TABLE OF PAIRS ASKS THE READER TO DO THE COMPARISON
                                          *      THE SERVER ALREADY DID. **
                                          *
                                          * ⛔ THE COMPARISON IS THE SERVER'S. `paperworkMismatch` and
                                          * `palletMismatch` arrive already judged by `sameValue` — the
                                          * same function the attach prompt uses.
                                          *
                                          * ⚠️ IN THE CHEVRON ONLY. Pete confirmed the collapsed card
                                          * stays as it is; the rail is already carrying a warning count
                                          * and a licence strip.
                                          *
                                          * ⚠️ A ROW PER FIELD THE GATE ASKED FOR, not per value it got.
                                          * A blank row reading "—" says "he was asked and gave nothing",
                                          * which is a different fact from the row being absent.
                                          * ⛔ NOTHING RENDERS AT ALL when the site collects none of it:
                                          * `paperworkPending` and `palletCountPending` are NULL in that
                                          * case, and so are the values.
                                          */}
                                        {/**
                                          * ============================================================
                                          * ⭐⭐⭐ WHICH TRUCK IS THIS, AND WHERE DOES IT GO (2026-09-24)
                                          * ============================================================
                                          *
                                          * > Pete: *"Door D2 and The appointment number need to be at the top,
                                          * > above the Driver Entered Info and be in its own box. License on
                                          * > file should be in that box too."*
                                          *
                                          * ⛔⛔ THESE THREE FACTS WERE SCATTERED DOWN THE CARD as unboxed
                                          * lines, below the DRIVER ENTERED box, mixed in with the latest text
                                          * and the work clock. They are the three things a scheduler looks for
                                          * FIRST -- which booking, which door, is he cleared to be here -- and
                                          * they were the last things on the card.
                                          *   ** THE FACTS SOMEBODY OPENS A CARD FOR BELONG AT THE TOP OF IT.
                                          *      EVERYTHING ELSE IS DETAIL, AND DETAIL IS WHAT SCROLLING IS
                                          *      FOR. **
                                          *
                                          * ⚠️ THE DOOR IS STILL READ THROUGH FROM THE APPOINTMENT and never
                                          * stored on this row (Pete, 2026-08-19) -- a copy would go stale the
                                          * moment somebody moved the truck, on the card of the person moving
                                          * him. Only its POSITION changed.
                                          * ⚠️ AND ABSENCE IS STILL THE INTERESTING STATE: "No door yet" is
                                          * precisely when a driver is sitting in the lot waiting to be told.
                                          *
                                          * ⛔ THE BOX RENDERS UNCONDITIONALLY ON A MATCHED ARRIVAL. Every one
                                          * of its three rows has a real answer for "we do not know yet", and a
                                          * box that disappears when a truck has no door would hide the state
                                          * that most needs looking at.
                                          */}
                                        {!unmatched && (
                                            <div className="mt-1 rounded border border-border bg-muted/20 px-2 py-1.5">
                                                <div className="flex items-baseline gap-2">
                                                    <span className="min-w-0 flex-1 truncate font-medium">
                                                        {c.appointmentNumber}
                                                        {c.matchMethod && (
                                                            <span className="font-normal text-muted-foreground"> · {c.matchMethod}</span>
                                                        )}
                                                    </span>
                                                    {/* ⚠️ THE DOOR IS `shrink-0` AND RIGHT-ALIGNED, so a long
                                                        appointment number truncates and the door -- the thing a
                                                        driver is waiting to be told -- never does. */}
                                                    <span className="shrink-0 text-right">
                                                        {c.doorCode
                                                            ? <span className="font-medium">Door {c.doorCode}</span>
                                                            : <span className="text-amber-700">No door yet</span>}
                                                    </span>
                                                </div>
                                                {/* ⛔ THE LICENCE STATE, MOVED UP from the chip row at the foot
                                                    of the card. ⚠️ #315's rule is carried with it unchanged: a
                                                    calm "license on file" is SUPPRESSED when there is a warning,
                                                    because the two together read as a contradiction and the
                                                    reassuring half is the one an eye skimming twenty cards lands
                                                    on. The warning line below still says everything. */}
                                                {c.hasDriverIdentity
                                                    ? (c.licenceWarning ? null : (
                                                        <div className="mt-0.5 text-[11px] text-muted-foreground">license on file</div>
                                                    ))
                                                    : c.direction === 'OUTBOUND'
                                                        ? <div className="mt-0.5 text-[11px] font-medium text-amber-700">no license scanned</div>
                                                        : null}
                                            </div>
                                        )}
                                        {(c.poNumber || c.bolNumber || c.referenceNumber
                                          || c.palletCount !== null && c.palletCount !== undefined
                                          || c.paperworkPending === true || c.palletCountPending === true) && (
                                            <div className="mt-1 rounded border border-border bg-muted/20 px-2 py-1.5">
                                                {/* ⛔⛤ ~~"Load details"~~ — RENAMED 2026-09-24. The box holds what
                                                    the DRIVER TYPED, not what was booked, and "load" was the one
                                                    word in the product carrying two meanings (the appointment in
                                                    some strings, the WMS order in others). Pete retired it.
                                                      ** A HEADING THAT NAMES THE AMBIGUOUS WORD TEACHES IT. ** */}
                                                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                    Driver entered
                                                </div>
                                                {([
                                                    ['PO', c.poNumber, 'po'],
                                                    ['BOL', c.bolNumber, 'bol'],
                                                    ['Ref', c.referenceNumber, 'reference'],
                                                ] as const).map(([label, value, key]) => {
                                                    if (!value) return null;
                                                    const m = (c.paperworkMismatch ?? []).find(x => x.field === key);
                                                    return (
                                                        <div key={key} className="mt-0.5 text-[11px]">
                                                            <span className="text-muted-foreground">{label} </span>
                                                            <span className="font-medium [overflow-wrap:anywhere]">{value}</span>
                                                            {m && (
                                                                <span className="ml-1 text-amber-700">
                                                                    <AlertTriangle className="mb-px mr-0.5 inline h-3 w-3" />
                                                                    does not match {m.booked} on the appointment
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {/* ⛔ `!= null` AND NOT A TRUTHY TEST. A pallet count of 0 is
                                                    a real answer from a driver and would vanish from this
                                                    card under `c.palletCount &&`. */}
                                                {c.palletCount !== null && c.palletCount !== undefined && (
                                                    <div className="mt-0.5 text-[11px]">
                                                        <span className="text-muted-foreground">Pallets </span>
                                                        <span className="font-medium">{c.palletCount}</span>
                                                        {c.palletMismatch && (
                                                            <span className="ml-1 text-amber-700">
                                                                <AlertTriangle className="mb-px mr-0.5 inline h-3 w-3" />
                                                                does not match {c.palletMismatch.booked} on the appointment
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                {/* ⚠️ `=== true`, never truthy — NULL means the gate never
                                                    asked, and an "owes us one" line on a site that does not
                                                    collect paperwork is a chase nobody can close. */}
                                                {/* ⛔⛤ ~~"Owes a PO, BOL or reference number"~~ — it is a
                                                    WARNING and it now lives with the warnings. Pete:
                                                    *"Same with Load Details - not sure what this is -
                                                    another warning???"* It read as one because this block
                                                    contained one. Load details is now only facts. */}
                                                {c.palletCountPending === true && (
                                                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                                                        Pallet count not given.
                                                    </div>
                                                )}
                                                {/**
                                                  * ---- ⭐⭐ THE BOX NUMBERS MOVED IN HERE (2026-09-24) --------
                                                  *
                                                  * Pete: *"we need to make this neater and formatted."*
                                                  *
                                                  * ⛔⛤ THEY USED TO RENDER AS TWO UNLABELLED LINES BELOW THIS
                                                  * BOX, and one of them printed a bare `T0217040` with no label
                                                  * at all — the same PO already shown above it, because the
                                                  * older line joined container/PO/reference with a dot and only
                                                  * the PO existed. That is what made Pete ask what the repeated
                                                  * number was.
                                                  *   ** TWO RENDERINGS OF ONE FACT DO NOT READ AS EMPHASIS. THEY
                                                  *      READ AS TWO FACTS. **
                                                  *
                                                  * ⚠️ MONOSPACED, AND THEY DO NOT TRUNCATE. These are read
                                                  * against a printed document; a container number cut short
                                                  * still looks like a match, which is worse than not showing it.
                                                  * ⚠️ A ROW PER VALUE PRESENT, not a row per field asked — unlike
                                                  * the paperwork rows above, an absent trailer number is not a
                                                  * question anybody put to him.
                                                  */}
                                                {([
                                                    ['TRL',   c.trailerNumber],
                                                    ['CONT',  c.containerNumber],
                                                    ['PLATE', c.trailerPlate],
                                                ] as const).map(([label, value]) => value ? (
                                                    <div key={label} className="mt-0.5 font-mono text-[11px] tracking-tight [overflow-wrap:anywhere]">
                                                        <span className="font-sans text-muted-foreground">{label} </span>
                                                        <span className="font-medium">{value}</span>
                                                    </div>
                                                ) : null)}
                                            </div>
                                        )}
                                        {/**
                                          * ---- ⭐ THE WHOLE NUMBER LIVES HERE (Pete, 2026-09-09) ---------
                                          *
                                          * > *"The full phone number can be shown in the breakout below."*
                                          *
                                          * ⛔⛔ THIS LINE IS LOAD-BEARING, NOT DECORATION. The facts row above
                                          * now shows `x8549`, which nobody can dial. ⭐ **If this is ever
                                          * removed, the card holds no dialable number at all** — and migration
                                          * 144 calls the phone the real-world lookup key, the thing a scheduler
                                          * uses to reach the man standing in the lobby.
                                          *
                                          * ⚠️ IT WRAPS AND DOES NOT TRUNCATE. `[overflow-wrap:anywhere]` moved
                                          * down here with the number: a wrapped number is ugly and correct, a
                                          * truncated one is tidy and gets misdialled. There is room to be ugly
                                          * here — the breakout is full width and already open.
                                          *
                                          * ⭐ FIRST IN THE BREAKOUT, on purpose. Expanding a card to find a
                                          * phone number should not mean reading past the appointment, the door
                                          * and the last text to get to it.
                                          */}
                                        {/**
                                          * ---- ⛔⛔ ~~THE FULL NUMBER, A SECOND TIME~~ REMOVED (2026-09-10) ---
                                          *
                                          * > Pete: *"we don't need the full phone number listed twice. When you
                                          * > click on the x0484 it opens the communication box with the phone
                                          * > number. The full phone number right above the Call the Driver in
                                          * > TEAMS can be removed (redundant)."*
                                          *
                                          * ⚠️ THE ARGUMENT FOR KEEPING IT WAS COPY-AND-PASTE — *"the full number
                                          * is still selectable text… so it can be copied into whatever they DO
                                          * dial from"*. ⛔ THAT ARGUMENT DIED WHEN THE (…) GAINED
                                          * "Call <number> in Teams", which is the thing it was for, one line
                                          * below where this used to sit.
                                          *
                                          * ⭐ THE NUMBER HAS NOT GONE. The `x0484` chip on the title row carries
                                          * it in its `title` and opens the comms box with it — same handler,
                                          * `setSmsFor(c.checkInId)`, which is why removing this loses no route.
                                          */}
                                        {/**
                                          * ---- ⭐⭐ THE TEXTING MENU LIVES IN THE BREAKOUT NOW ------------
                                          *
                                          * > Pete, 2026-09-09: *"I don't think we have room for the note in the
                                          * > collapsed panel but it would go nicely on the expansion."*
                                          *
                                          * ⚠️ ~~IT SAT UNDER THE DRIVER'S NAME~~, in the always-visible region —
                                          * fine when the card never collapsed, wrong the moment it did: three
                                          * message buttons unfolding above the facts row pushed every other
                                          * arrival down the rail.
                                          *
                                          * ⭐ IT IS STILL INLINE, NOT A POPOVER, and that part has not changed:
                                          * *"this panel is a narrow right rail, and a floating popover would
                                          * either overflow the viewport or cover the next card — which is the one
                                          * the scheduler is comparing against."* ⛔ Do not turn it into one.
                                          *
                                          * ⭐ AND THE NOTE PETE MEANT ALREADY EXISTS — `DoorTextDialog` has "Add a
                                          * line (optional)", capped at 160 characters with a live preview, sent as
                                          * `sendDoorText(..., note)`. ⚠️ It is a NOTE APPENDED TO A COMPOSED
                                          * MESSAGE, not free text: §1N.4 — *"the body is composed server-side,
                                          * never accepted from the caller … no authenticated caller can push
                                          * arbitrary text through Symbia's toll-free number."*
                                          */}
                                        {/**
                                          * ---- ⭐⭐ AND SO IS THIS ONE (Pete, 2026-09-09) ------------
                                          *
                                          * > *"the No Apppointment warning should allow you to single
                                          * > click and open the new appointment popup."*
                                          *
                                          * ⭐ SAME HANDLER AS THE "New appointment" BUTTON twenty lines
                                          * below — `onCreateFromCheckIn(c)`, which prefills the dialog
                                          * from this arrival. ⛔ NOT a second path to the same screen:
                                          * calling the parent's one callback is what keeps the prefill
                                          * from drifting apart from the button's.
                                          *
                                          * ⚠️ BOTH STAY. The word "No appointment" is where the eye lands
                                          * when the card opens; the button is where the hand goes when
                                          * the scheduler has already read the row and decided. Removing
                                          * either would make one of those two people hunt.
                                          *
                                          * ⚠️ `disabled` MIRRORS THE BUTTON'S `busy !== null` — a control
                                          * that fires mid-save is how two appointments get made for one
                                          * truck.
                                          */}
                                        {/**
                                          * ---- ⛔⛤ ~~THE "No appointment" HEADING~~ MOVED 2026-09-23 --
                                          *
                                          * It is now warning #1 in `CardWarnings` below, in the same box
                                          * as every other warning, carrying the same click
                                          * (`onCreateFromCheckIn`).
                                          *
                                          * ⚠️ AN EARLIER NOTE HERE SAID *"BOTH STAY. The word 'No
                                          * appointment' is where the eye lands"* - and that was true of a
                                          * card where nothing else was boxed. Pete asked for one shape for
                                          * all of them and chose the uniform box over the emphasis when
                                          * the trade was put to him directly.
                                          *   ** EMPHASIS IS RELATIVE. WHEN EVERY OTHER WARNING GAINS A
                                          *      BOX, THE UNBOXED ONE STOPS LEADING AND STARTS LOOKING
                                          *      UNFINISHED. **
                                          */}
                                        {unmatched ? null : (
                                            // ⚠️ A FRAGMENT, because this branch now renders TWO
                                            // siblings. Without it the parser stops at the second
                                            // element with "Expected )" -- which is a build error,
                                            // caught, rather than anything reaching a screen.
                                            <>
                                            {/**
                                              * ⭐⭐ THE PAPERWORK NUMBERS, ON THE CARD ITSELF (#291).
                                              *
                                              * Pete: *"did we put container id on the actual card
                                              * too?"* — no, we had not. It was on the HOVER card and
                                              * in the detail dialog, both of which need a mouse and
                                              * a deliberate move. ⚠️ The card is what a scheduler
                                              * reads while a driver is standing in front of them.
                                              *
                                              * ⭐ SAME ORDER AS EVERYWHERE ELSE (#305): PO, Ref,
                                              * Cont, Trailer — HIS numbers — and our appointment
                                              * number last. A driver has never seen A00000247.
                                              *
                                              * ⛔ THE CONTAINER AND TRAILER ARE MONOSPACED AND DO
                                              * NOT TRUNCATE. They are read against a printed
                                              * document, and a container number cut short still
                                              * looks like a match — which is worse than not showing
                                              * it. The appointment number may truncate; it is ours.
                                              */}
                                            {/**
                                              * ---- ⛔⛔⛔ THE REPEATED NUMBERS, FINALLY GONE (2026-09-24) ----
                                              *
                                              * > Pete: *"What is the PO TO217163 information that isn't in a box
                                              * > and formated?"*
                                              *
                                              * ⛔⛔ THE ANSWER WAS: THE SAME FIELDS, PRINTED A SECOND TIME.
                                              * These lines read `c.poNumber`, `c.referenceNumber`,
                                              * `c.containerNumber` and `c.trailerNumber` -- the IDENTICAL
                                              * properties the DRIVER ENTERED box above already renders, labelled.
                                              * Not a booked-versus-arrived pairing, which is what they LOOKED
                                              * like sitting underneath it. Just a duplicate.
                                              *
                                              * ⚠️ AND THE DISAGREEMENT CASE WAS NEVER THESE LINES' JOB. The box
                                              * already carries `paperworkMismatch` and `palletMismatch` and prints
                                              * *"does not match {booked} on the appointment"* against the offending
                                              * row. So "show the booked value only when it differs" -- Pete's
                                              * choice when offered three options -- was ALREADY BUILT, in the right
                                              * place, and these lines were adding nothing to it on any card.
                                              *   ** A DUPLICATE THAT SITS BELOW THE ORIGINAL READS AS A SECOND,
                                              *      DIFFERENT FACT. IT IS THE ONE KIND OF REDUNDANCY THAT COSTS
                                              *      MORE THAN THE SPACE IT TAKES. **
                                              *
                                              * ⛔⛤ ~~`{c.appointmentNumber} · {c.matchMethod}`~~ and ~~the Door
                                              * line~~ -- MOVED UP into the identity box, not deleted. Pete: *"Door
                                              * D2 and The appointment number need to be at the top."*
                                              *
                                              * ⚠️ THE FRAGMENT AND ITS `unmatched` GUARD SURVIVE because the work
                                              * clock below still needs them.
                                              */}
                                            </>
                                        )}

                                        {/* ⭐ THE LATEST TEXT, ON THE CARD (#150).
                                            Pete, 2026-08-20: *"I think we put the latest one on
                                            the actual card somehow and the previous texts in the
                                            driver / appointment logs. The scheduler can then see
                                            the latest automated texts sent."*

                                            ⛔ A FAILURE IS LOUD AND A SUCCESS IS QUIET. `ok`
                                            comes from the event TYPE server-side, never from
                                            parsing a message — a failure wearing the word "Sent"
                                            is precisely how the broken SMS path hid for hours
                                            (#133). Red is the whole point of putting this here:
                                            once door texts send themselves, nobody is watching a
                                            dialog for the result.

                                            ⚠️ Absent means never texted, which is NOT the same as
                                            failed — so nothing renders at all rather than a
                                            reassuring tick. */}
                                        {/* ⛔⛤ ~~THE LATEST TEXT LINE~~ (✓ Door text · 1h 54m) — SUPPRESSED
                                            2026-09-24. Pete: *"Door Text - 1 hr 59m can be suppressed for right
                                            now. We have the communications in the right hand panels now."*
                                            ⭐ #150 PUT IT HERE WHEN THE CARD WAS THE ONLY PLACE COMMS EXISTED.
                                            The Communications rail now holds the whole history, scoped by a
                                            click, so this line was the card answering a question another panel
                                            answers better and in full.
                                            ⚠️ SUPPRESSED, NOT DELETED, AND PETE SAID "for right now". The
                                            FAILURE half of this is the part worth missing: a door text that did
                                            not send still renders nowhere else on this card. ⬜ If the rail turns
                                            out not to surface failures loudly enough, this comes back — which is
                                            why the markup is struck through below rather than removed.
                                              ** A SUCCESS MOVING TO ANOTHER SCREEN IS TIDYING. A FAILURE MOVING
                                                 TO ANOTHER SCREEN IS A BET THAT SOMEBODY LOOKS AT IT. ** */}
                                        {SHOW_LAST_TEXT_ON_CARD && c.lastText && (
                                            <div
                                                className={`mt-0.5 truncate ${c.lastText.ok
                                                    ? 'text-muted-foreground'
                                                    : 'font-medium text-destructive'}`}
                                                title={c.lastText.note ?? undefined}
                                            >
                                                {c.lastText.ok ? '\u2713' : '\u26a0'}{' '}
                                                {c.lastText.kind === 'paperwork' ? 'Paperwork text' : 'Door text'}
                                                {c.lastText.ok ? '' : ' FAILED'}
                                                {c.lastText.whenUtc ? ` \u00b7 ${elapsed(c.lastText.whenUtc, now)}` : ''}
                                            </div>
                                        )}

                                        {/* \u2b50 THE ONE THING THE SCREEN NEVER SAID \u2014 see `sentNote`. */}
                                        {sentNote?.id === c.checkInId && (
                                            <div className="mt-0.5 truncate font-medium text-emerald-700">
                                                {'\u2713'} Text sent{sentNote.to ? ` \u00b7 ${sentNote.to}` : ''}
                                            </div>
                                        )}

                                        {/* ⛔⛤ ~~THE UNLABELLED JOIN~~ — REMOVED 2026-09-24.
                                            It printed `[container, PO, reference].filter(Boolean).join(' · ')`
                                            with NO LABEL, so an arrival carrying only a PO rendered a bare
                                            `T0217040` directly beneath the same PO already labelled in the
                                            box above. Pete, looking at it: *"the PO is repeated."*
                                            ⭐ Every one of these values now appears exactly once, labelled,
                                            inside DRIVER ENTERED.
                                              ** A LINE THAT LABELS NOTHING CANNOT BE READ AS A DIFFERENT
                                                 FACT FROM THE ONE ABOVE IT - SO IT IS READ AS A SECOND
                                                 ONE. ** */}

                                        {/**
                                          * ⭐ #146/#168 — THE TRAILER LINE, ITS OWN ROW.
                                          *
                                          * ⛔ TrailerNumber HAS BEEN STORED SINCE MIGRATION 139
                                          * AND WAS NEVER RENDERED ANYWHERE. The panel's edit form
                                          * could change a value the card would not show — so a
                                          * scheduler could only see it by opening the editor, and
                                          * the search could match on something invisible.
                                          *
                                          * ⚠️ SEPARATE FROM THE REFERENCE ROW ABOVE, on purpose.
                                          * Those three are all "a number off the paperwork" and
                                          * read as one class. These two are about the physical
                                          * box at the door, which is a different question a
                                          * scheduler asks at a different moment.
                                          */}
                                        {/* ⛔⛤ ~~THE TRAILER LINE~~ — MOVED INTO DRIVER ENTERED 2026-09-24.
                                            ⚠️ #146/#168's argument for a separate row still stands and is
                                            PRESERVED, not discarded: the box numbers are a different question
                                            from the paperwork numbers. They are now separated by position and
                                            typeface INSIDE one labelled box, rather than by being homeless
                                            underneath it.
                                              ** A DISTINCTION WORTH KEEPING IS NOT WORTH A SECOND UNLABELLED
                                                 CONTAINER. ** */}

                                        {/* Work state, straight from the labour clock. */}
                                        {c.work && (
                                            <div className={`mt-1 ${c.work.stale ? 'text-amber-800' : 'text-emerald-700'}`}>
                                                {c.work.code === 'LOADING' ? 'Loading' : 'Unloading'}
                                                {' · '}{elapsed(c.work.startedUtc, now)}
                                                {c.work.workerName ? ` · ${c.work.workerName}` : ''}
                                                {/* ⚠️ Nothing closes a labour session on a timer unless the
                                                    auto-close sweep is on, so a session can sit open for days.
                                                    Saying so is better than showing a truck as being worked. */}
                                                {c.work.stale && (
                                                    <span className="block text-[10px]">
                                                        probably not closed — check with the floor
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Evidence. Absent is the interesting case, so absence is
                                            what gets called out rather than presence. */}
                                        <div className="mt-1 flex flex-wrap gap-x-2 text-[10px] text-muted-foreground">
                                            {/* ⛔ ONLY IF HE WAS ASKED (#269). Rendering on the
                                                absence of a timestamp alone put an amber chip on
                                                EVERY card at any site with the option switched
                                                off — the colour that means "do something" spent
                                                on nothing, which is how amber stops being read. */}
                                            {c.safetyAckExpected && !c.foodSafetyAckUtc && (
                                                <span className="text-amber-700">no safety ack</span>
                                            )}
                                            {/* ⛔ OUTBOUND WITHOUT A LICENCE IS THE ONE THAT
                                                MATTERS (design §3): an outbound truck is TAKING
                                                goods away, and a fictitious pickup is the actual
                                                threat. Inbound stays quiet on purpose — calling
                                                it out on every delivering truck would train the
                                                colour away before it was ever needed. */}
                                            {/* ⛔ "license on file" IS SUPPRESSED WHEN THERE IS A
                                                WARNING (#315). The two together read as a
                                                contradiction — a calm "license on file" beside
                                                "licence has expired" — and the reassuring half is
                                                the one an eye skimming twenty cards lands on.
                                                The warning line below says everything this chip
                                                would have. */}
                                            {/* ⛔⛤ ~~THE LICENCE STATE~~ — MOVED UP into the identity box at
                                                the top of the detail, 2026-09-24. Pete: *"License on file should
                                                be in that box too."* ⚠️ #315's suppression rule travelled WITH
                                                it and is unchanged: no calm "license on file" beside a warning.
                                                ⛔ The rest of this chip row stays — licence PHOTO, language and
                                                "entered by staff" are about the capture, not about whether the
                                                driver is cleared to be here, which is what the box answers. */}
                                            {c.hasDlImage && <span>license photo</span>}
                                            {c.language && <span>{c.language}</span>}
                                            {c.checkInSource !== 'Kiosk' && <span>entered by staff</span>}
                                        </div>

                                        {/**
                                          * ⭐ #315 — THE LICENCE WARNING, AT SCAN TIME.
                                          *
                                          * Pete: *"this should show up also when we scan in his
                                          * license with a warning somewhere on the driver card."*
                                          *
                                          * ⛔ IT IS A LINE, NOT A CHIP, AND THAT IS THE POINT. The
                                          * row above is where "language" and "license photo" live
                                          * — things a scheduler reads when she is already curious.
                                          * This is the one that has to interrupt her, so it gets
                                          * its own row, an icon, and the full sentence rather than
                                          * two words she has to hover to expand.
                                          *
                                          * ⚠️ TWO WEIGHTS, NOT ONE COLOUR FOR EVERYTHING. `blocking`
                                          * is the server's `requiresOverride` — the same flag the
                                          * release dialog stops on — so amber-on-amber means "this
                                          * truck will not leave until somebody signs for it" and
                                          * the quieter grey-amber means "worth a look". Painting
                                          * both loud would make the loud one mean nothing, which
                                          * is #269 all over again.
                                          *
                                          * ⛔ NO CLASS, NO NUMBER, NO EXPIRY, NO NAMES. The server
                                          * chose this sentence for a rail that faces a lobby; the
                                          * specifics are behind the licence icon, one truck at a
                                          * time. Do not "improve" this by rendering the findings.
                                          */}
                                        {/**
                                          * ---- ⭐⭐ IT IS A BUTTON NOW (Pete, 2026-09-09) -------------
                                          *
                                          * > *"on the breakout, single clicking on the Not A Commercial
                                          * > drivers license should pull up the drivers license scan
                                          * > screen that you currently do when selecting the warning
                                          * > icon."*
                                          *
                                          * ⭐ THE SENTENCE THAT STATES THE PROBLEM IS NOW THE CONTROL
                                          * THAT OPENS IT. `setLicenceFor(c)` is the SAME call the licence
                                          * icon in the tool strip makes and the same one the header
                                          * triangle used to make — one destination, three doors, no
                                          * fourth definition of "open the licence".
                                          *
                                          * ⚠️ ~~A `<span>`~~. It read as a label, so the only way to act
                                          * on the loudest line on the card was to find a small icon
                                          * somewhere else. The underline is what says it is clickable —
                                          * amber text on amber fill cannot also carry that job by colour.
                                          *
                                          * ⛔ THE `<div>` COULD NOT SIMPLY TAKE AN onClick. A clickable
                                          * div is invisible to a keyboard and to a screen reader, and
                                          * this row is the one that must not be missable.
                                          *
                                          * ⛔ stopPropagation, for the reason every control here has it:
                                          * the card root opens the editor on double-click.
                                          */}

                                        {/**
                                          * ---- ⭐⭐ #73 — HE OWES A NUMBER -------------------
                                          *
                                          * > Pete, 2026-09-23: *"This should be an alert on the
                                          * > driver card in our alert section"*
                                          *
                                          * ⭐ THE SAME IDIOM AS THE LICENCE WARNING ABOVE, ON
                                          * PURPOSE — a full-width amber button whose SENTENCE is
                                          * the control that opens the fix. A scheduler who has
                                          * learned that the amber line is clickable has learned it
                                          * for both.
                                          *   ** AN ALERT THAT DOES NOT OPEN THE THING THAT CLEARS
                                          *      IT IS A NOTIFICATION, NOT A TASK. **
                                          *
                                          * ⚠️ THE QUIET WEIGHT, NOT THE BORDERED ONE. `blocking`
                                          * on a licence means an outbound load will not leave until
                                          * somebody signs for it; this stops no truck — it costs a
                                          * place in the queue. Painting it the same as the loud one
                                          * is how the loud one stops meaning anything (#269).
                                          *
                                          * ⚠️ THE TEXT COMES FROM `arrivalWarnings`, so the
                                          * header count and this line cannot disagree about what is
                                          * wrong or how it is worded.
                                          *
                                          * ⛔ stopPropagation and onDoubleClick, like every control
                                          * on this card: the root opens the editor on double-click.
                                          */}
                                        {/**
                                          * ⭐⭐ #73 — THE DISAGREEMENT, IN THE SAME IDIOM.
                                          *
                                          * ⚠️ ONE ROW AT A TIME. This and the owed-number row
                                          * below are mutually exclusive by construction, so the
                                          * card never grows two amber lines about the same box.
                                          *
                                          * ⛔ IT OPENS THE DIALOG, WHICH EDITS THE **TRUCK'S**
                                          * NUMBERS — and the dialog says so in as many words. A
                                          * disagreement has three possible cures (the booking is
                                          * stale, the driver typed it wrong, or it is the wrong
                                          * truck on the wrong load) and nothing here knows which.
                                          * So the control goes to the one record this panel owns
                                          * and NAMES the other rather than pretending to fix it.
                                          *   ** WHEN A SCREEN CANNOT KNOW WHICH OF TWO RECORDS IS
                                          *      WRONG, IT MAY NOT SILENTLY PICK ONE. IT SAYS WHICH
                                          *      ONE ITS BUTTON CHANGES. **
                                          */}
                                        {/* ⛔⛤ ~~THE TWO VEHICLE BLOCKS~~ — folded into `CardWarnings`
                                            above. Each used to re-derive its own text with
                                            `arrivalWarnings(c).find(w => w.code === '...')`, which is a
                                            loop written out longhand; the divergence lived in the spaces
                                            between the copies. */}

                                        {/**
                                          * ---- ⛔⛔ EVERY BUTTON HERE NEEDS ITS OWN `title` ---------
                                          *
                                          * > Pete, 2026-09-09: *"when you hover over those 3 buttons,
                                          * > it should tell u the full name of the button, not double
                                          * > click to edit"*
                                          *
                                          * ⚠️ THIS IS NOT A MISSING FEATURE, IT IS AN INHERITED ONE.
                                          * The card root carries `title="Double-click to edit"`, and a
                                          * `title` is inherited by every descendant that does not set
                                          * its own — so hovering ANY untitled control on this card
                                          * produced a tooltip about the CARD. ⛔ It was not blank, it
                                          * was CONFIDENTLY WRONG, which is why it read as a bug rather
                                          * than an omission.
                                          *
                                          * ⭐ THE TOOLTIP SAYS WHAT THE BUTTON DOES, not what it is
                                          * called. "Attach…" is already on the face of the button;
                                          * repeating it teaches nothing. The three differ in ways the
                                          * labels cannot carry — see #212a: "Attach…" lists EVERY live
                                          * load today, "Find load…" lists only the ones matching this
                                          * driver and says what they matched on.
                                          *
                                          * ⛔ SO ANY CONTROL ADDED TO THIS CARD LATER MUST BRING A
                                          * `title` WITH IT. Forgetting one does not produce silence —
                                          * it produces "Double-click to edit" on a button that does
                                          * something else entirely.
                                          */}
                                        {/**
                                          * ---- ⭐⭐⭐ COMMS AT THE FOOT, WITH THE ACTIONS UNDER IT ------
                                          *
                                          * > Pete, 2026-09-24: *"Mark Departed and those buttons should be at
                                          * > the very bottom right below Email the Driver box."*
                                          *
                                          * ⛔⛤ THIS BLOCK USED TO SIT IN THE MIDDLE OF THE DETAIL, between
                                          * the DRIVER ENTERED box and the work clock -- so opening the comms
                                          * panel pushed three hundred pixels of facts DOWN the card, and the
                                          * scheduler lost his place in the thing he opened the card to read.
                                          *   ** A PANEL THAT EXPANDS IN THE MIDDLE OF A LIST MOVES EVERYTHING
                                          *      THE READER WAS LOOKING AT. AT THE FOOT IT MOVES NOTHING. **
                                          *
                                          * ⭐ AND IT PUTS THE ACTIONS WHERE PETE ASKED FOR THEM BY MOVING ONE
                                          * BLOCK RATHER THAN TWO: the buttons were already last, so dropping
                                          * the comms panel immediately above them satisfies both halves.
                                          */}
                                        {smsFor === c.checkInId && (
                                            /**
                                              * ⭐⭐ FULL WIDTH, LIKE THE CLIENT CARD (Pete, 2026-09-24).
                                              *
                                              * ⛔⛤ THIS COMMENT IS A PLAIN BLOCK, NOT THE JSX `{ }` FORM, AND
                                              * THE FIRST DRAFT GOT IT WRONG. This div is the SOLE child of
                                              * `{smsFor === c.checkInId && ( ... )}` -- an EXPRESSION position,
                                              * where two siblings are a syntax error, and a JSX comment is a
                                              * second sibling. `tsc` answered "')' expected" here and
                                              * "Expected corresponding closing tag for JSX fragment" 340 lines
                                              * later, which is the error you actually have to debug.
                                              *   ** A COMMENT SYNTAX IS CHOSEN BY POSITION, AND "INSIDE JSX" IS
                                              *      NOT ONE POSITION. ** This file records the identical mistake
                                              *      made in a ternary branch in GanttCalendar.tsx.
                                              *
                                              * > *"whole box should be used like in the client box, where text
                                              * > the client box goes all the way to the left. We are loosing
                                              * > vaulable space on the driver card."*
                                              *
                                              * ⛔ THE PARTY CARDS HAVE NO ARRIVAL COLUMN, which is the entire
                                              * reason their comms boxes already reach the card edge and these
                                              * did not. It was never a styling difference between the two
                                              * cards; it was a column one of them has.
                                              * ⭐ `-ml-14` = the `w-12` column (3rem) plus the row's `gap-2`
                                              * (0.5rem). ⚠️ Both halves of that sum are literal classes on the
                                              * row above, so a change to either is a change to this.
                                              *   ** A NEGATIVE MARGIN IS AN ARITHMETIC DEPENDENCY ON A SIBLING.
                                              *      IT IS ONLY SAFE WHILE BOTH NUMBERS ARE WRITTEN DOWN. **
                                              */
                                            <div className="-ml-14 mt-1.5 space-y-1.5">
                                            {/**
                                              * ---- ⭐⭐ TWO PANELS, NOT ONE LIST (Pete, 2026-09-09) ----------
                                              *
                                              * > *"Call the driver in Teams correct. I also want the abiltiy to
                                              * > text the driver from here. and either select the canned messages
                                              * > (dock door assigned, truck unloaded, etc) or to be able to enter
                                              * > my own text and send it to the driver. Should be two panels
                                              * > (Call the driver in Teams)(Text the Driver)"*
                                              *
                                              * ⚠️ ~~ONE FLAT MENU~~ — it put a call link at the bottom of a list
                                              * of texts, so on an unmatched card the whole thing read as an
                                              * apology with a stray link under it. ⭐ Two headed panels say what
                                              * each half DOES, and let one work while the other cannot.
                                              *
                                              * ⛔ CALL IS FIRST AND IS NEVER GATED. It needs a number, not an
                                              * appointment — see the note on the Teams row.
                                              */}

                                            {/* ============ 1. CALL ============ */}
                                            <div className="rounded border bg-background p-1 shadow-sm">
                                                <div className="px-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                    Call the driver in Teams
                                                </div>
                                                {(() => {
                                                    const href = teamsCallHref(c.driverPhoneEntered);
                                                    const cls = 'flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-[11px]';
                                                    if (!href) {
                                                        /* ⭐ §1N.2 — ABSENCE IS REPORTED, NEVER PAPERED OVER. */
                                                        return (
                                                            <div className={`${cls} text-muted-foreground`}>
                                                                <PhoneCall className="h-3 w-3 shrink-0" />
                                                                {c.driverPhoneEntered
                                                                    ? 'Cannot dial this number automatically — copy it above.'
                                                                    : 'No phone number on this arrival.'}
                                                            </div>
                                                        );
                                                    }
                                                    return (
                                                        <a
                                                            href={href}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => { e.stopPropagation(); setSmsFor(null); }}
                                                            onDoubleClick={(e) => e.stopPropagation()}
                                                            title={`Call ${c.driverPhoneEntered} in Microsoft Teams`}
                                                            className={`${cls} hover:bg-muted`}
                                                        >
                                                            <PhoneCall className="h-3 w-3 shrink-0" />
                                                            Call {c.driverPhoneEntered}
                                                        </a>
                                                    );
                                                })()}
                                            </div>

                                            {/* ============ 2. TEXT ============ */}
                                            {/**
                                              * ---- ⛔⛔ "ENTER MY OWN TEXT" IS THE **NOTE**, AND HERE IS WHY ---
                                              *
                                              * ⚠️ Pete asked to *"enter my own text and send it to the driver"*.
                                              * ⛔ ARCH §1N.4 FORBIDS A FREE BODY, in those words:
                                              *
                                              *   *"A preview endpoint and a send endpoint both build the message
                                              *   from the current row … no authenticated caller can push arbitrary
                                              *   text through Symbia's toll-free number. A scheduler may add a
                                              *   short, length-capped note; that is all."*
                                              *
                                              * ⭐ SO EACH CANNED MESSAGE OPENS A PREVIEW WITH A 160-CHARACTER
                                              * LINE OF YOUR OWN — `DoorTextDialog`'s *"Add a line (optional)"*,
                                              * live-previewed, appended to the composed body. That is as close to
                                              * free text as the rule allows, and it is already built.
                                              *
                                              * ⚠️ THE HINT BELOW SAYS SO ON SCREEN. A scheduler who came here for
                                              * free text must not have to discover the note by opening all three
                                              * dialogs — and must not conclude the feature is missing.
                                              *
                                              * ⛔ RAISED WITH PETE 2026-09-09 rather than silently substituted.
                                              * If the rule is to change, it changes in the arch doc and on the
                                              * server first — not by a control here quietly sending a raw body.
                                              */}
                                            <div className="rounded border bg-background p-1 shadow-sm">
                                                <div className="px-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                    Text the driver
                                                </div>
                                            {/**
                                              * ---- ⭐⭐ ~~"NOT UNTIL THIS ARRIVAL IS MATCHED"~~ — GONE ------
                                              *
                                              * > Pete, 2026-09-09: *"I said we can text the driver period. Not a
                                              * > message telling me I can't do it because of the status."*
                                              *
                                              * ⛔⛔ THE REFUSAL WAS ACCURATE AND STILL WRONG. "The three canned
                                              * messages cannot be composed without a load" was true. "The driver
                                              * cannot be texted" does not follow from it — and that inference was
                                              * being made on the scheduler's behalf, twice, in a panel whose
                                              * entire job is reaching the man in the yard.
                                              *
                                              * ⭐ IT WAS A MISSING ENDPOINT, NOT A UI DECISION. Every send route
                                              * was keyed on an appointment id. `POST mgmt/checkins/{id}/text`
                                              * (#340) is keyed on the ARRIVAL, so this box works on every card.
                                              *
                                              * ⚠️ §1N.4 STILL HOLDS: the server composes a facility stem and
                                              * appends this line. The caller sends the LINE, never the message.
                                              */}
                                            {unmatched ? (
                                                /**
                                                 * ⚠️ WHY THERE IS A SENTENCE HERE AT ALL, given Pete
                                                 * removed one from this exact spot on 2026-09-09:
                                                 * *"Not a message telling me I can't do it because of
                                                 * the status."*
                                                 *
                                                 * ⛔ THAT ONE REFUSED TO TEXT. This one refuses nothing
                                                 * — the box below is open and works. It answers the
                                                 * question Pete asked on 2026-09-10 looking at two
                                                 * cards side by side: *"why are these to text options
                                                 * different?"* ⭐ The shortcuts are composed from the
                                                 * load and its door, so a card with neither cannot
                                                 * have them; without a word here that reads as a
                                                 * broken card rather than a different one.
                                                 */
                                                <div className="px-1 pb-1 text-[10px] text-muted-foreground">
                                                    Standard messages need an appointment — match this arrival to get them.
                                                </div>
                                            ) : (
                                            <>
                                                {/**
                                                  * ⭐ THE CANNED MESSAGES COME FIRST (Pete, 2026-09-10):
                                                  * *"put the Or send a standard Message above Text the Driver -
                                                  * freeform text should be last option from top of the square."*
                                                  *
                                                  * ⭐ AND HE IS RIGHT ABOUT THE ORDER OF WORK: three taps cover
                                                  * the common cases, and typing is the exception. Putting the
                                                  * textarea first made the fast path scroll past the slow one.
                                                  *
                                                  * ⚠️ STILL ONLY ON A MATCHED CARD. They are composed from the
                                                  * load and its door, so they genuinely need one — but their
                                                  * absence no longer takes the whole panel down with it, which
                                                  * is why the freeform box below renders either way.
                                                  */}
                                                {/* ⛔⛤ ~~"Or send a standard message"~~ — REMOVED 2026-09-24.
                                                    Pete: *"The Or send a standard message needs to be removed."*
                                                    ⚠️ The three buttons below say what they are; a heading that
                                                    names the category above them is a caption for a picture that
                                                    already has one.
                                                    ⛔ THE `border-t` WENT WITH IT AND IS RESTORED on the first
                                                    button instead — the rule was separating the freeform box from
                                                    the canned list, and that separation is still wanted. */}
                                                {onTextDoor && (
                                                    <button
                                                        type="button"
                                                        disabled={busy !== null}
                                                        onClick={() => { keepCardOpen(c.checkInId); onTextDoor(c); }}
                                                        title="Open the door text, with a line you can add"
                                                        className="flex w-full items-center gap-1.5 rounded border border-transparent px-2 py-1
                                                                   text-left text-[11px] hover:border-border hover:bg-muted disabled:opacity-40"
                                                    >
                                                        <DoorOpen className="h-3 w-3 shrink-0" />
                                                        {c.doorCode
                                                            ? `Text his door (${c.doorCode})`
                                                            : 'Text driver — no door assigned yet'}
                                                    </button>
                                                )}
                                                {/**
                                                  * ⭐⭐ #302 — THE WORK TEXT, ABOVE THE PAPERWORK ONE
                                                  * because it happens first in the visit: unloading
                                                  * starts, then the paperwork is ready. ⚠️ The menu
                                                  * reads in the order the day happens.
                                                  */}
                                                {onTextWork && (
                                                    <button
                                                        type="button"
                                                        disabled={busy !== null}
                                                        onClick={() => { keepCardOpen(c.checkInId); onTextWork(c); }}
                                                        title="Tell the driver work on his truck has started"
                                                        className="flex w-full items-center gap-1.5 rounded border border-transparent px-2 py-1
                                                                   text-left text-[11px] hover:border-border hover:bg-muted disabled:opacity-40"
                                                    >
                                                        <MessageSquare className="h-3 w-3 shrink-0" />
                                                        {c.direction === 'OUTBOUND'
                                                            ? 'Text — loading started'
                                                            : 'Text — unloading started'}
                                                    </button>
                                                )}
                                                {onTextPaperwork && (
                                                    <button
                                                        type="button"
                                                        disabled={busy !== null}
                                                        onClick={() => { keepCardOpen(c.checkInId); onTextPaperwork(c); }}
                                                        title="Tell the driver his paperwork is ready to collect"
                                                        className="flex w-full items-center gap-1.5 rounded border border-transparent px-2 py-1
                                                                   text-left text-[11px] hover:border-border hover:bg-muted disabled:opacity-40"
                                                    >
                                                        <MessageSquare className="h-3 w-3 shrink-0" />
                                                        Text — paperwork is ready
                                                    </button>
                                                )}
                                                {/* ⛔⛤ ~~"Pick one, then add your own line (160 characters) in
                                                    the preview before sending."~~ — REMOVED 2026-09-24. Pete:
                                                    *"The Pick one, then add your own line blurb needs to be
                                                    removed."*
                                                    ⚠️ IT WAS INSTRUCTIONS FOR A FLOW, ON A CARD, PERMANENTLY.
                                                    A scheduler reads it once and then pays for it on every card
                                                    for the rest of the year.
                                                      ** ONBOARDING TEXT THAT NEVER GOES AWAY IS NOT ONBOARDING,
                                                         IT IS FURNITURE. **
                                                    ⚠️ The behaviour it described is unchanged — the canned
                                                    buttons still open a preview you can add a line to. Only the
                                                    standing explanation went. */}
                                            </>
                                            )}
                                            <div className="px-1 pb-1">
                                                <textarea
                                                    value={adHocText}
                                                    onChange={(e) => setAdHocText(e.target.value.slice(0, 160))}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onDoubleClick={(e) => e.stopPropagation()}
                                                    rows={2}
                                                    maxLength={160}
                                                    placeholder="Text a message to this driver…"
                                                    aria-label="Message to the driver"
                                                    className="w-full resize-none rounded border bg-background px-1.5 py-1 text-[11px]"
                                                />
                                                <div className="mt-0.5 flex items-center gap-2">
                                                    {/* ⚠️ THE COUNTER IS ALWAYS SHOWN. 160 is the cap the SERVER
                                                        enforces too; a scheduler who hits it silently would
                                                        watch the end of a sentence disappear. */}
                                                    <span className={`text-[10px] ${adHocText.length >= 160 ? 'font-medium text-amber-700' : 'text-muted-foreground'}`}>
                                                        {adHocText.length}/160
                                                    </span>
                                                    <button
                                                        type="button"
                                                        /* ⛔ DISABLED ON AN EMPTY MESSAGE, because the server
                                                           REFUSES one — the note IS the message here, and a bare
                                                           facility stem reaching a driver's phone is worse than
                                                           a disabled button. */
                                                        disabled={adHocBusy || adHocText.trim().length === 0 || !c.driverPhoneEntered}
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            setAdHocBusy(true);
                                                            setAdHocError(null);
                                                            try {
                                                                const r = await sendCheckInText(
                                                                    await getToken(), c.checkInId,
                                                                    [c.driverPhoneEntered as string], adHocText.trim(),
                                                                );
                                                                /* ⛔ A 200 IS NOT A DELIVERY. The route answers 200
                                                                   with the per-recipient outcomes in the body, so a
                                                                   carrier refusal must be READ, not assumed away —
                                                                   this is the exact shape that let a broken SMS path
                                                                   go unnoticed for a day in August. */
                                                                if ((r.delivered ?? 0) > 0) {
                                                                    /* ⛔⛤ ~~"a successful send closes the text panel"~~
                                                                       -- 2026-09-24. It does not, any more. The panel
                                                                       stays open so the confirmation below is visible;
                                                                       only the chevron and the comms icons close it. */
                                                                    /* ⭐ ...AND IT SAYS SO. See `sentNote`. */
                                                                    noteSent(c.checkInId, phoneTail(c.driverPhoneEntered));
                                                                    keepCardOpen(c.checkInId);
                                                                    void load(true);
                                                                } else {
                                                                    setAdHocError(
                                                                        r.results?.[0]?.detail
                                                                        || r.results?.[0]?.outcome
                                                                        || 'The carrier did not accept it.');
                                                                }
                                                            } catch (err) {
                                                                setAdHocError(err instanceof Error ? err.message : String(err));
                                                            } finally {
                                                                setAdHocBusy(false);
                                                            }
                                                        }}
                                                        className="ml-auto rounded border bg-foreground px-2 py-0.5 text-[11px]
                                                                   text-background disabled:opacity-40"
                                                    >
                                                        {adHocBusy ? 'Sending…' : 'Send'}
                                                    </button>
                                                </div>
                                                {/* ⭐ ABSENCE AND FAILURE BOTH REPORTED — §1N.2. */}
                                                {!c.driverPhoneEntered && (
                                                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                                                        No phone number on this arrival.
                                                    </div>
                                                )}
                                                {adHocError && (
                                                    <div className="mt-0.5 text-[10px] font-medium text-rose-700">
                                                        Not sent — {adHocError}
                                                    </div>
                                                )}
                                            </div>

                                            {/**
                                              * ============ 3. EMAIL ============
                                              *
                                              * ⭐ Pete, 2026-09-20: *"Communication needs to stay but add the
                                              * email things we discussed before"*, and, asked directly whether
                                              * the DRIVER card gets one too: **yes**.
                                              *
                                              * ⚠️ THE DESIGN NEVER SPECIFIED ONE HERE. Decision 13 put the new
                                              * email section on the PARTY cards; drivers were not mentioned. So
                                              * this was asked rather than assumed, and the answer widened it.
                                              *
                                              * ⛔⛔ AND THERE IS NOWHERE TO SEND. `dock.DriverCheckIn` carries a
                                              * PHONE — that is the whole premise of the panel above, and of the
                                              * kiosk that captures it. There is no driver email column anywhere
                                              * measured. ⚠️ A driver is reached on the cab's phone, which is why
                                              * this has never existed; that is an argument, not a refusal, and
                                              * Pete's answer stands.
                                              *
                                              * ✅ SO IT SHIPS DISABLED, CARRYING ITS OWN REASON — decision 185's
                                              * precedent, chosen by Pete again on 2026-09-20 for the party
                                              * cards. The section is visible, the gap is visible, and nothing
                                              * pretends to work.
                                              *   ** A CONTROL THAT STATES ITS OWN BLOCKER MAKES A MISSING FIELD
                                              *      VISIBLE. ONE THAT LOOKS LIVE MAKES IT A SUPPORT CALL. **
                                              * ⬜ It needs a `DriverEmail` column and §3B says that migration is
                                              * immutable once run — so it is recorded, not written in passing.
                                              */}
                                            <div className="rounded border bg-background p-1 shadow-sm">
                                                <div className="px-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                    Email the driver
                                                </div>
                                                <button
                                                    type="button"
                                                    disabled
                                                    title="No email is stored for drivers — arrivals capture a phone, not an address"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex w-full items-center gap-1.5 rounded px-2 py-1
                                                               text-left text-[11px] opacity-40"
                                                >
                                                    <Mail className="h-3 w-3 shrink-0" />
                                                    Email — his load and door
                                                </button>
                                                <div className="px-2 pb-1 text-[10px] text-muted-foreground">
                                                    No email is stored for drivers — an arrival captures a phone,
                                                    not an address.
                                                </div>
                                            </div>

                                            </div>
                                            </div>
                                        )}
                                        {/* ⭐ THE ACTIONS SPAN THE CARD TOO — same `-ml-14` as the comms
                                            boxes directly above them, for the same reason and off the same
                                            two numbers. A button strip that stopped short of the boxes above
                                            it would read as a different section. */}
                                        <div className="-ml-14 mt-1.5 flex flex-wrap gap-1">
                                            {unmatched && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => setMenuFor(menuFor === c.checkInId ? null : c.checkInId)}
                                                        disabled={busy !== null}
                                                        title="Attach this arrival to one of today's appointments"
                                                        className="rounded border px-1.5 py-0.5 text-[11px] disabled:opacity-40"
                                                    >
                                                        Attach…
                                                    </button>
                                                    {/**
                                                      * ⭐ FIND THE LOAD FOR ME — the same
                                                      * panel the licence scan opens, reachable
                                                      * without one.
                                                      *
                                                      * ⛔ THIS IS #212a AND IT IS LOAD-BEARING:
                                                      * the scan is the natural TRIGGER for
                                                      * matching, never a PRECONDITION. A licence
                                                      * that will not read, or one left in the
                                                      * cab, must not mean there is no way to
                                                      * attach the truck — that gets discovered
                                                      * at a gate, by a queue.
                                                      *
                                                      * ⚠️ It differs from "Attach…" on purpose:
                                                      * that lists EVERY live load today, this
                                                      * lists the ones that match this driver and
                                                      * says what they matched on.
                                                      */}
                                                    <button
                                                        type="button"
                                                        onClick={() => setMatchFor(c)}
                                                        disabled={busy !== null}
                                                        title="Suggest appointments that match this driver, and say what matched"
                                                        className="rounded border px-1.5 py-0.5 text-[11px] disabled:opacity-40"
                                                    >
                                                        Find load…
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => onCreateFromCheckIn(c)}
                                                        disabled={busy !== null}
                                                        title="Create a new appointment for this arrival"
                                                        className="rounded border px-1.5 py-0.5 text-[11px] disabled:opacity-40"
                                                    >
                                                        New appointment
                                                    </button>
                                                </>
                                            )}
                                            {/**
                                              * ⛔ EDIT / TEXT DOOR / PAPERWORK READY / SCAN LICENSE
                                              * / PAPERWORK / VOID USED TO BE SIX BUTTONS HERE.
                                              * They are now the icon strip beside the driver's name,
                                              * and Edit is a double-click on the card — Pete,
                                              * 2026-08-25: *"This completely removes all buttons and
                                              * gives a clean, much need real estate look."*
                                              *
                                              * ⭐ WHAT SURVIVES AS WORDS IS WHAT CHANGES STATE.
                                              * "Attach…" and "New appointment" are the whole job of
                                              * an unmatched card, and "Mark Departed" sends a truck
                                              * off site through the outbound release gate. Those are
                                              * decisions, not tools, and a nameless icon is the
                                              * wrong shape for a decision.
                                              */}
                                            {/* ⭐ MARK DEPARTED, to match what the appointment
                                                can do (#141). ⛔ It calls the SAME endpoint the
                                                appointment uses, deliberately — that path holds
                                                the outbound release gate (#97), and a driver card
                                                that departed a truck by another route would be a
                                                way around a check the appointment enforces.
                                                ⚠️ Only for a matched arrival that has not already
                                                gone: there is no appointment to depart otherwise. */}
                                            {onMarkDeparted && !unmatched && !departed(c) && (
                                                <button
                                                    type="button"
                                                    onClick={() => onMarkDeparted(c)}
                                                    disabled={busy !== null}
                                                    title="Send this truck off site - runs the outbound release checks"
                                                    className="rounded border px-1.5 py-0.5 text-[11px] disabled:opacity-40"
                                                >
                                                    Mark Departed
                                                </button>
                                            )}
                                        </div>

                                        {/**
                                          * ---- ⛔⚤ ~~THE ARRIVAL'S OWN LOG, INLINE ON THE CARD (#140)~~ ----------
                                          *      **REMOVED 2026-09-20.**
                                          *
                                          * ⭐ Pete: *"The Arrival History, for example, doesn't need to be on the card
                                          * anymore because single clicking the driver will show the history in the
                                          * history panel."*
                                          *
                                          * ⭐⭐ DECISION 194 IS WHAT RETIRED IT, NOT A CHANGE OF TASTE. A single click
                                          * now scopes the History RAIL to this driver, so the card was rendering a
                                          * second copy of a list already on screen, in a narrower column, capped at
                                          * eight lines.
                                          *   ** WHEN A NEW SURFACE ANSWERS A QUESTION BETTER, THE OLD ANSWER IS NOT
                                          *      "ALSO AVAILABLE" — IT IS A SECOND PLACE TO BE WRONG. **
                                          *
                                          * ⚠️ AND EVERYTHING THAT FED IT WENT WITH IT — `histFor`, `histPinned`,
                                          * `toggleHistPin`, `beginHover`, the (i) card icon and the `…` menu item. A
                                          * state nobody renders is not harmless: it stays in `detailOpen`, in
                                          * `collapseAllCards`, and in the next reader's mental model.
                                          *   ** DELETING A FEATURE MEANS DELETING WHAT FED IT, NOT JUST WHAT SHOWED IT. **
                                          *
                                          * ⚠️ ONE CONSEQUENCE, NAMED RATHER THAN DISCOVERED: the History rail obeys the
                                          * two-panel cap (decision 193). If it is evicted, this driver's history is now
                                          * reachable only by closing another panel — where the card used to carry a copy.
                                          */}

                                        {menuFor === c.checkInId && (
                                            <div className="mt-1.5 rounded border bg-background p-1.5">
                                                {attachable.length === 0 ? (
                                                    <div className="text-[11px] text-muted-foreground">
                                                        No open appointments today to attach to.
                                                    </div>
                                                ) : (
                                                    <select
                                                        autoFocus
                                                        defaultValue=""
                                                        onChange={(e) => e.target.value && void doAttach(c, e.target.value)}
                                                        className="w-full rounded border px-1 py-0.5 text-[11px]"
                                                    >
                                                        <option value="" disabled>Choose an appointment…</option>
                                                        {attachable.map((a) => (
                                                            <option key={String(a.appointmentId)} value={String(a.appointmentId)}>
                                                                {hhmm(a.scheduledStartUtc)} · {a.appointmentNumber} · {a.carrierCode}
                                                                {a.drivers && a.drivers.length > 0 ? ' (has driver)' : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        )}
                                        </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                        })}
                    </div>
                    ))
                )}
            </div>

            {/**
              * ---- ⭐ VOIDED ARRIVALS, AND WHY THEY ARE DOWN HERE -----------------
              *
              * Pete, 2026-08-25: *"Yes, show voided."* — after being told that a
              * voided arrival's documents survive intact and simply become
              * unreachable, because the card carrying the only route to them
              * disappears.
              *
              * ⛔ OUTSIDE THE SCROLLING LIST ON PURPOSE. The list above is the work:
              * trucks that are here now. This is a record to be inspected, and
              * putting it in the same scroll would mix "deal with this" and "look
              * this up" in the one column a supervisor reads at a glance.
              *
              * ⚠️ NO ARRIVAL NUMBER IS SHOWN, AND NONE IS INVENTED. A voided row has
              * never held a position in the day's sequence, so any number here would
              * collide with a live truck's.
              */}
            {(showVoided || voided.length > 0) && (
                <div className="border-t">
                    <button
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted/50"
                        onClick={() => setShowVoided(v => !v)}
                    >
                        <span>Voided arrivals{showVoided ? ` · ${voided.length}` : ''}</span>
                        <span aria-hidden="true">{showVoided ? '▾' : '▸'}</span>
                    </button>
                    {showVoided && (
                        <div className="max-h-48 overflow-y-auto border-t">
                            {voided.length === 0 ? (
                                <div className="px-3 py-2 text-xs text-muted-foreground">
                                    Nothing was voided today.
                                </div>
                            ) : voided.map(v => (
                                <div key={v.checkInId} className="flex items-start gap-2 border-b px-3 py-1.5 text-xs last:border-b-0">
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-muted-foreground line-through">
                                            {v.driverName || 'Driver not named'}
                                            {v.driverPhoneEntered ? ` · ${v.driverPhoneEntered}` : ''}
                                        </div>
                                        {/* ⭐ THE REASON IS THE POINT OF THE ROW. */}
                                        <div className="truncate text-[11px] text-muted-foreground">
                                            {v.voidReason || 'no reason recorded'}
                                            {v.appointmentNumber ? ` · was on ${v.appointmentNumber}` : ''}
                                        </div>
                                    </div>
                                    {/**
                                      * ⭐ THE WHOLE REASON THIS SECTION EXISTS. Without it the
                                      * documents are stored, unvoided, correctly linked and
                                      * unreachable.
                                      */}
                                    {v.paperworkCount > 0 && onVoidedPaperwork && (
                                        <button
                                            type="button"
                                            title={`Paperwork — ${v.paperworkCount} still on file`}
                                            aria-label={`Paperwork — ${v.paperworkCount} still on file`}
                                            className="mt-0.5 shrink-0 rounded p-1 hover:bg-muted"
                                            onClick={() => onVoidedPaperwork(v)}
                                        >
                                            <Eye className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <SuggestedLoadDialog
                open={suggestion != null}
                result={suggestion?.result ?? null}
                documentName={suggestion?.documentName ?? ''}
                onView={() => onSuggestionView?.()}
                onAttach={(appointmentId) => {
                    const c = rows.find(x => x.checkInId === suggestion?.checkInId);
                    onSuggestionClose?.();
                    // ⭐ THE ONE ATTACH PATH. See the prop's comment.
                    if (c) void doAttach(c, appointmentId);
                }}
                onClose={() => onSuggestionClose?.()}
            />

            {/**
              * ---- ⭐⭐ ONE HIDDEN FILE INPUT FOR THE WHOLE PANEL --------------------
              *
              * > Pete, 2026-09-08: *"go directly to file explorer, pick a file(s), then
              * > back to the driver panel."*
              *
              * ⚠️ ~~THE ATTACH DIALOG WAS HERE~~ and it lasted about an hour. It was the
              * right answer to *"looks terrible"* — an inline drop zone in a 340px rail
              * pushed every card below it down — and the wrong answer to what he wanted,
              * which is no surface at all. ⭐ The dialog with `autoBrowse` still left a
              * popup sitting behind the picker and another one to dismiss afterwards:
              * TWO extra steps to remove one.
              *
              * ⛔ RENDERED ONCE, NOT PER CARD. Twenty arrivals would be twenty inputs.
              *
              * ⚠️ `multiple` — he said *"pick a file(s)"*, and `useFileUpload` already
              * loops sequentially and reports a partial count if one of five fails.
              *
              * ⛔⛔ SNAPSHOT, THEN CLEAR, THEN LET IT RUN — AND THE ORDER IS LOAD-BEARING.
              * `run()` takes its snapshot as its first statement, synchronously, before
              * any await. Clearing `value` after that call is therefore safe and is what
              * lets the SAME file be picked twice in a row — which people do when they
              * think the first go did not work. ⚠️ Clearing BEFORE `run` would hand it an
              * empty list and upload nothing, silently: #288 exactly.
              */}
            <input
                ref={paperworkInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                    void paperwork.run(e.target.files);
                    e.target.value = '';
                }}
            />

            {/**
              * ---- ⭐⭐ THE DRIVER CARD'S MENU (#341) ---------------------------
              *
              * ⚠️ RENDERED ONCE, AT PANEL LEVEL, not per card. `ContextMenu`
              * positions itself at a screen coordinate and closes on click-outside
              * or Escape; one instance driven by `cardMenu` is the whole thing.
              *
              * ⛔ `onClose` FIRES BEFORE THE ITEM'S OWN HANDLER on some paths, so
              * the menu clears itself and the action runs — never the other way
              * round, which would leave a menu floating over a dialog it opened.
              */}
            {cardMenu && (
                <ContextMenu
                    x={cardMenu.x}
                    y={cardMenu.y}
                    onClose={() => setCardMenu(null)}
                    items={cardMenuItems(cardMenu.c)}
                />
            )}

            {/**
              * ---- ⭐⭐⭐ THE PARTY CARD'S `…` MENU (Pete, 2026-09-20, item 13) -------
              *
              * ⛔⛔ EVERY ITEM IS EITHER REAL OR CARRIES ITS BLOCKER IN THE LABEL, and
              * that is forced by `MenuItem`: it has `disabled` but no `title`, so a
              * greyed row with a tidy label would be a dead line with no explanation
              * anywhere on the screen. ⚠️ Putting the reason IN the words is the only
              * place it can go, and it is the better place anyway — a tooltip is a
              * thing you have to already suspect.
              *   ** IF THE TYPE HAS NOWHERE TO PUT THE REASON, THE REASON GOES IN THE
              *      NAME. A DISABLED ROW THAT DOES NOT SAY WHY IS A BUG REPORT. **
              *
              * ⛔ THE RECORD SCREEN IS THE REAL BLOCKER AND IT IS NAMED, NOT OMITTED.
              * Decision 190 E — the client / carrier / consignee admin screens — does
              * not exist, which is also why item 11 (double-click a party card) is not
              * built: a double-click's only sensible destination is that screen, and a
              * gesture that opens nothing teaches the scheduler the card is broken.
              *   ** A GESTURE WITH NOWHERE TO GO IS NOT A MISSING HANDLER, IT IS A
              *      MISSING DESTINATION. BUILD THE DESTINATION FIRST. **
              *
              * ⭐ `Scope the board` / `Clear the scope` ARE THE SAME TWO CALLS THE CARD
              * CLICK MAKES — `onChooseParty` and `resetAll`, not a second pair. The
              * menu is the discoverable copy of a gesture, exactly as the driver card's
              * "All options" is the discoverable copy of a right-click.
              */}
            {partyMenu && (
                <ContextMenu
                    x={partyMenu.x}
                    y={partyMenu.y}
                    onClose={() => setPartyMenu(null)}
                    items={partyMenuItems(partyMenu.row)}
                />
            )}

            {/**
              * ⭐ #73 — ⚠️ `load(true)` AND `onChanged()`, the same pair the licence
              * dialog uses. The flag is cleared by the SERVER inside the same UPDATE,
              * so the card cannot be patched in place from here: only a reload knows
              * whether the alert is gone.
              *   ** A SCREEN THAT GUESSES WHAT THE SERVER DECIDED IS A SECOND COPY
              *      OF THE RULE. **
              */}
            <VehicleNumberDialog
                open={vehicleFor !== null}
                checkIn={vehicleFor}
                getToken={getToken}
                onClose={() => setVehicleFor(null)}
                onSaved={() => { void load(true); onChanged(); }}
            />

            {/* ⭐ MOUNTED BESIDE ITS TWIN, with the same props in the same order — the
                two dialogs answer the two halves of the same question and a reader
                comparing them should find nothing to compare. */}
            <PaperworkNumberDialog
                open={paperworkFor !== null}
                checkIn={paperworkFor}
                getToken={getToken}
                onClose={() => setPaperworkFor(null)}
                onSaved={() => { void load(true); onChanged(); }}
            />

            <LicenceScanDialog
                open={licenceFor !== null}
                checkIn={licenceFor}
                getToken={getToken}
                onClose={() => setLicenceFor(null)}
                // Reload rather than patch the row in place: a scan can change the
                // driver's NAME on the arrival, and a card still showing what he
                // typed would contradict the record just written.
                onSaved={(identity) => {
                    void load(true);
                    onChanged();
                    /**
                     * ⭐ STRAIGHT INTO "WHICH LOAD IS THIS?" — the whole point of Pete's
                     * ordering. The scheduler is looking at this driver right now, and
                     * the licence has just made the identifiers better than the phone
                     * number he typed at the gate.
                     *
                     * ⚠️ ONLY WHEN THE ARRIVAL IS STILL UNATTACHED. Re-scanning a licence
                     * on a truck already on a load must not re-open the question.
                     */
                    /**
                     * ⛔ THE NAME WE JUST LEARNED, NOT THE ONE FROM BEFORE THE SCAN.
                     *
                     * ⚠️ This passed `licenceFor` straight through — the arrival AS IT
                     * WAS — so "Which load is this?" opened saying *"Driver not named"*
                     * seconds after the licence had told us TONY DANIEL ORTEGA (#235).
                     * `load(true)` above will refresh the row eventually, but the
                     * dialog opens on THIS tick and would render the stale object.
                     *
                     * ⭐ A screen that contradicts the screen behind it teaches a
                     * scheduler to trust neither, which is expensive on the one
                     * workflow whose output is evidence.
                     */
                    const c = licenceFor;
                    if (c && c.appointmentId === null) {
                        setMatchFor(identity?.displayName
                            ? { ...c, driverName: identity.displayName }
                            : c);
                    }
                }}
            />

            <MatchArrivalDialog
                open={matchFor !== null}
                checkIn={matchFor}
                getToken={getToken}
                onClose={() => setMatchFor(null)}
                onAttach={(c, appointmentId) => void doAttach(c, appointmentId)}
                onCreate={(c) => onCreateFromCheckIn(c)}
            />

            <ConfirmDialog
                open={attachAsk !== null}
                title="Attach a different driver?"
                message={attachAsk
                    ? `${attachAsk.apptNumber} already lists ${attachAsk.names}. `
                      + `Attach ${attachAsk.c.driverName || 'this arrival'} to it as well?`
                    : ''}
                confirmLabel="Attach"
                busy={busy !== null}
                onConfirm={() => { if (attachAsk) void attachNow(attachAsk.c, attachAsk.appointmentId); }}
                onCancel={() => setAttachAsk(null)}
            />

            {/* ⚠️ EDIT IS A PANEL AT THE FOOT, not an inline swap inside the card.
                A card that turns into a form loses its arrival number, its status
                colour and its position in the list at the moment the scheduler is
                trying to check he is editing the right truck. */}
            {/**
              * ---- ⭐⭐ A DIALOG, NOT A PANEL AT THE FOOT (Pete, 2026-09-10) ----
              *
              * > *"the edit driver should be a popup, not inline like it is
              * > today"* — sent while he was stuck in the inline version with the
              * > Save and Cancel buttons clipped off the bottom of the rail.
              *
              * ⛔ THE INLINE PANEL HAD NO MAX-HEIGHT AND NO SCROLL. On a short
              * rail its buttons fell past the end of the card with no gesture
              * that could reach them — the form was open, unusable, and the only
              * way out was reloading the page. ⚠️ Exactly the shape the dialog
              * shell was fixed for on 2026-09-09: a container taller than its box
              * that cannot scroll traps the reader.
              *
              * ⭐ `DialogContent` already solves this properly — `max-h-[90vh]`,
              * an inner scroller, and a close button pinned OUTSIDE it. Reusing
              * the shell is why this is a deletion rather than a new scrollbar.
              *
              * ⚠️ THE OLD NOTE HERE ARGUED FOR THE PANEL: *"a card that turns
              * into a form loses its arrival number, its status colour and its
              * position in the list."* ⭐ THAT CONCERN IS MET, NOT DISMISSED —
              * the arrival number is in the dialog title, and the card behind is
              * still on screen rather than replaced.
              */}

            <Dialog open={editFor !== null} onOpenChange={(o) => { if (!o) setEditFor(null); }}>
                {/**
                  * ⛔ IT SCROLLS. Pete, 2026-09-10: *"Make sure a scollbar is added if
                  * needed."* This popup now carries a header row, a tool strip, an
                  * expandable options list AND eight fields — on a laptop that is
                  * taller than the viewport, and a dialog that overflows puts Save
                  * off the bottom edge with no way to reach it. ⚠️ That exact trap is
                  * what the inline arrival editor was replaced for: *"It is now stuck
                  * here with no options."*
                  */}
                <DialogContent className="flex max-h-[85vh] max-w-md flex-col overflow-y-auto">
                    {editFor && (
                    <>
                    <DialogHeader>
                        {/* ⛔ THE DRIVER'S NAME IS IN THE TITLE. A dialog that says only
                            "arrival #1" is one you cannot check you are on the right truck
                            from — and this one edits eight fields. */}
                        {/**
                          * ⛔⛔ THE PLACEHOLDER IS NOT OPTIONAL — IT IS THE CARD'S ANCHOR.
                          *
                          * > Pete, 2026-09-10: *"Also not showing the header info? Thought
                          * > we had that as a task. for the driver - matching the header
                          * > from the driver card"*
                          *
                          * ⚠️ `driverName ? … : ''` DROPPED THE WHOLE CLAUSE on an arrival
                          * checked in before anybody carded the driver — which is most of
                          * them, at the moment they are edited. The card renders
                          * `{c.driverName || 'Awaiting driver name'}` and keeps the
                          * weight, deliberately (see its long note on why a placeholder is
                          * not automatically secondary). ⛔ Matching the card means
                          * matching that too, not just the fields that happen to be set.
                          */}
                        <DialogTitle>
                            Arrival #{editFor.arrivalSeq ?? '\u2014'}
                            {` \u00b7 ${editFor.driverName || 'Awaiting driver name'}`}
                        </DialogTitle>
                        {/**
                          * ---- ⭐⭐ THE CARD'S OWN HEADER FACTS, ON THE POPUP -----------
                          *
                          * > Pete, 2026-09-10: *"This needs to have a header like the driver
                          * > card. Show Arrival number, truck number, time, carrier, status,
                          * > dock door and the issues icon."*
                          *
                          * ⛔ EVERY VALUE COMES FROM THE HELPER THE CARD USES — `statusWord`,
                          * `statusTone`, `arrivalWarnings`, `apptById` — never from a second
                          * reading of the same fields. ⚠️ A popup that computed "Arrived" its
                          * own way would eventually disagree with the card behind it about
                          * the same truck, and the person would have no way to tell which was
                          * right.
                          *
                          * ⚠️ THE ISSUES ICON IS DELIBERATELY NOT A BUTTON HERE. On the card
                          * it expands the warning rows; there is nothing to expand in a modal,
                          * so it carries the same count and the same `title` — every warning
                          * on its own line — and does not pretend to be clickable.
                          */}
                        {(() => {
                            const word = statusWord(editFor);
                            const warns = arrivalWarnings(editFor).map(w => w.text);
                            return (
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                                    {editFor.clientTruckNo !== null && (
                                        <span className="rounded bg-slate-600 px-1 py-0.5 font-semibold text-white"
                                              title={clientTruckTitle(editFor)}>
                                            truck {editFor.clientTruckNo}
                                        </span>
                                    )}
                                    <span>{hhmm(editFor.checkedInUtc)}</span>
                                    {editFor.carrierCode && <span>· {editFor.carrierCode}</span>}
                                    {word && (
                                        <span className={'font-medium ' + statusTone(word)}>· {word}</span>
                                    )}
                                    {/**
                                      * ---- ⛔⛔ THE STATUS SLOT IS NOT EMPTY WHEN THERE IS NO LOAD ----
                                      *
                                      * > Pete: *"not showing the header info … matching the header from
                                      * > the driver card"* — on an arrival with no appointment, where
                                      * > truck number, carrier, status and door are ALL legitimately
                                      * > absent and the header rendered a time and nothing else.
                                      *
                                      * ⛔⛔ `statusWord` RETURNS NULL HERE BY DESIGN, and its own comment
                                      * says why: *"the card already shouts 'No appointment'"*. ⚠️ THAT
                                      * JUSTIFICATION IS ABOUT THE CARD. This popup inherited the null and
                                      * did NOT inherit the shout — the fact was demoted to grey body text
                                      * reading "Not on a load yet", eight lines further down. §4.14: a
                                      * decision that was true where it was written, reused where its
                                      * precondition does not hold.
                                      *
                                      * ⭐ SAME WORD, SAME AMBER, SAME SINGLE HANDLER. `onCreateFromCheckIn`
                                      * is the card's one path to the prefilled new-appointment dialog —
                                      * calling it here is not a second route to that screen, which is
                                      * exactly what keeps the prefill from drifting.
                                      *
                                      * ⚠️ IT CLOSES THIS DIALOG FIRST. Two modals stacked leaves the
                                      * lower one's overlay swallowing clicks on the upper — the same
                                      * pointer-events trap `arrivalIcons(…, inDialog)` already exists to
                                      * dodge for the (…) menu.
                                      */}
                                    {!word && editFor.appointmentId === null && (
                                        <button
                                            type="button"
                                            onClick={() => { const c = editFor; setEditFor(null); onCreateFromCheckIn(c); }}
                                            title="Create an appointment for this arrival"
                                            className="flex items-center gap-1 rounded px-0.5 font-medium
                                                       text-amber-800 hover:bg-amber-100"
                                        >
                                            · <span className="underline decoration-dotted">No appointment</span>
                                        </button>
                                    )}
                                    {/* ⛔ THE ARRIVAL'S OWN `doorCode`, which is what the card
                                        shows. `AppointmentDto` carries no door field — the
                                        first cut read one off it and did not compile, which
                                        is the good version of that mistake. */}
                                    {editFor.doorCode && (
                                        <span className="flex items-center gap-0.5">
                                            · <DoorOpen className="h-3 w-3" />{editFor.doorCode}
                                        </span>
                                    )}
                                    {warns.length > 0 && (
                                        <span
                                            title={warns.join('\n')}
                                            aria-label={`${warns.length} warning${warns.length === 1 ? '' : 's'}: ${warns.join(' ')}`}
                                            className="ml-auto flex shrink-0 items-center gap-0.5 text-amber-700"
                                        >
                                            <AlertTriangle className="h-4 w-4" />
                                            <span className="flex h-4 min-w-4 items-center justify-center rounded-full
                                                             bg-amber-600 px-1 text-[9px] font-bold leading-none text-white">
                                                {warns.length}
                                            </span>
                                        </span>
                                    )}
                                </div>
                            );
                        })()}
                    </DialogHeader>
                    <div className="text-xs space-y-2">
                    {/**
                      * ---- ⭐⭐ THE CARD'S OWN TOOL STRIP, ON THE POPUP ------------
                      *
                      * > Pete, 2026-09-10: *"an replication almost of the driver card
                      * > functionality wise"* … *"with the same ICONs and '...' on that
                      * > page."*
                      *
                      * ⛔ `arrivalIcons` IS THE CARD'S STRIP, NOT A COPY OF IT — see its
                      * note. `inDialog` changes two behaviours (the history, and the (…))
                      * and removes no icon, because "same ICONs" is the request.
                      */}
                    <div className="-mt-1 border-b pb-2">
                        {arrivalIcons(editFor, true)}
                    </div>

                    {/**
                      * ⭐ THE (…) LIST, INLINE. The same `cardMenuItems` the right-click
                      * menu builds, so this cannot drift from it: Attach / Find load / New
                      * appointment on an unmatched arrival, Text and Call on every one, the
                      * load's own menu appended verbatim.
                      */}
                    {dialogOptions && (
                        <div className="space-y-0.5 rounded border bg-muted/40 p-1">
                            {cardMenuItems(editFor).map((it, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    disabled={busy !== null}
                                    onClick={() => {
                                        /* ⛔ CLOSE FIRST. Several of these open a dialog of
                                           their own, and two modals stacked is how the editor
                                           ended up behind the options list. */
                                        setEditFor(null);
                                        it.onClick();
                                    }}
                                    className={'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left '
                                        + 'text-xs hover:bg-muted disabled:opacity-40 '
                                        + (it.destructive ? 'text-destructive' : '')}
                                >
                                    {it.icon}
                                    {it.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/**
                      * ⚠️ THE LOAD, WHEN THERE IS ONE. ⛔ ~~"Not on a load yet"~~ NO LONGER
                      * RENDERS HERE: the header now carries the card's amber "No
                      * appointment" marker, and a grey restatement of it eight lines lower
                      * was the SECOND place this dialog said one thing twice (the first
                      * being the duplicated title). The carrier moved into the header row
                      * with the rest of the card's facts for the same reason.
                      */}
                    {editFor.appointmentNumber && (
                        <div className="text-[11px] text-muted-foreground">
                            On {editFor.appointmentNumber}
                        </div>
                    )}

                    {/* ⛔ ~~A SECOND "Edit arrival #N" HEADING WAS HERE~~ and is gone
                        (2026-09-10). This body was written to stand alone and was later
                        given a DialogHeader, so it printed its own title underneath the
                        real one — twice on one dialog, in Pete's screenshot. */}
                    {/* ⛔ Arrival number and truck number are absent ON PURPOSE.
                        Both are derived from check-in order, so a typed-over value
                        means two trucks claiming the same number with nothing able
                        to arbitrate. A wrong number is fixed by voiding and
                        checking in again, which renumbers the whole day. */}
                    <p className="text-muted-foreground">
                        The arrival and truck numbers are generated from check-in order and
                        cannot be edited. Void and check in again to renumber.
                    </p>
                    {([
                        ['driverName', 'Driver'],
                        ['driverPhone', 'Phone'],
                        ['carrierCode', 'Carrier'],
                        ['poNumber', 'PO'],
                        ['bolNumber', 'BOL'],
                        ['referenceNumber', 'Reference'],
                        ['containerNumber', 'Container'],
                        ['trailerNumber', 'Trailer'],
                        ['trailerPlate', 'Trailer plate'],
                    ] as const).map(([key, label]) => (
                        <label key={key} className="block">
                            <span className="text-muted-foreground">{label}</span>
                            <input
                                className="mt-0.5 w-full rounded border px-2 py-1 text-xs"
                                value={draft[key] ?? ''}
                                onChange={(e) => setDraft(d => ({ ...d, [key]: e.target.value }))}
                            />
                        </label>
                    ))}
                    <div className="flex gap-2 pt-1">
                        <button
                            type="button"
                            className="rounded border px-2 py-1 text-[11px]"
                            onClick={() => setEditFor(null)}
                            disabled={busy !== null}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="rounded border bg-primary px-2 py-1 text-[11px] text-primary-foreground disabled:opacity-40"
                            onClick={() => void saveEdit()}
                            disabled={busy !== null}
                        >
                            {busy === editFor.checkInId ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                    </div>
                    </>
                    )}
                </DialogContent>
            </Dialog>

            {voidFor && (
                <div className="border-t p-3 text-xs space-y-2">
                    <div className="font-medium">Void arrival #{voidFor.arrivalSeq ?? '—'}</div>
                    {/* ⚠️ Voiding CLOSES THE SEQUENCE UP behind it — the view excludes
                        voided rows — so a client never sees trucks #1, #2, #4 with no #3
                        to explain. Correct, but worth saying out loud before it happens. */}
                    <p className="text-muted-foreground">
                        The row is kept, not deleted, but it leaves today&apos;s arrival order and the
                        trucks behind it renumber. A reason is required.
                    </p>
                    <input
                        autoFocus
                        value={voidReason}
                        onChange={(e) => setVoidReason(e.target.value)}
                        placeholder="Duplicate scan, wrong entry…"
                        className="w-full rounded border px-2 py-1"
                    />
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => void doVoid()}
                            disabled={voidReason.trim().length < 3 || busy !== null}
                            className="rounded border px-2 py-1 font-medium text-destructive disabled:opacity-40"
                        >
                            Void it
                        </button>
                        <button
                            type="button"
                            onClick={() => { setVoidFor(null); setVoidReason(''); }}
                            className="rounded px-2 py-1 text-muted-foreground"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/**
              * ⭐ #143 — LIVE LABOUR, UNDER THE ARRIVALS. Pete, 2026-08-24: *"Lets also
              * put the labor slide in with the Paperwork (under Driver Checkins)"*.
              *
              * ⚠️ INSIDE THIS RAIL, NOT BESIDE IT — the same reason the note below
              * gives for paperwork. This <aside> is a fixed 340px column; a second
              * panel next to it would become a second column and halve the schedule.
              *
              * ⭐ AND IT SHARES onHoverAppointment WITH THE CARDS ABOVE, so hovering a
              * worker outlines the truck he is on in the grid — the cross-highlight
              * that already links an arrival card to its block.
              */}
            {/**
              * ⛔ PAPERWORK SITS ABOVE LABOR, AND THE ORDER IS THE FEATURE.
              *
              * Pete, 2026-08-25: *"maybe you swap Labor with Paperwork so you can drag
              * the paperwork directly to the checkin drivers right above it."*
              *
              * ⭐ THE DRIVER CARDS ARE DIRECTLY ABOVE THIS. Dropping a scan on a driver
              * is now a few pixels of travel instead of a trip past the whole grid —
              * and a drag that never crosses the schedule cannot be fumbled onto an
              * appointment by accident on the way.
              *
              * ⚠️ A SLOT, NOT A SIBLING. This <aside> is a fixed 340px column, so a
              * second panel beside it in the page would become a second column and
              * halve the schedule. Paperwork belongs inside this rail.
              */}
            {/**
              * ⭐⭐ ONE SCROLLER PER SECTION — NOT ONE FOR ALL THREE (2026-08-27).
              *
              * Pete: *"We don't want the sections to keep expanding down so you can't
              * see the others, they each need to have a slider."*
              *
              * ⛔ A SINGLE SCROLLER ROUND ALL THREE WAS THE WRONG SHAPE and he is
              * right about why: it lets a long Scanned Paperwork list push Labor and
              * Communications below the fold, so finding them means scrolling past
              * paperwork every time. Three panels, three independent scrollbars.
              *
              * ⭐ `shrink` WITH `basis-auto`, NOT `flex-1`. This is the whole trick:
              *   · flex-grow 0 + basis auto → a short section takes ONLY its own
              *     height, so a collapsed Communications is one row of chrome and
              *     claims nothing;
              *   · flex-shrink 1 + min-h-0  → when the three together overflow, they
              *     give up space IN PROPORTION to their size and each scrolls inside
              *     what is left.
              * ⚠️ `flex-1` would have forced equal thirds and handed a third of the
              * column to a section that is switched off.
              *
              * ⛔ AND THE OUTER BOX DOES NOT SCROLL — `overflow-hidden`. A scrollbar
              * here would be a fourth one, and nesting scrollers is how a wheel event
              * moves the wrong list.
              */}
            {/**
              * ⭐⭐ THE WRAPPERS NO LONGER SCROLL — THE PANELS DO (2026-08-27).
              *
              * Pete: *"when you scroll those sections, the 'Scanned Paperwork' title bar
              * should not disappear."*
              *
              * ⛔ EACH WRAPPER WAS `overflow-y-auto`, WHICH PUT THE SCROLLBAR AROUND THE
              * WHOLE PANEL — header included. The header answered with `sticky top-0`,
              * which works right up until a wheel event chains from the document list's
              * own scroller to this one, and then the header travels with it.
              *
              * ⭐ NOW EACH PANEL OWNS ITS SCROLLING INTERNALLY: header `shrink-0`, body
              * `overflow-y-auto`. These wrappers only need to bound the height, so
              * `overflow-hidden` is exactly right — and a header outside every scroller
              * cannot scroll away no matter how the rail is resized.
              *
              * ⚠️ `shrink` + basis-auto IS UNCHANGED (#283) and still does the real work:
              * a folded section claims one row, and the three share what is left in
              * proportion when they overflow.
              */}
            {/**
              * ---- ⭐⭐ THE THREE OPTIONAL CARDS, PER FACILITY (migration 172) --------
              *
              * ⛔ THE OUTER BOX IS GATED TOO, not just its children. It carries the
              * `border-t` that separates the rail's cards from the driver list; with
              * all three switched off, leaving it in place would draw a hairline under
              * an empty region — a divider dividing nothing, which reads as a card that
              * failed to load rather than one nobody configured.
              *
              * ⛔⛔ AND `CHECKIN` IS NOT GATED ANYWHERE. It is `IsOptional = 0` in the
              * vocabulary and `setRailPanel` refuses to switch it off, but the deeper
              * reason is that this component IS the check-in panel — a flag that could
              * hide it would be a flag that empties the screen it is configuring.
              */}
            {/* ⚠️ ~~`shrink-0 max-h-[60%]`~~ — the cap was what stopped an expanded card
                reaching the bottom of the column, and `shrink-0` stopped the group
                claiming the space the driver list had given up. Both go when a card is
                open; with none open the group is three headers and should not grow.
                ⛔ THIS COMMENT SITS ABOVE THE CONDITIONAL, NOT INSIDE IT. A braced JSX
                comment is a CHILD, not an expression, so placing one directly after
                `&& (` is a parse error — the same slip as the tool strip on 2026-09-08.
                ⚠️ And do not quote one INSIDE a comment: its closing star-slash ends the
                comment early, which is how this note failed on its first attempt. */}
            </div>
            {/* ---- COLUMN 2: everything else ---- */}
            {anyPanelConfigured && (
            <RailSlotContext.Provider value={railSlot}>
            <div className={twoColumn
                ? `w-[340px] shrink-0 flex min-h-0 flex-col border ${FRAME_BORDER} rounded-lg bg-card overflow-hidden`
                : 'contents'}>
            {/* ⛔ `border-t` ONLY IN ONE-COLUMN MODE. Side by side it would draw a
                hairline across the top of a card that already has a border. */}
            <div className={`flex flex-col overflow-hidden ${twoColumn ? 'min-h-0 flex-1' : `border-t ${anyPanelOpen
                ? 'min-h-0 flex-1'
                : 'shrink-0'}`}`}>
            {/* ⚠️ `footer` IS THE PAPERWORK CARD and the page decides WHICH one —
                `PaperworkSliver` on a SharePoint site, `ScanFolderPanel` on a local
                one, exactly one of the two ever mounted (migration 162's rule carried
                up to the UI). ⭐ This flag says whether the card is AVAILABLE; it must
                never be read as choosing between those two implementations. */}
            {/**
              * ---- ⛔⛤ ~~THE RESET BAR~~ — REMOVED THE DAY IT SHIPPED (§0.3) --------
              *
              * ⭐ Pete: *"instead of putting the Show ALL panel above Attached Paperwork,
              * I think we remove that panel entirely and put a little circle R with a
              * tooltip next to the circle i on the driver panel."*
              *
              * ⚠️ IT WENT THROUGH THREE PLACEMENTS IN ONE EVENING — below the paperwork
              * panel, above it, then gone — and the third is right for a reason the first
              * two missed: **it was a whole row of chrome for a state that already has a
              * name three lines above it.** The title says `Client · HELLMAN`; a bar
              * repeating "Showing HELLMAN (client)" underneath it is the grey-box fault in
              * larger type.
              *   ** IF THE STATE IS ALREADY ON SCREEN, THE EXIT DOES NOT NEED TO RESTATE
              *      IT — IT NEEDS TO BE NEXT TO IT. **
              * ✅ The `R` now sits beside the ⓘ in the title row. See there.
              */}
            {panelOn('PAPERWORK') && (
            <div {...slotProps('PAPERWORK')}>
            {footer}

            </div>
            )}
            {/**
              * ---- ⭐⭐ WMS ORDERS (#333), BETWEEN PAPERWORK AND LABOR --------
              *
              * ⭐ SortOrder 25 in `dock.RailPanel` (migration 175) puts it here:
              * the rail reads in the order the day happens — the truck arrives,
              * its paperwork is filed, the ORDER is what the paperwork is about,
              * then the labour to work it, then the messages about it.
              *
              * ⚠️ IT NEEDS A SCOPE TO SAY WHAT IT IS SHOWING (§1Z.2), so it does
              * not mount without one. ⛔ A panel that cannot state its range is
              * the ±90-day shape §1Z.1 replaced.
              */}
            {panelOn('ORDERS') && ordersScope && (
            <div {...slotProps('ORDERS')}>
            <OrdersPanel
                facilityCode={facilityCode}
                getToken={getToken}
                /* ⭐ Pete 2026-09-25 — the rail follows the board. ⚠️ An UNBOOKED
                   order has no appointment and survives it; see lib/boardScope.ts. */
                boardApptIds={boardApptIds}
                /**
                  * ---- ⭐⭐⭐ THE DENOMINATOR FOR "12 / 36" (Pete, 2026-09-27) -----
                  *
                  * > **"Three numbers: 36 appts / 12 matched / 15 orders"**
                  *
                  * ⭐ `appointments.length`, NOT A NEW PROP FROM THE PAGE. This prop is
                  * the RAW day list — its own docstring calls it *"Today's appointments,
                  * for the attach picker"* and the page passes it WHOLE precisely so a
                  * filtered-off truck can still be attached to. So it is already the
                  * unfiltered count `Calendar.tsx` shows in the status bar, arrived at
                  * from the same array, and it cannot drift from it.
                  *   ** THE NUMBER YOU CAN DERIVE FROM DATA YOU ALREADY HOLD NEEDS NO
                  *      WIRE, AND A WIRE IS A THING THAT CAN BE FORGOTTEN. **
                  *
                  * ⛔⛔ AND IT IS WITHHELD WHEN THE SCOPE IS NOT ONE DAY. `appointments`
                  * is a DAY; `ordersScope` is whatever range the page asked the panel
                  * for. They are the same thing today — this rail is day-only, which is
                  * why `boardApptIds` is day-only too — but if that ever stops being
                  * true, a week's orders over a day's appointments is a ratio that reads
                  * fine and is wrong.
                  *   ** A NUMBER THAT IS CORRECT BECAUSE OF A CONDITION ELSEWHERE MUST
                  *      CHECK THAT CONDITION, NOT INHERIT IT. **
                  * ⭐ `undefined` makes the panel draw two numbers instead of three,
                  * which is the honest degradation.
                  */
                apptsInRange={ordersScope.fromDate === ordersScope.toDate
                    ? appointments.length
                    : undefined}
                /* ⭐ Pete, 2026-09-27 — the toolbar Refresh is a SCREEN refresh, and
                   this was the one panel it did not reach. Its three siblings below
                   have taken this prop all along. */
                refreshKey={refreshKey}
                fromDate={ordersScope.fromDate}
                toDate={ordersScope.toDate}
                scopeLabel={ordersScope.scopeLabel}
                /* ⭐⭐ Pete, 2026-09-27 — hover scopes, click latches. ⛔ An order row
                   sends its BOOKINGS, not an id: it can be on several trucks, and
                   `focusFromBookedOrder` is the only builder that copes. */
                onHoverOrder={railHoverOrder}
                onChooseOrder={railChooseOrder}
                focus={railScope('orders')}
                onClearFocus={onClearRailFocus}
                onCreateFromOrder={onCreateFromOrder
                    ? (row) => onCreateFromOrder({
                        orderNumber: row.orderNumber,
                        wmsSource: row.wmsSource,
                        orderType: row.orderType,
                    })
                    : undefined}
                onMatchOrder={onMatchOrder}
            />
            </div>
            )}
            {panelOn('LABOUR') && (
            <div {...slotProps('LABOUR')}>
            <LabourPanel
                facilityCode={facilityCode}
                date={date}
                getToken={getToken}
                boardApptIds={boardApptIds}
                refreshKey={refreshKey}
                /* ⭐⭐ THE RING **AND** THE SCOPE (Pete, 2026-09-27). The `'rail'` call
                   still marks the matching chip exactly as before; `railHoverAppt` adds
                   the half that was missing — a real focus, so the board narrows and the
                   other four rails follow. ⛔ Both, not one: the ring is what tells you
                   WHICH chip, and the scope is what Pete asked for. */
                onHoverAppointment={(id, origin) => {
                    onHoverAppointment?.(id, origin);
                    railHoverAppt('labour', id);
                }}
                onChooseAppointment={railChooseAppt}
                /* ⛔ ITS OWN SCOPE EXCLUDES ITS OWN PREVIEW — see `railScope`. */
                focus={railScope('labour')}
                onClearFocus={onClearRailFocus}
            />

            {/**
              * ⭐ BELOW LABOR, WHICH IS WHERE PETE ASKED FOR IT (#263):
              * *"a Communication slider below labor that shows us all the
              * communications as they go out."*
              *
              * ⚠️ NO `date` PROP, AND THAT IS DELIBERATE. This is a rolling window
              * over `CreatedUtc` — when the engine ACTED — not a facility-local
              * day. Tying it to the date picker would empty the panel whenever a
              * scheduler looked at tomorrow's board, which is exactly when an
              * automated send is most worth watching.
              *
              * ⛔ IT STARTS OFF. Its switch is in its own header and the choice is
              * remembered per browser; a feed that turns itself on for everybody
              * is a feed that polls for people who never asked for it.
              */}
            </div>
            )}
            {panelOn('COMMS') && (
            <div {...slotProps('COMMS')}>
            <CommunicationsPanel
                facilityCode={facilityCode}
                /* ⛔ DECISION 215's party scope is keyed on the day. A signature
                   change ships every caller; this is the only one. */
                date={date}
                getToken={getToken}
                refreshKey={refreshKey}
                boardApptIds={boardApptIds}
                /* ⛔ ITS OWN SCOPE EXCLUDES ITS OWN PREVIEW — see `railScope`. */
                focus={railScope('comms')}
                onChooseAppointment={railChooseAppt}
                onClearFocus={onClearRailFocus}
                /* ---- ⭐⭐ THE CROSS-HIGHLIGHT (Pete, 2026-09-23) --------------
                   > *"when I hover over the communications it doesn't highlight the
                   > appointment ... not sure it highlights all the communications
                   > asscoiated the appointment."*

                   ⛔ THESE TWO LINES ARE THE WHOLE FIRST HALF OF THE BUG. Both
                   values were already in scope here — `LabourPanel`, thirty lines
                   above, takes the first one — and this mount simply never passed
                   them, so a message could neither mark nor be marked.
                     ** A PROP THAT IS IN SCOPE AND NOT PASSED LOOKS EXACTLY LIKE A
                        FEATURE THAT WAS NEVER ASKED FOR. **

                   ⚠️ THE PANEL SENDS ORIGIN `'rail'`. That is what keeps
                   `previewFocus` null and stops the list re-querying under its own
                   cursor — see the row in CommunicationsPanel.tsx. */
                /* ⭐⭐ THE RING **AND** THE SCOPE (Pete, 2026-09-27) — identical to the
                   Labour mount above and for the same reason: the `'rail'` call marks the
                   matching chip, `railHoverAppt` adds the focus that narrows the board. */
                onHoverAppointment={(id, origin) => {
                    onHoverAppointment?.(id, origin);
                    railHoverAppt('comms', id);
                }}
                highlightAppointmentId={highlightAppointmentId}
            />
            </div>
            )}
            {/**
              * ---- ⭐⭐ HISTORY (Pete, 2026-09-10) ----------------------------
              *
              * > *"a new panel called History. Same concept as the others, when you
              * > hover a driver, it shows you the driver history. When you hover the
              * > appointment, it shows you the appointment history."*
              *
              * ⛔ IT SHIPS IN THE SAME CHANGE THAT REMOVES THE CARD'S ARRIVAL HISTORY
              * — *"We can then remove Arrival History from the Driver Card, so it
              * manages the space better."* Shipping one without the other would put
              * the same eight lines on screen twice.
              */}
            {panelOn('HISTORY') && (
            <div {...slotProps('HISTORY')}>
            <HistoryPanel
                getToken={getToken}
                /* ⛔ DECISION 215's party branch needs the frame — BOTH halves, the
                   site and the day. A signature change ships every caller; this is
                   the only one, checked not recalled. */
                facilityCode={facilityCode}
                date={date}
                refreshKey={refreshKey}
                focus={effFocus ?? effParty}
                onClearFocus={onClearRailFocus}
            />
            </div>
            )}
            </div>
            </div>
            </RailSlotContext.Provider>
            )}
        </aside>
    );
}
